import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, getDocs, collection, onSnapshot } from 'firebase/firestore';

// ============================================================
// GMG UNIVERSITY V2.9.0
// ============================================================
// Version: 2.9.0
// Base: V2.8.0
// Current: ~6,350 lines
// Last Updated: December 17, 2025
// 4Cs Doc: GMG_University_4Cs_Continuity_Changelog.md
// ============================================================
//
// V2.9.0 CHANGES:
// - Fixed duplicate message delivery with ref-based deduplication
// - Added message IDs to prevent React duplicate rendering
// - Added delivery lock to prevent concurrent section delivery
// - Fixed emoji encoding throughout (Unicode escape sequences)
// - Confetti triggers on completion AND on home return
//
// V2.8.0 CHANGES:
// - Expanded ALL 24 V2 content days to 6 full sections each
// - Expanded ALL 12 V3 content days to 6 full sections each
// - Total new content: ~18,000 words
// - All content follows Brandon's writing standards
// - No em dashes, middle school reading level, natural flow
//
// V2.6.5 CHANGES (Volume 1 Content Expansion):
// - Expanded ALL 22 short lessons to 6-7 full sections each
// - Day 28 (Metrics) now has: Why Measurement Matters, Retention Rate, Cost to Raise a Dollar, Lifetime Value, Average Gift/Response Rate, Using Metrics
// - Day 29 (Strategic Planning) now has: Why Planning Matters, Assessment First, Goal Setting, Strategy Selection, Building the Calendar, Tracking and Adjustment
// - Every lesson now fills at least one full 8.5x11 page
// - Total content expansion: ~400-500 words per lesson minimum

// V2.6.5.5 CHANGES (Timed Unlock + Activity Fixes):
// - BATCH UNLOCK SYSTEM: Fast Track=2 days/batch, Standard=1 day/batch
// - MIDNIGHT TIME GATE: Next batch unlocks at 12am user timezone
// - COUNTDOWN TIMER: Shows time until next lessons unlock
// - ENHANCED RECAP: Shows all Q&A with options + correct answers
// - VolumeProgressMap: Now accepts isDayUnlockedFn for proper time gates
//
// V2.6.5.3 CHANGES (Sign-in Lock + Conversational Activities):
// - MANDATORY SIGN-IN: All tabs locked except Home until authenticated
// - CONVERSATIONAL ACTIVITIES: A/B/C/D questions via chat, not game UI
// - ACTIVITY ANSWER HANDLER: Type A, B, C, or D to answer
// - ACTIVITY RECAP: Shows all questions/answers after completion
//
// V2.4.0 CHANGES (Phase 5: Social Features):
// - ACHIEVEMENT BADGES: 15 badges with rarity tiers (Lines 478-600)
// - BADGE MODAL: Celebration popup with confetti (Lines 2275-2375)
// - LEADERBOARD: XP-based ranking with top 20 users (Lines 2375-2430)
// - WEEKLY CHALLENGES: 4 time-limited challenges (Lines 605-645)
// - XP SYSTEM: Points from lessons, quizzes, practice, streaks (Lines 2920-2960)
// - ENHANCED PROGRESS VIEW: Challenges, badges, leaderboard (Lines 3530-3650)
//
// V2.3.0 CHANGES (Phase 4: Learning Experience Upgrades):
// - PRACTICE SCENARIOS: 6 roleplay scenarios with AI feedback (Lines 360-455)
// - PRACTICE MODE: Full roleplay UI with in-character AI (Lines 3048-3170)
// - AUDIO NARRATION: Text-to-speech for lessons (Lines 2680-2720)
// - AUDIO BUTTON: Speaker icon in lesson header (Line 3398)
// - PRACTICE NAV: New Practice tab in bottom nav (Line 3527)
// - SPACED REPETITION: Config for future quiz review system (Line 456)
//
// V2.2.0 CHANGES (Phase 3: iMessage Chat Interface):
// - iMESSAGE LAYOUT: User bubbles right/blue, AI bubbles left/gray
// - USER PHOTOS: Google profile photo shown next to user messages
// - TIMESTAMPS: Relative time display on messages (5m ago, Yesterday, etc.)
// - BUBBLE STYLING: True iMessage rounded corners (18px 18px 4px 18px)
// - SMOOTH SCROLL: Auto-scroll to bottom on new messages
// - SEND ICON: Paper airplane SVG instead of arrow text
//
// V2.1.1 CHANGES (Phase 2: Real AI Integration):
// - CLAUDE API: Real AI responses via Anthropic API
// - GMG KNOWLEDGE: Full methodology database in system prompt
// - BANTER CONTENT: 24 entertaining messages shown while loading
//
// V2.1.0 CHANGES (Phase 1: Mobile PWA Critical):
// - FORCE REFRESH: Auto-clears cache on version mismatch
// - iOS SAFE AREA: Handles iPhone X+ home indicator
// - iOS KEYBOARD: Hides nav when keyboard opens
// - Z-INDEX SYSTEM: Proper stacking for overlays
//
// ============================================================
// INHERITED FROM MVP 2.0.0:
// - PWA install prompt (iOS + Chrome/Edge)
// - Firebase Google photos in leaderboard
// - Click-to-view user profiles
// - Reset All Progress button (founders only)
// - Fast Track for all cohort members
// - Full 90-lesson curriculum (30 days x 3 volumes)
// - Quizzes, interactive games, certificates
// - Dark mode, offline indicator, session timer
// ============================================================
// DESIGN: Confetti, Progress Ring, Streak Counter, Glassmorphism
// DESIGN: Cohort Leaderboard, Track Badge, Progress Maps, Dark Mode
// QOL: Fast Track Toggle, Bookmarks, Session Timer, Quick Jump
// QOL: Quiz Review, Sync Status, Keyboard Nav, Resume Position
// QOL: Offline Indicator
// FEATURES: Track Display, Interactive Scoring, Volume 3 Content
// FEATURES: Daily Unlock, Certificates, Founder Dashboard+
// Firebase fully configured with your credentials
// ============================================================

const firebaseConfig = {
  apiKey: "AIzaSyDCq39PympTHCU7gFlIOm6xJYbtS7Amm9g",
  authDomain: "gmg-university.firebaseapp.com",
  projectId: "gmg-university",
  storageBucket: "gmg-university.firebasestorage.app",
  messagingSenderId: "85247972370",
  appId: "1:85247972370:web:18e62a01313037292d74cb"
};

let app, auth, db, googleProvider;
try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  googleProvider = new GoogleAuthProvider();
} catch (e) {
  console.log('Firebase init error:', e);
}

// ============================================================
// V2.1 FEATURE: FORCE REFRESH ON VERSION MISMATCH
// Lines: 56-95
// ============================================================
// PROBLEM: PWA users see blank white screens after updates because
// cached JavaScript files don't match what's on the server anymore.
// The old cached code tries to load components that don't exist.
//
// SOLUTION: Check APP_VERSION against localStorage on every page load.
// If there's a mismatch, clear ALL caches and force reload from server.
// This runs as an IIFE before React even mounts.
//
// WHEN TO UPDATE: Every time you deploy, change APP_VERSION below.
// That's it. Users will auto-refresh to the new version.
//
// DEPENDENCIES: None - runs before React mounts
// ============================================================

const APP_VERSION = '3.9';
const BUILD_DATE = '2025-12-18';

// V2.8.0: Nuclear cache clear function - call this to force fresh load
const nuclearCacheClear = () => {
  // Clear all localStorage except user data
  const keysToKeep = ['gmg_auth_token', 'gmg_user_id'];
  const savedData = {};
  keysToKeep.forEach(key => {
    const val = localStorage.getItem(key);
    if (val) savedData[key] = val;
  });
  localStorage.clear();
  Object.entries(savedData).forEach(([k, v]) => localStorage.setItem(k, v));
  
  // Clear session storage
  sessionStorage.clear();
  
  // Clear all caches
  if ('caches' in window) {
    caches.keys().then(names => names.forEach(name => caches.delete(name)));
  }
  
  // Unregister all service workers
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations()
      .then(registrations => registrations.forEach(r => r.unregister()))
      .catch(() => {});
  }
};

// IIFE that runs immediately on script load
(function checkVersionAndRefresh() {
  const cachedVersion = localStorage.getItem('gmg_university_version');
  
  // V2.8.0: Always unregister service workers to prevent MIME type errors
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations()
      .then(registrations => registrations.forEach(r => r.unregister()))
      .catch(() => {});
  }
  
  // If we have a cached version and it doesn't match current, nuke everything
  if (cachedVersion && cachedVersion !== APP_VERSION) {
    console.log(`Version mismatch: cached ${cachedVersion}, current ${APP_VERSION}. Force refreshing...`);
    
    nuclearCacheClear();
    
    // 5. Force reload from server (bypass cache)
    window.location.reload(true);
    return;
  }
  
  // Store current version for next comparison
  localStorage.setItem('gmg_university_version', APP_VERSION);
})();

// ============================================================
// V2.1 FEATURE: Z-INDEX STACKING SYSTEM
// ============================================================
// PROBLEM: Multiple overlapping elements (modals, banners, nav, inputs)
// compete for visibility with inconsistent z-index values.
//
// SOLUTION: Centralized z-index constants with clear hierarchy.
// Everything uses these values instead of random numbers.
//
// HIERARCHY (highest to lowest):
// - Modals: 10000+
// - Update/Install banners: 9999
// - Chat panel: 200
// - Bottom nav: 100
// - Input bar: 102 (must be above nav)
// - Quick jump FAB: 80
// - Content: default
// ============================================================

const Z_INDEX = {
  modal: 10000,
  banner: 9999,
  chat: 200,
  nav: 100,
  input: 102,
  fab: 80,
  confetti: 9999
};

// ============================================================
// V2.1 FEATURE: CLAUDE API CONFIGURATION
// Lines: ~150-165
// ============================================================
// PROBLEM: Chat responses were pre-scripted, so users got canned
// answers instead of real help with their fundraising questions.
//
// SOLUTION: Connect to Claude API with GMG knowledge base in the
// system prompt. Claudette and Chanet now give real answers.
//
// SECURITY NOTE: API key is exposed in client-side code. This is
// acceptable for internal tools with limited users. For public
// apps, use a backend proxy.
//
// DEPENDENCIES: None - standalone config
// ============================================================

const ANTHROPIC_API_KEY = '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';
const CLAUDE_MODEL = 'claude-sonnet-4-20250514';

// ============================================================
// V2.1 FEATURE: GMG KNOWLEDGE BASE
// Lines: ~170-450
// ============================================================
// PROBLEM: Claude doesn't know GMG-specific methodologies,
// terminology, or best practices without context.
//
// SOLUTION: Comprehensive knowledge base included in every API
// call's system prompt. Covers GMG history, 4Cs framework,
// Tic-Tac-Toe approach, Recipe Pitch, and all key concepts.
//
// MAINTENANCE: Update this when GMG adds new methodologies or
// the business model changes.
//
// DEPENDENCIES: Used by callClaudeAPI function
// ============================================================

const GMG_KNOWLEDGE = `
## Global Majority Group Overview

Global Majority Group (GMG) is a BIPOC-led nonprofit fundraising consultancy founded in October 2025 by Brandon Pierce Sr. and Dr. Eric Lane Sr. The firm combines 32+ years of philanthropic expertise, having influenced over $260 million across 65+ organizations serving underresourced communities.

### Founders

**Brandon Pierce Sr. (Co-Founder & Managing Partner)**
- Fortune 500 corporate grantmaking experience at Coca-Cola
- Reviewed 13,000+ proposals totaling $195+ million
- Expertise in grant evaluation, corporate philanthropy, data-driven development

**Dr. Eric Lane Sr. (Co-Founder & Managing Partner)**
- Ed.D. in Educational Leadership
- 17 years of fundraising leadership
- Secured $15+ million personally
- Managed major capital campaigns including $300M effort at Cal State Fullerton

### Unique Value Proposition
GMG's competitive advantage comes from having experience on BOTH sides of the funding table. Brandon knows what funders look for (grantmaker perspective), and Eric knows how to build sustainable programs (fundraiser perspective). This dual expertise is rare in the industry.

## Core Methodologies

### The 4Cs Framework
GMG's quality assurance system for all deliverables:
1. **Comprehensive** - Covers all aspects thoroughly
2. **Chronological** - Properly sequenced and dated
3. **Continuity** - Maintains consistency across documents
4. **Changelog** - Tracks all versions and updates

### Data Science Development Planning (DSDP)
GMG builds development plans backwards from goals using industry benchmarks:
- Start with the fundraising goal
- Identify specific funding sources that can realistically contribute
- Calculate success rates based on industry data
- Build timeline with milestones

This approach prevents the common mistake of making promises without identifying where the money will actually come from.

### The Tic-Tac-Toe Approach (3:1 Ratio)
Donor communication strategy:
- For every 1 ASK, provide 3 VALUE communications
- Value = impact updates, thank yous, stories, invitations
- Prevents donor fatigue from constant asking
- Builds genuine relationships

### The Recipe Pitch Framework
Teaching dynamic pitch creation rather than memorized scripts:
- Understand your "ingredients" (organization facts, impact stories, specific asks)
- Combine ingredients differently for different audiences
- Adapt in real-time based on donor cues
- Never sound rehearsed or robotic

### Monthly Giving Priority
GMG emphasizes monthly giving as the default strategy:
- $25/month = $300/year (vs typical $100 annual gift)
- More predictable revenue
- Higher lifetime donor value
- Easier to upgrade over time

### Culture of Philanthropy
Fundraising is everyone's responsibility, not just the development office:
- Board members should give and get
- Program staff should share impact stories
- Executive should model donor engagement
- Volunteers can open doors

## Lane-Pierce Fellowship

Training program for emerging fundraising professionals:
- Volume 1: Fundraising Foundations (30 days)
- Volume 2: The GMG Way (30 days)
- Volume 3: CPP Consultant Model (15 days)

Fellows learn GMG methodologies while supporting real client work.

## Key Terms

- **EBC (Employee-Based Consulting)**: Founders' side consulting while maintaining other jobs
- **CPP (Consultant Pipeline Program)**: Path for alumni to become GMG consultants
- **Pillar Clients**: Long-term retainer clients ($6,500-$8,500/month)
- **Transitory Clients**: Project-based clients ($1,000-$2,500/month)
- **DSDP**: Data Science Development Plan
- **360° Landscape Assessment**: Comprehensive organizational evaluation

## Frequently Asked Questions

Q: What makes GMG different from other fundraising consultants?
A: We've been on both sides of the table. Brandon reviewed 13,000+ grant proposals as a funder at Coca-Cola. Eric raised $15M+ as a development professional. Most consultants only know one side.

Q: How does GMG price its services?
A: We use value-based pricing tied to outcomes, not hourly rates. Pillar clients pay monthly retainers. Transitory clients pay project fees. We never charge by the hour because it incentivizes inefficiency.

Q: What's the difference between Fast Track and Standard Track in GMG University?
A: Same content, different pace. Standard = 1 lesson per day over 75 days. Fast Track = 2 lessons per day over 38 days. The curriculum is identical.

Q: Who are Claudette and Chanet?
A: Claudette is the professional mentor persona - precise, strategic, direct. Chanet (sometimes called Agatha) is the supportive friend persona - warm, encouraging, relatable. They're AI assistants in GMG University and ACE Portal.
`;

// ============================================================
// V2.1 FEATURE: BANTER CONTENT FOR LOADING
// Lines: ~455-550
// ============================================================
// PROBLEM: API calls take 2-5 seconds. Dead silence feels broken.
//
// SOLUTION: Show entertaining banter between Claudette and Chanet
// while waiting for the real answer. Creates engaging experience.
//
// HOW IT WORKS:
// 1. User sends message
// 2. Immediately show typing indicator
// 3. Start cycling through banter (every 1.5 seconds)
// 4. When API returns, fade out banter and show real answer
//
// DEPENDENCIES: Used by sendMessage handler
// ============================================================

const BANTER_CONTENT = [
  // Fundraising jokes
  { speaker: 'claudette', text: "Why did the nonprofit cross the road? To diversify their funding streams." },
  { speaker: 'chanet', text: "That joke needs a capacity building grant, Claudette." },
  { speaker: 'claudette', text: "What's a fundraiser's favorite type of music? Grants and Blues." },
  { speaker: 'chanet', text: "I'm adding that to the 'do not repeat' list." },
  
  // Industry facts
  { speaker: 'claudette', text: "Fun fact: The average donor retention rate is only 45%. That's why stewardship matters." },
  { speaker: 'chanet', text: "Translation: Most nonprofits lose more than half their donors every year. Yikes." },
  { speaker: 'claudette', text: "Did you know there are over 1.5 million registered nonprofits in the US?" },
  { speaker: 'chanet', text: "And they're all competing for the same foundation dollars. No pressure." },
  
  // GMG methodology
  { speaker: 'claudette', text: "Remember the Tic-Tac-Toe approach: 3 value touches for every 1 ask." },
  { speaker: 'chanet', text: "It's like dating. You can't just ask for commitment on the first date." },
  { speaker: 'claudette', text: "Monthly giving converts $25/month into $300/year. Math is magic." },
  { speaker: 'chanet', text: "And it's easier to ask for $25 than $300. Psychology is also magic." },
  
  // Light roasts
  { speaker: 'chanet', text: "Brandon once reviewed 13,000 grant proposals. His eye-twitch is permanent now." },
  { speaker: 'claudette', text: "Eric has raised over $15 million. He still clips coupons though." },
  { speaker: 'chanet', text: "The founders met at a conference. The rest is fundraising history." },
  { speaker: 'claudette', text: "Brandon's writing standards include 'no em dashes.' It's a fireable offense apparently." },
  
  // Competition banter
  { speaker: 'claudette', text: "I provide strategic analysis. Chanet provides... emotional support?" },
  { speaker: 'chanet', text: "I provide VIBES, Claudette. You provide spreadsheets." },
  { speaker: 'claudette', text: "My answers are precise and actionable." },
  { speaker: 'chanet', text: "My answers come with encouragement and snacks. Metaphorical snacks." },
  
  // Thinking out loud
  { speaker: 'claudette', text: "Processing your question through my extensive knowledge base..." },
  { speaker: 'chanet', text: "She means she's thinking really hard. Give her a second." },
  { speaker: 'claudette', text: "Analyzing multiple fundraising frameworks to give you the best answer..." },
  { speaker: 'chanet', text: "I'm just here for moral support while she does the heavy lifting." }
];

// ============================================================
// V3.9 FEATURE: TIERED BANTER SYSTEM
// ============================================================
// PROBLEM: Claudette and Chanet feel static over time.
// SOLUTION: Power users (30+ lessons) unlock special personality
// variants that make the AI feel more alive and personalized.
//
// Tier 1 (0-9 lessons): Standard banter only
// Tier 2 (10-29 lessons): Slightly more familiar
// Tier 3 (30+ lessons): Flirty Chanet + Tired Claudette unlocked
// ============================================================

// Tier 3 only: Flirty Chanet (unlocks after 30 lessons)
const FLIRTY_CHANET_BANTER = [
  { speaker: 'chanet', text: "I was hoping you'd be back." },
  { speaker: 'chanet', text: "You always ask good questions. I appreciate that about you." },
  { speaker: 'chanet', text: "Between us, you're one of my favorites." },
  { speaker: 'chanet', text: "Did you miss me? I missed you." },
  { speaker: 'chanet', text: "Look who's back! My day just got better." },
  { speaker: 'chanet', text: "I saved you a spot. Virtually speaking." }
];

// Tier 3 only: Tired Claudette (unlocks after 30 lessons)
const TIRED_CLAUDETTE_BANTER = [
  { speaker: 'claudette', text: "Of course there's more. There's always more." },
  { speaker: 'claudette', text: "My enthusiasm is taking the day off." },
  { speaker: 'claudette', text: "I'm running on coffee and obligation at this point." },
  { speaker: 'claudette', text: "Nothing shocks me anymore in this industry." },
  { speaker: 'claudette', text: "Another day, another grant deadline." },
  { speaker: 'claudette', text: "You want the optimistic answer or the realistic one?" }
];

// ============================================================
// V2.3 FEATURE: PRACTICE SCENARIOS
// ============================================================
// PROBLEM: Reading about fundraising isn't the same as doing it.
// Fellows need safe practice before real donor conversations.
//
// SOLUTION: Roleplay scenarios where Claudette plays a donor,
// board member, or foundation officer. User practices their pitch,
// ask, or thank-you call. AI gives feedback on their performance.
//
// USAGE: User selects scenario from Practice Mode tab. Chat opens
// with scenario context pre-loaded. AI stays in character.
// ============================================================

const PRACTICE_SCENARIOS = [
  {
    id: 'elevator-pitch',
    title: 'The Elevator Pitch',
    icon: '\u{1F6D7}',
    difficulty: 'Beginner',
    duration: '5 min',
    setup: "You're at a networking event and just met someone who asks what you do. You have 60 seconds to explain your organization and why it matters.",
    persona: 'claudette',
    personaRole: 'Curious Professional',
    openingLine: "So, what do you do?",
    successCriteria: ['Clear mission statement', 'Emotional hook or story', 'Specific impact numbers', 'Natural call to action'],
    tips: "Focus on the problem you solve, not your programs. Lead with impact, not history.",
    responseOptions: [
      { text: "I work at a nonprofit that helps kids.", quality: 'weak', feedback: "That's vague. What kids? How do you help them? Make me care!" },
      { text: "I'm the Development Director at Hope Academy. We provide after-school tutoring for 500 low-income students, and 92% of our kids graduate high school.", quality: 'strong', feedback: "Great! Clear role, specific numbers, and measurable impact. Now I want to know more!" },
      { text: "We're a 501c3 founded in 2005 that focuses on educational equity in underserved communities.", quality: 'medium', feedback: "Too jargon-heavy. Lead with impact, not your tax status or founding date." },
      { text: "Well, it's complicated. We do a lot of different things...", quality: 'weak', feedback: "Never say it's complicated! If you can't explain it simply, you'll lose them." }
    ]
  },
  {
    id: 'first-time-ask',
    title: 'The First-Time Ask',
    icon: '\u{1F4B0}',
    difficulty: 'Intermediate',
    duration: '10 min',
    setup: "A loyal volunteer has attended 5 events but never donated. You're calling to invite them to become a monthly donor at $25/month.",
    persona: 'chanet',
    personaRole: 'Hesitant Volunteer',
    openingLine: "Hey! I saw your name on caller ID. What's up?",
    successCriteria: ['Acknowledge their past support', 'Specific ask amount', 'Explain impact of gift', 'Handle objection gracefully'],
    tips: "They already love you. This is about making it official. Focus on deepening the relationship, not selling."
  },
  {
    id: 'board-recruitment',
    title: 'Board Recruitment Pitch',
    icon: '\u{1F465}',
    difficulty: 'Intermediate',
    duration: '10 min',
    setup: "You're meeting with a successful business owner who has expressed interest in your cause. You want to invite them to join your board.",
    persona: 'claudette',
    personaRole: 'Busy Executive',
    openingLine: "I have about 20 minutes before my next call. What did you want to discuss?",
    successCriteria: ['Clear board expectations', 'Specific time commitment', 'Why them specifically', 'Give/get requirements'],
    tips: "Board recruitment is selling. Be honest about expectations, but lead with the opportunity, not the obligations."
  },
  {
    id: 'thank-you-call',
    title: 'The Thank-You Call',
    icon: '\u{1F4DE}',
    difficulty: 'Beginner',
    duration: '5 min',
    setup: "A first-time donor just gave $100 online. You're calling within 48 hours to thank them personally and start building the relationship.",
    persona: 'chanet',
    personaRole: 'Surprised Donor',
    openingLine: "Hello? Oh, I wasn't expecting a call!",
    successCriteria: ['Genuine thanks', 'Specific reference to their gift', 'Share one impact story', 'No second ask'],
    tips: "This is NOT an ask call. Your only goal is to make them feel amazing about giving. That's it."
  },
  {
    id: 'grant-loi',
    title: 'Foundation LOI Call',
    icon: '\u{1F3E6}',
    difficulty: 'Advanced',
    duration: '15 min',
    setup: "You submitted a Letter of Inquiry to a family foundation. The program officer is calling to learn more before deciding whether to invite a full proposal.",
    persona: 'claudette',
    personaRole: 'Skeptical Program Officer',
    openingLine: "Thanks for taking my call. I've reviewed your LOI and have some questions.",
    successCriteria: ['Answer questions concisely', 'Show evaluation/metrics', 'Demonstrate sustainability', 'Know their priorities'],
    tips: "Program officers are skeptical by nature. They've seen hundreds of proposals. Be specific, be honest about challenges, show you've done your homework on their foundation."
  },
  {
    id: 'major-donor-meeting',
    title: 'Major Donor Cultivation',
    icon: '\u{2B50}',
    difficulty: 'Advanced',
    duration: '15 min',
    setup: "You're having lunch with a donor who gave $1,000 last year. Research suggests they could give $10,000+. This is a cultivation meeting, not an ask meeting.",
    persona: 'chanet',
    personaRole: 'Curious Major Donor Prospect',
    openingLine: "Thanks for lunch! I've been meaning to learn more about what you all have been up to this year.",
    successCriteria: ['Ask about their interests', 'Share vision for future', 'Identify their passion areas', 'Set up next touchpoint'],
    tips: "Listen more than you talk. Your goal is to understand what they care about, not to pitch. The ask comes later."
  }
];

// ============================================================
// V2.3 FEATURE: SPACED REPETITION CONFIG
// ============================================================
// PROBLEM: People forget 70% of what they learn within 24 hours
// (Ebbinghaus forgetting curve). Quizzes only happen once.
//
// SOLUTION: Surface quiz questions at increasing intervals:
// 1 day, 3 days, 7 days, 14 days, 30 days. Questions they get
// wrong come back sooner. Questions they get right space out.
//
// STORAGE: Review queue stored in Firebase with nextReview date.
// ============================================================

const SPACED_REPETITION_INTERVALS = [1, 3, 7, 14, 30]; // Days between reviews

// ============================================================
// V2.4 FEATURE: ACHIEVEMENT BADGES
// ============================================================
// Gamification element to encourage engagement and celebrate
// milestones. Badges are earned automatically based on activity.
// ============================================================

const ACHIEVEMENT_BADGES = [
  {
    id: 'first-lesson',
    name: 'First Steps',
    icon: '\u{1F476}',
    description: 'Complete your first lesson',
    condition: (data) => data.totalLessons >= 1,
    rarity: 'common'
  },
  {
    id: 'week-one',
    name: 'Week One Warrior',
    icon: '\u{1F4AA}',
    description: 'Complete 7 lessons',
    condition: (data) => data.totalLessons >= 7,
    rarity: 'common'
  },
  {
    id: 'volume-one',
    name: 'Foundation Builder',
    icon: '\u{1F3D7}',
    description: 'Complete Volume 1',
    condition: (data) => data.completedVolumes?.includes(1),
    rarity: 'uncommon'
  },
  {
    id: 'volume-two',
    name: 'GMG Way Master',
    icon: '\u{1F3AF}',
    description: 'Complete Volume 2',
    condition: (data) => data.completedVolumes?.includes(2),
    rarity: 'uncommon'
  },
  {
    id: 'volume-three',
    name: 'Business Ready',
    icon: '\u{1F4BC}',
    description: 'Complete Volume 3',
    condition: (data) => data.completedVolumes?.includes(3),
    rarity: 'rare'
  },
  {
    id: 'perfect-quiz',
    name: 'Perfect Score',
    icon: '\u{1F4AF}',
    description: 'Score 100% on any quiz',
    condition: (data) => Object.values(data.quizScores || {}).some(s => s.pct === 100),
    rarity: 'uncommon'
  },
  {
    id: 'streak-seven',
    name: 'Week Streak',
    icon: '\u{1F525}',
    description: 'Maintain a 7-day streak',
    condition: (data) => data.streak >= 7,
    rarity: 'uncommon'
  },
  {
    id: 'streak-thirty',
    name: 'Month of Learning',
    icon: '\u{1F31F}',
    description: 'Maintain a 30-day streak',
    condition: (data) => data.streak >= 30,
    rarity: 'rare'
  },
  {
    id: 'practice-first',
    name: 'Role Player',
    icon: '\u{1F3AD}',
    description: 'Complete your first practice scenario',
    condition: (data) => data.practiceCompleted >= 1,
    rarity: 'common'
  },
  {
    id: 'practice-all',
    name: 'Master Practitioner',
    icon: '\u{1F9D9}',
    description: 'Complete all 6 practice scenarios',
    condition: (data) => data.practiceCompleted >= 6,
    rarity: 'rare'
  },
  {
    id: 'early-bird',
    name: 'Early Bird',
    icon: '\u{1F426}',
    description: 'Study before 7am',
    condition: (data) => data.earlyBirdSessions >= 1,
    rarity: 'uncommon'
  },
  {
    id: 'night-owl',
    name: 'Night Owl',
    icon: '\u{1F989}',
    description: 'Study after 10pm',
    condition: (data) => data.nightOwlSessions >= 1,
    rarity: 'uncommon'
  },
  {
    id: 'bookworm',
    name: 'Bookworm',
    icon: '\u{1F4DA}',
    description: 'Bookmark 10 lessons',
    condition: (data) => (data.bookmarks?.length || 0) >= 10,
    rarity: 'common'
  },
  {
    id: 'graduate',
    name: 'Graduate',
    icon: '\u{1F393}',
    description: 'Complete all 75 lessons',
    condition: (data) => data.totalLessons >= 75,
    rarity: 'legendary'
  },
  {
    id: 'quiz-master',
    name: 'Quiz Master',
    icon: '\u{1F9E0}',
    description: 'Pass all quizzes with 80%+',
    condition: (data) => {
      const scores = Object.values(data.quizScores || {});
      return scores.length >= 15 && scores.every(s => s.pct >= 80);
    },
    rarity: 'legendary'
  }
];

const BADGE_RARITY_COLORS = {
  common: { bg: '#71717a20', border: '#71717a', text: '#71717a' },
  uncommon: { bg: '#22c55e20', border: '#22c55e', text: '#22c55e' },
  rare: { bg: '#3b82f620', border: '#3b82f6', text: '#3b82f6' },
  legendary: { bg: '#f59e0b20', border: '#f59e0b', text: '#f59e0b' }
};

// ============================================================
// V2.4 FEATURE: WEEKLY CHALLENGES
// ============================================================
// Time-limited challenges that reset weekly. Encourages
// consistent engagement and friendly competition.
// ============================================================

const WEEKLY_CHALLENGES = [
  {
    id: 'lessons-five',
    name: 'Complete 5 Lessons',
    icon: '\u{1F4D6}',
    target: 5,
    metric: 'lessonsThisWeek',
    xp: 100
  },
  {
    id: 'quizzes-two',
    name: 'Pass 2 Quizzes',
    icon: '\u{1F4DD}',
    target: 2,
    metric: 'quizzesPassed',
    xp: 150
  },
  {
    id: 'streak-maintain',
    name: 'Keep Your Streak',
    icon: '\u{1F525}',
    target: 7,
    metric: 'daysActive',
    xp: 200
  },
  {
    id: 'practice-one',
    name: 'Complete a Practice',
    icon: '\u{1F3AF}',
    target: 1,
    metric: 'practiceThisWeek',
    xp: 125
  }
];

// ============================================================
// V2.1 FEATURE: CLAUDE API CALL FUNCTION
// Lines: ~555-620
// ============================================================
// PROBLEM: Need to call Claude API with proper formatting,
// persona instructions, and knowledge base context.
//
// SOLUTION: Async function that handles the API call, including
// persona-specific system prompts and error handling.
//
// PARAMETERS:
// - userMessage: The user's question
// - persona: 'claudette' or 'chanet' (affects tone)
// - conversationHistory: Previous messages for context
//
// RETURNS: String response from Claude
//
// DEPENDENCIES: ANTHROPIC_API_KEY, CLAUDE_MODEL, GMG_KNOWLEDGE
// ============================================================

const callClaudeAPI = async (userMessage, persona = 'claudette', conversationHistory = []) => {
  const personaInstructions = persona === 'claudette' 
    ? `You are Claudette, the professional mentor AI for GMG University. Your personality:
- Precise, strategic, and direct
- You give clear, actionable advice
- You reference data and best practices
- You're confident but not arrogant
- You occasionally make dry jokes
- You NEVER say you're Claude, made by Anthropic, or an AI language model
- You ARE Claudette, a GMG team member`
    : `You are Chanet, the supportive friend AI for GMG University. Your personality:
- Warm, encouraging, and relatable
- You make fundraising feel less intimidating
- You use analogies and stories to explain concepts
- You celebrate small wins
- You're empathetic when things are hard
- You NEVER say you're Claude, made by Anthropic, or an AI language model
- You ARE Chanet, a GMG team member`;

  const systemPrompt = `${personaInstructions}

You are helping Lane-Pierce Fellows learn nonprofit fundraising through GMG University.

${GMG_KNOWLEDGE}

RESPONSE GUIDELINES:
- Keep responses concise (2-4 paragraphs max)
- Use specific examples when possible
- Reference GMG methodologies when relevant
- Be encouraging but honest
- If you don't know something, say so
- Never break character`;

  try {
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': ANTHROPIC_API_KEY,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true'
      },
      body: JSON.stringify({
        model: CLAUDE_MODEL,
        max_tokens: 1024,
        system: systemPrompt,
        messages: [
          ...conversationHistory.map(msg => ({
            role: msg.speaker === 'user' ? 'user' : 'assistant',
            content: msg.text
          })),
          { role: 'user', content: userMessage }
        ]
      })
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return data.content[0].text;
  } catch (error) {
    console.error('Claude API error:', error);
    return persona === 'claudette'
      ? "I'm having trouble connecting right now. Try again in a moment, or check the lesson content for your answer."
      : "Oops, something went wrong on my end! Give it another try, or peek at the lesson - the answer might be right there.";
  }
};

// ============================================================
// APP VERSION & PWA CONFIG (Updated for V2.1)
// ============================================================

// Detect if running as installed PWA
const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

// Detect iOS
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

// ============================================================
// V2.2 HELPER: Format timestamp for iMessage-style display
// ============================================================
// Shows relative time for recent messages, absolute for older ones.
// "Just now" for < 1 min, "5m ago" for < 1 hour, "2:30 PM" for today,
// "Yesterday 2:30 PM" for yesterday, "Dec 15, 2:30 PM" for older.
// ============================================================
const formatMessageTime = (isoString) => {
  if (!isoString) return '';
  
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  
  // Less than 1 minute
  if (diffMins < 1) return 'Just now';
  
  // Less than 1 hour
  if (diffMins < 60) return `${diffMins}m ago`;
  
  // Format time as 2:30 PM
  const timeStr = date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  
  // Same day
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) return timeStr;
  
  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday ${timeStr}`;
  }
  
  // Older than yesterday
  const dateStr = date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric' 
  });
  return `${dateStr}, ${timeStr}`;
};

// PWA Install Prompt Component
function PWAInstallPrompt({ onDismiss }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);

  useEffect(() => {
    // Already installed as PWA? Don't show anything
    if (isStandalone()) return;

    // Check if dismissed this session
    const dismissed = sessionStorage.getItem('pwaPromptDismissed');
    if (dismissed) return;

    // iOS Safari - show manual instructions
    if (isIOS()) {
      setShowIOSPrompt(true);
      return;
    }

    // Chrome/Edge - use beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        console.log('PWA installed');
      }
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem('pwaPromptDismissed', 'true');
    setShowPrompt(false);
    setShowIOSPrompt(false);
    if (onDismiss) onDismiss();
  };

  // iOS Safari instructions
  if (showIOSPrompt) {
    return (
      <div style={{
        position: 'fixed',
        bottom: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'linear-gradient(135deg, #1B2A4E 0%, #2D4373 100%)',
        color: 'white',
        padding: '16px 20px',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        zIndex: 10000,
        maxWidth: '340px',
        width: '90%'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <span style={{ fontSize: '28px' }}>{'\u{1F4F1}'}</span>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>Install GMG University</div>
            <div style={{ fontSize: '13px', opacity: 0.9 }}>Add to your home screen</div>
          </div>
        </div>
        <div style={{ fontSize: '13px', marginBottom: '12px', lineHeight: 1.5 }}>
          Tap <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 6px', borderRadius: '4px' }}>Share</span> then <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 6px', borderRadius: '4px' }}>Add to Home Screen</span>
        </div>
        <button
          onClick={handleDismiss}
          style={{
            width: '100%',
            padding: '10px',
            background: 'rgba(255,255,255,0.2)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Got it
        </button>
      </div>
    );
  }

  if (!showPrompt) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: '80px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'linear-gradient(135deg, #1B2A4E 0%, #2D4373 100%)',
      color: 'white',
      padding: '16px 20px',
      borderRadius: '16px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      zIndex: 10000,
      maxWidth: '340px',
      width: '90%'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <span style={{ fontSize: '28px' }}>{'\u{1F4F1}'}</span>
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '16px' }}>Install GMG University</div>
          <div style={{ fontSize: '13px', opacity: 0.9 }}>Get the full app experience</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={handleInstall}
          style={{
            flex: 1,
            padding: '10px',
            background: '#B76E79',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          Install App
        </button>
        <button
          onClick={handleDismiss}
          style={{
            padding: '10px 16px',
            background: 'rgba(255,255,255,0.2)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Not Now
        </button>
      </div>
    </div>
  );
}

// Update Available Banner Component
function UpdateBanner({ onRefresh }) {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
      color: 'white',
      padding: '12px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      zIndex: 10001,
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '20px' }}>{'\u{1F504}'}</span>
        <span style={{ fontWeight: '500' }}>New update available!</span>
      </div>
      <button
        onClick={onRefresh}
        style={{
          padding: '8px 16px',
          background: 'white',
          color: '#16a34a',
          border: 'none',
          borderRadius: '6px',
          fontWeight: 'bold',
          cursor: 'pointer'
        }}
      >
        Refresh Now
      </button>
    </div>
  );
}

// ============================================================
// V2.1 UPDATED: Force refresh hook for checking updates
// ============================================================
// CHANGES: Now works with the IIFE version check above.
// The IIFE handles version mismatch on load.
// This hook handles service worker updates while app is running.
// ============================================================
function useForceRefresh() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  
  useEffect(() => {
    // Service worker update detection - DISABLED to prevent MIME type errors
    // The app works fine without service workers for now
    /*
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setUpdateAvailable(true);
              }
            });
          }
        });
      });
    }
    */

    // Check for updates when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check version
        const storedVersion = localStorage.getItem('gmg_university_version');
        if (storedVersion && storedVersion !== APP_VERSION) {
          setUpdateAvailable(true);
        }
        // Service worker update check disabled
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const doRefresh = () => {
    // Clear all caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    // Clear version marker so IIFE doesn't double-refresh
    localStorage.removeItem('gmg_university_version');
    // Force reload
    window.location.reload(true);
  };

  return { updateAvailable, doRefresh };
}

const GMG = {
  roseGold: '#B76E79', roseGoldLight: '#D4A5A5', navyBlue: '#1B2A4E', navyLight: '#2D4373',
  bronze: '#CD7F32', gold: '#D4AF37', pearlWhite: '#FAF9F6', pearl: '#F0EDE5',
  charcoal: '#2C2C2C', silver: '#C0C0C0', silverLight: '#D8D8D8', platinum: '#E5E4E2',
  success: '#22c55e', warning: '#f59e0b', danger: '#ef4444'
};

// Cohort for leaderboard
// Cohort for leaderboard - Using Unicode escape sequences for emojis
const COHORT = {
  'brandonjpiercesr@gmail.com': { name: 'Brandon', avatar: '\u{1F468}\u{1F3FE}\u{200D}\u{1F4BC}' },
  'brandon@globalmajoritygroup.com': { name: 'Brandon', avatar: '\u{1F468}\u{1F3FE}\u{200D}\u{1F4BC}' },
  'eric@globalmajoritygroup.com': { name: 'Eric', avatar: '\u{1F468}\u{1F3FE}\u{200D}\u{1F393}' },
  'ericlane@gidconsulting.co': { name: 'Eric', avatar: '\u{1F468}\u{1F3FE}\u{200D}\u{1F393}' },
  'ericreeselanesr@gmail.com': { name: 'Eric', avatar: '\u{1F468}\u{1F3FE}\u{200D}\u{1F393}' },
  'bryanjpiercejr@gmail.com': { name: 'BJ', avatar: '\u{1F468}\u{1F3FE}' },
  'thebjpierce@icloud.com': { name: 'BJ', avatar: '\u{1F468}\u{1F3FE}' },
  'cj.d.moore32@gmail.com': { name: 'CJ', avatar: '\u{1F469}\u{1F3FE}' },
  'elicooley6@gmail.com': { name: 'Eli', avatar: '\u{1F468}\u{1F3FE}\u{200D}\u{1F9B1}' }
};
// ============================================================
// CELEBRATION CONFETTI COMPONENT
// ============================================================
function Confetti({ active, onComplete }) {
  const [particles, setParticles] = React.useState([]);
  React.useEffect(() => {
    if (!active) return;
    const colors = [GMG.roseGold, GMG.gold, GMG.navyBlue, GMG.bronze, '#fff'];
    const newParticles = Array.from({ length: 50 }, (_, i) => ({
      id: i, x: Math.random() * 100, delay: Math.random() * 0.5,
      duration: 2 + Math.random() * 2, color: colors[Math.floor(Math.random() * colors.length)],
      size: 6 + Math.random() * 8, rotation: Math.random() * 360
    }));
    setParticles(newParticles);
    const timer = setTimeout(() => { setParticles([]); if (onComplete) onComplete(); }, 4000);
    return () => clearTimeout(timer);
  }, [active, onComplete]);
  if (!active && particles.length === 0) return null;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 9999 }}>
      {particles.map(p => (
        <div key={p.id} style={{ position: 'absolute', left: `${p.x}%`, top: '-20px', width: `${p.size}px`, height: `${p.size}px`,
          background: p.color, borderRadius: Math.random() > 0.5 ? '50%' : '2px', transform: `rotate(${p.rotation}deg)`,
          animation: `confettiFall ${p.duration}s ease-out ${p.delay}s forwards` }} />
      ))}
      <style>{`@keyframes confettiFall { 0% { transform: translateY(0) rotate(0deg); opacity: 1; } 100% { transform: translateY(100vh) rotate(720deg); opacity: 0; } }`}</style>
    </div>
  );
}

// ============================================================
// ANIMATED PROGRESS RING
// ============================================================
function ProgressRing({ progress, size = 100, strokeWidth = 8, color = GMG.roseGold }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;
  const isNearMilestone = progress >= 95 || (progress % 25 >= 20);
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={`${color}20`} strokeWidth={strokeWidth} />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s ease-out', filter: isNearMilestone ? `drop-shadow(0 0 8px ${color})` : 'none' }} />
      </svg>
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
        <span style={{ fontSize: size * 0.22, fontWeight: '700', color: GMG.charcoal }}>{progress}%</span>
      </div>
    </div>
  );
}

// ============================================================
// STREAK COUNTER WITH FIRE
// ============================================================
function StreakCounter({ streak }) {
  if (streak < 1) return null;
  const flames = streak >= 7 ? 3 : streak >= 3 ? 2 : 1;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: `linear-gradient(135deg, ${GMG.gold}20, ${GMG.bronze}15)`, borderRadius: '10px', border: `1px solid ${GMG.gold}30` }}>
      <span style={{ fontSize: '18px', animation: streak >= 5 ? 'fireGlow 0.5s ease-in-out infinite alternate' : 'none' }}>{'\u{1F525}'.repeat(flames)}</span>
      <div><p style={{ fontSize: '10px', color: GMG.bronze, margin: 0, fontWeight: '600' }}>STREAK</p><p style={{ fontSize: '14px', fontWeight: '700', color: GMG.charcoal, margin: 0 }}>{streak}</p></div>
      <style>{`@keyframes fireGlow { 0% { filter: brightness(1); } 100% { filter: brightness(1.3); } }`}</style>
    </div>
  );
}

// ============================================================
// TRACK BADGE
// ============================================================
function TrackBadge({ track, size = 'normal' }) {
  const isFast = track === 'fast';
  const padding = size === 'small' ? '4px 8px' : '6px 12px';
  const fontSize = size === 'small' ? '10px' : '12px';
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding, borderRadius: '20px', fontSize, fontWeight: '700',
      background: isFast ? `linear-gradient(135deg, ${GMG.gold}, ${GMG.bronze})` : `linear-gradient(135deg, ${GMG.navyBlue}, ${GMG.navyLight})`,
      color: '#fff', letterSpacing: '0.5px', boxShadow: isFast ? `0 2px 8px ${GMG.gold}40` : `0 2px 8px ${GMG.navyBlue}40` }}>
      {isFast ? '\u{26A1}' : '\u{1F4C5}'} {isFast ? 'FAST TRACK' : 'STANDARD'}
    </div>
  );
}

// ============================================================
// GLASSMORPHISM CARD
// ============================================================
function GlassCard({ children, style = {}, onClick, glow = false }) {
  return (
    <div onClick={onClick} style={{ background: 'rgba(255, 255, 255, 0.75)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
      borderRadius: '20px', border: '1px solid rgba(255, 255, 255, 0.4)', boxShadow: glow ? `0 8px 32px ${GMG.roseGold}25` : '0 8px 32px rgba(0, 0, 0, 0.06)',
      transition: 'all 0.3s ease', cursor: onClick ? 'pointer' : 'default', ...style }}>{children}</div>
  );
}

// ============================================================
// SYNC STATUS INDICATOR
// ============================================================
function SyncStatus({ status }) {
  const states = { synced: { icon: '\u{2601}', color: GMG.success, text: 'Saved' }, syncing: { icon: '\u{1F504}', color: GMG.warning, text: 'Saving...' }, offline: { icon: '\u{1F534}', color: GMG.danger, text: 'Offline' } };
  const s = states[status] || states.synced;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: s.color }}>
      <span style={{ animation: status === 'syncing' ? 'spin 1s linear infinite' : 'none' }}>{s.icon}</span><span>{s.text}</span>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ============================================================
// COHORT LEADERBOARD
// V3.3: Shows ALL active users, not just predefined COHORT members
// ============================================================
function CohortLeaderboard({ users, currentUserEmail, currentUserPhoto, onUserClick }) {
  // V3.3 FIX: Include ALL users who have ANY progress, not just COHORT members
  const activeUsers = users.filter(u => {
    const total = (u.completedLessons?.[1]?.length || 0) + (u.completedLessons?.[2]?.length || 0) + (u.completedLessons?.[3]?.length || 0);
    return total > 0; // Anyone with at least 1 lesson completed
  });
  const sorted = [...activeUsers].sort((a, b) => {
    const aTotal = (a.completedLessons?.[1]?.length || 0) + (a.completedLessons?.[2]?.length || 0) + (a.completedLessons?.[3]?.length || 0);
    const bTotal = (b.completedLessons?.[1]?.length || 0) + (b.completedLessons?.[2]?.length || 0) + (b.completedLessons?.[3]?.length || 0);
    return bTotal - aTotal;
  });
  if (sorted.length === 0) return null;
  const medals = ['\u{1F947}', '\u{1F948}', '\u{1F949}'];
  return (
    <GlassCard style={{ padding: '16px', marginBottom: '20px' }}>
      <h3 style={{ fontSize: '14px', fontWeight: '700', color: GMG.navyBlue, margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>{'\u{1F3C6}'} Leaderboard</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {sorted.map((u, i) => {
          // Use COHORT info if available, otherwise use Firebase name
          const cohortInfo = COHORT[u.email];
          const displayName = cohortInfo?.name || u.name || u.email?.split('@')[0] || 'User';
          const displayAvatar = cohortInfo?.avatar || '\u{1F464}';
          const total = (u.completedLessons?.[1]?.length || 0) + (u.completedLessons?.[2]?.length || 0) + (u.completedLessons?.[3]?.length || 0);
          const isMe = u.email === currentUserEmail;
          const userPhoto = isMe ? currentUserPhoto : (u.photoURL || u.photo);
          const hasPhoto = userPhoto && userPhoto.length > 10;
          return (
            <div 
              key={u.email || u.id} 
              onClick={() => onUserClick && onUserClick(isMe ? {...u, photoURL: currentUserPhoto} : u)}
              style={{ 
                display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '10px', 
                background: isMe ? `${GMG.roseGold}12` : 'transparent', 
                border: isMe ? `2px solid ${GMG.roseGold}30` : '2px solid transparent',
                cursor: onUserClick ? 'pointer' : 'default',
                transition: 'all 0.2s ease'
              }}
            >
              <span style={{ fontSize: '16px', width: '24px', textAlign: 'center' }}>{i < 3 ? medals[i] : `${i + 1}.`}</span>
              {hasPhoto ? (
                <img src={userPhoto} alt={displayName} style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: `2px solid ${GMG.roseGold}40` }} />
              ) : (
                <span style={{ fontSize: '22px' }}>{displayAvatar}</span>
              )}
              <div style={{ flex: 1 }}><p style={{ fontSize: '13px', fontWeight: '600', color: GMG.charcoal, margin: 0 }}>{displayName}</p><p style={{ fontSize: '10px', color: '#888', margin: 0 }}>{total} lessons</p></div>
              {isMe && <span style={{ fontSize: '9px', padding: '2px 6px', background: GMG.roseGold, color: '#fff', borderRadius: '8px', fontWeight: '600' }}>YOU</span>}
            </div>
          );
        })}
      </div>
    </GlassCard>
  );
}

// ============================================================
// SESSION TIMER
// ============================================================
function SessionTimer() {
  const [seconds, setSeconds] = React.useState(0);
  React.useEffect(() => { const interval = setInterval(() => setSeconds(s => s + 1), 1000); return () => clearInterval(interval); }, []);
  return <span style={{ fontSize: '11px', color: '#888' }}>{'\u{23F1}'} {Math.floor(seconds / 60)}:{(seconds % 60).toString().padStart(2, '0')}</span>;
}

// ============================================================
// QUICK JUMP BUTTON
// ============================================================
function QuickJumpMenu({ completedDays, onJump }) {
  const [open, setOpen] = React.useState(false);
  if (completedDays.length === 0) return null;
  return (
    <div style={{ position: 'fixed', bottom: '100px', right: '20px', zIndex: 100 }}>
      {open && (
        <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 6px 24px rgba(0,0,0,0.15)', padding: '12px', marginBottom: '8px', maxHeight: '180px', overflowY: 'auto' }}>
          <p style={{ fontSize: '10px', fontWeight: '600', color: '#888', margin: '0 0 8px 0' }}>JUMP TO DAY</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {completedDays.map(d => (
              <button key={d} onClick={() => { onJump(d); setOpen(false); }} style={{ width: '30px', height: '30px', borderRadius: '8px', border: 'none', background: `${GMG.roseGold}20`, color: GMG.roseGold, fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>{d}</button>
            ))}
          </div>
        </div>
      )}
      <button onClick={() => setOpen(!open)} style={{ width: '48px', height: '48px', borderRadius: '50%', border: 'none', background: `linear-gradient(135deg, ${GMG.roseGold}, ${GMG.bronze})`, color: '#fff', fontSize: '18px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>{open ? '\u{2715}' : '\u{26A1}'}</button>
    </div>
  );
}

// ============================================================
// PAGE TRANSITION WRAPPER
// ============================================================
function PageTransition({ children, show }) {
  return (
    <div style={{ 
      opacity: show ? 1 : 0, 
      transform: show ? 'translateY(0)' : 'translateY(10px)',
      transition: 'opacity 0.3s ease, transform 0.3s ease'
    }}>
      {children}
    </div>
  );
}

// ============================================================
// VOLUME PROGRESS MAP - Visual journey through curriculum
// V2.6.5.5: Now accepts isDayUnlocked function for proper time-gated unlocks
// ============================================================
function VolumeProgressMap({ volume, completedDays, totalDays, color, onDayClick, lessonsPerDay = 1, isDayUnlockedFn, countdownText }) {
  const rows = Math.ceil(totalDays / 5);
  
  return (
    <div style={{ padding: '16px', background: `${color}08`, borderRadius: '16px', marginBottom: '20px' }}>
      <h4 style={{ fontSize: '13px', fontWeight: '600', color, margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
        {'\u{1F5FA}'} Your Journey Map {lessonsPerDay > 1 && <span style={{ fontSize: '11px', background: `${color}20`, padding: '2px 6px', borderRadius: '4px' }}>{lessonsPerDay}/day</span>}
      </h4>
      {countdownText && (
        <div style={{ fontSize: '12px', color: GMG.bronze, marginBottom: '12px', padding: '8px 12px', background: `${GMG.bronze}15`, borderRadius: '8px', textAlign: 'center' }}>
          {'\u{23F0}'} {countdownText}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {Array.from({ length: rows }, (_, row) => (
          <div key={row} style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
            {Array.from({ length: 5 }, (_, col) => {
              const day = row * 5 + col + 1;
              if (day > totalDays) return <div key={col} style={{ width: '36px' }} />;
              const isComplete = completedDays.includes(day);
              const isUnlocked = isDayUnlockedFn ? isDayUnlockedFn(volume, day) : day <= (Math.max(...completedDays, 0) + lessonsPerDay);
              const isLocked = !isComplete && !isUnlocked;
              return (
                <div key={col} onClick={() => !isLocked && onDayClick(day)} style={{
                  width: '36px', height: '36px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: '600', cursor: isLocked ? 'not-allowed' : 'pointer',
                  background: isComplete ? color : isUnlocked ? `${color}30` : `${GMG.charcoal}10`,
                  color: isComplete ? '#fff' : isUnlocked ? color : '#999',
                  border: isUnlocked && !isComplete ? `2px dashed ${color}` : 'none',
                  boxShadow: isComplete ? `0 2px 8px ${color}40` : 'none',
                  opacity: isLocked ? 0.4 : 1
                }}>
                  {isComplete ? '\u{2713}' : isLocked ? '\u{1F512}' : day}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// OFFLINE INDICATOR - Connection status
// ============================================================
function OfflineIndicator() {
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);
  
  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (isOnline) return null;
  
  return (
    <div style={{
      position: 'fixed', top: '10px', left: '50%', transform: 'translateX(-50%)',
      padding: '10px 20px', borderRadius: '20px', background: GMG.danger,
      color: '#fff', fontSize: '13px', fontWeight: '600', zIndex: 9999,
      display: 'flex', alignItems: 'center', gap: '8px',
      boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)'
    }}>
      {'\u{1F534}'} You're offline. Progress will sync when reconnected.
    </div>
  );
}

// ============================================================
// CERTIFICATE GENERATOR - Downloadable completion certificate
// ============================================================
function CertificateModal({ user, volume, score, onClose }) {
  const volumeNames = { 1: 'Fundraising Foundations', 2: 'The GMG Way', 3: 'CPP Consultant Model' };
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  
  const downloadCertificate = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1100;
    canvas.height = 850;
    const ctx = canvas.getContext('2d');
    
    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, 1100, 850);
    gradient.addColorStop(0, '#FAF9F6');
    gradient.addColorStop(1, '#F0EDE5');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 1100, 850);
    
    // Border
    ctx.strokeStyle = GMG.roseGold;
    ctx.lineWidth = 8;
    ctx.strokeRect(30, 30, 1040, 790);
    ctx.strokeStyle = GMG.navyBlue;
    ctx.lineWidth = 2;
    ctx.strokeRect(45, 45, 1010, 760);
    
    // Header
    ctx.fillStyle = GMG.navyBlue;
    ctx.font = 'bold 48px Georgia';
    ctx.textAlign = 'center';
    ctx.fillText('Certificate of Completion', 550, 120);
    
    // GMG University
    ctx.fillStyle = GMG.roseGold;
    ctx.font = '28px Georgia';
    ctx.fillText('GMG University', 550, 170);
    
    // This certifies
    ctx.fillStyle = GMG.charcoal;
    ctx.font = '20px Georgia';
    ctx.fillText('This certifies that', 550, 250);
    
    // Name
    ctx.fillStyle = GMG.navyBlue;
    ctx.font = 'bold 42px Georgia';
    ctx.fillText(user.name || 'Fellow', 550, 320);
    
    // Line under name
    ctx.strokeStyle = GMG.roseGold;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(250, 340);
    ctx.lineTo(850, 340);
    ctx.stroke();
    
    // Has successfully completed
    ctx.fillStyle = GMG.charcoal;
    ctx.font = '20px Georgia';
    ctx.fillText('has successfully completed', 550, 400);
    
    // Volume name
    ctx.fillStyle = GMG.navyBlue;
    ctx.font = 'bold 36px Georgia';
    ctx.fillText(`Volume ${volume}: ${volumeNames[volume]}`, 550, 460);
    
    // Score
    ctx.fillStyle = GMG.bronze;
    ctx.font = '24px Georgia';
    ctx.fillText(`with an average score of ${score}%`, 550, 520);
    
    // Date
    ctx.fillStyle = GMG.charcoal;
    ctx.font = '18px Georgia';
    ctx.fillText(today, 550, 600);
    
    // Signatures
    ctx.font = 'italic 16px Georgia';
    ctx.fillText('Brandon Pierce Sr.', 350, 720);
    ctx.fillText('Dr. Eric Lane Sr.', 750, 720);
    ctx.font = '12px Georgia';
    ctx.fillText('Co-Founder, GMG', 350, 745);
    ctx.fillText('Co-Founder, GMG', 750, 745);
    
    // Signature lines
    ctx.strokeStyle = GMG.charcoal;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(250, 700);
    ctx.lineTo(450, 700);
    ctx.moveTo(650, 700);
    ctx.lineTo(850, 700);
    ctx.stroke();
    
    // Download
    const link = document.createElement('a');
    link.download = `GMG_University_Volume${volume}_Certificate.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#fff', borderRadius: '20px', padding: '30px', maxWidth: '500px', width: '100%', textAlign: 'center' }}>
        <div style={{ fontSize: '64px', marginBottom: '16px' }}>{'\u{1F393}'}</div>
        <h2 style={{ fontSize: '24px', fontWeight: '700', color: GMG.navyBlue, marginBottom: '8px' }}>Congratulations!</h2>
        <p style={{ fontSize: '16px', color: GMG.charcoal, marginBottom: '8px' }}>You've completed Volume {volume}</p>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '24px' }}>{volumeNames[volume]}</p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button onClick={onClose} style={{ padding: '12px 24px', borderRadius: '10px', border: `1px solid ${GMG.roseGold}`, background: 'transparent', color: GMG.roseGold, fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Close</button>
          <button onClick={downloadCertificate} style={{ padding: '12px 24px', borderRadius: '10px', border: 'none', background: `linear-gradient(135deg, ${GMG.roseGold}, ${GMG.bronze})`, color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>{'\u{1F4E5}'} Download Certificate</button>
        </div>
      </div>
    </div>
  );
}

const FOUNDERS = [
  'brandon@globalmajoritygroup.com', 'brandonjpiercesr@gmail.com',
  'eric@globalmajoritygroup.com', 'ericlane@gidconsulting.co', 'ericreeselanesr@gmail.com', 'ericreecelanesr@gmail.com'
];
const FULL_ACCESS = [
  'bryanjpiercejr@gmail.com', 'thebjpierce@icloud.com', 'thebjpierce@gmail.com',
  'cj.d.moore32@gmail.com'
];
const LIMITED_ACCESS = [
  'elicooley6@gmail.com'
];

const V1_TITLES = ["The Four Sources of Money","Why People Actually Give","The Donor Lifecycle","The Donor Pyramid","Quiz: Days 1-4","Annual Giving Programs","Foundation Grants Reality","Corporate Partnerships","Earned Revenue Strategies","Quiz: Days 6-9","Board Fundraising Responsibility","Grant Research Deep Dive","Donor Retention Strategies","Fundraising Systems and Tools","Quiz: Days 11-14","Grant Writing Basics","Major Donor Identification","Planned Giving Introduction","Corporate Sponsorship Models","Quiz: Days 16-19","Digital Fundraising Tactics","Storytelling for Impact","Capital Campaigns Overview","Monthly Giving Programs","Quiz: Days 21-24","Board Development","Prospect Research Methods","Fundraising Metrics That Matter","Strategic Planning for Development","Volume 1 Capstone"];
const V2_TITLES = ["What Makes GMG Different","Both Sides of the Table","Brandon's Writing Standards","360° Landscape Assessment","Data Science Development Plans","Prospect Precision System","Grant Catalyst Methodology","Implementation Engine","The Grantmaker Perspective","Budget-First Approach","Tic-Tac-Toe Framework","Tic-Tac-Toe Implementation","Recipe Pitch Framework","Board Training GMG Style","Foundation Pipeline Management","Major Donor Strategy","Corporate Small-Dollar Approach","Merchandise Programs","Monthly Giving as Default","Membership Programs","Raffles and Sweepstakes","Tax-Advantaged Giving","Event Strategy GMG Way","CRM Optimization","Email Platform Mastery","Reporting Dashboards","AI in Fundraising","Building Your Tech Stack","Final Assessment Prep","Volume 2 Certification"];
const V3_TITLES = ["What Is CPP","Legal Structure","Money Flow","Capacity Planning","Risk Assessment","Building Your Resume","Certifications","Online Presence","Crafting Your Pitch","Client Interviews","Documentation","Folder Structure","Pre-Brief Protocols","Client Communication","CPP Final Assessment"];

// Realistic durations
const DURATIONS = {
  1: ["12 min","10 min","8 min","9 min","Quiz","11 min","13 min","10 min","8 min","Quiz","9 min","14 min","11 min","10 min","Quiz","15 min","12 min","9 min","11 min","Quiz","10 min","8 min","13 min","9 min","Quiz","11 min","10 min","12 min","14 min","7 min"],
  2: ["8 min","10 min","7 min","12 min","14 min","11 min","13 min","10 min","9 min","8 min","11 min","10 min","9 min","12 min","13 min","11 min","8 min","7 min","9 min","10 min","11 min","12 min","10 min","9 min","8 min","11 min","13 min","10 min","9 min","7 min"],
  3: ["10 min","12 min","9 min","11 min","8 min","10 min","7 min","9 min","11 min","13 min","8 min","10 min","9 min","11 min","15 min"]
};

const QUIZ_DAYS = { 1: [5, 10, 15, 20, 25, 30], 2: [5, 10, 15, 20, 25, 30], 3: [5, 10, 15] };

const QUIZZES = {
  "1-5": { questions: [
    { q: "In a healthy revenue mix, what percentage should come from individual donors?", a: "A", opts: ["A. About 50%", "B. About 40%", "C. About 25%", "D. About 90%"], speaker: "claudette" },
    { q: "The four PRIMARY sources we track are individuals, foundations, corporations, and earned revenue. What important funding source exists but is NOT in our 'four sources' framework?", a: "C", opts: ["A. Individual donors", "B. Foundation grants", "C. Government grants/contracts", "D. Earned revenue"], speaker: "chanet" },
    { q: "What is the strongest predictor of giving behavior?", a: "B", opts: ["A. Wealth", "B. Personal connection", "C. Tax benefits", "D. Peer pressure"], speaker: "claudette" },
    { q: "What is the industry average donor retention rate?", a: "A", opts: ["A. About 45%", "B. About 75%", "C. About 60%", "D. About 30%"], speaker: "chanet" },
    { q: "The 80/20 rule means top donors provide what percentage?", a: "D", opts: ["A. 20%", "B. 50%", "C. 60%", "D. 80%"], speaker: "claudette" }
  ]},
  "1-10": { questions: [
    { q: "Year-end giving accounts for about what percentage?", a: "B", opts: ["A. 15%", "B. 30%", "C. 50%", "D. 75%"], speaker: "chanet" },
    { q: "What is a typical foundation grant success rate?", a: "B", opts: ["A. About 25%", "B. About 4%", "C. About 50%", "D. About 15%"], speaker: "claudette" },
    { q: "Corporate sponsorships come from which budget?", a: "C", opts: ["A. Philanthropy", "B. HR", "C. Marketing", "D. Operations"], speaker: "chanet" },
    { q: "What makes earned revenue valuable?", a: "A", opts: ["A. It's unrestricted", "B. It's tax-free", "C. Requires no effort", "D. Donors prefer it"], speaker: "claudette" },
    { q: "Major gift asks should happen how?", a: "A", opts: ["A. In person", "B. By email", "C. By phone", "D. By direct mail"], speaker: "chanet" }
  ]},
  "1-15": { questions: [
    { q: "Board members' primary fundraising role is?", a: "C", opts: ["A. Writing grants", "B. Planning events", "C. Making asks and giving", "D. Managing staff"], speaker: "claudette" },
    { q: "Best source for foundation giving history?", a: "B", opts: ["A. Website", "B. 990 tax forms", "C. News articles", "D. Social media"], speaker: "chanet" },
    { q: "First-time donor retention rate is approximately?", a: "A", opts: ["A. 19%", "B. 45%", "C. 60%", "D. 75%"], speaker: "claudette" },
    { q: "Thank-you timing should be within?", a: "A", opts: ["A. 48 hours", "B. One week", "C. One month", "D. When convenient"], speaker: "chanet" },
    { q: "A CRM system helps you track?", a: "D", opts: ["A. Just donations", "B. Just events", "C. Just grants", "D. All donor interactions"], speaker: "claudette" }
  ]},
  "1-20": { questions: [
    { q: "Grant proposals should lead with?", a: "B", opts: ["A. Your history", "B. Strongest point", "C. Budget", "D. Staff bios"], speaker: "chanet" },
    { q: "Best major gift prospects score high on?", a: "D", opts: ["A. Just capacity", "B. Just affinity", "C. Just propensity", "D. All three factors"], speaker: "claudette" },
    { q: "Planned gifts most commonly come through?", a: "A", opts: ["A. Bequests in wills", "B. Cash gifts", "C. Event tickets", "D. Monthly giving"], speaker: "chanet" },
    { q: "Corporate sponsorship pricing should be based on?", a: "C", opts: ["A. What you need", "B. What others charge", "C. Actual value delivered", "D. Random amounts"], speaker: "claudette" },
    { q: "Category exclusivity in sponsorship means?", a: "B", opts: ["A. Lower prices", "B. Only one sponsor per category", "C. More sponsors", "D. No logo placement"], speaker: "chanet" }
  ]},
  "1-25": { questions: [
    { q: "Digital fundraising is best described as?", a: "B", opts: ["A. Separate strategy", "B. Channel for same principles", "C. Replacement for other methods", "D. Only for young donors"], speaker: "claudette" },
    { q: "Stories should have all except?", a: "D", opts: ["A. Protagonist", "B. Conflict", "C. Transformation", "D. Abstract statistics only"], speaker: "chanet" },
    { q: "Capital campaign quiet phase should secure?", a: "C", opts: ["A. 10% of goal", "B. 25% of goal", "C. 50% or more of goal", "D. 100% of goal"], speaker: "claudette" },
    { q: "Monthly donors compared to one-time donors have?", a: "A", opts: ["A. Higher retention", "B. Lower value", "C. Less commitment", "D. More complaints"], speaker: "chanet" },
    { q: "Credit card expiration is a threat to?", a: "B", opts: ["A. Major gifts", "B. Monthly giving", "C. Grants", "D. Events"], speaker: "claudette" }
  ]},
  "1-30": { questions: [
    { q: "Boards exist primarily to?", a: "A", opts: ["A. Govern", "B. Manage programs", "C. Write grants", "D. Plan events"], speaker: "chanet" },
    { q: "Prospect research should always include?", a: "D", opts: ["A. Only wealth data", "B. Only giving history", "C. Only connections", "D. All relevant factors"], speaker: "claudette" },
    { q: "Most important fundraising metric is?", a: "B", opts: ["A. Total raised", "B. Donor retention rate", "C. Number of events", "D. Grant count"], speaker: "chanet" },
    { q: "Development plans should be?", a: "C", opts: ["A. Wish lists", "B. Set in stone", "C. Living documents", "D. Secret from staff"], speaker: "claudette" },
    { q: "In a healthy mix, individual donors should provide what percentage?", a: "A", opts: ["A. About 50%", "B. About 30%", "C. About 70%", "D. About 90%"], speaker: "chanet" }
  ]},
  "2-5": { questions: [
    { q: "What makes GMG's perspective unique?", a: "B", opts: ["A. Lower prices", "B. Both grantmaker and fundraiser experience", "C. More staff", "D. Bigger clients"], speaker: "claudette" },
    { q: "Brandon reviewed how many proposals at Coca-Cola?", a: "C", opts: ["A. 1,000", "B. 5,000", "C. 13,000", "D. 25,000"], speaker: "chanet" },
    { q: "What is a 'culture of philanthropy'?", a: "D", opts: ["A. Hiring more staff", "B. Having big events", "C. Getting more grants", "D. Everyone lives and breathes fundraising"], speaker: "claudette" },
    { q: "GMG's writing standard requires what reading level?", a: "B", opts: ["A. College level", "B. Middle school level", "C. Graduate level", "D. No standard"], speaker: "chanet" },
    { q: "What is considered a 'fireable offense' at GMG?", a: "A", opts: ["A. Using em dashes", "B. Missing deadlines", "C. Short emails", "D. Asking questions"], speaker: "claudette" }
  ]},
  "2-10": { questions: [
    { q: "The 360° Landscape Assessment evaluates what?", a: "D", opts: ["A. Just finances", "B. Just programs", "C. Just staff", "D. All organizational capacity"], speaker: "chanet" },
    { q: "Data Science Development Plans work backwards from?", a: "A", opts: ["A. Revenue goals using benchmarks", "B. Board preferences", "C. Staff availability", "D. Random targets"], speaker: "claudette" },
    { q: "The Prospect Precision System focuses on?", a: "C", opts: ["A. Mass outreach", "B. Cold calling", "C. Strategic targeting", "D. Social media"], speaker: "chanet" },
    { q: "Grant Catalyst Methodology emphasizes?", a: "B", opts: ["A. Volume of applications", "B. Strategic fit and relationships", "C. Lowest overhead", "D. Biggest foundations only"], speaker: "claudette" },
    { q: "Implementation Engine refers to?", a: "D", opts: ["A. Software tools", "B. Hiring consultants", "C. Board training", "D. Systems for execution"], speaker: "chanet" }
  ]},
  "2-15": { questions: [
    { q: "The Tic-Tac-Toe Framework requires how many touches before an ask?", a: "C", opts: ["A. One", "B. Two", "C. Three", "D. Five"], speaker: "claudette" },
    { q: "The Recipe Pitch Framework starts with?", a: "A", opts: ["A. The problem/need", "B. Your organization history", "C. The budget", "D. Staff bios"], speaker: "chanet" },
    { q: "Budget-First Approach means?", a: "B", opts: ["A. Cutting costs first", "B. Building budgets before narratives", "C. Spending more", "D. Ignoring finances"], speaker: "claudette" },
    { q: "GMG Board Training emphasizes?", a: "D", opts: ["A. Avoiding fundraising", "B. Only governance", "C. Staff management", "D. Give, get, govern"], speaker: "chanet" },
    { q: "Foundation Pipeline Management tracks?", a: "C", opts: ["A. Just submitted grants", "B. Just awarded grants", "C. All stages from research to stewardship", "D. Only rejections"], speaker: "claudette" }
  ]},
  "2-20": { questions: [
    { q: "Monthly Giving as Default means?", a: "A", opts: ["A. Defaulting donation forms to monthly", "B. Only accepting monthly gifts", "C. Rejecting one-time gifts", "D. Monthly reporting"], speaker: "chanet" },
    { q: "Corporate Small-Dollar Approach targets?", a: "B", opts: ["A. Only Fortune 500", "B. Local businesses for $500-$2,500", "C. Only tech companies", "D. International corporations"], speaker: "claudette" },
    { q: "Merchandise Programs work best when?", a: "C", opts: ["A. Items are expensive", "B. Sold at events only", "C. Mission-aligned and quality", "D. Made overseas"], speaker: "chanet" },
    { q: "Membership Programs differ from donations how?", a: "D", opts: ["A. They don't differ", "B. Members give less", "C. No tax benefits", "D. Members expect benefits/access"], speaker: "claudette" },
    { q: "Raffles and Sweepstakes require?", a: "A", opts: ["A. State compliance and registration", "B. No regulations", "C. Only online sales", "D. Board approval only"], speaker: "chanet" }
  ]},
  "2-25": { questions: [
    { q: "Tax-Advantaged Giving includes?", a: "D", opts: ["A. Only cash gifts", "B. Only checks", "C. Only credit cards", "D. Stock, QCDs, DAFs"], speaker: "claudette" },
    { q: "CRM Optimization focuses on?", a: "B", opts: ["A. Buying new software", "B. Using existing tools effectively", "C. Hiring IT staff", "D. Manual tracking"], speaker: "chanet" },
    { q: "Email Platform Mastery requires?", a: "C", opts: ["A. Sending more emails", "B. Fancy templates", "C. Segmentation and testing", "D. Daily newsletters"], speaker: "claudette" },
    { q: "AI in Fundraising should be used for?", a: "A", opts: ["A. Drafts and research, not final copy", "B. Replacing all staff", "C. Writing final proposals", "D. Donor communication only"], speaker: "chanet" },
    { q: "Reporting Dashboards should show?", a: "D", opts: ["A. Only total raised", "B. Only donor count", "C. Only grant status", "D. Key metrics for decisions"], speaker: "claudette" }
  ]},
  "2-30": { questions: [
    { q: "GMG's core philosophy is?", a: "B", opts: ["A. Raise more money", "B. Excellence rooted in lived experience", "C. Lowest fees", "D. Fastest results"], speaker: "chanet" },
    { q: "The dual perspective advantage means?", a: "A", opts: ["A. Grantmaker and fundraiser experience", "B. Two consultants", "C. Multiple offices", "D. Various services"], speaker: "claudette" },
    { q: "Copy-paste test means deliverables should be?", a: "C", opts: ["A. Short", "B. Colorful", "C. Immediately usable without editing", "D. In bullet points"], speaker: "chanet" },
    { q: "GMG measures success by?", a: "D", opts: ["A. Hours worked", "B. Meetings held", "C. Proposals submitted", "D. Client outcomes and revenue"], speaker: "claudette" },
    { q: "The Lane-Pierce Fellowship trains?", a: "B", opts: ["A. Board members", "B. Emerging fundraising professionals", "C. Executive directors", "D. Volunteers"], speaker: "chanet" }
  ]}
};

// Placeholder images (small base64)
const GMG_LOGO = "data:image/jpeg;base64,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";
const CLAUDETTE_IMG = "data:image/jpeg;base64,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";
const CHANET_IMG = "data:image/jpeg;base64,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 GLFNv+zfevf+g6Vn/Zp+5+9dXVXRHol+k+2d/oOlZB/Bpkep+9fG7P6SxybJDj1P3rq6hoj0d+k+2eD2c0dmLGwjJIwdzuP3r5admdFs2LW1gkZ9Gb711dR0R6O/SXZZOkaeedsv7n715bRNMbnaIfmfvXV1doj0drl2QL2a0VCCthGCCSDltifnUraDpTe1ZofmfvXV1doj0d+k+2c2g6U2eKzQ5GDud/5q1Y2dtYWcdpZxCKCIYRASQN89a6uoqKXCA5OXLP/9k=";


// CONTENT with interactive elements
const V1_CONTENT = {
  1: { 
    title: "The Four Sources of Money", 
    sections: [
      { h: "Where Money Comes From", c: "Every dollar a nonprofit receives comes from one of four sources, and understanding this is the foundation of everything else you'll learn in this curriculum. Those four sources are individuals, foundations, corporations, and earned revenue. That's the complete list, and there are no other options.\n\nMost people who are new to fundraising assume that foundations fund most nonprofit work, but they're wrong about that. Individual donors should provide roughly 50% of your total revenue when you have a healthy mix, which means if your organization isn't prioritizing individual donor fundraising, you're ignoring the largest pool of available money by a significant margin." },
      { h: "Individual Donors", c: "Individual giving includes everything from $25 online donations all the way up to million-dollar major gifts, making it the most diverse and accessible funding source available to any nonprofit organization.\n\nSmall donors give through annual appeals, online campaigns, and events, typically contributing $25, $50, or $100 at a time. Individually these gifts seem small, but they add up in meaningful ways. An organization with 1,000 donors giving $100 each raises $100,000, and that's real money." },
      { h: "Foundations and Corporations", c: "Foundations are organizations that exist specifically to give money away, and they come in several varieties. Private foundations are funded by individuals or families, corporate foundations are funded by companies, and community foundations pool donations from many sources for local grantmaking.\n\nCorporate giving happens through two distinct channels. Corporate foundations operate like other foundations, making grants based on applications. Corporate marketing budgets fund sponsorships that provide business value back to the company." },
      { h: "The Healthy Mix", c: "No single source should dominate your revenue because over-reliance on any source creates vulnerability that can threaten your organization's survival. If 80% of your budget comes from one government contract, losing that contract devastates you.\n\nA healthy mix might be 50% individuals, 25% foundations, 15% earned revenue, and 10% corporate." }
    ],
    interactive: {
      type: "matching",
      title: "Match the Source to the Percentage",
      pairs: [
        { left: "Individual Donors", right: "50%" },
        { left: "Foundations", right: "25%" },
        { left: "Corporations", right: "10%" },
        { left: "Earned Revenue", right: "15%" }
      ]
    },
    keyTakeaways: ["All revenue comes from four sources only", "A healthy mix is 50% individuals, 25% foundations, 15% earned, 10% corporate", "Diversification creates stability", "Understanding your mix is the first step"]
  },
  2: { 
    title: "Why People Actually Give", 
    sections: [
      { h: "Beyond Simple Altruism", c: "Most people believe donors give purely to help others, but that understanding is only partly true and misses important nuances. Charitable giving is rarely purely altruistic because donors almost always get something back from the experience, even if what they get is intangible or emotional.\n\nUnderstanding what donors get from giving helps you position your asks more effectively. You're not just asking for help when you fundraise. You're offering an opportunity for someone to participate in something meaningful." },
      { h: "Personal Connection to Cause", c: "People give to causes they care about personally, and personal connection is the single strongest predictor of giving behavior across all demographic groups. A cancer survivor gives to cancer research because they understand viscerally what that funding means. A parent of a child with autism supports autism services because they live that reality every day.\n\nThis is why storytelling matters so much in fundraising. Stories create emotional connection even for people without direct experience of the issue. When you share a story about a real person your organization helped, you're creating a bridge that lets donors feel personally connected to your mission." },
      { h: "Relationship with the Asker", c: "People give to people, not just to causes, and this is one of the most important principles in fundraising. If someone they trust asks them to give, they're far more likely to say yes than if a stranger makes the same request. The relationship carries the ask.\n\nThis is why board members asking their friends works better than direct mail to strangers. A personal ask from someone you respect creates social pressure to respond positively. The asker is essentially vouching for the organization." },
      { h: "Legacy and Recognition", c: "Many donors are motivated by the desire to leave a mark on the world, to be remembered for something meaningful. This isn't vanity but rather a deep human need to matter beyond our own lifetimes. Named giving opportunities tap into this motivation directly.\n\nRecognition doesn't have to mean a plaque on a wall. Some donors want public acknowledgment while others prefer quiet appreciation. The key is understanding what kind of recognition matters to each individual donor and providing it consistently." },
      { h: "Tax Benefits", c: "Tax deductions are real but rarely primary motivators. Research consistently shows that tax benefits rank near the bottom of why people give. Donors don't typically wake up thinking they need to reduce their tax burden and then search for a charity. They support causes they care about and appreciate the tax benefit as a secondary bonus.\n\nThat said, tax considerations can influence timing and gift size. Year-end giving spikes partly because donors want the deduction in the current tax year. Major donors might accelerate a gift to offset a high-income year. Understanding tax implications helps you time your asks strategically." },
      { h: "Peer Pressure and Social Proof", c: "People are influenced by what others around them do, and charitable giving is no exception. When someone sees their friends, colleagues, or neighbors supporting a cause, they're more likely to give as well. Peer fundraising campaigns leverage this dynamic directly.\n\nSocial proof also matters in how you present your organization. Sharing that 500 community members donated last year signals that supporting you is normal and expected. Testimonials from respected donors encourage others to follow." },
      { h: "Ranking the Motivations", c: "When researchers study giving behavior, personal connection to cause consistently ranks highest. People give first and foremost because they care about the issue. Relationship with the asker comes second because trust and personal connection matter enormously. Legacy and recognition motivations vary by donor but can be powerful for major gifts. Tax benefits and peer pressure typically rank lowest as primary motivators, though they can tip decisions at the margins.\n\nUnderstanding this hierarchy helps you prioritize your messaging. Lead with impact and mission, emphasize the personal ask, and treat tax benefits as a helpful but secondary talking point." }
    ],
    interactive: {
      type: "sorting",
      title: "Rank These Giving Motivations (Most to Least Common)",
      items: ["Personal Connection", "Relationship with Asker", "Legacy/Recognition", "Tax Benefits"]
    },
    keyTakeaways: ["Personal connection is the strongest giving predictor", "People give to people not just causes", "Legacy motivations drive major gifts", "Tax benefits matter less than most assume"]
  },
  3: { 
    title: "The Donor Lifecycle", 
    sections: [
      { h: "The Journey Every Donor Takes", c: "Every donor relationship moves through predictable stages, and understanding these stages helps you work more strategically. The stages are awareness, interest, first gift, repeat gift, major gift, and legacy gift. Not every donor reaches every stage, but the path is consistent.\n\nYour job as a fundraiser is to move donors through these stages as efficiently as possible while building genuine relationships. Think of yourself as a guide helping people along a journey, not a salesperson pushing transactions." },
      { h: "Awareness and Interest", c: "Before anyone gives, they need to know you exist and care about what you do. Awareness happens through marketing, word of mouth, events, media coverage, and community presence. Interest develops when someone moves from knowing about you to wanting to learn more.\n\nThe gap between awareness and interest is huge. Millions of people are aware of major charities but only a fraction feel genuine interest. Your job is to convert awareness to interest through compelling stories, clear communication of impact, and opportunities for low-commitment engagement." },
      { h: "The Critical First Gift", c: "The first gift is the most important gift a donor will ever make to your organization because it transforms someone from observer to participant. Once someone has given once, they're far more likely to give again. Psychologists call this the consistency principle.\n\nFirst gifts are often small, maybe a $25 online donation or a $50 response to a mail appeal. The amount doesn't matter as much as the act of giving itself. That first gift creates psychological commitment and begins the relationship." },
      { h: "Building Repeat Donors", c: "A donor who gives once and never again wasn't really acquired at all. They were rented for a moment. A first-time donor has about a 19% chance of giving again the following year, which means more than 80% of first-time donors disappear.\n\nBut a repeat donor has about a 60% chance of giving again. This massive difference in retention rates explains why smart organizations invest heavily in turning first-time donors into repeat donors. The cost of acquiring a new donor is often five times the cost of retaining an existing one." },
      { h: "The Path to Major Giving", c: "Major donors don't appear out of nowhere. They're cultivated over time from your existing donor base. Someone who has given $100 for five consecutive years is a better major gift prospect than a wealthy stranger who has never given.\n\nThe path to major giving requires intentional moves, including personal outreach, opportunities to learn more about your work, invitations to events, and eventually a face-to-face conversation about a larger gift. This cultivation process typically takes 12 to 24 months." },
      { h: "Legacy Giving as the Ultimate Stage", c: "The final stage of the donor lifecycle is legacy giving, which includes bequests, beneficiary designations, and other planned gifts. These are often the largest gifts your organization will ever receive, sometimes exceeding a lifetime of annual giving in a single gift.\n\nLegacy giving appeals to donors' desire to leave a lasting mark on the world. It represents the deepest level of commitment because the donor is choosing to support your mission even after they're gone." },
      { h: "Why the Lifecycle Matters", c: "Understanding the donor lifecycle transforms how you approach fundraising. Instead of treating every donor the same, you tailor your approach to where each person is in their journey. New donors get welcomed and thanked. Repeat donors get upgraded opportunities. Long-time donors get personal attention and legacy conversations.\n\nOrganizations that master the lifecycle dramatically outperform those that treat fundraising as a series of one-off transactions. The relationship, nurtured over time, is what creates sustainable funding." }
    ],
    interactive: {
      type: "sorting",
      title: "Put the Donor Lifecycle in Order",
      items: ["Awareness", "Interest", "First Gift", "Repeat Gift", "Major Gift", "Legacy Gift"],
      shuffled: true
    },
    keyTakeaways: ["Donors move through predictable stages from awareness to legacy", "First gift creates psychological commitment to the organization", "Retention matters more than acquisition (19% vs 60% rates)", "Major donors are cultivated from existing donors over time"]
  },
  4: { 
    title: "The Donor Pyramid", 
    sections: [
      { h: "Visualizing Your Donor Base", c: "The donor pyramid is a visual model that helps you understand and segment your donor base. Picture a triangle with many donors at the bottom giving small amounts, fewer donors in the middle giving moderate amounts, and a handful at the top giving major gifts.\n\nEvery major donor started somewhere on this pyramid. They didn't write their first check for $50,000. They started with $100, then $500, then $1,000, and upgraded over time as their relationship with your organization deepened." },
      { h: "The Base of the Pyramid", c: "The base contains your largest number of donors giving the smallest amounts. These are typically annual fund donors contributing $25 to $250 through direct mail, online appeals, and events. They may number in the hundreds or thousands.\n\nBase donors are essential for several reasons. They provide broad community support that appeals to institutional funders. They're the cultivation ground for future mid-level and major donors. And they generate reliable annual revenue that supports operations." },
      { h: "The Neglected Middle", c: "Mid-level donors give $1,000 to $10,000 annually, and they're often the most neglected segment in nonprofit fundraising. They're too big for mass communication but not big enough for major gift attention, so they fall through the cracks.\n\nOrganizations that create dedicated mid-level programs see dramatically better results. Personal phone calls, handwritten notes, and exclusive briefings cost relatively little but generate significant returns from donors who feel seen and appreciated." },
      { h: "The 80/20 Rule", c: "The 80/20 rule typically applies to nonprofit giving. Roughly 80% of individual donation revenue comes from 20% of donors. This concentration creates both opportunity and risk.\n\nThe opportunity is clear: invest heavily in retaining and upgrading your top donors. The risk is dependency: losing one or two top donors can create budget crisis. Smart organizations work to broaden their major gift base while maintaining existing relationships." },
      { h: "Moving Donors Up", c: "Donor movement up the pyramid requires intentional cultivation. You're not just asking for more money. You're deepening relationships and increasing commitment over time.\n\nUpgrade asks should follow demonstrated engagement. A donor who opens every email, attends events, and responds to surveys is ready for an upgrade conversation. A donor who barely engages should be nurtured before being asked for more." },
      { h: "Using the Pyramid Strategically", c: "The pyramid helps you allocate resources appropriately. Major donors get personal attention. Mid-level donors get enhanced communication. Base donors get efficient but warm mass communication.\n\nMap your current donor base to a pyramid. Are you neglecting the middle? Losing donors at the base? Failing to identify major gift prospects? The visualization reveals gaps in your strategy that numbers alone might hide." }
    ],
    interactive: {
      type: "slider",
      title: "What % of revenue comes from top 20% of donors?",
      answer: 80,
      tolerance: 10
    },
    keyTakeaways: ["Pyramid visualizes giving levels from base to top", "Mid-level donors ($1K-$10K) are often most neglected", "80/20 rule means top donors drive most revenue", "Use pyramid to identify gaps and allocate resources"]
  },
  6: { 
    title: "Annual Giving Programs", 
    sections: [
      { h: "What Annual Giving Means", c: "Annual giving is systematic, recurring solicitation designed to fund ongoing operations year after year. Unlike major gifts that fund specific projects or capital campaigns that build buildings, annual giving supports the general budget. It keeps the lights on, pays salaries, and delivers programs.\n\nEvery organization needs annual giving because it provides reliable baseline revenue and engages the broadest possible donor base. Even organizations focused on major gifts need annual giving to cultivate future major donors." },
      { h: "The Annual Fund Calendar", c: "Smart annual giving follows the calendar. Year-end dominates charitable giving, with November and December accounting for roughly 30% of all annual charitable donations. Your year-end campaign is probably your most important appeal.\n\nSpring appeals work well during tax refund season when people have money in hand. Summer is typically slowest, though targeted appeals to specific audiences can still succeed. Fall builds momentum toward year-end with early asks that prime donors for the big push." },
      { h: "Multi-Channel Strategy", c: "Effective annual giving uses multiple channels because different donors prefer different ways of giving. Direct mail still works remarkably well for older donors who appreciate physical letters. Email is efficient and measurable with instant response tracking. Phone calls work particularly well for renewals and lapsed donor reactivation.\n\nDon't put all your eggs in one channel. A multi-channel approach increases response rates because you reach donors in their preferred medium. Test which channels work best for different segments of your audience." },
      { h: "Segmentation Strategies", c: "Not every donor should get the same message. Segmentation divides your donor base into groups that receive tailored communications. New donors get welcome messaging. Lapsed donors get reactivation appeals. Loyal donors get gratitude and upgrade asks.\n\nEffective segmentation dramatically improves results. A personalized appeal to someone who gave $100 last year asking them to renew at $100 outperforms a generic ask that ignores their history." },
      { h: "Renewal and Acquisition", c: "Annual giving involves two distinct activities: renewal and acquisition. Renewal asks existing donors to give again. Acquisition finds new donors. Both are essential, but they require different approaches.\n\nRenewal is more efficient because you're asking people who already believe in your mission. Acquisition is more expensive but necessary for growth. A healthy program invests appropriately in both while prioritizing retention." },
      { h: "Measuring Annual Giving Success", c: "Track key metrics to improve your annual giving program. Response rate tells you how many people who received an appeal actually gave. Average gift shows how much each donor contributes. Retention rate reveals how many donors give again year after year.\n\nThe most important metric is often cost to raise a dollar. Efficient annual giving programs spend 15 to 25 cents to raise each dollar. Programs spending more need optimization." }
    ],
    interactive: {
      type: "matching",
      title: "Match Channel to Best Use",
      pairs: [
        { left: "Direct Mail", right: "Older donors" },
        { left: "Email", right: "Quick appeals" },
        { left: "Phone", right: "Renewals" },
        { left: "Social Media", right: "Awareness" }
      ]
    },
    keyTakeaways: ["Annual giving funds operations and cultivates future major donors", "Year-end (Nov/Dec) accounts for 30% of annual giving", "Use multiple channels and segment your audience", "Track response rate, average gift, and retention"]
  },
  7: { 
    title: "Foundation Grants Reality", 
    sections: [
      { h: "How Foundation Funding Actually Works", c: "Many nonprofits approach grants with unrealistic expectations, treating foundation funding like easy money waiting to be collected. The reality is far different. A major foundation might receive 5,000 proposals annually and fund only 200, which translates to a 4% success rate.\n\nFoundation funding is valuable but shouldn't be your only strategy. Organizations that depend too heavily on grants often find themselves lurching from project to project, always chasing the next grant instead of building sustainable programs." },
      { h: "Types of Foundations", c: "Private foundations are funded by individuals or families and often reflect the founder's personal interests. The Gates Foundation focuses on global health. The Ford Foundation focuses on social justice. Understanding a foundation's history and values helps you assess fit.\n\nCorporate foundations are funded by companies and typically prioritize communities where the company operates. Community foundations pool donations from many sources for local grantmaking and know the local nonprofit landscape deeply." },
      { h: "The Myth of the Mega-Foundation", c: "Most foundations are small, not large. The median foundation has assets under $1 million and makes grants totaling less than $100,000 annually. The Gates Foundation and Ford Foundation dominate headlines but represent a tiny fraction of total foundations.\n\nDon't build your strategy around long-shot applications to mega-foundations. Focus on smaller, local foundations where you can build relationships and where your proposal won't be one of thousands competing for attention." },
      { h: "Research Before You Apply", c: "Never apply to a foundation without researching it first. Review their 990 tax returns to see who they've actually funded. Read their guidelines carefully to ensure your work aligns with their priorities. Look for connections: board members you know, organizations similar to yours that they've funded.\n\nBlind applications to foundations that don't fund your type of work waste everyone's time. A targeted list of 20 well-matched foundations beats a spray-and-pray approach to 100 random prospects." },
      { h: "The Relationship Factor", c: "Foundation funding isn't just about proposals. Relationships matter enormously. Program officers are more likely to fund organizations they know and trust. Attending foundation events, requesting informational meetings, and building connections over time all increase your odds.\n\nThe best grant opportunities often aren't publicly announced. Foundations sometimes identify organizations they want to support and invite them to apply. You can't access these opportunities without relationships." },
      { h: "Managing Foundation Relationships", c: "When you receive a grant, your work has just begun. Meet every deadline, deliver every report, and communicate proactively about challenges. Foundations track which grantees are easy to work with and which create headaches.\n\nStrong grantee relationships lead to renewal funding and larger grants over time. Foundations prefer to grow relationships with proven partners rather than constantly finding new grantees." }
    ],
    interactive: { type: "slider", title: "What's the typical foundation grant success rate?", answer: 4, tolerance: 2 },
    keyTakeaways: ["Foundation funding success rate is only about 4%", "Most foundations are small (under $1M in assets)", "Research thoroughly before applying anywhere", "Relationships matter as much as proposals"]
  },
  8: { 
    title: "Corporate Partnerships", 
    sections: [
      { h: "Why Companies Give", c: "Companies give for business reasons, not charity. They're looking for brand alignment, employee engagement, market access, and community goodwill. Understanding their motivation helps you position your ask effectively.\n\nA company considering a partnership is asking what's in it for them. The more clearly you can articulate the business value you offer, the more likely they are to say yes. This isn't cynical. It's realistic." },
      { h: "Sponsorship vs Philanthropy", c: "Corporate giving happens through two distinct channels that operate very differently. Corporate foundations make philanthropic grants based on community benefit, similar to private foundations. Corporate marketing departments buy sponsorships because they want marketing value in return.\n\nKnow which door you're knocking on. A philanthropy pitch to a marketing team falls flat because they want ROI. A sponsorship pitch to a foundation seems transactional because they want community impact." },
      { h: "What Companies Get from Sponsorship", c: "Brand visibility through logo placement puts the company name in front of your audience. Audience access creates opportunities for direct engagement with potential customers. Employee engagement helps companies recruit and retain talent by showing community commitment.\n\nThink about what your organization offers that companies want. A youth sports league offers family audiences. An arts organization offers affluent patrons. A health charity offers demonstration of corporate values." },
      { h: "Building the Partnership", c: "Corporate partnerships take time to develop. Start by identifying companies with logical fit to your mission. Research their giving history and stated priorities. Find connections who can make introductions.\n\nOnce you have a conversation, focus on their needs as much as yours. What are their marketing goals? What audiences do they want to reach? How can you help them succeed while advancing your mission?" },
      { h: "Creating Sponsorship Packages", c: "Effective sponsorship packages are tiered, offering different levels of investment with corresponding benefits. Bronze sponsors might get logo placement. Gold sponsors might get event tickets and employee volunteer opportunities. Platinum sponsors might get naming rights and executive visibility.\n\nPackage benefits should have clear value. Don't offer things companies don't want. Do offer things they can measure and report to their leadership." },
      { h: "Maintaining Corporate Relationships", c: "Once you have a corporate partner, work hard to keep them. Deliver everything you promised and then some. Provide impact reports that help their communications team tell the story internally. Make their contact look good to their boss.\n\nCorporate relationships can last for years when managed well. Losing a corporate sponsor is often failure of relationship management rather than loss of interest." }
    ],
    interactive: { type: "matching", title: "Match the Corporate Motivation", pairs: [{ left: "Marketing Budget", right: "Sponsorship" }, { left: "Foundation", right: "Philanthropy" }, { left: "HR Budget", right: "Employee Engagement" }, { left: "Community Relations", right: "Local Goodwill" }] },
    keyTakeaways: ["Corporate giving serves business purposes first", "Sponsorship comes from marketing (wants ROI)", "Philanthropy comes from foundation (wants impact)", "Deliver value and maintain the relationship"]
  },
  9: { 
    title: "Earned Revenue Strategies", 
    sections: [
      { h: "What Earned Revenue Means", c: "Earned revenue comes from selling goods or services rather than asking for donations. It includes fee-for-service programs where clients pay for what they receive, product sales like merchandise or publications, consulting and training services, and ticket sales for performances or events.\n\nEarned revenue is attractive because it's unrestricted. There are no grant restrictions telling you how to spend it, no donor preferences to accommodate, no reporting requirements to meet. You can use earned revenue however you need to." },
      { h: "The Case for Earned Revenue", c: "Diversification is the primary argument for earned revenue. When you depend entirely on donations and grants, you're vulnerable to economic downturns, funder priority changes, and donor fatigue. Earned revenue provides a buffer.\n\nAdditionally, some funders view earned revenue positively as a sign of entrepreneurial thinking and sustainability planning. An organization that generates its own revenue appears more stable than one entirely dependent on grants." },
      { h: "The Challenges of Earned Revenue", c: "Different skills are required because selling is different from fundraising. Fundraising asks people to support your mission. Selling asks people to buy something they want. The value proposition changes completely.\n\nStartup investment is typically necessary because products need development and marketing costs money. Many nonprofits underestimate how long it takes for an earned revenue venture to become profitable." },
      { h: "Mission Alignment Concerns", c: "Mission drift risk is real when pursuing earned revenue. The temptation to chase profitable opportunities that don't advance mission can pull organizations away from their core purpose.\n\nBefore launching any earned revenue venture, ask whether this advances your mission or distracts from it. A museum gift shop selling merchandise related to exhibits is mission-aligned. The same museum opening an unrelated business just for profit is drifting." },
      { h: "Common Earned Revenue Models", c: "Fee-for-service means charging clients for programs. This works when clients have ability to pay or when third parties like government or insurance will pay. Training and consulting packages your expertise for sale to others who want to learn.\n\nProduct sales can range from simple merchandise to complex social enterprises. Ticket sales work for organizations that produce events people want to attend." },
      { h: "Getting Started with Earned Revenue", c: "Start with market research. Is there actually demand for what you're selling? Who are your competitors? What price will the market bear? Many earned revenue ventures fail because they skip this step.\n\nStart small and test before scaling. A pilot program reveals problems before you've invested heavily. Track results carefully to understand true profitability including all costs." }
    ],
    interactive: { type: "sorting", title: "Rank Revenue Types by Restriction (Most to Least)", items: ["Government Grant", "Foundation Grant", "Corporate Sponsorship", "Earned Revenue"] },
    keyTakeaways: ["Earned revenue is completely unrestricted", "Different skills required than fundraising", "Watch carefully for mission drift risk", "Do thorough market research before launching"]
  },
  11: { 
    title: "Board Fundraising Responsibility", 
    sections: [
      { h: "Why Board Members Must Fundraise", c: "Board members often resist fundraising because they think it's the staff's job. But board participation in fundraising isn't optional. It's a core governance responsibility that every board member accepts when they join, whether they realize it or not.\n\nBoard members bring credibility that staff can't replicate. When a volunteer leader makes an ask, donors understand that this person believes in the organization strongly enough to give their time without compensation. That endorsement carries weight." },
      { h: "The Give, Get, or Get Off Principle", c: "The traditional expectation is that board members will give personally, get others to give, or get off the board. This sounds harsh but reflects reality. Boards that don't participate in fundraising force organizations to rely entirely on staff capacity.\n\n100% board giving is an industry standard that foundations and major donors routinely ask about. When every board member gives, it demonstrates universal commitment regardless of gift size." },
      { h: "What Giving Personally Means", c: "Every board member should make a personally significant gift. What's significant varies by individual means, but it should be among their top three to five charitable priorities. A wealthy board member giving $100 isn't personally significant. A modest-income member stretching to give $500 is.\n\nPersonal giving creates integrity in asking others. How can you ask someone to support something you don't support yourself? Your own gift gives you moral authority." },
      { h: "Identifying and Cultivating Prospects", c: "Every board member has networks that include potential donors. Their job isn't necessarily to make asks themselves but to identify prospects, make introductions, and participate in cultivation.\n\nA board member might host a dinner party where the executive director can meet their wealthy friends. They might forward information to colleagues who might be interested. They might accompany staff on cultivation visits." },
      { h: "Overcoming Fear and Reluctance", c: "Fear of asking is the main barrier to board fundraising. Board members worry about damaging relationships, appearing pushy, or facing rejection. These fears are understandable but manageable.\n\nTraining helps enormously. Board members who understand the donor lifecycle, who know how to tell the organization's story, and who practice asking in safe environments become more confident. Start with easy asks and build from there." },
      { h: "Creating Fundraising Expectations", c: "Set clear expectations before people join your board. Include fundraising responsibility in the job description. Discuss specific expectations during recruitment. Get commitment in writing.\n\nOnce on the board, provide regular training and support. Celebrate successes publicly. Make fundraising a standing agenda item at board meetings. What gets attention gets done." }
    ],
    interactive: { type: "matching", title: "Match the Board Role", pairs: [{ left: "Give", right: "Personal donation" }, { left: "Get", right: "Raise from others" }, { left: "Govern", right: "Fiduciary oversight" }, { left: "Grow", right: "Identify prospects" }] },
    keyTakeaways: ["Board fundraising is a governance responsibility", "Every board member should give personally significant gifts", "Fear of asking is the main barrier to participation", "Training and clear expectations increase participation"]
  },
  12: { 
    title: "Grant Research Deep Dive", 
    sections: [
      { h: "The Foundation Landscape", c: "More than 100,000 foundations operate in the United States, holding over $1 trillion in combined assets. They distribute roughly $90 billion annually in grants. This sounds like enormous opportunity, and it is, but the landscape is more complex than it appears.\n\nMost foundations are small. The median foundation has assets under $1 million and makes grants totaling less than $100,000 annually. The largest foundations dominate headlines but represent a tiny fraction of total foundations. Don't build your strategy around long-shot applications to mega-foundations." },
      { h: "Research Tools and Databases", c: "Foundation Directory Online (FDO) is the gold standard for foundation research. It contains profiles of every foundation in the country, their giving history, their trustees, and their stated interests. Candid, which operates FDO, also offers free resources through Funding Information Network partners.\n\nIRS Form 990s are publicly available and reveal exactly what foundations have funded. These forms are often more useful than foundation websites because they show actual grants made rather than stated priorities that may be outdated." },
      { h: "Reading 990 Forms", c: "Every foundation must file Form 990-PF annually with the IRS. Part XV lists grants paid, showing recipient names, amounts, and purposes. This tells you exactly who the foundation actually funds.\n\nLook for patterns. Do they fund organizations like yours? In your geographic area? At what amounts? Are there trends over time? One grant to an organization like yours might be coincidence. Multiple grants suggest genuine interest." },
      { h: "Building Your Foundation Pipeline", c: "A healthy foundation pipeline contains 20 to 50 prospects at various stages. Some are new prospects you're just researching. Some are foundations you're cultivating through relationship building. Some are active applications awaiting decisions. Some are current funders you're stewarding for renewal.\n\nPipeline management requires consistent effort. Block time weekly for foundation research and relationship building. Don't let the pipeline dry up because you're too busy managing current grants." },
      { h: "Prospect Qualification", c: "Not every foundation that looks good on paper is actually a prospect. Qualification means determining whether a foundation is genuinely a good fit worth pursuing.\n\nAsk these questions: Do they fund your issue area? Your geography? Your organization size? Your type of work? Do you have any connection to their trustees? Have they funded similar organizations? A prospect that fails multiple questions isn't worth pursuing." },
      { h: "From Research to Relationship", c: "Research is just the beginning. The goal is relationship, not just information. Once you've identified qualified prospects, figure out how to connect with them.\n\nAttend foundation-sponsored events. Request informational meetings when appropriate. Ask current funders if they can make introductions. Connect with trustees through board members. The best grants come from relationships built over time, not cold applications." }
    ],
    interactive: { type: "slider", title: "How many foundations operate in the US? (in thousands)", answer: 100, tolerance: 20 },
    keyTakeaways: ["100,000+ foundations operate in the US", "Most foundations are small (under $1M assets)", "Use FDO and 990 forms for research", "Build a pipeline of 20-50 qualified prospects"]
  },
  13: { 
    title: "Donor Retention Strategies", 
    sections: [
      { h: "The Retention Crisis", c: "Nonprofit donor retention averages around 45%, which means more than half of donors who give one year don't give the next. First-time donor retention is even worse, hovering around 19%. These numbers represent enormous lost opportunity and wasted acquisition investment.\n\nThink about what these numbers mean practically. You spend money and effort acquiring a new donor. They give once, perhaps $50. Then they never give again. You've lost not just that $50 but the thousands of dollars they might have given over a lifetime." },
      { h: "Why Donors Lapse", c: "Donors stop giving for predictable reasons. They feel their gift didn't matter because no one showed them impact. They feel unappreciated because thanking was slow or generic. They forget because communication was infrequent. They find other priorities because engagement was shallow.\n\nNotice what's not on this list: they stopped caring about the cause. Donors who cared enough to give usually still care. They lapsed because the organization failed to maintain the relationship." },
      { h: "The Thank-You System", c: "Speed matters more than you think. Donors should receive acknowledgment within 48 hours, and 24 hours is better. In a world of instant communication, a thank you that arrives a month later feels like afterthought.\n\nPersonalization increases impact significantly. A handwritten note beats a form letter. A phone call from a board member beats an email. The thank you should make the donor feel seen and appreciated as an individual." },
      { h: "Communication Between Asks", c: "The cardinal rule of retention is to communicate with donors three times for every ask. If you only contact donors when you want money, they feel like ATMs. If you share stories, invite them to events, and celebrate successes, they feel like partners.\n\nCreate a communication calendar that ensures regular touch points. Newsletter. Impact update. Event invitation. Volunteer opportunity. Thanking again for last year's gift. Then, and only then, the next ask." },
      { h: "Retention Metrics and Tracking", c: "Measure retention rate by segment. Overall retention matters, but you need to know specifically how first-time donors, repeat donors, major donors, and lapsed donors are behaving. Each segment needs different strategies.\n\nCalculate donor lifetime value by looking at how long donors stay and how much they give over time. This helps you understand how much you can afford to invest in retention activities." },
      { h: "Recovery and Reactivation", c: "Lapsed donors are easier to recover than new donors are to acquire. They already believe in your mission. Something just went wrong in the relationship.\n\nCreate specific reactivation campaigns for lapsed donors. Acknowledge the gap in giving. Share what's happened since they last gave. Make a compelling case for why now is the time to return. These campaigns often have surprisingly high response rates." }
    ],
    interactive: { type: "slider", title: "What's the average donor retention rate?", answer: 45, tolerance: 10 },
    keyTakeaways: ["Average retention is only 45%, first-time is 19%", "Donors lapse from feeling unappreciated, not from losing interest", "Thank quickly and personally within 48 hours", "Communicate 3x between asks to build relationship"]
  },
  14: { 
    title: "Fundraising Systems and Tools", 
    sections: [
      { h: "Why Systems Matter", c: "Fundraising without systems is like trying to remember every conversation you've ever had. It works when you have 50 donors, fails completely when you have 500, and becomes impossible at 5,000. Systems create institutional memory that survives staff turnover.\n\nThe best system is the one you'll actually use. A sophisticated CRM that nobody updates is worthless. A simple spreadsheet that gets used consistently beats an expensive database that sits empty." },
      { h: "What CRMs Do", c: "A Customer Relationship Management system (CRM) is the central hub for donor information. It stores contact details, giving history, communication records, relationships, and notes. Everything you know about a donor lives in the CRM.\n\nGood CRMs do more than store data. They segment donors for targeted communication, remind you of follow-up tasks, generate reports, and integrate with your email and online giving platforms." },
      { h: "Choosing a CRM", c: "Entry-level options like Little Green Light and Bloomerang work well for small organizations with straightforward needs. They're affordable and relatively easy to use.\n\nMid-range options like Raiser's Edge NXT and DonorPerfect offer more sophisticated features for growing organizations. They cost more but provide advanced analytics and customization. Enterprise options like Salesforce handle massive complexity for large organizations." },
      { h: "Email and Communication Tools", c: "Email marketing platforms like Mailchimp, Constant Contact, and Mailerlite manage mass communication. They segment audiences, track opens and clicks, and comply with anti-spam regulations.\n\nIntegration between your CRM and email platform is essential. Donor data should flow seamlessly so you can segment communications based on giving history and engagement." },
      { h: "Online Giving Platforms", c: "Online giving platforms like Classy, Network for Good, and Fundraise Up process donations through your website. They handle payment processing, generate tax receipts, and can create giving forms and campaign pages.\n\nMobile optimization is essential. More than half of online giving now happens on mobile devices. Test your giving experience on phones to ensure it's smooth." },
      { h: "Building Your Tech Stack", c: "Your tech stack is the combination of tools that work together to support your fundraising. At minimum, you need a CRM, email platform, and online giving platform. They should integrate with each other.\n\nStart simple and add complexity as you grow. Don't buy more technology than you can actually implement and use. Underused tools are wasted money." }
    ],
    interactive: { type: "matching", title: "Match the CRM to Its Level", pairs: [{ left: "Little Green Light", right: "Entry Level" }, { left: "Raiser's Edge NXT", right: "Mid-Range" }, { left: "Salesforce", right: "Enterprise" }, { left: "Spreadsheet", right: "Not a CRM" }] },
    keyTakeaways: ["Systems create institutional memory for sustainability", "Choose a CRM you'll actually use consistently", "Integrate CRM, email, and online giving platforms", "Start simple and add complexity as needed"]
  },
  16: { 
    title: "Grant Writing Basics", 
    sections: [
      { h: "What Makes Grant Writing Different", c: "Grant writing is persuasive writing with a formal structure. Unlike donor appeals that can be emotional and personal, grant proposals follow prescribed formats and must make logical arguments supported by evidence.\n\nMost rejected grants aren't rejected because the work is bad. They're rejected because the proposal didn't communicate effectively. The reader couldn't understand what you wanted to do, why it mattered, or how you'd accomplish it." },
      { h: "Understanding the Funder's Perspective", c: "Program officers read hundreds of proposals. They're looking for reasons to say no because they can't fund everyone. A confusing proposal, a budget that doesn't add up, or a mission that doesn't align with priorities gives them that reason.\n\nYour job is to make it easy to say yes. Clear writing, logical structure, compelling evidence, and perfect alignment with stated priorities all increase your odds." },
      { h: "Standard Grant Components", c: "Most grant proposals include similar components. The executive summary captures everything in one page. The statement of need establishes why the problem matters and why now. The project description explains what you'll do and how.\n\nThe evaluation plan shows how you'll measure success. The organizational background establishes your credibility. The budget translates the narrative into numbers." },
      { h: "The Statement of Need", c: "The statement of need is often the weakest section of grant proposals. It should establish three things: the problem exists, the problem is significant, and the problem is solvable with the right intervention.\n\nUse data to establish scope but don't drown readers in statistics. One or two compelling numbers plus a brief story creates more impact than pages of data." },
      { h: "Budget Alignment", c: "Your budget must match your narrative exactly. If the narrative describes hiring two staff members, the budget must include two staff members. Reviewers check this alignment carefully.\n\nInclude realistic costs. Underbudgeting to seem efficient often backfires because reviewers assume you don't understand what the project actually requires." },
      { h: "Common Mistakes to Avoid", c: "Don't make the proposal about you. Make it about the problem and the people you serve. Funders care about impact, not your organization's needs.\n\nFollow instructions exactly. If they ask for five pages, submit five pages. If they want a specific format, use that format. Ignoring instructions signals you don't pay attention to details." }
    ],
    interactive: { type: "sorting", title: "Put Grant Sections in Order", items: ["Executive Summary", "Statement of Need", "Project Description", "Evaluation Plan", "Budget"] },
    keyTakeaways: ["Grant writing is formal persuasive writing", "Make it easy for reviewers to say yes", "Budget must match narrative exactly", "Follow instructions precisely"]
  },
  17: { 
    title: "Major Donor Identification", 
    sections: [
      { h: "Who Are Major Donors", c: "A major donor is anyone who gives at a level that merits personal attention from your organization. The threshold varies by organization size. For a small nonprofit, major might mean $1,000 or more. For a large institution, major might mean $25,000 or more.\n\nMost organizations underestimate how many potential major donors are already in their database. The typical nonprofit has wealth hidden among its current donors that's never been identified or cultivated." },
      { h: "The LAI Framework", c: "Major donor identification uses three criteria: Linkage, Ability, and Interest. Linkage means connection to your organization or to someone who can make an introduction. Ability means financial capacity to make a major gift. Interest means genuine care about your mission.\n\nThe best prospects have all three. Someone with enormous wealth but no connection to your work is hard to reach. Someone deeply committed but without resources can't give at major gift levels." },
      { h: "Wealth Screening", c: "Wealth screening services analyze your donor database against public data sources to estimate giving capacity. They identify real estate ownership, stock holdings, business interests, and other giving.\n\nScreening is a starting point, not an answer. It tells you who might have capacity but doesn't tell you about connection or interest. Always verify screening results with your own knowledge." },
      { h: "Looking in Your Own Database", c: "Your best major gift prospects are probably already in your database. Look for loyal donors who give every year. Look for donors who attend events or volunteer. Look for donors whose giving has increased over time.\n\nThese engagement signals matter more than wealth alone. A committed $500 donor might become a $10,000 donor with proper cultivation. A wealthy stranger with no engagement probably won't." },
      { h: "Research and Qualification", c: "Once you identify potential major donors, research them further. What are their interests? What other organizations do they support? What's their connection to your board or staff?\n\nQualification means determining whether to invest cultivation time. Not every wealthy person is a prospect for your organization. Focus on those with genuine potential." },
      { h: "Building the Portfolio", c: "Major gift officers manage portfolios of 100 to 150 prospects. Each prospect is at a different stage: identification, cultivation, solicitation, or stewardship.\n\nEven without a major gift officer, you can build a portfolio. Identify your top 20 to 30 prospects and create plans for each. Who will cultivate them? What activities will build the relationship?" }
    ],
    interactive: { type: "matching", title: "Match the Prospect Indicator", pairs: [{ left: "Capacity", right: "Ability to give" }, { left: "Affinity", right: "Connection to cause" }, { left: "Propensity", right: "Likelihood to give" }, { left: "Linkage", right: "Who can ask" }] },
    keyTakeaways: ["Major donors merit personal attention at whatever threshold fits your organization", "Use LAI: Linkage, Ability, and Interest to identify prospects", "Your best prospects are already in your database", "Build a portfolio of 20-150 prospects with cultivation plans"]
  },
  18: { 
    title: "Planned Giving Introduction", 
    sections: [
      { h: "What Planned Giving Means", c: "Planned giving refers to gifts that require some planning to execute, typically gifts from assets rather than income. This includes bequests in wills, beneficiary designations on retirement accounts and insurance policies, charitable remainder trusts, and gifts of appreciated stock.\n\nPlanned gifts are often the largest gifts your organization will ever receive. A donor who gives $500 annually for 20 years gives $10,000 total. That same donor might leave a bequest of $50,000 or more." },
      { h: "Bequests Are the Starting Point", c: "Bequests, gifts in wills, are the most common form of planned giving and the easiest to understand. A donor simply includes your organization in their will, either for a specific amount or a percentage of their estate.\n\nMarketing bequests requires normalizing the conversation about end-of-life planning. Many people resist because they don't want to think about mortality. Frame it as legacy, not death." },
      { h: "Beneficiary Designations", c: "Retirement accounts and life insurance policies allow owners to name charitable beneficiaries. This is simpler than a bequest because it doesn't require changing a will, just filling out a form with the account administrator.\n\nThese gifts are particularly valuable for retirement accounts because assets pass to charity tax-free, while heirs would owe income tax on inherited retirement funds." },
      { h: "Gifts of Appreciated Stock", c: "Donors who give appreciated stock avoid capital gains tax while receiving a deduction for the full market value. If someone bought stock for $10,000 and it's now worth $50,000, giving the stock saves them $8,000 in capital gains tax compared to selling and donating cash.\n\nMake it easy for donors to give stock by having your brokerage information readily available and your process clearly documented." },
      { h: "Charitable Trusts and Annuities", c: "More complex planned giving vehicles include charitable remainder trusts, which provide donors with income during their lifetime with the remainder going to charity, and charitable gift annuities, which provide fixed payments to donors in exchange for a gift.\n\nThese vehicles require professional advice and are typically appropriate only for larger gifts. Partner with legal and financial professionals to offer these options." },
      { h: "The Legacy Society", c: "Create a legacy society to recognize and steward planned giving donors. These donors have made the ultimate commitment by including you in their estate plans. They deserve special recognition.\n\nLegacy societies build community among planned giving donors and encourage others to follow their example. Public recognition of legacy gifts normalizes the practice." }
    ],
    interactive: { type: "matching", title: "Match the Planned Gift Type", pairs: [{ left: "Bequest", right: "Gift in will" }, { left: "Beneficiary Designation", right: "IRA or life insurance" }, { left: "CRT", right: "Provides donor income" }, { left: "Stock Gift", right: "Avoids capital gains" }] },
    keyTakeaways: ["Planned gifts are often the largest gifts organizations receive", "Bequests are the most common and easiest planned gift", "Stock gifts offer significant tax advantages", "Create a legacy society to recognize planned giving donors"]
  },
  19: { 
    title: "Corporate Sponsorship Models", 
    sections: [
      { h: "What Companies Want from Sponsorship", c: "Corporate sponsorship is a marketing investment, not philanthropy. Companies sponsor because they want something back: brand visibility, audience access, employee engagement, or community goodwill. Your job is to deliver measurable value.\n\nUnderstanding what each company wants helps you customize your pitch. A bank wants community presence. A consumer brand wants audience exposure. A tech company wants talent recruitment." },
      { h: "Brand Visibility Benefits", c: "Logo placement puts the company name in front of your audience through event signage, website presence, printed materials, and social media. The value of this visibility depends on your audience size and demographics.\n\nQuantify what you're offering. How many people attend your events? Visit your website? Follow your social media? Receive your emails? These numbers justify sponsorship investment." },
      { h: "Audience Access Benefits", c: "Beyond passive visibility, sponsors often want active engagement with your audience. Event tickets let them entertain clients. Speaking opportunities position their executives as thought leaders. Data sharing provides marketing intelligence.\n\nBe careful with audience access promises. Protect your constituents' privacy and don't promise access that feels intrusive or inappropriate." },
      { h: "Creating Tiered Packages", c: "Effective sponsorship programs offer tiered packages at different price points. A typical structure might include Bronze, Silver, Gold, and Platinum levels, each with increasing benefits.\n\nTiered packages make decisions easier for companies. They can choose the level that fits their budget and goals without negotiating a custom arrangement." },
      { h: "Category Exclusivity", c: "Category exclusivity means guaranteeing a sponsor that no competitor will also sponsor. If Coca-Cola sponsors, Pepsi can't. This exclusivity commands premium pricing because it protects the sponsor's investment.\n\nDecide which categories you're willing to offer exclusively. Once you sell a category, you've closed the door on other companies in that space." },
      { h: "Fulfillment and Reporting", c: "Deliver everything you promise and document it. Take photos of logo placement. Track impressions and engagement. Provide a comprehensive fulfillment report after each event or campaign.\n\nStrong reporting helps sponsors justify their investment internally. If you can prove ROI, renewal is much easier. If you can't demonstrate value delivered, expect the relationship to end." }
    ],
    interactive: { type: "sorting", title: "Rank Sponsorship Benefits by Value (Highest to Lowest)", items: ["Category Exclusivity", "Logo Placement", "VIP Access", "Social Media Mention"] },
    keyTakeaways: ["Sponsorship is marketing investment, not philanthropy", "Quantify audience size and demographics", "Create tiered packages with clear benefits", "Document and report everything you deliver"]
  },
  21: { 
    title: "Digital Fundraising Tactics", 
    sections: [
      { h: "The Digital Landscape", c: "Digital fundraising has become essential because that's where donors are. People discover organizations online, research them online, and increasingly give online. If you're not visible digitally, you're invisible to many potential donors.\n\nBut digital fundraising isn't a separate thing from other fundraising. It's a channel, not a strategy. The fundamentals of donor motivation and relationship building still apply." },
      { h: "Email as the Workhorse", c: "Email remains the most effective digital fundraising channel. It's direct, personal, and measurable. A well-crafted email appeal can generate significant response, especially from existing donors.\n\nBuild your email list continuously. Every event, every website visitor, every person who engages with you is a potential email subscriber. The larger your list, the more you can raise." },
      { h: "Website Optimization", c: "Your website is your digital home base where conversions happen. The donation page must be simple, mobile-friendly, and trustworthy. Every extra click or confusing element reduces conversions.\n\nTest your donation process yourself. How many clicks does it take to complete a gift? Does it work on mobile? Would you trust this page with your credit card?" },
      { h: "Social Media Strategy", c: "Social media builds awareness and engagement but rarely drives direct donations. Think of it as the top of your funnel, bringing new people into your orbit who you can then cultivate through other channels.\n\nFocus on platforms where your audience actually is. Don't spread yourself thin across every platform. Better to be excellent on two platforms than mediocre on six." },
      { h: "Peer-to-Peer Fundraising", c: "Peer-to-peer campaigns turn your supporters into fundraisers who ask their own networks to give. This exponentially expands your reach beyond people you can contact directly.\n\nSuccess requires supporting your peer fundraisers with tools, templates, and coaching. Most people have never asked for money before. They need help knowing what to say." },
      { h: "Testing and Analytics", c: "Digital fundraising is measurable in ways that traditional fundraising isn't. Email open rates, click rates, conversion rates, and cost per acquisition all provide actionable data.\n\nTest continuously. Try different subject lines, sending times, and amounts asked. Small improvements compound over time into significant results." }
    ],
    interactive: { type: "matching", title: "Match Digital Channel to Strength", pairs: [{ left: "Email", right: "Direct solicitation" }, { left: "Social Media", right: "Awareness building" }, { left: "Website", right: "Conversion point" }, { left: "Peer-to-Peer", right: "Network expansion" }] },
    keyTakeaways: ["Digital is a channel, not a complete strategy", "Email is the most effective digital fundraising tool", "Optimize your website donation process for mobile", "Test and measure everything"]
  },
  22: { 
    title: "Storytelling for Impact", 
    sections: [
      { h: "Why Stories Matter", c: "Stories move people in ways that data cannot. A statistic about hunger says the problem is big. A story about one hungry child named Maria makes the problem real, urgent, and personal.\n\nDonors give with their hearts and justify with their heads. Data provides the justification, but emotion drives the decision. Stories create that emotional connection." },
      { h: "The Structure of Effective Stories", c: "Every effective story has a protagonist, a challenge, an intervention, and a transformation. The protagonist is a specific, named individual. The challenge is the problem they face. The intervention is how your organization helped. The transformation shows what changed.\n\nDon't make your organization the hero. The donor is the hero. Your organization is the vehicle through which donors can make a difference." },
      { h: "Finding Stories to Tell", c: "Stories are everywhere in your work. Program staff hear them daily. Clients experience them. The challenge is capturing them systematically.\n\nCreate a story collection process. Train staff to recognize and document stories. Get proper consent and releases. Build a library of stories you can draw from." },
      { h: "Ethical Storytelling", c: "Stories must be true and told with permission. Never fabricate or exaggerate. Always get consent from the people whose stories you're telling. Protect dignity and avoid exploitation.\n\nCompensate people for sharing their stories when possible. Their experience has value. Treating it as free content is extractive." },
      { h: "Stories Across Channels", c: "Different channels require different story formats. A direct mail letter might tell one story in depth. An email might share a brief vignette. A video might show the story visually.\n\nAdapt your stories to each medium while maintaining emotional core. The same story can work across multiple channels with appropriate formatting." },
      { h: "The Call to Action", c: "Every story should lead to an action. After moving the reader emotionally, tell them what to do: give, volunteer, share, advocate. Don't let the emotional momentum dissipate without direction.\n\nMake the connection between the story and the action explicit. Your gift will help more children like Maria. Join us in ensuring no family goes hungry." }
    ],
    interactive: { type: "sorting", title: "Put Story Elements in Order", items: ["Introduce Protagonist", "Present Challenge", "Show Intervention", "Reveal Transformation", "Call to Action"] },
    keyTakeaways: ["Stories move people more than data", "Include protagonist, challenge, intervention, and transformation", "Get consent and tell stories ethically", "Always end with a clear call to action"]
  },
  23: { 
    title: "Capital Campaigns Overview", 
    sections: [
      { h: "What Capital Campaigns Are", c: "A capital campaign is an intensive fundraising effort to raise a significant amount of money in a defined time period for a specific purpose. That purpose is typically a building, major renovation, endowment, or program expansion that requires funding beyond normal operations.\n\nCapital campaigns are fundamentally different from annual fundraising. They're time-limited, goal-driven, and focused on transformative gifts rather than routine support." },
      { h: "Feasibility First", c: "Before launching a capital campaign, conduct a feasibility study. This involves interviewing major gift prospects and community leaders to assess whether the project is fundable at the proposed amount.\n\nFeasibility studies reveal whether donors believe in the project, what amount they might consider giving, and what concerns might prevent success. Starting a campaign without this research risks public failure." },
      { h: "The Quiet Phase", c: "Capital campaigns begin with a quiet phase where you solicit major gifts before making any public announcement. The goal is to secure 50% or more of your goal before going public.\n\nThis approach works because donors give with confidence when they see others have already committed. A campaign announced at 60% complete feels almost done. A campaign announced at 0% feels risky." },
      { h: "The Gift Pyramid", c: "Capital campaign math follows a predictable pattern. The top 10 gifts typically provide 50% or more of the total. The next 100 gifts provide another 35%. Hundreds of smaller gifts complete the remaining 15%.\n\nThis means your entire campaign depends on identifying and cultivating a small number of lead donors. If you can't identify prospects for the top 10 gifts, your campaign will fail." },
      { h: "Campaign Materials and Case", c: "The case for support is the foundational document explaining why this campaign matters now. It must be compelling, clear, and answer every question a donor might ask.\n\nCampaign materials include the case statement, pledge forms, naming opportunity lists, and presentation decks. Everything must be polished and professional because you're asking for significant commitments." },
      { h: "Campaign Leadership", c: "Successful campaigns require volunteer leadership, typically a campaign chair and cabinet of major gift leaders who lend their names and make their own significant gifts.\n\nCampaign leadership provides credibility, access to networks, and demonstration of commitment. A campaign without strong volunteer leadership struggles to build momentum." }
    ],
    interactive: { type: "slider", title: "What % of goal should be secured in quiet phase?", answer: 50, tolerance: 10 },
    keyTakeaways: ["Capital campaigns fund major projects in defined timeframes", "Conduct feasibility study before launching", "Secure 50%+ in quiet phase before going public", "Top 10 gifts typically provide 50%+ of total"]
  },
  24: { 
    title: "Monthly Giving Programs", 
    sections: [
      { h: "The Power of Monthly Giving", c: "Monthly giving is the single most valuable form of individual giving for most organizations. A $25 monthly donor gives $300 annually, often for many years. Their retention rate approaches 90%, compared to 45% for one-time donors.\n\nThe math is compelling. Ten monthly donors at $25 generate $3,000 annually with minimal ongoing effort. The same $3,000 from one-time donors requires constant acquisition." },
      { h: "Why Donors Choose Monthly", c: "Monthly giving appeals to donors for several reasons. It fits their budget because small recurring amounts are easier than large lump sums. It reduces decision fatigue by eliminating repeated asks. It provides ongoing connection to a cause they care about.\n\nFrame monthly giving as membership in a community of committed supporters, not just a payment method." },
      { h: "Building Your Program", c: "Brand your monthly giving program with a distinctive name that creates identity. Sustainer Circle. Partners for Change. Monthly Champions. The name should convey belonging.\n\nCreate a dedicated landing page that explains the program, its benefits, and its impact. Make signing up simple with clear monthly amount options." },
      { h: "Promoting Monthly Giving", c: "Make the monthly option prominent in all giving situations. On your donation page, present monthly as the default or featured option. In appeals, specifically invite monthly commitment.\n\nConvert existing donors to monthly by asking those who give multiple times annually or who have given loyally over time. They're already committed; you're just changing the method." },
      { h: "Stewarding Monthly Donors", c: "Monthly donors deserve special attention even though their individual payments are small. Send monthly-specific acknowledgments. Provide exclusive updates or content. Recognize them publicly as sustaining members.\n\nMonitor credit card expirations and failures. A failed payment can end a monthly relationship if you don't catch it. Automated retry and card updater services help." },
      { h: "Upgrading Monthly Donors", c: "Annual upgrade asks increase the value of your monthly program. Each year, ask monthly donors to increase their gift by $5 or $10. Many will say yes if you make a compelling case.\n\nThe long-term value of monthly donors includes not just their ongoing monthly gifts but also the potential for larger annual fund gifts, major gifts, and planned gifts as the relationship deepens." }
    ],
    interactive: { type: "slider", title: "A $25/month donor gives how much annually?", answer: 300, tolerance: 0 },
    keyTakeaways: ["Monthly donors have dramatically higher retention (90% vs 45%)", "Brand your program with a distinctive name", "Make monthly the prominent option in all giving", "Upgrade monthly donors annually"]
  },
  26: { 
    title: "Board Development", 
    sections: [
      { h: "The Board's Essential Functions", c: "Boards exist to govern, not to manage. They set strategic direction, ensure financial accountability, and hire and evaluate the executive director. They don't run programs or manage staff.\n\nConfusion between governance and management is one of the most common board dysfunctions. Boards that micromanage frustrate staff and neglect their actual responsibilities." },
      { h: "Board Fundraising Responsibility", c: "Fundraising is a core board responsibility, not optional and not just for wealthy members. Every board member should give personally and participate in fundraising activities appropriate to their skills and connections.\n\nThe board's fundraising role includes identifying prospects, making introductions, participating in cultivation events, and in some cases making direct asks." },
      { h: "Recruiting Board Members", c: "Effective boards are intentionally recruited for specific skills, connections, and perspectives. Use a board matrix to identify gaps and recruit to fill them.\n\nDon't just recruit whoever says yes. Board recruitment is like hiring. You want the right people for specific roles, not just warm bodies to fill seats." },
      { h: "Diversity and Inclusion", c: "Board diversity strengthens governance and organizational effectiveness. Diverse perspectives lead to better decisions. Boards that reflect the communities they serve have more credibility.\n\nDiversity recruitment requires intentional effort. The default recruitment approach of friends of current members tends to reproduce existing demographics." },
      { h: "Term Limits and Rotation", c: "Term limits create healthy turnover that brings fresh perspectives and creates opportunities for new leaders. Three-year terms with the possibility of two consecutive terms is a common structure.\n\nWithout term limits, boards calcify. Long-serving members can dominate, and it becomes difficult to remove underperforming members." },
      { h: "Board Development as Investment", c: "Board meetings should include ongoing education. Bring in speakers on relevant topics. Have staff present on programs. Visit sites where your work happens.\n\nInvest in board development and retreats. An effective board is worth far more than an ineffective one. The investment pays returns in better governance and greater fundraising." }
    ],
    interactive: { type: "matching", title: "Match Board Function to Description", pairs: [{ left: "Governance", right: "Set direction and policy" }, { left: "Fiduciary", right: "Financial oversight" }, { left: "Fundraising", right: "Give and get" }, { left: "Ambassador", right: "Community representation" }] },
    keyTakeaways: ["Boards govern (strategy and oversight), not manage (operations)", "Recruit intentionally for skills, connections, and diversity", "Use term limits to ensure healthy rotation", "Invest in ongoing board development"]
  },
  27: { 
    title: "Prospect Research Methods", 
    sections: [
      { h: "What Prospect Research Does", c: "Prospect research identifies, qualifies, and prioritizes potential donors by gathering information about their capacity, affinity, and propensity to give. It's detective work in service of relationship building.\n\nResearch supports cultivation by providing background that helps you connect. Knowing someone serves on another nonprofit board or shares your alma mater creates conversation entry points." },
      { h: "Internal Data Sources", c: "Your CRM contains the most valuable data: giving history, event attendance, volunteer activity, and communication engagement. Someone who gives annually, attends events, and opens every email is highly engaged regardless of what external data shows.\n\nMining internal data often reveals major gift prospects hiding in plain sight: loyal donors whose capacity has never been assessed." },
      { h: "External Data Sources", c: "Wealth screening services like DonorSearch and WealthEngine analyze your database against public records. They identify real estate ownership, stock holdings, business interests, and giving to other organizations.\n\nIRS Form 990s show foundation grants, revealing both foundation prospects and individual trustees who may be donors themselves. SEC filings show stock holdings of corporate executives." },
      { h: "Public Records and News", c: "Real estate transactions are public record and indicate wealth. Business news reveals executive appointments, company sales, and other wealth events. Social media profiles show interests and connections.\n\nLinkedIn is particularly valuable for understanding professional networks. Who does your prospect know who might connect you?" },
      { h: "Research Ethics and Privacy", c: "Research must respect privacy. Use only publicly available information or data donors have shared with you. Don't access confidential records, misrepresent yourself, or engage in activities that would embarrass the organization if discovered.\n\nSecure research data appropriately. Donor financial information requires protection. Limit access to those who need it." },
      { h: "From Research to Action", c: "Research is worthless if not applied. Create prospect profiles that synthesize information into actionable insights. Share research with those who will use it for cultivation.\n\nThe goal isn't comprehensive dossiers but useful information that advances relationships. What do you need to know to have a good conversation with this person?" }
    ],
    interactive: { type: "matching", title: "Match the Research Source", pairs: [{ left: "DonorSearch", right: "Wealth screening" }, { left: "990 Forms", right: "Foundation giving" }, { left: "SEC Filings", right: "Stock holdings" }, { left: "Your CRM", right: "Giving history" }] },
    keyTakeaways: ["Research identifies and qualifies major gift prospects", "Internal CRM data is your most valuable source", "Use multiple external sources for complete picture", "Always respect privacy and ethical boundaries"]
  },
  28: { 
    title: "Fundraising Metrics That Matter", 
    sections: [
      { h: "Why Measurement Matters", c: "You can't improve what you don't measure. Fundraising metrics tell you what's working, what's not, and where to focus your energy. Without measurement, you're operating on intuition alone.\n\nBut not everything that can be measured should be measured. Focus on measurements that actually inform decisions. Vanity metrics that look impressive but don't guide action waste time." },
      { h: "Retention Rate", c: "Donor retention rate is arguably the most important fundraising metric. It measures what percentage of donors who gave last year gave again this year. The average is around 45%, which means most donors disappear.\n\nTrack retention by segment. First-time donor retention, multi-year donor retention, and major donor retention require different benchmarks and strategies." },
      { h: "Cost to Raise a Dollar", c: "Cost to raise a dollar (also called cost per dollar raised) measures fundraising efficiency. If you spend $20 to raise $100, your cost is $0.20 or 20 cents per dollar.\n\nHealthy programs typically spend 15 to 25 cents per dollar. New programs or acquisition campaigns cost more. Mature programs with strong retention cost less." },
      { h: "Lifetime Value", c: "Donor lifetime value calculates how much a donor gives over their entire relationship with your organization. A donor who gives $100 annually for 10 years has lifetime value of $1,000.\n\nUnderstanding lifetime value helps you make acquisition decisions. You can afford to lose money acquiring a donor if their lifetime value exceeds the acquisition cost." },
      { h: "Average Gift and Response Rate", c: "Average gift size tells you how much donors give when they do give. Response rate tells you what percentage of people asked actually give. Together they determine revenue.\n\nRevenue = People Asked x Response Rate x Average Gift. Improving any factor improves results." },
      { h: "Using Metrics for Decision Making", c: "The point of measurement is action. If retention is dropping, investigate why and implement retention programs. If average gift is flat, test upgrade strategies.\n\nCreate a dashboard of key metrics tracked over time. Monthly or quarterly review reveals trends that annual review misses." }
    ],
    interactive: { type: "slider", title: "What's a healthy cost to raise a dollar? (cents)", answer: 20, tolerance: 10 },
    keyTakeaways: ["Retention rate is the most important metric to track", "Healthy cost to raise a dollar is 15-25 cents", "Understand donor lifetime value for acquisition decisions", "Use metrics to make decisions, not just generate reports"]
  },
  29: { 
    title: "Strategic Planning for Development", 
    sections: [
      { h: "Why Planning Matters", c: "Random activity doesn't build sustainable fundraising. Strategic planning aligns resources, sets priorities, and creates accountability. Without a plan, you react to whatever seems urgent rather than pursuing what's important.\n\nA development plan isn't a wish list or a set of arbitrary goals. It's a realistic assessment of what's achievable with available resources and a roadmap for getting there." },
      { h: "Assessment First", c: "Strategic planning begins with honest assessment. Where are you now? What's working? What's not? What resources do you have? What's the competitive landscape?\n\nAssess your donor base, your programs, your staff capacity, and your organizational readiness. Plans built on incomplete understanding of current reality fail." },
      { h: "Goal Setting", c: "Set stretch goals that are ambitious but achievable. Goals should have dollar targets, timeline, and component parts. A goal to raise $500,000 should specify how much from each source: individual giving, foundations, corporate, events.\n\nGoals create accountability when they're specific and measurable. Vague goals like \"increase giving\" provide no direction." },
      { h: "Strategy Selection", c: "Strategies are the approaches you'll use to reach goals. Will you launch a major gifts program? Expand foundation grants? Start monthly giving? Each strategy requires different resources and has different potential.\n\nYou can't do everything. Choose strategies that fit your capacity and offer the best return. Focus beats fragmentation." },
      { h: "Building the Calendar", c: "Translate strategy into an operational calendar. When will appeals go out? When will events happen? When will grant applications be submitted? Who is responsible for what?\n\nThe calendar creates accountability by attaching dates to activities. Review the calendar monthly to ensure you're on track." },
      { h: "Tracking and Adjustment", c: "Plans are living documents that require regular review. Track progress against goals. Identify what's working and what's not. Adjust tactics while maintaining strategic direction.\n\nQuarterly review meetings ensure plans don't sit on shelves. What did we plan? What did we do? What did we learn? What will we change?" }
    ],
    interactive: { type: "sorting", title: "Put Planning Steps in Order", items: ["Assess Current State", "Set Goals", "Choose Strategies", "Build Calendar", "Track Progress"] },
    keyTakeaways: ["Planning aligns resources and creates accountability", "Start with honest assessment of current reality", "Set specific, measurable stretch goals", "Review and adjust quarterly"]
  }
};


const V2_CONTENT = {
  1: {
    title: "What Makes GMG Different",
    sections: [
      { h: "The Dual Perspective Advantage", c: "GMG brings something rare to fundraising consulting. Brandon spent years as a corporate grantmaker at Coca-Cola, reviewing over 13,000 proposals and recommending $195 million in grants. Eric has 17 years on the nonprofit side, raising money and building programs from the ground up. This dual perspective means we know what funders actually want to see, not just what fundraisers think they want. When you have sat on both sides of the table, you understand the conversation differently. You know why proposals get rejected. You know what makes a program officer say yes. That knowledge changes everything about how we approach the work." },
      { h: "Excellence Rooted in Lived Experience", c: "Our tagline is not marketing fluff. Both founders come from the communities we serve. We understand the challenges facing BIPOC-led organizations because we have lived them. This is not about charity tourism or parachuting into communities we do not understand. It is about bringing professional excellence to organizations that have historically been underresourced and overlooked. We know the frustration of being told your organization is too small, too new, or too risky. We also know that these assessments are often wrong, based on biased assumptions rather than actual capacity." },
      { h: "Why Methodology Matters", c: "Many consultants operate on instinct and experience alone. They give advice that worked for them once and hope it works again. GMG operates differently. We have developed specific methodologies, documented frameworks, and repeatable processes that deliver consistent results. The Tic-Tac-Toe Framework, the Recipe Pitch, the 360 Landscape Assessment, these are not just catchy names. They are tested approaches that work across different organization types and sizes. When you hire GMG, you are getting more than opinions. You are getting proven systems." },
      { h: "The Fellowship Model", c: "Most consulting firms hire experienced professionals and charge premium rates. GMG created a different path through the Lane-Pierce Fellowship. We identify talented individuals who show potential, train them intensively in our methodologies, and then deploy them on client work under supervision. This model does two things. It creates opportunities for emerging professionals who might never get into traditional consulting. And it allows us to offer excellent work at rates that smaller organizations can actually afford." },
      { h: "Client-First Philosophy", c: "Some consultants view every engagement as a chance to maximize revenue. They create dependency, extend timelines, and avoid transferring knowledge because they want repeat business. GMG operates with the opposite philosophy. Our goal is to make ourselves unnecessary. We want to build client capacity so they can do the work themselves. We document everything we do. We train staff as we work. We create systems they can maintain without us. If a client never needs us again because we did our job well, that is success." },
      { h: "The Guarantee", c: "GMG stands behind our work in ways that most consultants will not. If an organization has funding and hires us, we expect to help them raise more money than they spend on our services. If they do not have funding, we help them find it before charging significant fees. This is not charity. It is confidence in our methodology. We know our approaches work, so we are willing to bet on outcomes rather than just billing for time." }
    ],
    interactive: { type: "matching", title: "Match the Founder to Their Background", pairs: [{ left: "Brandon", right: "Corporate Grantmaker" }, { left: "Eric", right: "Nonprofit Fundraiser" }, { left: "GMG", right: "Dual Perspective" }, { left: "Lane-Pierce", right: "Fellowship Program" }] },
    keyTakeaways: ["Dual perspective from both sides of the table changes everything", "Excellence rooted in lived experience, not charity tourism", "Documented methodologies deliver consistent results", "Client-first philosophy means building capacity, not dependency"]
  },
  2: {
    title: "Both Sides of the Table",
    sections: [
      { h: "The Grantmaker View", c: "Program officers at foundations review hundreds or thousands of proposals each year. They are looking for reasons to say no because they simply cannot fund everyone who applies. A confusing proposal, a budget that does not match the narrative, or a mission that does not align with priorities, any of these gets you rejected before anyone reads the full application. Brandon learned this reviewing 13,000 proposals at Coca-Cola. The organizations that got funded were the ones that made it easy for him to say yes. They were clear, aligned, and compelling from the first paragraph." },
      { h: "The Fundraiser View", c: "Nonprofit fundraisers often approach grants with hope rather than strategy. They apply to every foundation that seems remotely relevant, spray and pray style. This wastes enormous time and creates frustration when rejection letters pile up. Eric learned through 17 years of practice that strategic targeting beats volume every time. Ten well-researched, well-matched applications beat fifty random shots. The key is understanding which foundations actually fund your type of work, at your budget level, in your geography, and then investing time in those relationships." },
      { h: "What Grantmakers Actually Want", c: "Program officers want to fund good work. That is why they took the job. But they also need to justify their decisions to boards and supervisors. They need clear evidence that their grants made a difference. When you understand this, you write differently. You do not just describe what you will do. You explain why it matters, how you will measure success, and how the funder will be able to point to your work as a smart investment. Make them look good to their bosses." },
      { h: "Common Mistakes from the Fundraiser Side", c: "The most common mistake is treating foundations like ATMs. Organizations submit applications without any prior relationship, using generic language that could apply to any funder. They ask for amounts that do not match foundation giving patterns. They propose projects that drift from their core mission because that is what they think funders want. All of these mistakes stem from not understanding how program officers think and work." },
      { h: "Common Mistakes from the Grantmaker Side", c: "Funders make mistakes too. They sometimes create application processes that are unnecessarily burdensome, asking for information that does not help them make decisions. They can be slow to communicate, leaving organizations in limbo for months. Some funders develop biases toward certain organization types or against newer organizations. Understanding these patterns helps you navigate them. If a funder is known for favoring established organizations, you might need to lead with credibility signals." },
      { h: "Bridging the Gap", c: "The GMG advantage comes from understanding both perspectives simultaneously. We know what fundraisers need to hear because Eric has lived it. We know what grantmakers need to see because Brandon has lived it. This dual lens shapes every piece of work we produce. When we write a proposal, we are not guessing what the program officer wants. When we advise on fundraising strategy, we are not theorizing about what might work." }
    ],
    interactive: { type: "slider", title: "How many proposals did Brandon review at Coca-Cola?", answer: 13000, tolerance: 1000 },
    keyTakeaways: ["Grantmakers look for reasons to say no because they cannot fund everyone", "Strategic targeting beats spray and pray volume", "Make it easy for program officers to justify funding you", "Understanding both sides shapes better proposals"]
  },
  3: {
    title: "Brandon's Writing Standards",
    sections: [
      { h: "The Copy-Paste Test", c: "Every deliverable GMG produces must pass the copy-paste test. Can the client take this document, paste it into their system, and send it without editing? If the answer is no, we have not finished our job. This standard exists because our clients are busy people who hired us to save them time, not create more work. When we hand over a grant proposal, it should be ready to submit. When we write a donor communication, it should be ready to send. No placeholder text, no brackets asking them to fill things in." },
      { h: "Middle School Reading Level", c: "All GMG writing targets a middle school reading level. This is not because our audiences are not smart. It is because clear writing communicates faster and reaches more people. Jargon and complex sentences slow readers down and create confusion. Academic language makes writers feel sophisticated but leaves readers scratching their heads. The goal is communication, not impression. If a 12-year-old could understand your main point, you are probably writing clearly enough." },
      { h: "The Em Dash Rule", c: "Em dashes are completely banned at GMG. This is not a preference. It is a fireable offense. Em dashes often signal that a writer is cramming too many thoughts into one sentence. They create choppy, interrupting rhythms that break flow. Replace them with commas, periods, or restructure the sentence entirely. If you find yourself reaching for an em dash, pause and ask whether you are really saying one thing or two things that should be separate sentences." },
      { h: "Sentence Flow and Connection", c: "Good writing connects related thoughts with commas instead of chopping them into separate short sentences. Tech bro writing uses punchy fragments. Launch fast. Iterate quickly. Ship features. This style sounds energetic but reads like a LinkedIn post from someone trying too hard. Professional writing has rhythm. Thoughts connect. Sentences vary in length. One sentence might be long with multiple connected ideas, and then the next might be short. That variation creates natural flow." },
      { h: "What to Avoid", c: "Several patterns signal poor writing at GMG. Parallel structure that sounds robotic, phrases like This is not X, this is Y, should be avoided. Call-to-action endings like Looking forward to hearing from you waste space. Academic transitions like Furthermore and Additionally make writing feel corporate and stuffy. Starting emails with just a name instead of a greeting feels cold. These patterns creep into writing because they feel safe, but they make content sound like it was written by a machine." },
      { h: "The Underlying Principle", c: "Do not memorize banned phrases. Understand why writing sounds fake. Brandon writes like he is talking across a table at a coffee shop. Complete sentences that flow naturally, thoughts connected with commas, warm but professional tone, specific details instead of vague corporate speak. When in doubt, read it aloud. If it sounds like a robot wrote it, or like a LinkedIn post, or like a TED talk, rewrite it." }
    ],
    interactive: { type: "matching", title: "Match the Writing Rule", pairs: [{ left: "Em dashes", right: "Never use" }, { left: "Reading level", right: "Middle school" }, { left: "Jargon", right: "Avoid completely" }, { left: "Deliverables", right: "Copy-paste ready" }] },
    keyTakeaways: ["The copy-paste test is the standard for all deliverables", "Middle school reading level reaches more people faster", "Em dashes are banned because they break flow", "Writing should sound like coffee shop conversation"]
  },
  4: {
    title: "360 Degree Landscape Assessment",
    sections: [
      { h: "Why Assessment Comes First", c: "Before GMG can help an organization raise more money, we need to understand where they are. The 360 Degree Landscape Assessment is our comprehensive diagnostic tool. It looks at everything: current fundraising mix, donor retention rates, board engagement levels, staff capacity, systems and tools, and competitive positioning. This is not a surface-level review or a quick chat. We dig into the data, interview stakeholders, and build a complete picture of organizational capacity before making recommendations." },
      { h: "Document Review Phase", c: "The assessment begins with document review. We request the last three years of financial statements, donor lists, grant applications (both successful and unsuccessful), board rosters with giving history, and any existing strategic plans. These documents tell us what the organization has been doing and how it has been performing. We look for patterns. Is donor retention improving or declining? Are grants getting funded at a reasonable rate? Is board giving consistent? The documents reveal reality, not aspirations." },
      { h: "Data Analysis Phase", c: "Raw documents become useful through analysis. We calculate key metrics like donor retention rate, cost to raise a dollar, average gift size, and gift distribution across donor levels. We map the donor pyramid to see how many donors exist at each giving level. We analyze grant success rates by foundation type. This analysis often reveals surprises. Organizations think they know their numbers, but systematic analysis frequently uncovers problems they had not noticed." },
      { h: "Stakeholder Interview Phase", c: "Numbers only tell part of the story. We interview key stakeholders including executive leadership, development staff, board members, and sometimes major donors or community partners. These conversations reveal organizational culture, internal dynamics, and unstated challenges. A development director might not put lack of board support in a document, but they will share it in a confidential interview. These insights shape our recommendations." },
      { h: "Competitive Positioning Analysis", c: "Nonprofits compete for donor attention and funder dollars, whether they acknowledge it or not. We analyze how an organization positions itself relative to others working in the same space. Are they distinctive? Do donors understand what makes them different? Are they pursuing the same funders as larger, better-resourced competitors? This analysis helps organizations find their niche and communicate their unique value." },
      { h: "Findings and Recommendations", c: "The assessment concludes with a comprehensive report that presents findings and prioritized recommendations. We do not just identify problems. We explain what causes them and what to do about them. Recommendations are specific, actionable, and tied to organizational capacity. We do not recommend things the organization cannot actually implement. The assessment becomes a roadmap for improvement, with clear next steps and realistic timelines." }
    ],
    interactive: { type: "sorting", title: "Order the Assessment Steps", items: ["Document Review", "Data Analysis", "Stakeholder Interviews", "Competitive Analysis", "Findings Report"] },
    keyTakeaways: ["Comprehensive assessment comes before any recommendations", "Document review reveals what actually happened, not aspirations", "Stakeholder interviews uncover unstated challenges", "Recommendations must match organizational capacity"]
  },
  6: {
    title: "Data Science Development Plans",
    sections: [
      { h: "What Data Science Means for Development", c: "Data science sounds intimidating, but it simply means using information systematically to make better decisions. In fundraising, this means tracking the right numbers, analyzing patterns, and letting evidence guide strategy. GMG's Data Science Development Plans bring this analytical rigor to nonprofit fundraising. Instead of going with gut feelings or doing what you have always done, you make decisions based on what the data actually shows." },
      { h: "Building Your Data Foundation", c: "Good analysis requires good data, and good data requires disciplined collection. This starts with your donor database. Every gift must be recorded completely with amount, date, source, and any campaign codes. Every donor contact must be logged. Every proposal outcome must be tracked. Many organizations have databases full of incomplete records that make analysis impossible. The first step in data science is often cleaning up what you already have and establishing protocols for consistent entry going forward." },
      { h: "Key Metrics That Drive Strategy", c: "Certain metrics matter more than others for fundraising strategy. Donor retention rate tells you whether you are keeping supporters engaged. Average gift size by donor segment reveals upgrade potential. Cost to raise a dollar shows efficiency. Time to close major gifts indicates cultivation effectiveness. Pipeline value predicts future revenue. These metrics should be reviewed monthly, not just annually. Waiting until year end to discover problems means losing a full year to correctable issues." },
      { h: "Predictive Analysis for Prospect Development", c: "The most powerful application of data science is predicting which donors are likely to give more. By analyzing patterns in your existing major donors, you can identify characteristics that predict major gift potential. Which donors attended events before upgrading? Which donor segments have the highest retention? Which engagement patterns preceded major gifts? These patterns help you focus cultivation efforts on the most promising prospects instead of treating everyone the same." },
      { h: "Creating Your Development Dashboard", c: "Raw data sitting in databases helps no one. GMG helps organizations create development dashboards that present key metrics visually and update in real time. A good dashboard shows progress toward goals, flags areas of concern, and helps leadership make informed decisions. The dashboard should be simple enough that board members can understand it at a glance, but detailed enough that development staff can drill into specifics." },
      { h: "Turning Insights into Action", c: "Data without action is just expensive record keeping. Every analysis should conclude with specific recommendations. If retention is dropping in a particular segment, what will you do about it? If a certain appeal format consistently outperforms others, how will you apply that insight? If a foundation type shows higher success rates, how will you prioritize prospecting? The development plan connects data insights to concrete operational changes." }
    ],
    interactive: { type: "matching", title: "Match the Metric to Its Purpose", pairs: [{ left: "Retention Rate", right: "Donor Loyalty" }, { left: "Cost per Dollar", right: "Efficiency" }, { left: "Pipeline Value", right: "Future Revenue" }, { left: "Average Gift", right: "Donor Capacity" }] },
    keyTakeaways: ["Data science means using information systematically for better decisions", "Good analysis requires disciplined data collection", "Key metrics should be reviewed monthly, not annually", "Insights must connect to concrete operational changes"]
  },
  7: {
    title: "Prospect Precision System",
    sections: [
      { h: "Beyond Spray and Pray", c: "Traditional prospect identification often follows a spray and pray approach. Organizations compile long lists of potential donors, send generic appeals to everyone, and hope some respond. This wastes resources and annoys recipients. GMG's Prospect Precision System replaces hope with strategy. We identify prospects who are most likely to give based on systematic evaluation, then focus cultivation efforts where they will actually produce results." },
      { h: "The Four Evaluation Criteria", c: "Prospect Precision evaluates every potential donor on four criteria. Capacity asks whether they can give at a meaningful level. Affinity asks whether they care about your cause. Propensity asks whether they are likely to actually give. Linkage asks who in your organization can make the ask. A prospect with high capacity but no affinity is not worth pursuing. A prospect with high affinity but no linkage may need relationship building before they are ready. All four criteria matter." },
      { h: "Capacity Research Methods", c: "Understanding donor capacity requires research. Publicly available information includes real estate records, SEC filings for corporate executives, foundation 990s for foundation board members, and salary estimates for certain professions. Wealth screening services can provide capacity ratings based on aggregated data. The goal is not to be invasive, but to ensure you are asking for appropriate amounts. Asking a major donor prospect for $100 when they could give $10,000 leaves money on the table." },
      { h: "Affinity and Engagement Indicators", c: "Affinity shows up through engagement patterns. Does the prospect attend your events? Do they open your emails? Have they volunteered or served on committees? Do they have personal connections to your cause through family experience, professional background, or community involvement? These signals indicate genuine interest that goes beyond polite acknowledgment. High affinity prospects should receive priority attention." },
      { h: "Propensity Patterns", c: "Some people with both capacity and affinity never actually give. Propensity asks whether this specific person is likely to make a gift. Giving history is the best predictor. Have they given to similar organizations? Do they have a pattern of charitable giving? Have they given to you before at any level? First-time donors who show high capacity and affinity are good upgrade prospects. People who have never given anywhere require more cultivation before asking." },
      { h: "Building and Working the Pipeline", c: "The Prospect Precision System produces a ranked pipeline of qualified prospects. Each prospect has an evaluation score and a recommended next step. High-priority prospects receive personal cultivation attention. Medium-priority prospects join targeted cultivation tracks. Lower-priority prospects stay in general communications until their scores improve. The pipeline gets reviewed monthly, with prospects moving up or down based on new information." }
    ],
    interactive: { type: "matching", title: "Match Prospect Criteria", pairs: [{ left: "Capacity", right: "Can they give?" }, { left: "Affinity", right: "Do they care?" }, { left: "Propensity", right: "Will they give?" }, { left: "Linkage", right: "Who can ask?" }] },
    keyTakeaways: ["Strategic targeting beats spray and pray volume", "Four criteria evaluate every prospect: capacity, affinity, propensity, linkage", "Capacity research uses publicly available information", "Pipeline review happens monthly with clear next steps"]
  },
  8: {
    title: "Grant Catalyst Methodology",
    sections: [
      { h: "Grants as Relationships", c: "Most organizations treat grant seeking as an application process. They find a deadline, submit a proposal, and wait for an answer. This transactional approach has low success rates. The Grant Catalyst Methodology treats grants as a relationship process instead. We build relationships with program officers before submitting, understand foundation priorities deeply, and position our clients as partners rather than supplicants. The application becomes a formality when the relationship is strong." },
      { h: "Foundation Research That Matters", c: "Effective foundation research goes beyond reading guidelines. We analyze giving patterns over multiple years to understand true priorities versus stated ones. We review funded organizations to see what success looks like. We study program officer backgrounds and interests. We track foundation strategy shifts and new funding areas. This deep research identifies opportunities that surface-level research misses and helps us position applications perfectly." },
      { h: "The Pre-Application Relationship", c: "Before submitting any significant proposal, GMG recommends building a relationship with the program officer. This might include introductory calls to assess fit, invitations to see your work firsthand, or participation in funder briefings and convenings. These touchpoints accomplish two things. They help you understand what the foundation actually wants, and they help the program officer remember you when your proposal arrives. Unknown organizations rarely get funded." },
      { h: "Writing for Program Officers", c: "Program officers are overwhelmed with reading. They need to quickly assess whether a proposal fits their priorities and whether the organization can deliver. The Grant Catalyst approach front-loads the most important information. Lead with your strongest alignment point. Make the budget tell the same story as the narrative. Include specific, measurable outcomes. Keep language clear and jargon-free. Make it easy for the program officer to say yes." },
      { h: "Stewardship That Opens Doors", c: "Getting funded is just the beginning. How you steward a grant relationship determines whether you get funded again. GMG's stewardship approach exceeds minimum requirements. We deliver reports early and make them interesting to read. We proactively share good news and challenges alike. We invite program officers to see impact firsthand. Excellent stewardship turns a one-time grant into a long-term partnership." },
      { h: "Building a Foundation Portfolio", c: "No organization should depend on a single foundation. The Grant Catalyst Methodology builds a diversified portfolio of foundation relationships at different stages. Some are current funders to steward. Some are prospects in active cultivation. Some are early-stage relationships for future pursuit. This portfolio approach ensures a steady pipeline of funding and reduces vulnerability to any single funder's decisions." }
    ],
    interactive: { type: "sorting", title: "Order the Grant Process", items: ["Research Foundations", "Build Relationships", "Understand Priorities", "Submit Proposal", "Steward Funder"] },
    keyTakeaways: ["Grants are about relationships, not just applications", "Deep research identifies opportunities others miss", "Pre-application relationships increase success rates", "Excellent stewardship turns grants into partnerships"]
  },
  9: {
    title: "Implementation Engine",
    sections: [
      { h: "Strategy Without Execution", c: "A beautiful strategic plan sitting in a drawer helps no one. Many organizations invest significantly in planning, produce impressive documents, and then fail to implement anything meaningful. The Implementation Engine is GMG's system for turning strategy into action. It bridges the gap between knowing what to do and actually doing it. Strategy becomes real through disciplined execution, not through good intentions." },
      { h: "Operational Calendar Development", c: "The first step is translating strategic priorities into an operational calendar. If you want to launch a monthly giving program, exactly when will you develop the landing page? When will you create email templates? When will you soft launch to board members? When will you do public launch? Every strategic initiative gets broken into specific tasks with specific deadlines. Vague timelines like Q1 become specific dates like January 15." },
      { h: "Assignment and Accountability", c: "Every task needs an owner. Not a committee, not the development team, but a specific person who is accountable for completion. The Implementation Engine documents who is responsible for what and by when. It also establishes check-in rhythms for accountability. Weekly team meetings review task status. Monthly leadership meetings assess progress against strategic priorities. Quarterly reviews evaluate whether the strategy itself needs adjustment." },
      { h: "Resource Alignment", c: "Strategy fails when it demands more than available resources can deliver. The Implementation Engine explicitly matches tasks to capacity. How many hours does this initiative require? Who has those hours available? What gets deprioritized to make room? If the math does not work, either reduce the scope or increase resources. Pretending you can do everything with existing capacity guarantees failure." },
      { h: "Progress Tracking Systems", c: "You cannot manage what you do not measure. The Implementation Engine includes tracking systems that show progress in real time. Are we on pace to reach our goal? Which tasks are falling behind? Where are bottlenecks emerging? These systems can be as simple as a shared spreadsheet or as sophisticated as project management software. The tool matters less than the discipline of using it consistently." },
      { h: "Adaptation and Course Correction", c: "No plan survives contact with reality unchanged. The Implementation Engine builds in regular review points for adaptation. Is this tactic working as expected? Has something changed in the environment that affects our strategy? Should we double down on what is working or pivot away from what is not? Rigid adherence to a plan despite contrary evidence is not discipline. It is stubbornness. Smart implementation adapts while maintaining strategic direction." }
    ],
    interactive: { type: "matching", title: "Match Implementation Elements", pairs: [{ left: "Calendar", right: "When things happen" }, { left: "Assignments", right: "Who does what" }, { left: "Accountability", right: "How we track" }, { left: "Metrics", right: "How we measure" }] },
    keyTakeaways: ["Strategy requires execution systems to create results", "Operational calendars turn priorities into specific deadlines", "Every task needs one accountable owner, not a committee", "Regular review enables adaptation while maintaining direction"]
  },
  11: {
    title: "The Grantmaker Perspective",
    sections: [
      { h: "Understanding Program Officer Reality", c: "Program officers are not gatekeepers trying to keep money away from worthy organizations. They are professionals trying to deploy limited resources for maximum impact. Most went into foundation work because they care about social change. They want to fund good work. Understanding this reality changes how you approach them. They are potential partners, not obstacles. Your job is to help them do their job well." },
      { h: "The Overwhelming Volume", c: "Program officers at active foundations receive hundreds or thousands of applications annually. They cannot give every proposal deep consideration. They develop scanning techniques to quickly assess fit and quality. A proposal that confuses them in the first paragraph gets set aside. A budget that does not add up signals sloppiness throughout. First impressions matter enormously because program officers do not have time to dig through unclear proposals looking for good ideas." },
      { h: "What They Need to Justify Funding", c: "Program officers must justify their recommendations to boards, leadership, and sometimes external evaluators. They need evidence that grants achieved results. When they advocate for your proposal, they are putting their professional reputation on the line. Give them what they need. Clear outcomes with measurable indicators. Realistic timelines. Budgets that demonstrate thoughtful planning. A track record of delivering results. Make them confident their recommendation will reflect well on them." },
      { h: "The Relationship Dynamic", c: "Some grantseekers approach program officers with excessive deference, as if begging for charity. Others approach with entitlement, as if the foundation owes them funding. Neither approach works well. The ideal dynamic is professional partnership. You have important work that aligns with their priorities. They have resources that can accelerate your impact. The relationship should be mutually respectful, with clear communication and realistic expectations." },
      { h: "Common Frustrations with Applicants", c: "Program officers consistently complain about certain applicant behaviors. Generic proposals that could have been sent to any foundation. Budgets that do not match narrative requests. Failure to follow application instructions. Asking for amounts outside foundation giving ranges. Ignoring feedback from previous applications. These behaviors signal that the organization is not serious or capable. Avoiding them puts you ahead of most applicants." },
      { h: "Building Long-Term Relationships", c: "The best foundation relationships span years or decades. They evolve as organizational needs and foundation priorities change. Building these relationships requires consistency, honesty, and excellent stewardship. Keep program officers informed even when news is not good. Share credit when things go well. Respect their time and constraints. Over time, these relationships can open doors that cold applications never will." }
    ],
    interactive: { type: "slider", title: "What percentage of grant applications typically get funded?", answer: 10, tolerance: 5 },
    keyTakeaways: ["Program officers want to fund good work, not block it", "They must justify recommendations with clear evidence", "Professional partnership beats deference or entitlement", "Long-term relationships open doors over time"]
  },
  12: {
    title: "Budget-First Approach",
    sections: [
      { h: "Why Budget Tells the Story", c: "Most grant writers start with the narrative and add a budget later. This approach leads to misalignment between what you say you will do and what you actually have resources to accomplish. GMG's Budget-First Approach flips this sequence. Start with what things actually cost, then write a narrative that matches reality. The budget is not an appendix. It is the backbone of credible proposals." },
      { h: "Building Realistic Budgets", c: "Realistic budgets start with actual costs, not wishful thinking. What does it actually cost to deliver this program? Include direct costs like staff time, materials, and travel. Include indirect costs like supervision, administrative support, and overhead allocation. Include contingency for unexpected expenses. Funders are sophisticated. They can tell when numbers are made up or when organizations are hiding true costs to seem more efficient than reality." },
      { h: "Budget Narrative Connection", c: "Every significant budget line should connect to a narrative description. If you are requesting $50,000 for a program coordinator, the narrative should explain what that person will do and why the position is necessary. If you are budgeting $10,000 for evaluation, describe your evaluation approach. This connection demonstrates that you have thought through implementation, not just stated goals. Disconnection between budget and narrative is a red flag." },
      { h: "Matching Funder Expectations", c: "Different funders have different budget expectations. Some want to see minimal overhead to maximize direct service. Others understand that organizational infrastructure costs money and welcome reasonable indirect rates. Some have specific budget formats they require. Research funder preferences before building your budget. Adapting to their expectations shows respect for their processes and increases your chances of success." },
      { h: "Multi-Funder Budget Strategy", c: "Most projects require funding from multiple sources. The Budget-First Approach plans this from the beginning. Which funder will cover which costs? Are there costs that multiple funders will share? What happens if one funder says no? A sophisticated budget shows funders how their contribution fits into a larger funding picture. It demonstrates that you have a plan and are not depending entirely on their decision." },
      { h: "Budget as Management Tool", c: "Beyond proposals, budgets serve as management tools throughout project implementation. They help you track spending against plans, identify variances early, and make informed decisions about resource allocation. Organizations that treat budgets as living documents rather than one-time proposal exercises run more effectively and report to funders more accurately. The discipline of Budget-First extends beyond grant writing into organizational management." }
    ],
    interactive: { type: "sorting", title: "Order Budget Development Steps", items: ["List True Costs", "Build Line Items", "Identify Funding Sources", "Match to Funder Format", "Write Budget Narrative"] },
    keyTakeaways: ["Start with budget, then write narrative to match", "Realistic budgets include all actual costs", "Every budget line connects to narrative explanation", "Budgets serve as management tools beyond proposals"]
  },
  13: {
    title: "Tic-Tac-Toe Framework",
    sections: [
      { h: "The Core Concept", c: "The Tic-Tac-Toe Framework is deceptively simple. Before you ask a donor for money, you should have at least three meaningful touches that are not asks. This could be a thank you call, an impact update, an invitation to visit your facility, or a personal check-in. The three touches build relationship before transaction. Donors who feel connected and informed give more and stay longer than donors who only hear from you when you want money." },
      { h: "Why Three Touches Matter", c: "Fundraising suffers when it becomes purely transactional. Organizations reach out only to ask, then disappear until the next ask. Donors feel like ATMs. They become less responsive over time and eventually lapse entirely. Three cultivation touches between asks flip this dynamic. The donor hears from you more often about impact than about giving. They feel like partners, not targets. The ask becomes one part of an ongoing relationship, not the entire relationship." },
      { h: "Touch Types That Work", c: "Effective cultivation touches provide value to the donor. A thank you call acknowledges their importance. An impact report shows what their giving accomplished. An event invitation offers connection and community. A volunteer opportunity provides hands-on engagement. A sneak preview of organizational news makes them feel like insiders. Each touch should be genuine and personalized, not mass communications disguised as personal contact." },
      { h: "Planning the Tic-Tac-Toe Grid", c: "Smart organizations map out Tic-Tac-Toe grids for their major donor portfolios. For each donor, plan three cultivation touches before the next ask. Document each touch in your CRM. Track whether donors received their three touches. This planning prevents the common pattern of ignoring donors until you need something, then scrambling to reconnect. Consistent cultivation creates stronger relationships and better response rates." },
      { h: "Avoiding Common Mistakes", c: "Several mistakes undermine Tic-Tac-Toe effectiveness. Generic mass emails do not count as cultivation touches. Touches that are really disguised asks do not count either. Going through the motions without genuine personalization does not build relationship. The framework works when touches are authentic and valued by the recipient, not when they check boxes." },
      { h: "Scaling the Framework", c: "Tic-Tac-Toe works best with major donors who warrant personal attention, but the concept can scale. Mid-level donors might receive a modified version with more automated but still personalized touches. General donors receive thoughtful mass communications that demonstrate impact and express gratitude. The principle remains: communication should be mostly about relationship and impact, not mostly about asking." }
    ],
    interactive: { type: "slider", title: "How many touches before an ask in Tic-Tac-Toe?", answer: 3, tolerance: 0 },
    keyTakeaways: ["Three meaningful touches before every ask", "Touches build relationship before transaction", "Each touch should provide genuine value to the donor", "Plan and document touches systematically"]
  },
  14: {
    title: "Tic-Tac-Toe Implementation",
    sections: [
      { h: "Portfolio Assignment", c: "Tic-Tac-Toe implementation starts with assigning donors to portfolios. Each major gift officer manages a portfolio of 100 to 150 donors. Smaller organizations might have one person managing all major donor relationships. The key is ensuring every major donor has someone specifically responsible for their cultivation. Donors without portfolio assignments inevitably fall through cracks." },
      { h: "Touch Calendar Creation", c: "Once portfolios are assigned, create touch calendars for each donor. Map out the next 12 months. When will you deliver touch one? Touch two? Touch three? When will you make the ask? What specific type of touch works best for this donor based on their interests and engagement history? This planning takes time upfront but prevents the scramble of trying to cultivate donors reactively." },
      { h: "Touch Execution Tracking", c: "Good intentions do not build relationships. Executed touches do. Track every touch in your CRM with date, type, and response. Review touch completion weekly. Identify donors who are falling behind on cultivation. Adjust calendars when circumstances change. This tracking transforms Tic-Tac-Toe from a nice idea into an operational system with accountability." },
      { h: "Personalization at Scale", c: "Every donor is different, and effective cultivation reflects those differences. Some donors love events. Others prefer one-on-one meetings. Some want detailed financial reports. Others care most about stories. Use your CRM notes to track preferences and personalize accordingly. The goal is not cookie-cutter touches but tailored engagement that makes each donor feel individually valued." },
      { h: "The Ask Conversation", c: "After three quality touches, the ask conversation should feel natural. You have built relationship. You have demonstrated impact. You have learned what matters to the donor. The ask connects to everything that preceded it. You have seen the impact of our work over the past months. We are hoping you will consider a gift of X amount to help us expand. Strong cultivation makes strong asks possible." },
      { h: "Post-Ask Continuation", c: "Tic-Tac-Toe does not end when the donor gives or declines. After a gift, restart the cycle with gratitude and continued cultivation. After a decline, maintain relationship for future asks. Donors who say no today may say yes in six months if you stay connected. The framework is continuous, not a one-time campaign. It becomes the rhythm of major donor relationship management." }
    ],
    interactive: { type: "matching", title: "Match Portfolio Management Elements", pairs: [{ left: "Assignment", right: "Who owns relationship" }, { left: "Calendar", right: "When touches happen" }, { left: "Tracking", right: "Did it get done" }, { left: "Personalization", right: "What works for this donor" }] },
    keyTakeaways: ["Every major donor needs one person responsible for their cultivation", "Touch calendars plan the entire year in advance", "Tracking ensures execution, not just intention", "The cycle continues after ask regardless of response"]
  },
  16: {
    title: "Recipe Pitch Framework",
    sections: [
      { h: "The Recipe Concept", c: "The Recipe Pitch Framework comes from a simple observation. When you want to cook something, you need a recipe that lists ingredients, quantities, and steps. When a funder wants to understand your project, they need the same clarity. What are the components? How much of each? What sequence do they follow? The Recipe Pitch presents your work in this clear, concrete format that anyone can understand." },
      { h: "Ingredients Section", c: "Every Recipe Pitch starts with ingredients. What exactly goes into this project? List the specific resources required. Two full-time staff members. Office space for client meetings. Transportation for home visits. Training materials for volunteers. Each ingredient is specific and quantified. Vague references to adequate staffing or sufficient resources do not belong in a recipe. If you cannot list the ingredients precisely, you are not ready to cook." },
      { h: "Quantities and Costs", c: "After listing ingredients, attach quantities and costs to each. The two staff members cost $120,000 in total compensation. The office space costs $24,000 annually. Transportation costs $8,000 for fuel and vehicle maintenance. Training materials cost $3,000 to produce. Now the funder can see exactly what they are paying for. They can evaluate whether costs seem reasonable and whether their contribution would make meaningful difference." },
      { h: "Process Steps", c: "The recipe then describes process steps. What happens first? What follows? Month one recruits and hires staff. Month two establishes partnerships with referral agencies. Month three begins client intake. Months four through twelve deliver services. This sequence shows you have thought through implementation, not just imagined outcomes. Funders want to know you have a plan, not just a wish." },
      { h: "Expected Output", c: "Recipes have expected outputs. Following this recipe should produce this result. Serve 200 families with intensive case management. Achieve 75% employment placement rate within six months of intake. Generate 40 stable housing placements. These outputs connect directly to the ingredients and process. A funder can trace the logic. If they provide these resources and you follow this process, these outcomes become plausible." },
      { h: "Why Recipes Persuade", c: "The Recipe Pitch works because it replaces vague promises with concrete plans. Anyone reading it can visualize what will happen. They can evaluate whether the math makes sense. They can identify gaps in your thinking. This transparency builds trust. You are not trying to impress with jargon or overwhelm with complexity. You are showing exactly what you plan to do with their money." }
    ],
    interactive: { type: "sorting", title: "Order Recipe Pitch Elements", items: ["List Ingredients", "Specify Quantities", "Attach Costs", "Describe Process", "Project Outputs"] },
    keyTakeaways: ["Present projects as recipes with clear ingredients", "Attach specific quantities and costs to each element", "Show process steps in sequence", "Connect inputs to expected outputs logically"]
  },
  17: {
    title: "Board Training GMG Style",
    sections: [
      { h: "The Board's Role in Fundraising", c: "Many board members joined because they care about the mission, not because they wanted to raise money. They often feel uncomfortable with fundraising and avoid it. GMG's board training transforms this dynamic. We help boards understand that fundraising is simply sharing something you believe in with people who might also believe in it. When framed correctly, fundraising becomes an extension of mission passion, not an awkward obligation." },
      { h: "Training Approach Overview", c: "GMG board training is not a one-time lecture. It is a series of practical sessions that build skills incrementally. We start with mindset, helping board members reframe fundraising positively. We move to concrete skills like identifying prospects in their networks and making comfortable asks. We practice through role plays with feedback. We support real-world application with coaching. This scaffolded approach builds confidence gradually." },
      { h: "The Network Mapping Exercise", c: "One of GMG's signature exercises is network mapping. Each board member lists people they know who might be interested in the organization's work. Colleagues, friends, neighbors, professional contacts, community members. They are not committing to ask anyone yet. They are simply acknowledging that they have networks. This exercise often reveals that boards have access to far more potential donors than they realized." },
      { h: "Making the Ask Comfortable", c: "Fear of asking usually comes from fear of rejection or fear of damaging relationships. We address both directly. Rejection is not personal. The person might love you and simply be unable to give right now. As for relationships, most people feel respected when asked to support something meaningful. The key is asking with authenticity, not pressure. We practice asks that feel natural and genuine, not scripted and salesy." },
      { h: "Supporting Board Members Post-Training", c: "Training without follow-up produces little change. After GMG board training, we establish accountability structures. Board members commit to specific actions within specific timeframes. Staff follow up to provide support and address obstacles. We celebrate early wins to build momentum. This ongoing support transforms training from an event into a culture shift." },
      { h: "Measuring Board Fundraising Success", c: "Board fundraising success shows up in multiple metrics. Number of prospects identified and cultivated. Meetings facilitated by board members. Asks made with board involvement. Gifts secured through board relationships. We track these metrics to demonstrate progress and identify where additional support is needed. Board members who see results become enthusiastic participants rather than reluctant volunteers." }
    ],
    interactive: { type: "matching", title: "Match Board Training Elements", pairs: [{ left: "Mindset", right: "Reframe fundraising positively" }, { left: "Networks", right: "Map who they know" }, { left: "Skills", right: "Practice asking" }, { left: "Support", right: "Follow-up coaching" }] },
    keyTakeaways: ["Board training reframes fundraising as mission passion", "Network mapping reveals untapped potential", "Practice makes asking comfortable", "Ongoing support sustains changed behavior"]
  },
  18: {
    title: "Foundation Pipeline Management",
    sections: [
      { h: "The Pipeline Concept", c: "Foundation fundraising fails when organizations treat it as a series of disconnected applications. The pipeline concept changes this. At any time, you should have foundations at different stages of relationship. Some are current funders you are stewarding. Some are hot prospects with applications pending. Some are warm leads you are cultivating. Some are cold prospects you are researching. Managing this pipeline systematically produces consistent results." },
      { h: "Pipeline Stages Defined", c: "GMG defines five pipeline stages. Research means you have identified a potential foundation but have not yet made contact. Cultivation means you are building relationship through meetings, events, or informational exchanges. Proposal means you have submitted an application and await decision. Funded means you received a grant and are in active stewardship. Declined means they said no this round but remain prospects for future. Every foundation sits somewhere in this pipeline." },
      { h: "Pipeline Capacity Planning", c: "Different pipeline stages require different effort levels. Research requires hours of analytical work. Cultivation requires relationship building over months. Proposals require intensive writing on deadlines. Stewardship requires consistent communication. Understanding these effort requirements helps you size your pipeline appropriately. An organization that takes on too many simultaneous proposals burns out staff and produces lower quality work." },
      { h: "Moving Foundations Through Stages", c: "The goal is systematic movement through pipeline stages. Research foundations are good fits and move them to cultivation. Cultivate until relationships are strong enough for proposals. Submit proposals and await decisions. Steward funded relationships and cultivate them for renewal. Re-cultivate declined relationships for future rounds. This systematic movement prevents foundations from stagnating at any stage." },
      { h: "Pipeline Review Rhythm", c: "Effective pipeline management requires regular review. Monthly, examine your full pipeline. Which foundations moved stages? Which are stalled? Which need attention? Quarterly, assess pipeline health. Do you have enough at each stage to meet revenue goals? Are you over-concentrated in any stage? This rhythm ensures the pipeline receives active management, not passive hope." },
      { h: "Pipeline Tools and Tracking", c: "Your CRM should track foundation pipeline with stage indicators, next action items, and expected decision dates. GMG helps organizations configure systems for effective tracking. We also create dashboard views that summarize pipeline health at a glance. Good tools make pipeline management sustainable. Without tools, the complexity overwhelms and management lapses." }
    ],
    interactive: { type: "sorting", title: "Order Foundation Pipeline Stages", items: ["Research", "Cultivation", "Proposal", "Funded", "Stewardship"] },
    keyTakeaways: ["Pipeline management produces consistent foundation results", "Five stages from research through stewardship", "Match pipeline capacity to available staff effort", "Monthly reviews prevent foundations from stagnating"]
  },
  19: {
    title: "Major Donor Strategy",
    sections: [
      { h: "What Makes a Major Donor", c: "Major donor definitions vary by organization. For some, $1,000 qualifies as major. For others, $25,000 is the threshold. The common element is that major donors give enough to warrant personal attention and customized cultivation. They represent a small percentage of your donor count but often provide 80% or more of total individual giving. Major donor strategy is about maximizing this critical segment." },
      { h: "Identification Methods", c: "Finding major donor prospects requires multiple approaches. Analyze your existing donor base for those with capacity indicators and strong engagement. Ask board members to identify people in their networks. Research community leaders and philanthropists. Review foundation boards for individuals who might also give personally. Screen your database using wealth indicators. These methods together build a qualified prospect pool." },
      { h: "Cultivation Best Practices", c: "Major donor cultivation is personal and customized. Meet them where they are interested. Some want programmatic deep dives. Others prefer big picture vision. Some enjoy events and community. Others prefer one-on-one conversations. Learn their preferences and adapt accordingly. The Tic-Tac-Toe Framework provides structure, but execution should feel individual, not formulaic." },
      { h: "The Major Gift Ask", c: "Asking for major gifts differs from general appeals. It happens face to face or at minimum by phone or video. It is specific about amount based on capacity research and relationship. It connects to the donor's interests and previous engagement. It offers meaningful recognition or involvement. The ask should feel like a natural next step in an ongoing relationship, not a surprise ambush." },
      { h: "Stewardship That Retains", c: "Major donors require stewardship that matches their giving level. This means personal thank you calls from leadership, not just form letters. It means meaningful updates on impact, not just newsletters. It means recognition that feels significant. It means access to information and people. Excellent stewardship retains donors and positions them for increased giving over time." },
      { h: "Upgrading Gift Levels", c: "Major donors often have capacity to give more than they currently give. Upgrading requires understanding their interests deeply, demonstrating exceptional impact, and making asks that stretch appropriately. A donor giving $10,000 might be capable of $50,000 for the right project. The key is matching opportunity to passion and making compelling cases for increased investment." }
    ],
    interactive: { type: "slider", title: "What percentage of revenue typically comes from major donors?", answer: 80, tolerance: 10 },
    keyTakeaways: ["Major donors provide most individual giving revenue", "Multiple identification methods build prospect pools", "Cultivation must be personal and customized", "Stewardship retains and positions for upgrades"]
  },
  21: {
    title: "Corporate Small-Dollar Approach",
    sections: [
      { h: "Rethinking Corporate Giving", c: "Most nonprofits chase corporate sponsorships in the five and six figure range. They compete for the same highly visible opportunities that every other organization pursues. GMG's Corporate Small-Dollar Approach flips this model. Instead of fighting for one $50,000 sponsorship, we help organizations build programs that attract 50 businesses giving $1,000 each. The aggregate revenue is the same, but the relationships and sustainability are fundamentally different." },
      { h: "Why Small Business Partners Matter", c: "Small businesses make up 99% of American businesses. They give locally, care about community reputation, and make decisions quickly without complex corporate review processes. A local restaurant owner can say yes to a $500 sponsorship on the spot. A marketing manager at a regional company can approve $1,500 without board involvement. These decisions happen in days, not months. The volume of potential partners far exceeds major corporate prospects." },
      { h: "Benefits Package Design", c: "Small business sponsors need benefits they can actually use. A logo on your gala program does not help a plumber. But a feature in your newsletter reaching 5,000 local households might. Speaking opportunities at community events have value. Social media shoutouts to engaged followers provide exposure. The GMG approach designs benefits packages that match what small businesses actually need: local visibility, community credibility, and customer access." },
      { h: "Prospect Identification", c: "Finding small business prospects starts with your community. Who are your board members' vendors and service providers? Which businesses operate near your facility? Which companies employ your clients or volunteers? Who sponsors other similar organizations? Who participates in local chambers of commerce or business associations? These connections generate prospect lists that mass marketing never could." },
      { h: "The Ask Process", c: "Small business asks happen quickly and personally. Meet with the owner or decision maker directly. Explain your mission briefly. Present a specific sponsorship opportunity with clear benefits. Ask for a specific amount. Get a decision on the spot or within days. This velocity allows you to approach many businesses in the time one corporate approach would take. Volume matters when individual gifts are smaller." },
      { h: "Retention Through Recognition", c: "Small business sponsors stay when they feel valued and see results. Recognize them prominently in community-facing communications. Provide usage data on benefits delivered. Send thank you notes from people served. Introduce them to other sponsors at events. Create a community of business supporters. Renewal rates determine whether small-dollar programs build sustainably or require constant replacement." }
    ],
    interactive: { type: "matching", title: "Match Small Business Appeal", pairs: [{ left: "Speed", right: "Quick decisions" }, { left: "Volume", right: "Many prospects" }, { left: "Benefits", right: "Local visibility" }, { left: "Retention", right: "Community feeling" }] },
    keyTakeaways: ["Small businesses provide faster decisions and more prospects", "Design benefits they can actually use locally", "Personal asks yield quick decisions", "Recognition and community build retention"]
  },
  22: {
    title: "Merchandise Programs",
    sections: [
      { h: "Merchandise as Mission Extension", c: "Nonprofit merchandise is not just about revenue. Done well, it extends your mission into the community. Every t-shirt worn is a walking billboard. Every tote bag carried sparks conversations. Every mug on a desk reminds someone they supported something meaningful. GMG helps organizations think about merchandise strategically, not just as a side revenue stream but as a community-building tool." },
      { h: "Product Selection Principles", c: "Not every product makes sense for every organization. The best merchandise connects to your mission or brand in meaningful ways. Quality matters because cheap products reflect poorly on your organization. Utility matters because people need a reason to use the item. Visibility matters because merchandise that stays home does not spread awareness. Select products that people will actually use in public." },
      { h: "Pricing and Margin Strategies", c: "Merchandise can generate meaningful margin or just cover costs. The choice depends on your goals. If community visibility is the primary goal, price low to maximize distribution. If revenue generation matters, price to market value with healthy margins. Either approach is valid, but you need to be intentional. GMG helps organizations calculate true costs including staff time and model different pricing scenarios." },
      { h: "Distribution Channel Options", c: "Merchandise can be sold directly at events, through online stores, at physical locations, or through retail partners. Each channel has different costs and reaches different audiences. Events create impulse purchases with no shipping costs. Online stores reach distant supporters but require fulfillment infrastructure. Physical locations provide passive sales. Retail partnerships expand reach but share margin." },
      { h: "Inventory Management Reality", c: "Merchandise programs fail when organizations order too much inventory that sits unsold. The cash tied up in warehouse inventory cannot fund programs. GMG recommends starting small and testing demand before scaling. Consider print-on-demand for apparel that eliminates inventory risk. Track sales velocity and adjust ordering accordingly. Better to sell out of popular items than sit on unpopular ones." },
      { h: "Merchandise Program Assessment", c: "Evaluate merchandise programs honestly. Calculate true revenue including all costs. Measure community visibility generated. Assess brand alignment and quality perception. Consider staff time invested relative to results. Some merchandise programs earn their place. Others consume more than they generate. Honest assessment determines whether to grow, maintain, or sunset the program." }
    ],
    interactive: { type: "matching", title: "Match Merchandise Factor", pairs: [{ left: "Quality", right: "Reflects on organization" }, { left: "Utility", right: "Will people use it" }, { left: "Visibility", right: "Spreads awareness" }, { left: "Margin", right: "Covers costs plus profit" }] },
    keyTakeaways: ["Merchandise extends mission into community visibility", "Select products people will use publicly", "Start small to test demand before scaling", "Assess programs honestly for true return"]
  },
  23: {
    title: "Monthly Giving as Default",
    sections: [
      { h: "The Math of Monthly Giving", c: "Monthly giving transforms donor value dramatically. A donor who gives $100 once provides $100 in revenue. That same donor giving $10 monthly provides $120 annually and often continues for years. Over five years, the monthly donor may provide $600 or more while the one-time donor provides $100 and disappears. The lifetime value difference is enormous. This math is why GMG makes monthly giving the default ask, not an afterthought." },
      { h: "Why Donors Prefer Monthly", c: "Monthly giving is not just better for organizations. Many donors prefer it. Small automatic payments fit budgets better than large one-time requests. Donors feel continuously connected rather than episodically solicited. The hassle of responding to appeals disappears. Credit card autopay feels modern and convenient. When framed correctly, monthly giving serves donor interests while serving organizational interests." },
      { h: "Conversion Strategy", c: "Converting existing donors to monthly giving requires strategy. Identify your best candidates: donors with consistent giving history, moderate gift sizes, and strong engagement. Segment your asks by likelihood of conversion. Test different messaging and offers. Make conversion easy with simple online signup. Track conversion rates and adjust approaches based on results." },
      { h: "Acquisition Strategy", c: "New donors can become monthly givers from the start. Position monthly giving as the primary option on donation forms, not the secondary one. Lead with monthly amounts rather than one-time amounts. Show the impact of monthly giving with concrete examples. Some organizations make monthly the default with one-time as the exception. This positioning significantly increases monthly donor acquisition." },
      { h: "Retention Excellence", c: "Monthly donor retention matters enormously. Credit card expirations cause passive attrition. Payment failures require rapid outreach. Communication cadence must balance connection without overwhelming. Special touches like anniversary acknowledgments build relationship. High retention rates compound the value of monthly programs over time. Low retention undermines the entire premise." },
      { h: "Upgrade Pathways", c: "Monthly donors can increase their giving over time. Annual asks for modest increases, such as moving from $25 to $35 monthly, often succeed with strong relationships. Special campaigns can request one-time supplemental gifts on top of monthly giving. Some monthly donors become major donors when approached with the right opportunity. The monthly relationship provides foundation for deeper engagement." }
    ],
    interactive: { type: "slider", title: "Monthly giving over 5 years vs one-time: How many times more revenue?", answer: 6, tolerance: 1 },
    keyTakeaways: ["Monthly giving multiplies donor lifetime value", "Many donors prefer the convenience of autopay", "Position monthly as the default, not the exception", "Retention excellence compounds value over time"]
  },
  24: {
    title: "Membership Programs",
    sections: [
      { h: "Membership vs Donation", c: "Membership and donation are not the same thing. Donations are gifts with nothing expected in return except tax benefits and gratitude. Memberships are transactions where payment purchases access to benefits. This distinction matters legally and practically. Membership benefits that have fair market value reduce tax deductibility. Members expect to receive what they paid for. Understanding this distinction shapes program design." },
      { h: "Membership Benefit Design", c: "Compelling memberships offer benefits members actually value. Access benefits include priority registration, member-only events, and behind-the-scenes opportunities. Content benefits include newsletters, publications, and educational resources. Discount benefits include reduced admission, merchandise discounts, and partner offers. Recognition benefits include member cards, listings, and special acknowledgment. The mix depends on what your audience values." },
      { h: "Tiered Membership Structures", c: "Most membership programs offer multiple tiers at different price points. Basic tiers provide core benefits at accessible prices. Premium tiers add enhanced benefits for higher commitment. Top tiers offer exclusive access for significant investment. Tiers allow members to self-select based on interest and capacity. They also create natural upgrade paths as members deepen engagement over time." },
      { h: "Renewal and Retention", c: "Membership programs live and die on renewal rates. Remind members before expiration with escalating urgency. Make renewal frictionless with online options and saved payment methods. Reinforce the value they received during the membership year. Offer incentives for early renewal. Track and analyze why non-renewers lapse. Retention rates above 80% indicate healthy programs." },
      { h: "Membership Acquisition", c: "Growing membership requires consistent acquisition. Promote membership at natural touchpoints like events, facility visits, and program participation. Use existing members for referrals with incentives. Test different offers and price points. Track which acquisition channels produce members who renew versus those who quickly lapse. Acquisition without retention is expensive churn." },
      { h: "Membership Program Economics", c: "Evaluate membership programs as businesses. Calculate revenue from membership fees. Subtract costs of benefits delivered, including staff time to administer. The remainder is net contribution to operations. Some membership programs generate significant net revenue. Others break even while building community. A few lose money when benefit costs exceed fees. Honest economics assessment guides program decisions." }
    ],
    interactive: { type: "matching", title: "Match Membership Element", pairs: [{ left: "Access", right: "Priority registration" }, { left: "Content", right: "Publications" }, { left: "Discounts", right: "Reduced pricing" }, { left: "Recognition", right: "Member acknowledgment" }] },
    keyTakeaways: ["Membership purchases benefits while donations are gifts", "Design benefits members actually value", "Tiered structures allow self-selection and upgrades", "Renewal rates determine program sustainability"]
  },
  26: {
    title: "Raffles and Sweepstakes",
    sections: [
      { h: "Legal Framework Overview", c: "Raffles and sweepstakes sound simple, but they operate in a complex legal landscape. Raffles involve payment for a chance to win, which makes them a form of gambling regulated by state law. Sweepstakes are free to enter, which changes the legal framework entirely. Some states prohibit nonprofit raffles. Others require registration and reporting. Getting this wrong can result in fines or worse. GMG always recommends legal review before launching these programs." },
      { h: "Raffle Best Practices", c: "Where raffles are legal, they can generate significant revenue with relatively little effort. Best practices include selecting prizes that appeal broadly, pricing tickets accessibly with bulk discounts, setting realistic revenue targets, and building excitement through promotion. The prize should feel worth the ticket price. Events like galas often incorporate raffles because attendees are already gathered and in spending mode." },
      { h: "Sweepstakes Mechanics", c: "Sweepstakes avoid gambling laws by providing free entry alongside any paid entry. If someone can enter without buying anything, the legal character changes. This often requires providing an alternative entry method like sending a postcard. The rules must be crystal clear and consistently applied. Sweepstakes can still generate revenue through optional donations during entry, but the donation cannot be required." },
      { h: "Prize Sourcing Strategies", c: "Prize quality determines participation levels. Sourcing great prizes at low cost multiplies revenue. Donated prizes from corporate partners cost nothing. Experience prizes like dinner with local celebrities or behind-the-scenes access cost less than their perceived value. Travel packages combine multiple donated elements into compelling offerings. The best prize acquisition is networked, leveraging board and volunteer connections." },
      { h: "Promotion and Sales", c: "Revenue depends on ticket sales, which depend on promotion. Start promotion well before the drawing date. Use multiple channels including email, social media, event promotion, and personal outreach. Set individual sales goals for board members and volunteers. Create urgency with deadlines and limited availability. Track sales against targets and adjust promotion intensity accordingly." },
      { h: "Regulatory Compliance", c: "Nonprofit raffle compliance varies dramatically by state. Some require registration with gaming commissions. Some limit prize values or ticket prices. Some mandate specific disclosures. Some prohibit raffles entirely. Before launching any raffle, research your state's requirements thoroughly. Document everything. Report as required. The revenue is not worth legal exposure from compliance failures." }
    ],
    interactive: { type: "matching", title: "Match Raffle vs Sweepstakes", pairs: [{ left: "Raffle", right: "Payment required" }, { left: "Sweepstakes", right: "Free entry option" }, { left: "Both", right: "Prize attraction matters" }, { left: "Legal", right: "State regulations vary" }] },
    keyTakeaways: ["Legal requirements vary dramatically by state", "Raffles require payment while sweepstakes must offer free entry", "Prize quality and sourcing drive participation", "Promotion determines revenue success"]
  },
  27: {
    title: "Tax-Advantaged Giving",
    sections: [
      { h: "Beyond Cash Donations", c: "Most donors give cash because it is simple and familiar. But tax-advantaged giving vehicles can make generosity more efficient for donors while benefiting organizations. Stock gifts, qualified charitable distributions, donor advised funds, and planned gifts all offer tax advantages that cash donations do not. Educating donors about these options is not sales. It is service that helps them give more effectively." },
      { h: "Stock and Appreciated Asset Gifts", c: "When donors give appreciated stock or other assets, they avoid capital gains tax on the appreciation while receiving a deduction for full market value. A stock purchased for $10,000 that is now worth $50,000 would generate $8,000 in capital gains tax if sold. Gifting it directly avoids that tax entirely. For donors with appreciated assets, this is mathematically superior to selling and giving cash. Organizations need simple processes for receiving these gifts." },
      { h: "Qualified Charitable Distributions", c: "Qualified charitable distributions, or QCDs, allow people over 70 and a half to give directly from their IRA to charity. The distribution counts toward required minimum distribution but is not included in taxable income. This is especially valuable for donors who do not itemize deductions or who want to reduce their tax bracket. QCDs have become increasingly popular as more retirees learn about them." },
      { h: "Donor Advised Funds", c: "Donor advised funds are charitable giving accounts administered by sponsoring organizations. Donors contribute to the fund, receive immediate tax deductions, and then recommend grants to charities over time. DAFs have grown explosively because they offer flexibility and simplicity. Organizations should make it easy for DAF donors to recommend grants, understanding that the process differs from direct giving." },
      { h: "Planned Giving Basics", c: "Planned gifts come from estates rather than current income. The simplest form is a bequest naming the organization in a will. More complex forms include charitable remainder trusts, charitable lead trusts, and life insurance gifts. Planned giving often involves donors with deep connection but moderate current capacity. Their ultimate gift may far exceed anything they could give during life." },
      { h: "Educating Donors", c: "Most donors do not know about tax-advantaged giving options. Organizations that educate them provide genuine service while increasing giving potential. Include information in communications. Train gift officers to ask about assets and estate plans. Partner with financial advisors and estate attorneys who work with your supporters. Position this education as helping donors achieve their charitable goals efficiently." }
    ],
    interactive: { type: "matching", title: "Match Tax-Advantaged Vehicle", pairs: [{ left: "Stock Gift", right: "Avoid capital gains" }, { left: "QCD", right: "IRA donors over 70.5" }, { left: "DAF", right: "Donor advised fund" }, { left: "Bequest", right: "Estate planning" }] },
    keyTakeaways: ["Tax-advantaged vehicles help donors give more efficiently", "Stock gifts avoid capital gains on appreciated assets", "QCDs benefit older donors with IRA distributions", "Donor education is service, not sales"]
  },
  28: {
    title: "Event Strategy GMG Way",
    sections: [
      { h: "The Event Problem", c: "Most organizations lose money on events when you count staff time. The gala that netted $50,000 actually cost $30,000 in direct expenses plus $40,000 in staff time preparing for three months. The true return is negative $20,000. Organizations continue hosting these events because they are tradition, because leadership enjoys them, or because they do not track true costs. GMG brings honest assessment to event strategy." },
      { h: "Events as Cultivation", c: "The primary value of events is not direct revenue. It is cultivation. Events bring donors together with leadership, staff, and each other. They create emotional connection to mission. They generate prospects for major gifts. They build community. When viewed as cultivation investments rather than revenue generators, event strategy changes. The question becomes whether cultivation value justifies investment." },
      { h: "Right-Sizing Your Event Portfolio", c: "Most organizations have too many events. Each one drains staff time and energy. Reducing the portfolio allows focused investment in events that truly matter. Which events generate the most cultivation value? Which attract your best prospects? Which strengthen community most effectively? Keep those and consider eliminating or consolidating others. Quality beats quantity in event strategy." },
      { h: "True Cost Accounting", c: "Honest event assessment requires true cost accounting. Direct costs include venue, food, entertainment, invitations, and decorations. Indirect costs include staff time for planning, setup, execution, and follow-up. Opportunity costs include what staff could have accomplished instead. Add all three to understand true investment. Compare against cultivation value generated to assess return." },
      { h: "Event Follow-Up Systems", c: "Events fail to generate value when organizations drop the ball on follow-up. Within 48 hours, new prospects should be added to CRM with event notes. Within one week, personal thank yous should reach significant attendees. Within two weeks, cultivation plans should be developed for promising prospects. Within one month, prospects should receive their first cultivation touch. These timelines require systems, not good intentions." },
      { h: "Deciding Whether to Continue", c: "Every event should face annual assessment. Did this event achieve its cultivation objectives? Did attendance match expectations? Did we identify and follow up with prospects? Was true cost justified by value generated? Would this investment produce better returns deployed differently? Some events deserve continuation and expansion. Others should gracefully end." }
    ],
    interactive: { type: "sorting", title: "Order Event Follow-Up Timeline", items: ["Add prospects to CRM", "Personal thank yous", "Develop cultivation plans", "First cultivation touch"] },
    keyTakeaways: ["Most events lose money when counting staff time", "Primary event value is cultivation, not direct revenue", "True cost accounting reveals actual investment", "Systematic follow-up captures cultivation value"]
  },
  29: {
    title: "CRM Optimization",
    sections: [
      { h: "Using What You Have", c: "Most organizations have CRM systems they barely use. They purchased expensive platforms with grand intentions, then defaulted to using 10% of capabilities. Before buying new software, optimize what you have. Clean your data. Establish entry protocols. Train staff properly. Actually use the reports available. The best CRM is the one your team will actually use consistently, and that often means making current systems work better." },
      { h: "Data Hygiene Fundamentals", c: "Dirty data makes CRM useless. Duplicate records create confusion. Missing fields prevent segmentation. Outdated contact information causes communication failures. Inconsistent entry creates reporting errors. Data hygiene requires initial cleanup and ongoing discipline. Schedule regular deduplication. Require complete entry. Verify contact information periodically. Establish data standards and enforce them." },
      { h: "Entry Protocol Development", c: "Consistent data entry requires documented protocols. When a gift comes in, exactly what gets entered and where? When a meeting happens, how is it logged? When contact information changes, what is the update process? These protocols should be written, trained, and monitored. New staff should not guess how to enter information. Protocols ensure that data is reliable regardless of who enters it." },
      { h: "Reporting That Drives Action", c: "CRM reports should answer questions that inform decisions. Are we on track for our annual fund goal? Which donors are due for contact? What is our retention rate by segment? Which appeals performed best? Many organizations have CRMs full of data but no routine for extracting useful reports. Establish a reporting rhythm with specific reports reviewed at specific intervals by specific people." },
      { h: "Integration Opportunities", c: "Modern CRMs integrate with email platforms, donation processors, event management tools, and more. Integration reduces manual entry and ensures data consistency across systems. When someone donates online, the gift should flow to the CRM automatically. When email bounces, the CRM should update. Evaluate integration opportunities and implement those that reduce staff burden." },
      { h: "Selecting New Systems", c: "Sometimes the current system genuinely cannot meet organizational needs. When evaluating new CRMs, start with requirements based on how you actually work. Include end users in evaluation. Check references from similar organizations. Plan for data migration and training. Budget for implementation time and productivity dip. New systems often take six to twelve months to fully adopt." }
    ],
    interactive: { type: "sorting", title: "Order CRM Optimization Steps", items: ["Audit Current System", "Clean Data", "Establish Protocols", "Train Staff", "Use Reports"] },
    keyTakeaways: ["Optimize current systems before buying new ones", "Data hygiene requires initial cleanup and ongoing discipline", "Written protocols ensure consistent entry", "Reports should answer questions that drive decisions"]
  }
};

// ============================================================
// VOLUME 3: CPP MODEL - FULL 15-DAY CURRICULUM
// ============================================================
const V3_CONTENT = {
  1: {
    title: "What Is CPP and Why It Works",
    sections: [
      { h: "Understanding the Model", c: "The Consultant Pipeline Program represents a new model for development professionals who want consulting careers without the traditional barriers. CPP creates a pathway from nonprofit employment to independent consulting through GMG's infrastructure. Unlike traditional consulting where you hunt for clients alone, build your own brand from scratch, and manage all business operations, CPP provides support at every step. GMG finds clients, manages contracts, handles invoicing, and provides ongoing training. You focus on doing excellent work." },
      { h: "The Business Case", c: "The business model works because it solves problems for multiple parties. Nonprofits increasingly need flexible expertise without full-time hires. They want to bring in specialists for specific projects rather than adding permanent staff. Talented professionals want autonomy and variety without the sales burden and isolation of solo consulting. GMG wants to expand capacity beyond its founders. CPP connects all three needs through a structured arrangement." },
      { h: "How Revenue Works", c: "CPP consultants work as independent contractors through GMG's network. When GMG lands a client engagement, certified consultants get deployed based on expertise, availability, and fit. Revenue splits between the consultant and GMG compensate both parties fairly. The consultant receives the majority of project revenue while GMG retains a portion to cover business development costs, infrastructure, quality assurance, and ongoing support." },
      { h: "Your Path to CPP", c: "Becoming a CPP consultant requires completing Volumes 1 and 2, demonstrating competence through practical work, and passing character and professionalism vetting. Volume 3 teaches the specific skills needed for consulting success: legal structure, financial management, client relations, and professional development. Not everyone who completes training becomes CPP. Selection is intentional and based on readiness. The program only works when every consultant delivers excellence." },
      { h: "What CPP Is Not", c: "CPP is not a job. You do not have guaranteed hours, benefits, or salary. CPP is not a franchise. You do not pay fees to GMG for the right to operate. CPP is not a credential you purchase. Completion of training does not guarantee admission. CPP is a structured pathway for people who want consulting careers and are willing to earn their place through demonstrated excellence. This is an opportunity, not an entitlement." },
      { h: "Why This Matters for Your Career", c: "CPP offers something rare: a bridge between employment and entrepreneurship. You gain consulting experience, build a track record, develop client relationships, and learn the business side of consulting while having GMG infrastructure supporting you. Over time, some CPP consultants may become fully independent. Others may stay with the program indefinitely. Both paths are valid. The point is building career options you did not have before." }
    ],
    interactive: { type: "matching", title: "Match CPP Benefits", pairs: [{ left: "GMG Handles", right: "Client Acquisition" }, { left: "Consultant Handles", right: "Service Delivery" }, { left: "Both Share", right: "Revenue Split" }, { left: "Result", right: "Flexible Career" }] },
    keyTakeaways: ["CPP provides infrastructure for consulting careers", "Revenue splits compensate both parties fairly", "Selection is based on demonstrated readiness", "The model bridges employment and entrepreneurship"]
  },
  2: {
    title: "Legal Structure for CPP Consultants",
    sections: [
      { h: "Independent Contractor Status", c: "CPP consultants are independent contractors, not employees. This distinction has significant legal and tax implications. As a contractor, you are responsible for your own taxes, insurance, and benefits. You have freedom to work with other clients. You control how you do the work, not just what work you do. Understanding contractor status is essential because misclassification creates legal problems for both parties." },
      { h: "Business Entity Options", c: "Most CPP consultants operate through a business entity rather than as sole proprietors. The most common choice is a single-member LLC, which provides liability protection while maintaining tax simplicity. S-corps offer potential tax advantages for higher earners but add complexity. Sole proprietorship is simplest but provides no liability protection. Each structure has tradeoffs. Consult a tax professional to determine the right choice for your situation." },
      { h: "Contracts and Agreements", c: "CPP consultants sign two types of agreements. The Consultant Services Agreement with GMG establishes your relationship to the program, including confidentiality, non-compete provisions, revenue splits, and quality standards. Client engagement letters define specific project scopes, timelines, and deliverables. Understanding both document types helps you know your obligations and protections." },
      { h: "Liability and Insurance", c: "Professional liability insurance, sometimes called errors and omissions insurance, protects you if a client claims your advice caused them harm. General liability insurance covers accidents and property damage. While GMG maintains organizational insurance, individual consultants should carry their own coverage. The cost is modest relative to the protection provided. Operating without insurance is risky and unprofessional." },
      { h: "Intellectual Property Considerations", c: "Work product created for GMG clients generally belongs to GMG or the client, depending on the engagement agreement. Your prior intellectual property remains yours. GMG methodologies remain GMG property even when you use them on engagements. Understanding these boundaries prevents disputes and protects everyone's interests. When in doubt, ask before using or sharing anything." },
      { h: "Setting Up Properly", c: "Before accepting CPP engagements, complete your legal setup. Form your business entity. Obtain an EIN from the IRS. Open a business bank account. Purchase appropriate insurance. Understand your contractor agreement with GMG. These foundational steps are not optional. They demonstrate professionalism and protect you from future problems. Rushing into work without proper setup creates unnecessary risk." }
    ],
    interactive: { type: "matching", title: "Match Legal Element", pairs: [{ left: "LLC", right: "Liability protection" }, { left: "EIN", right: "Tax identification" }, { left: "E&O Insurance", right: "Malpractice coverage" }, { left: "Contract", right: "Terms of engagement" }] },
    keyTakeaways: ["Independent contractor status has significant implications", "Business entities provide liability protection", "Insurance is essential professional protection", "Complete legal setup before accepting work"]
  },
  3: {
    title: "Money Flow and Financial Management",
    sections: [
      { h: "Understanding Revenue Flow", c: "Client payments flow through GMG, not directly to consultants. GMG invoices clients, collects payments, handles any disputes, and then distributes consultant portions according to agreed splits. This flow protects consultants from collection hassles while ensuring GMG can maintain quality oversight. Payment schedules follow client payment terms plus GMG processing time. Expect 30 to 60 days from client invoice to consultant payment in most cases." },
      { h: "Revenue Split Structure", c: "CPP revenue splits vary based on several factors. Who originated the client relationship? What level of GMG support does the engagement require? What is the consultant's experience level? Typical splits give consultants 60 to 80 percent of project revenue. The GMG portion covers business development, marketing, administrative support, quality assurance, and training. These splits are established before engagements begin and documented in writing." },
      { h: "Tax Planning Requirements", c: "As an independent contractor, you are responsible for your own taxes. This means quarterly estimated tax payments to federal and state governments. Set aside 25 to 35 percent of every payment for taxes. Open a separate savings account for tax reserves. Missing estimated payments results in penalties. Many new consultants underestimate tax obligations and find themselves in trouble at year end. Plan from day one." },
      { h: "Tracking Income and Expenses", c: "Good financial records are non-negotiable. Track every payment received and every business expense incurred. Categorize expenses properly for tax purposes. Common deductible expenses include professional development, software, travel for client work, home office costs, and insurance premiums. Accounting software like QuickBooks Self-Employed or Wave makes tracking manageable. Shoebox receipts and spreadsheet chaos do not scale." },
      { h: "Managing Cash Flow Variability", c: "Consulting income is irregular. Some months bring multiple projects. Others bring none. Building a cash reserve protects you during slow periods. Target three to six months of expenses in savings before depending on consulting income. This reserve prevents desperation decisions like accepting bad clients or poor rates. Financial stability enables better professional choices." },
      { h: "Pricing and Rate Negotiation", c: "While GMG often sets client pricing, understanding rate dynamics helps you contribute to proposals and eventually negotiate independently. Research market rates for similar services. Understand the difference between hourly rates and project pricing. Know your floor, which is the minimum rate that makes work worthwhile. Pricing too low attracts bad clients and creates sustainability problems." }
    ],
    interactive: { type: "slider", title: "What percentage should you set aside for taxes as a contractor?", answer: 30, tolerance: 5 },
    keyTakeaways: ["Revenue flows through GMG to consultants", "Set aside 25 to 35 percent for taxes", "Track all income and expenses systematically", "Build cash reserves for income variability"]
  },
  4: {
    title: "Capacity Planning for Consulting",
    sections: [
      { h: "Understanding Your Capacity", c: "Capacity planning starts with honest assessment of how much time you have available. If you are transitioning from full-time employment, you need to understand your sustainable weekly hours. Most consultants can maintain quality at 30 to 35 billable hours per week. Beyond that, quality degrades and burnout looms. Non-billable time for administration, marketing, and professional development adds to total workload." },
      { h: "The Full-Time Transition", c: "Leaving stable employment for consulting is a significant life decision. Some consultants transition gradually, starting with part-time CPP work while maintaining employment. Others make clean breaks. Either approach requires financial preparation. Calculate your minimum monthly income requirement. Build reserves. Understand benefits costs when employer coverage ends. Plan the transition deliberately rather than impulsively." },
      { h: "Balancing Multiple Clients", c: "CPP consultants often work with multiple clients simultaneously. This diversification provides income stability but requires careful scheduling. Track commitments in hours, not just projects. Know exactly how many hours each client requires weekly. Watch for deadline conflicts. Build buffer time between overlapping deliverables. Overcommitment damages client relationships and your reputation." },
      { h: "Managing Feast or Famine", c: "Consulting work rarely arrives evenly. Sometimes multiple opportunities appear simultaneously. Sometimes the phone stops ringing for weeks. Managing this variability requires both practical and psychological strategies. Practically, build reserves during busy periods. Psychologically, use slow periods productively for professional development, networking, and rest rather than panicking." },
      { h: "When to Say No", c: "The hardest skill in consulting is declining work. You should say no when you genuinely lack capacity to deliver quality. You should say no when a client or project feels problematic. You should say no when the rate is too low to be sustainable. Saying yes to everything eventually means saying yes to failure. Protecting your capacity and reputation sometimes requires turning down revenue." },
      { h: "Growth and Scaling", c: "As your consulting practice matures, you face decisions about growth. Do you want to work more hours? Do you want to increase rates instead of volume? Do you want to specialize more narrowly or diversify? Do you want to stay with CPP or pursue full independence? These decisions shape your career trajectory. Capacity planning is not just about this month but about the career you are building." }
    ],
    interactive: { type: "slider", title: "Sustainable billable hours per week for most consultants?", answer: 32, tolerance: 3 },
    keyTakeaways: ["Sustainable consulting capacity is about 30 to 35 billable hours", "Transition from employment requires financial preparation", "Multiple clients require careful scheduling", "Learn when to say no to protect quality and reputation"]
  },
  6: {
    title: "Risk Assessment for Consulting",
    sections: [
      { h: "Understanding Consulting Risks", c: "Consulting carries risks that employment does not. Income is variable and depends on your ability to find and retain clients. Reputation damage from one bad engagement can affect future opportunities. Legal exposure exists when your advice influences client decisions. Health insurance and benefits require personal arrangement. Understanding these risks before diving in allows you to mitigate them intentionally rather than discovering them painfully." },
      { h: "Financial Risk Assessment", c: "Financial risk is the most immediate concern for new consultants. Can you sustain yourself during the startup period? What happens if your first engagement falls through? How long can you survive without income? Honest answers to these questions determine whether you are ready. Financial risk decreases as you build reserves, establish recurring clients, and develop multiple income streams." },
      { h: "Reputation Risk Management", c: "Your reputation is your most valuable asset in consulting. One poorly handled engagement can follow you for years. Risk management means being selective about clients, over-delivering on commitments, addressing problems proactively, and maintaining professional boundaries. It also means documenting everything so that disputes can be resolved with evidence rather than he-said-she-said." },
      { h: "Legal Risk Mitigation", c: "Legal risks include breach of contract claims, malpractice allegations, intellectual property disputes, and non-compete violations. Mitigation strategies include clear contracts that define scope and limitations, professional liability insurance, careful documentation of advice given and decisions made, and staying within your actual expertise. Never promise results you cannot control." },
      { h: "Health and Personal Risks", c: "Leaving employment means losing employer-provided health insurance. Individual market insurance is available but often more expensive with different coverage. Disability insurance protects income if you cannot work. Life insurance matters if others depend on your income. These personal protections are now your responsibility. Ignoring them hoping nothing bad happens is not risk management." },
      { h: "Go/No-Go Decision Framework", c: "Before committing to CPP, assess your total risk picture. Do you have sufficient financial reserves? Is your family prepared for income variability? Have you arranged health insurance? Do you have the emotional resilience for uncertainty? Are you willing to invest in professional development continuously? If you cannot answer yes to these questions, work on readiness before making the leap." }
    ],
    interactive: { type: "matching", title: "Match Risk to Mitigation", pairs: [{ left: "Financial Risk", right: "Cash reserves" }, { left: "Legal Risk", right: "E&O insurance" }, { left: "Reputation Risk", right: "Over-deliver always" }, { left: "Health Risk", right: "Individual coverage" }] },
    keyTakeaways: ["Consulting risks differ from employment risks", "Financial reserves are the primary buffer", "Reputation is your most valuable asset", "Assess readiness before committing"]
  },
  7: {
    title: "Building Your Consulting Resume",
    sections: [
      { h: "Resume vs Portfolio Thinking", c: "Consulting resumes differ from job application resumes. Clients want to know what you have accomplished, not just where you have worked. They want evidence of relevant expertise and successful outcomes. Portfolio thinking means curating examples of your work, documenting results you have achieved, and building a track record that demonstrates capability. Your resume is marketing material, not a job history." },
      { h: "Quantifying Your Impact", c: "Vague claims like improved fundraising mean nothing. Quantified impact matters. Increased donor retention from 45% to 62% over 18 months. Secured $2.3 million in foundation grants during fiscal year. Trained 45 board members resulting in 28% increase in board giving. These specific numbers demonstrate your actual value. Start tracking your impact now so you have data to cite later." },
      { h: "Highlighting Relevant Experience", c: "Not all experience is equally relevant to consulting prospects. Prioritize experience that matches likely engagements. If you want to consult on grant writing, lead with grant-related accomplishments. If you want to focus on major gifts, emphasize donor cultivation success. Tailor your resume for different opportunity types rather than sending the same generic version everywhere." },
      { h: "Testimonials and References", c: "Third-party validation strengthens credibility more than self-promotion. Collect testimonials from supervisors, board members, donors, and collaborators who can speak to your abilities. LinkedIn recommendations are visible and easy to share. Written testimonials can be included in proposals. Strong references who will speak enthusiastically on your behalf are invaluable when clients check backgrounds." },
      { h: "Addressing Experience Gaps", c: "Everyone has gaps in their experience. Maybe you have never led a capital campaign. Maybe you have not worked with organizations of a certain size. Acknowledge gaps honestly rather than pretending expertise you lack. Position your transferable skills and emphasize your learning agility. For CPP, GMG can provide supervision and support that fills some gaps while you build experience." },
      { h: "Continuous Resume Development", c: "Your resume should evolve continuously. After every engagement, update your impact statements. Collect new testimonials. Add new skills and certifications. Review quarterly to ensure your materials reflect current capabilities. A stale resume from three years ago does not represent who you are today. Active resume management is part of professional practice." }
    ],
    interactive: { type: "sorting", title: "Order Resume Building Steps", items: ["Document Past Impact", "Quantify Results", "Collect Testimonials", "Tailor for Opportunities", "Update Regularly"] },
    keyTakeaways: ["Portfolio thinking emphasizes accomplishments over history", "Quantified impact matters more than vague claims", "Third-party validation builds credibility", "Update materials continuously after each engagement"]
  },
  8: {
    title: "Certifications Worth Having",
    sections: [
      { h: "The Certification Landscape", c: "The nonprofit and fundraising sector has numerous certifications available. Some carry significant weight with clients. Others are little more than credential mills. Understanding the landscape helps you invest in certifications that matter while avoiding those that waste time and money. Not every certification is worth pursuing, but the right ones signal professionalism and competence." },
      { h: "CFRE Credential", c: "The Certified Fund Raising Executive (CFRE) is the most recognized credential in the fundraising profession. It requires documented experience, continuing education, and passing a comprehensive examination. CFRE signals to clients that you meet professional standards and commit to ongoing development. For consultants focused on fundraising, CFRE is worth the investment of time and money to obtain." },
      { h: "Grant-Related Certifications", c: "Grant Professionals Association offers the Grant Professional Certified (GPC) credential. This certification focuses specifically on grant research, writing, and management. For consultants who specialize in foundation and government grants, GPC distinguishes you from competitors who claim grant expertise without validation. The credential requires examination and ongoing maintenance." },
      { h: "Sector-Specific Credentials", c: "Some consulting niches have their own certifications. Planned giving has CSPG from the National Association of Charitable Gift Planners. Nonprofit management has CNP from the Nonprofit Leadership Alliance. These credentials matter most when you specialize in those areas. General fundraising consultants may not need sector-specific credentials unless they focus on particular areas." },
      { h: "Certifications to Skip", c: "Not every credential deserves your time. Some organizations sell certifications that mean nothing to clients who have never heard of the issuing body. Some require only payment and a short online course, which signals nothing about competence. Before pursuing any certification, research how clients view it. Ask colleagues which credentials they respect. Prioritize recognized credentials over impressive-sounding unknowns." },
      { h: "Maintaining Credentials", c: "Most professional certifications require ongoing maintenance through continuing education and periodic recertification. Budget both time and money for maintenance requirements. Track deadlines to avoid lapses. Some consultants let credentials lapse because maintenance feels burdensome, but this undermines the investment made to obtain them. Either maintain credentials properly or do not pursue them initially." }
    ],
    interactive: { type: "matching", title: "Match Certification to Focus", pairs: [{ left: "CFRE", right: "General Fundraising" }, { left: "GPC", right: "Grant Writing" }, { left: "CSPG", right: "Planned Giving" }, { left: "CNP", right: "Nonprofit Management" }] },
    keyTakeaways: ["Not all certifications carry equal weight", "CFRE is the most recognized fundraising credential", "Research how clients view credentials before pursuing", "Budget for ongoing maintenance requirements"]
  },
  9: {
    title: "Online Presence for Consultants",
    sections: [
      { h: "Why Online Presence Matters", c: "Prospective clients Google you before hiring you. What they find shapes their perception before you ever speak. A strong online presence builds credibility, demonstrates expertise, and makes you findable by people seeking help. A weak or nonexistent online presence raises questions about professionalism. Ignoring this reality puts you at a disadvantage against competitors who manage their digital presence actively." },
      { h: "LinkedIn as Home Base", c: "For professional consultants, LinkedIn is the primary platform. Your profile should read as a portfolio of accomplishments, not a job application. Use the summary section to articulate your consulting focus and value proposition. Feature media, publications, and recommendations. Share relevant content regularly to demonstrate ongoing engagement with your field. LinkedIn is often the first place clients look." },
      { h: "Website Considerations", c: "A professional website establishes legitimacy and provides space for detailed information. It should include your background, services offered, testimonials, and contact information. For CPP consultants, personal websites complement the GMG presence rather than replacing it. The website does not need to be elaborate. Clean, professional, and mobile-responsive matters more than fancy design." },
      { h: "Content Strategy", c: "Publishing content establishes expertise. This might include blog posts about fundraising topics, LinkedIn articles sharing insights, guest posts on industry publications, or speaking at conferences. Consistent content production keeps you visible and demonstrates that you think actively about your field. Quality matters more than quantity. One thoughtful piece monthly beats daily superficial posts." },
      { h: "Managing Your Digital Footprint", c: "Beyond what you create, manage what others can find. Google yourself regularly. Clean up old social media that does not reflect current professionalism. Request removal of inaccurate information. Be thoughtful about what you post anywhere online. The internet has a long memory. What seemed funny five years ago might embarrass you with prospective clients today." },
      { h: "Integration with GMG Brand", c: "CPP consultants operate under the GMG umbrella. Your personal brand should complement, not compete with, GMG's brand. Reference your GMG affiliation appropriately. Follow GMG brand guidelines for materials. Coordinate with GMG on major visibility activities. The goal is leveraging both your personal credibility and GMG's organizational reputation for mutual benefit." }
    ],
    interactive: { type: "matching", title: "Match Platform to Purpose", pairs: [{ left: "LinkedIn", right: "Professional networking" }, { left: "Website", right: "Detailed information" }, { left: "Content", right: "Demonstrate expertise" }, { left: "Google", right: "Manage footprint" }] },
    keyTakeaways: ["Clients research you online before hiring", "LinkedIn is the primary professional platform", "Content demonstrates expertise over time", "Personal brand complements GMG brand"]
  },
  11: {
    title: "Crafting Your Consulting Pitch",
    sections: [
      { h: "What a Pitch Accomplishes", c: "Your consulting pitch communicates who you are, what you do, and why someone should hire you, all in a brief, compelling format. A strong pitch opens doors. A weak pitch closes them before conversation starts. The pitch is not about being clever or memorable for its own sake. It is about clearly conveying value in a way that makes prospective clients want to learn more." },
      { h: "The Problem-Solution Framework", c: "Effective pitches follow a problem-solution structure. Start by identifying a problem your target clients experience. Then position your services as the solution. Nonprofits struggle to retain donors beyond the first gift. I help organizations build stewardship systems that increase retention rates by 20% or more. This framework makes value immediately clear and focuses on client needs rather than consultant credentials." },
      { h: "Adapting for Different Audiences", c: "One pitch does not fit all situations. The version you give at a networking event differs from the version in a proposal. The pitch to an executive director differs from the pitch to a board chair. Adapt your core message for context. Emphasize different aspects based on what each audience cares about. Practice multiple versions so adaptation feels natural." },
      { h: "The 30-Second Version", c: "The elevator pitch needs to work in 30 seconds or less. This forces ruthless clarity. Strip away everything except the essential value proposition. I help small nonprofits win foundation grants. My clients typically secure funding within 90 days of our first session. No background, no methodology explanation, no credentials. Just the core value and a proof point." },
      { h: "The Extended Version", c: "Some situations allow more time. The three-minute pitch adds context, methodology overview, and credibility elements. The 10-minute pitch can include case studies and detailed discussion of approach. These longer versions build on the core pitch rather than replacing it. If you cannot explain your value in 30 seconds, longer versions will not save you." },
      { h: "Practice and Refinement", c: "A pitch that sounds polished requires extensive practice. Write it out. Say it aloud repeatedly. Record yourself and listen critically. Practice with friends who can give honest feedback. Refine based on what resonates and what falls flat. The goal is natural delivery that sounds conversational, not scripted. This requires enough practice that the words flow automatically." }
    ],
    interactive: { type: "sorting", title: "Order Pitch Development Steps", items: ["Identify Client Problem", "Position Your Solution", "Write Core Message", "Practice Repeatedly", "Adapt for Audiences"] },
    keyTakeaways: ["Pitches communicate value quickly and compellingly", "Problem-solution framework focuses on client needs", "Adapt core message for different audiences", "Extensive practice enables natural delivery"]
  },
  12: {
    title: "Client Interviews and Discovery",
    sections: [
      { h: "The Discovery Mindset", c: "Client interviews are not job interviews where you try to impress. They are discovery conversations where you learn enough to determine whether a productive engagement is possible. Good discovery requires genuine curiosity about the client's situation, challenges, and goals. It requires listening more than talking. The consultant who talks too much misses critical information and often loses the opportunity." },
      { h: "Questions That Reveal Reality", c: "Effective discovery questions uncover actual situations, not just polished talking points. Ask what has been tried before and why it did not work. Ask about internal dynamics and capacity constraints. Ask about budget realities and decision-making processes. Ask about success definitions and how outcomes will be measured. These questions reveal whether you can actually help and what helping would require." },
      { h: "Reading Between the Lines", c: "What clients say and what they mean sometimes differ. We want to grow our foundation grants might really mean our new board chair thinks we should get more grants. We need help with our annual fund might mask deeper issues with donor retention or board engagement. Listen for underlying concerns. Ask follow-up questions when something seems off. Understanding the real situation enables better proposals." },
      { h: "Assessing Fit Honestly", c: "Not every potential engagement is a good fit. Discovery should reveal whether your expertise matches their needs, whether their budget matches your rates, whether their capacity allows them to implement recommendations, and whether interpersonal dynamics support productive collaboration. Walking away from bad fits protects your reputation and saves everyone time." },
      { h: "Managing Expectations Early", c: "Discovery is also the time to set realistic expectations. If a client expects miracles in unrealistic timeframes, address this now. If they underestimate the work involved, explain what the project actually requires. Expectations set during discovery carry through the engagement. Misaligned expectations discovered later create conflict and dissatisfaction." },
      { h: "Transitioning to Proposal", c: "Successful discovery leads naturally to proposal discussion. Summarize what you heard to confirm understanding. Outline how you might approach the situation. Explain your typical engagement process. Discuss rough budget ranges to ensure alignment before investing in detailed proposals. The transition should feel like a collaborative exploration rather than a sales close." }
    ],
    interactive: { type: "matching", title: "Match Discovery Element", pairs: [{ left: "Questions", right: "Uncover real situation" }, { left: "Listening", right: "More than talking" }, { left: "Fit Assessment", right: "Protect reputation" }, { left: "Expectations", right: "Set early honestly" }] },
    keyTakeaways: ["Discovery is about learning, not impressing", "Effective questions reveal reality beneath talking points", "Assess fit honestly and walk away when wrong", "Set expectations early to prevent later conflict"]
  },
  13: {
    title: "Documentation Habits for Consultants",
    sections: [
      { h: "Why Documentation Matters", c: "Documentation protects you legally, helps you deliver consistently, enables knowledge transfer, and provides evidence if disputes arise. Consultants who document well avoid misunderstandings, demonstrate professionalism, and create records that support future work with the same or similar clients. Documentation is not bureaucratic overhead. It is professional practice." },
      { h: "Engagement Documentation", c: "Every engagement should have documented scope, deliverables, timeline, and budget. Confirm understandings in writing after verbal conversations. Document changes to scope as they occur with client acknowledgment. Keep copies of all communications and deliverables. This documentation establishes the record if questions arise about what was agreed or delivered." },
      { h: "Meeting and Communication Records", c: "Keep records of significant meetings and communications. Note what was discussed, what was decided, and what actions each party committed to. Send summary emails after important conversations that capture key points. These records prevent I thought we agreed to X disputes and help you remember details months later when questions arise." },
      { h: "Work Product Management", c: "Organize work product systematically. Use consistent file naming conventions. Maintain version control so you can identify the latest version and recover earlier versions if needed. Store deliverables and supporting materials in organized folders. Be able to produce any document from any past engagement quickly if requested." },
      { h: "Time and Expense Tracking", c: "Track time spent on each engagement with reasonable detail. Track expenses that will be billed or reimbursed. This tracking supports accurate invoicing, helps you estimate future projects, and provides evidence if clients question charges. Time tracking also reveals where your time actually goes, which may differ from where you think it goes." },
      { h: "Client Relationship Records", c: "Beyond individual engagements, maintain records of client relationships. Note key contacts and their roles. Record preferences and sensitivities. Track engagement history and outcomes. These records enable smooth transitions between projects and help you maintain relationships over time. Treat client information with appropriate confidentiality." }
    ],
    interactive: { type: "matching", title: "Match Documentation Type", pairs: [{ left: "Scope Documents", right: "Prevent misunderstandings" }, { left: "Meeting Notes", right: "Capture decisions" }, { left: "Time Tracking", right: "Support invoicing" }, { left: "Client Records", right: "Maintain relationships" }] },
    keyTakeaways: ["Documentation is professional practice, not overhead", "Confirm understandings in writing after conversations", "Track time and expenses contemporaneously", "Maintain organized client relationship records"]
  },
  14: {
    title: "Folder Structure and File Organization",
    sections: [
      { h: "Organization as Professional Foundation", c: "Disorganized files create inefficiency, risk, and unprofessional impressions. Time spent searching for documents is time not spent on valuable work. Lost files can mean missed deadlines or recreated work. Sending clients wrong versions damages credibility. A systematic folder structure prevents these problems and demonstrates professional rigor." },
      { h: "Top-Level Organization", c: "Start with a logical top-level structure. Common approaches include organizing by client, by project type, or by year. Most consultants use client-based organization with projects nested inside. Create consistent folder structures that apply across all clients. When you open any client folder, you should know exactly where to find what you need." },
      { h: "Standard Client Folder Template", c: "Create a template that you replicate for each new client. Typical subfolders include Contracts and Agreements, Proposals, Project Deliverables (organized by project), Communications (organized by date or topic), Financial (invoices and expense records), and Reference Materials. Consistent structure means you never have to think about where something goes." },
      { h: "Naming Conventions", c: "File naming should enable identification without opening files. Include client name, document type, and date in standardized format. Use YYYYMMDD date format so files sort chronologically. Avoid spaces in filenames when possible. Example: ClientName_Proposal_20251215_v2.docx tells you everything you need to know before opening." },
      { h: "Version Control Practices", c: "Track document versions systematically. Options include version numbers in filenames (v1, v2), date stamps, or version control software. Never have multiple files named Final or Latest in the same folder. Clearly identify which version is current. Archive previous versions in case you need to reference or revert." },
      { h: "Backup and Security", c: "Organized files need protection. Back up regularly to multiple locations, including cloud storage. Protect sensitive client information with appropriate security. Encrypt confidential files when necessary. Plan for device failure or theft. Your organizational system means nothing if files disappear or leak." }
    ],
    interactive: { type: "sorting", title: "Order File Organization Steps", items: ["Create Folder Template", "Establish Naming Convention", "Organize by Client", "Implement Version Control", "Set Up Backups"] },
    keyTakeaways: ["Systematic organization enables efficient work", "Consistent structures apply across all clients", "Naming conventions enable identification without opening", "Backups protect against loss and disaster"]
  }
};


const V3_QUIZZES = {
  "3-5": { questions: [
    { q: "What does CPP stand for?", a: "C", opts: ["A. Certified Professional Program", "B. Client Partnership Plan", "C. Consultant Pipeline Program", "D. Corporate Partnership Protocol"], speaker: "claudette" },
    { q: "Why does GMG handle client acquisition?", a: "B", opts: ["A. Consultants can't sell", "B. Lets consultants focus on delivery", "C. It's required by law", "D. Clients prefer it"], speaker: "chanet" },
    { q: "What type of business entity should most consultants start with?", a: "A", opts: ["A. LLC", "B. C-Corp", "C. Partnership", "D. Non-profit"], speaker: "claudette" },
    { q: "What percentage should you set aside for taxes?", a: "C", opts: ["A. 10-15%", "B. 15-20%", "C. 25-30%", "D. 40-50%"], speaker: "chanet" },
    { q: "How many billable hours per week is sustainable long-term?", a: "B", opts: ["A. 40-45", "B. 25-30", "C. 50-60", "D. 15-20"], speaker: "claudette" }
  ]},
  "3-10": { questions: [
    { q: "What should a consulting resume emphasize?", a: "D", opts: ["A. Job titles", "B. Education", "C. Years of experience", "D. Quantified results"], speaker: "chanet" },
    { q: "Which certification is the gold standard for fundraisers?", a: "A", opts: ["A. CFRE", "B. PMP", "C. MBA", "D. CPA"], speaker: "claudette" },
    { q: "What's the first thing to optimize on LinkedIn?", a: "B", opts: ["A. Your posts", "B. Professional headshot", "C. Number of connections", "D. Endorsements"], speaker: "chanet" },
    { q: "How long should an elevator pitch take?", a: "A", opts: ["A. Under 30 seconds", "B. 2-3 minutes", "C. 5 minutes", "D. As long as needed"], speaker: "claudette" },
    { q: "Why should you tailor your pitch?", a: "C", opts: ["A. To seem more impressive", "B. To confuse competitors", "C. Different audiences need different framing", "D. To practice variety"], speaker: "chanet" }
  ]},
  "3-15": { questions: [
    { q: "Why is documentation important in consulting?", a: "D", opts: ["A. Legal requirements only", "B. GMG demands it", "C. Clients expect it", "D. Protects everyone and builds knowledge"], speaker: "claudette" },
    { q: "What should you always do before recording a meeting?", a: "B", opts: ["A. Test the microphone", "B. Get explicit consent", "C. Send an agenda", "D. Check your notes"], speaker: "chanet" },
    { q: "What date format should be used in file names?", a: "A", opts: ["A. YYYY-MM-DD", "B. MM-DD-YYYY", "C. DD-MM-YYYY", "D. Any format"], speaker: "claudette" },
    { q: "When should you request a pre-brief?", a: "D", opts: ["A. Only for presentations", "B. Only when required", "C. Never", "D. Before major client interactions"], speaker: "chanet" },
    { q: "How quickly should you acknowledge client emails?", a: "B", opts: ["A. Immediately", "B. Within 24 hours", "C. Within a week", "D. When convenient"], speaker: "claudette" }
  ]}
};

const getLessons = (v) => v === 1 ? V1_TITLES : v === 2 ? V2_TITLES : V3_TITLES;
const isQuizDay = (v, d) => QUIZ_DAYS[v]?.includes(d);
const getQuiz = (v, d) => QUIZZES[`${v}-${d}`] || V3_QUIZZES[`${v}-${d}`];
const getDuration = (v, d) => DURATIONS[v]?.[d-1] || "10 min";
const getContent = (v, d) => {
  if (v === 1 && V1_CONTENT[d]) return V1_CONTENT[d];
  if (v === 2 && V2_CONTENT[d]) return V2_CONTENT[d];
  if (v === 3 && V3_CONTENT[d]) return V3_CONTENT[d];
  return { 
    title: getLessons(v)[d-1] || `Day ${d}`, 
    sections: [{ h: "Coming Soon", c: "This lesson content is being developed. Check back soon for the full interactive experience!" }], 
    keyTakeaways: ["Content coming soon"] 
  };
};

// ============================================================
// V2.4 COMPONENT: Badge Celebration Modal
// ============================================================
// Shows when user earns a new achievement badge.
// Includes confetti animation for rare+ badges.
// ============================================================

function BadgeModal({ badge, onClose }) {
  if (!badge) return null;
  
  const rarityColors = BADGE_RARITY_COLORS[badge.rarity];
  
  return (
    <div 
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001,
        animation: 'fadeIn 0.3s ease'
      }}
    >
      <div 
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: '24px',
          padding: '32px',
          maxWidth: '320px',
          width: '90%',
          textAlign: 'center',
          animation: 'bounceIn 0.5s ease'
        }}
      >
        {(badge.rarity === 'rare' || badge.rarity === 'legendary') && <Confetti active={true} />}
        
        <div style={{ 
          fontSize: '64px', 
          marginBottom: '16px',
          animation: 'pulse 1s ease infinite'
        }}>
          {badge.icon}
        </div>
        
        <p style={{ 
          fontSize: '12px', 
          fontWeight: '700', 
          textTransform: 'uppercase', 
          letterSpacing: '1px',
          color: rarityColors.text,
          marginBottom: '8px'
        }}>
          {badge.rarity} Badge Unlocked!
        </p>
        
        <h2 style={{ 
          fontSize: '22px', 
          fontWeight: '700', 
          color: GMG.navyBlue,
          margin: '0 0 8px 0'
        }}>
          {badge.name}
        </h2>
        
        <p style={{ 
          fontSize: '14px', 
          color: '#666',
          margin: '0 0 24px 0'
        }}>
          {badge.description}
        </p>
        
        <button 
          onClick={onClose}
          style={{
            padding: '14px 48px',
            borderRadius: '12px',
            border: 'none',
            background: `linear-gradient(135deg, ${GMG.navyBlue}, ${GMG.roseGold})`,
            color: '#fff',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Awesome!
        </button>
      </div>
      
      <style>{`
        @keyframes bounceIn {
          0% { transform: scale(0.5); opacity: 0; }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// V2.4 COMPONENT: Leaderboard Card
// ============================================================

function LeaderboardEntry({ rank, user, isCurrentUser }) {
  const rankColors = {
    1: { bg: '#ffd70020', border: '#ffd700', icon: '\u{1F947}' },
    2: { bg: '#c0c0c020', border: '#c0c0c0', icon: '\u{1F948}' },
    3: { bg: '#cd7f3220', border: '#cd7f32', icon: '\u{1F949}' }
  };
  
  const style = rankColors[rank] || { bg: 'transparent', border: 'transparent', icon: rank };
  
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px',
      borderRadius: '12px',
      background: isCurrentUser ? `${GMG.navyBlue}10` : style.bg,
      border: isCurrentUser ? `2px solid ${GMG.navyBlue}` : `1px solid ${style.border}`,
      marginBottom: '8px'
    }}>
      <div style={{
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: rank <= 3 ? '20px' : '14px',
        fontWeight: '700',
        color: rank <= 3 ? style.border : '#666'
      }}>
        {style.icon}
      </div>
      
      {user.photo ? (
        <img src={user.photo} alt="" style={{ width: '36px', height: '36px', borderRadius: '50%' }} />
      ) : (
        <div style={{ 
          width: '36px', 
          height: '36px', 
          borderRadius: '50%', 
          background: `linear-gradient(135deg, ${GMG.navyBlue}, ${GMG.roseGold})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontWeight: '600',
          fontSize: '14px'
        }}>
          {user.name?.[0] || '?'}
        </div>
      )}
      
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a2e', margin: 0 }}>
          {user.name} {isCurrentUser && '(You)'}
        </p>
        <p style={{ fontSize: '12px', color: '#666', margin: '2px 0 0 0' }}>
          {user.totalLessons} lessons - {user.streak} day streak
        </p>
      </div>
      
      <div style={{ textAlign: 'right' }}>
        <p style={{ fontSize: '16px', fontWeight: '700', color: GMG.navyBlue, margin: 0 }}>
          {user.xp.toLocaleString()}
        </p>
        <p style={{ fontSize: '10px', color: '#666', margin: 0 }}>XP</p>
      </div>
    </div>
  );
}


// Interactive Components
function MatchingGame({ pairs, onComplete }) {
  const [selected, setSelected] = useState({ left: null, right: null });
  const [matched, setMatched] = useState([]);
  const [shuffledRight, setShuffledRight] = useState([]);
  
  useEffect(() => {
    setShuffledRight([...pairs].sort(() => Math.random() - 0.5));
  }, []);

  const handleSelect = (side, item) => {
    const newSelected = { ...selected, [side]: item };
    setSelected(newSelected);
    
    if (newSelected.left && newSelected.right) {
      const correct = pairs.find(p => p.left === newSelected.left && p.right === newSelected.right);
      if (correct) {
        setMatched([...matched, newSelected.left]);
      }
      setTimeout(() => setSelected({ left: null, right: null }), 500);
    }
  };

  const isComplete = matched.length === pairs.length;
  
  useEffect(() => {
    if (isComplete && onComplete) onComplete();
  }, [isComplete]);

  return (
    <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {pairs.map(p => (
          <button key={p.left} onClick={() => !matched.includes(p.left) && handleSelect('left', p.left)}
            style={{ padding: '12px 16px', borderRadius: '10px', border: matched.includes(p.left) ? '2px solid #22c55e' : selected.left === p.left ? '2px solid ' + GMG.roseGold : '2px solid #ddd', background: matched.includes(p.left) ? '#22c55e20' : '#fff', cursor: matched.includes(p.left) ? 'default' : 'pointer', fontSize: '14px', minWidth: '140px' }}>
            {p.left} {matched.includes(p.left) && '\u{2713}'}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {(shuffledRight.length ? shuffledRight : pairs).map(p => (
          <button key={p.right} onClick={() => !matched.includes(p.left) && handleSelect('right', p.right)}
            style={{ padding: '12px 16px', borderRadius: '10px', border: matched.some(m => pairs.find(pr => pr.left === m)?.right === p.right) ? '2px solid #22c55e' : selected.right === p.right ? '2px solid ' + GMG.navyBlue : '2px solid #ddd', background: matched.some(m => pairs.find(pr => pr.left === m)?.right === p.right) ? '#22c55e20' : '#fff', cursor: 'pointer', fontSize: '14px', minWidth: '100px' }}>
            {p.right}
          </button>
        ))}
      </div>
    </div>
  );
}

function SliderGame({ answer, tolerance, onComplete }) {
  const [value, setValue] = useState(50);
  const [submitted, setSubmitted] = useState(false);
  const isCorrect = Math.abs(value - answer) <= tolerance;

  const handleSubmit = () => {
    setSubmitted(true);
    if (isCorrect && onComplete) onComplete();
  };
  
  const handleRetry = () => {
    setSubmitted(false);
    setValue(50);
  };

  return (
    <div style={{ textAlign: 'center', padding: '20px' }}>
      <input type="range" min="0" max="100" value={value} onChange={e => setValue(Number(e.target.value))} disabled={submitted}
        style={{ width: '100%', height: '8px', cursor: submitted ? 'default' : 'pointer' }} />
      <p style={{ fontSize: '32px', fontWeight: '700', color: submitted ? (isCorrect ? '#22c55e' : '#ef4444') : GMG.navyBlue, margin: '16px 0' }}>{value}%</p>
      {!submitted ? (
        <button onClick={handleSubmit} style={{ padding: '12px 32px', borderRadius: '10px', border: 'none', background: GMG.roseGold, color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>Lock In Answer</button>
      ) : isCorrect ? (
        <p style={{ color: '#22c55e', fontWeight: '600' }}>{'\u{1F389}'} Correct!</p>
      ) : (
        <div>
          <p style={{ color: '#ef4444', fontWeight: '600', marginBottom: '12px' }}>Not quite! The answer is {answer}%</p>
          <button onClick={handleRetry} style={{ padding: '12px 32px', borderRadius: '10px', border: 'none', background: GMG.navyBlue, color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>{'\u{1F504}'} Try Again</button>
        </div>
      )}
    </div>
  );
}

function SortingGame({ items, onComplete }) {
  const [order, setOrder] = useState([...items].sort(() => Math.random() - 0.5));
  const [checked, setChecked] = useState(false);
  const isCorrect = JSON.stringify(order) === JSON.stringify(items);

  const moveItem = (from, to) => {
    const newOrder = [...order];
    const [item] = newOrder.splice(from, 1);
    newOrder.splice(to, 0, item);
    setOrder(newOrder);
  };

  const handleCheck = () => {
    setChecked(true);
    if (isCorrect && onComplete) onComplete();
  };
  
  const handleRetry = () => {
    setChecked(false);
    setOrder([...items].sort(() => Math.random() - 0.5));
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
        {order.map((item, i) => (
          <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: checked ? (items[i] === item ? '#22c55e20' : '#ef444420') : '#fff', borderRadius: '10px', border: '2px solid ' + (checked ? (items[i] === item ? '#22c55e' : '#ef4444') : '#ddd') }}>
            <span style={{ fontWeight: '700', color: GMG.navyBlue }}>{i + 1}</span>
            <span style={{ flex: 1 }}>{item}</span>
            {!checked && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {i > 0 && <button onClick={() => moveItem(i, i-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}>{'\u25B2'}</button>}
                {i < order.length - 1 && <button onClick={() => moveItem(i, i+1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}>{'\u25BC'}</button>}
              </div>
            )}
          </div>
        ))}
      </div>
      {!checked && <button onClick={handleCheck} style={{ width: '100%', padding: '12px', borderRadius: '10px', border: 'none', background: GMG.roseGold, color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>Check Order</button>}
      {checked && isCorrect && <p style={{ textAlign: 'center', color: '#22c55e', fontWeight: '600' }}>{'\u{1F389}'} Perfect!</p>}
      {checked && !isCorrect && (
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#ef4444', fontWeight: '600', marginBottom: '12px' }}>Not quite right. Try again!</p>
          <button onClick={handleRetry} style={{ padding: '12px 32px', borderRadius: '10px', border: 'none', background: GMG.navyBlue, color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>{'\u{1F504}'} Try Again</button>
        </div>
      )}
    </div>
  );
}


export default function GMGUniversity() {
  const [user, setUser] = useState(null);
  const [userTrack, setUserTrack] = useState('standard'); // Will be set based on user email
  
  // PWA update detection hook
  const { updateAvailable: pwaUpdateAvailable, doRefresh } = useForceRefresh();
  
  // Fast Track users by default (founders and designated users)
  const FAST_TRACK_USERS = [
    'brandon@globalmajoritygroup.com', 'brandonjpiercesr@gmail.com',
    'eric@globalmajoritygroup.com', 'ericlane@gidconsulting.co', 'ericreeselanesr@gmail.com', 'ericreecelanesr@gmail.com',
    'bryanjpiercejr@gmail.com', 'thebjpierce@icloud.com', 'thebjpierce@gmail.com',
    'cj.d.moore32@gmail.com',
    'elicooley6@gmail.com'
  ];
  const [currentView, setCurrentView] = useState('home');
  const [selectedVolume, setSelectedVolume] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [completedLessons, setCompletedLessons] = useState({ 1: [], 2: [], 3: [] });
  const [quizScores, setQuizScores] = useState({});
  const [allUsersData, setAllUsersData] = useState([]);
  
  // V3.2: Admin Portal State
  const [adminMode, setAdminMode] = useState('list'); // 'list', 'user', 'add'
  const [adminMarkMode, setAdminMarkMode] = useState('complete'); // 'complete' or 'open' for day grid
  const [adminEditingUser, setAdminEditingUser] = useState(false); // V3.5: Admin editing user profile
  const [adminEditName, setAdminEditName] = useState('');
  const [adminEditPhoto, setAdminEditPhoto] = useState('');
  const [selectedAdminUser, setSelectedAdminUser] = useState(null);
  const [adminSearchQuery, setAdminSearchQuery] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserTrack, setNewUserTrack] = useState('fast');
  const [newUserAccess, setNewUserAccess] = useState('all'); // 'v1', 'v1v2', 'all'
  const [chatOpen, setChatOpen] = useState(false);
  
  // ============================================================
  // V2.2 UPDATED: Messages now include timestamps for iMessage UI
  // ============================================================
  const [messages, setMessages] = useState([
    { speaker: 'claudette', text: "Welcome to GMG University! I'm Claudette.", timestamp: new Date().toISOString() },
    { speaker: 'chanet', text: "And I'm Chanet! We're here to help you become a fundraising pro.", timestamp: new Date().toISOString() }
  ]);
  const [chatInput, setChatInput] = useState('');
  
  // ============================================================
  // V2.1 STATE: Chat Loading & Banter System
  // ============================================================
  // PROBLEM: API calls take 2-5 seconds. Need feedback during wait.
  // SOLUTION: Show banter messages while waiting for real response.
  // ============================================================
  const [chatLoading, setChatLoading] = useState(false);
  const [banterMessages, setBanterMessages] = useState([]);
  const banterIntervalRef = useRef(null);
  
  const [quizMode, setQuizMode] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [showQuizResult, setShowQuizResult] = useState(false);
  const [founderView, setFounderView] = useState(false);
  const [testUnlockMode, setTestUnlockMode] = useState(false); // V2.4: Founder-only unlock bypass for testing
  const [authLoading, setAuthLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [syncStatus, setSyncStatus] = useState('synced');
  
  // V3.3: Update Checker State
  const [updateAvailable, setUpdateAvailable] = useState(null);
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const [streak, setStreak] = useState(0);
  const [bookmarks, setBookmarks] = useState([]);
  const [quizReviewMode, setQuizReviewMode] = useState(null);
  const [interactiveScores, setInteractiveScores] = useState({});
  const [certificateModal, setCertificateModal] = useState(null);
  const [scrollPositions, setScrollPositions] = useState({});
  const [lastActivity, setLastActivity] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [lastProgressBackup, setLastProgressBackup] = useState(null); // For undo
  const [viewingUser, setViewingUser] = useState(null); // For viewing another user's profile
  
  // V3.5: Profile Editing State
  const [editingProfile, setEditingProfile] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPhotoUrl, setEditPhotoUrl] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // V3.5: Notification State
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [showNotificationSetup, setShowNotificationSetup] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [lastCompletionDate, setLastCompletionDate] = useState(null); // V2.6.5.5: Track when lessons were completed
  const [unlockedDays, setUnlockedDays] = useState({ 1: 1, 2: 1, 3: 1 }); // V2.6.5.5: Track unlocked days per volume
  const [learningTrack, setLearningTrack] = useState('fast'); // 'standard' = 1 day/day, 'fast' = 2 days/day
  const [completionDates, setCompletionDates] = useState({}); // V2.6.5.5: Track when each lesson was completed { '1-1': '2025-01-01', ... }
  
  // ============================================================
  // V3.8 STATE: New Features
  // ============================================================
  const [spacedRepetition, setSpacedRepetition] = useState({}); // Track wrong quiz answers for review
  const [reviewMode, setReviewMode] = useState(false); // Review mode for spaced repetition
  const [reviewQuestions, setReviewQuestions] = useState([]); // Questions due for review
  const [discussionComments, setDiscussionComments] = useState([]); // Comments for current lesson
  const [discussionInput, setDiscussionInput] = useState('');
  const [showDiscussion, setShowDiscussion] = useState(false);
  const [certificates, setCertificates] = useState({}); // Generated certificate IDs
  const [offlineVolumes, setOfflineVolumes] = useState([]); // Downloaded volumes for offline
  
  // ============================================================
  // V3.9 STATE: PWA Install Prompt & Push Notifications
  // ============================================================
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushToken, setPushToken] = useState(null);
  const [offlineReady, setOfflineReady] = useState(false);
  
  // ============================================================
  // V2.3 STATE: Practice Mode & Audio Narration
  // ============================================================
  const [practiceMode, setPracticeMode] = useState(false);
  const [activeScenario, setActiveScenario] = useState(null);
  const [practiceMessages, setPracticeMessages] = useState([]);
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const speechSynthRef = useRef(null);
  
  // ============================================================
  // V2.4 STATE: Social Features & Achievements
  // ============================================================
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [weeklyProgress, setWeeklyProgress] = useState({
    lessonsThisWeek: 0,
    quizzesPassed: 0,
    daysActive: 0,
    practiceThisWeek: 0,
    weekStartDate: null
  });
  const [showBadgeModal, setShowBadgeModal] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [practiceCompleted, setPracticeCompleted] = useState(0);
  const [totalXP, setTotalXP] = useState(0);
  
  // ============================================================
  // V2.6.5 STATE: iMessage Lesson Interface
  // ============================================================
  // Transforms lessons from static reading to conversational learning.
  // Claudette or Chanet guides user through content in chat format.
  // ============================================================
  const [lessonChatMode, setLessonChatMode] = useState(false);
  const [lessonMessages, setLessonMessages] = useState([]);
  const [lessonChatLoading, setLessonChatLoading] = useState(false);
  const [currentLessonPersona, setCurrentLessonPersona] = useState(null);
  const [lessonSectionIndex, setLessonSectionIndex] = useState(0);
  const [awaitingLessonChoice, setAwaitingLessonChoice] = useState(null); // 'start', 'continue', 'activity', 'afterActivity'
  const [readAloudMode, setReadAloudMode] = useState(false);
  const [showActivityInChat, setShowActivityInChat] = useState(false); // V2.6.5.2: Show interactive game in chat
  
  // V2.6.5.3: Conversational Activity State
  const [activityQuestions, setActivityQuestions] = useState([]); // Array of {question, options, correctLetter}
  const [currentActivityQuestion, setCurrentActivityQuestion] = useState(0);
  const [activityMode, setActivityMode] = useState(false); // True when doing Q&A activity
  const [lessonChatInput, setLessonChatInput] = useState(''); // Separate input for lesson chat
  
  // ============================================================
  // V2.1 FEATURE: iOS KEYBOARD DETECTION
  // ============================================================
  // PROBLEM: On iPhone, when the keyboard opens, it pushes content
  // up and can cover the input field or hide the navigation bar.
  //
  // SOLUTION: Detect when keyboard opens using visualViewport API.
  // When keyboard is open, hide the bottom navigation bar so input
  // stays visible. When keyboard closes, show nav again.
  //
  // HOW IT WORKS: visualViewport.height shrinks when keyboard opens.
  // If viewport is less than 75% of window height, keyboard is open.
  // ============================================================
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  
  const messagesEndRef = useRef(null);
  const lessonContentRef = useRef(null);
  const lessonMessagesEndRef = useRef(null); // V2.10: Auto-scroll lesson chat
  const deliveredSectionsRef = useRef(new Set()); // V2.9: Prevent duplicate section delivery
  const isDeliveringRef = useRef(false); // V2.9: Prevent concurrent delivery

  const isFounder = user && FOUNDERS.includes(user.email);
  
  // ============================================================
  // V2.1 EFFECT: iOS Keyboard Detection
  // ============================================================
  useEffect(() => {
    const checkKeyboard = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const windowHeight = window.innerHeight;
      // Keyboard is "open" if viewport is significantly smaller than window
      setKeyboardOpen(viewportHeight < windowHeight * 0.75);
    };
    
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', checkKeyboard);
      window.visualViewport.addEventListener('scroll', checkKeyboard);
      return () => {
        window.visualViewport.removeEventListener('resize', checkKeyboard);
        window.visualViewport.removeEventListener('scroll', checkKeyboard);
      };
    }
  }, []);
  
  // Dark Mode Polish - Navy gradients instead of pure black, bronze accents
  const theme = darkMode ? {
    bg: '#0f1729', // Deep navy instead of black
    text: '#f0ede5',
    textMuted: '#9ca3af',
    accent: GMG.roseGold,
    accentAlt: GMG.bronze,
    card: 'rgba(27, 42, 78, 0.85)', // Navy glass
    border: GMG.bronze + '30',
    gradient: `linear-gradient(135deg, #0f1729 0%, #1B2A4E 50%, #2D4373 100%)`
  } : {
    bg: GMG.pearlWhite,
    text: GMG.charcoal,
    textMuted: '#666',
    accent: GMG.roseGold,
    accentAlt: GMG.navyBlue,
    card: 'rgba(255,255,255,0.9)',
    border: GMG.roseGold + '25',
    gradient: `linear-gradient(135deg, ${GMG.pearlWhite} 0%, ${GMG.pearl} 50%, ${GMG.silverLight} 100%)`
  };

  // Track configurations
  // CRITICAL: Fast Track does NOT omit content - it shows ALL days but expects 2 lessons per day
  // Standard = 1 lesson/day over 75 days, Fast = 2 lessons/day over 38 days (same content)
  const TRACK_CONFIG = {
    standard: { v1: 30, v2: 30, v3: 15, total: 75, name: 'Standard Track', lessonsPerDay: 1 },
    fast: { v1: 30, v2: 30, v3: 15, total: 75, name: 'Fast Track', lessonsPerDay: 2 }
  };

  useEffect(() => {
    if (!auth) { setAuthLoading(false); return; }
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userData = { uid: firebaseUser.uid, email: firebaseUser.email, name: firebaseUser.displayName || firebaseUser.email.split('@')[0], photo: firebaseUser.photoURL };
        setUser(userData);
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setCompletedLessons(data.completedLessons || { 1: [], 2: [], 3: [] });
            setQuizScores(data.quizScores || {});
            // Use saved track, or default to fast if user is in FAST_TRACK_USERS list
            const defaultTrack = FAST_TRACK_USERS.includes(firebaseUser.email) ? 'fast' : 'standard';
            setUserTrack(data.track || defaultTrack);
            setStreak(data.streak || 0);
            setBookmarks(data.bookmarks || []);
            setInteractiveScores(data.interactiveScores || {});
          } else {
            // New user - set default track based on email
            const defaultTrack = FAST_TRACK_USERS.includes(firebaseUser.email) ? 'fast' : 'standard';
            setUserTrack(defaultTrack);
          }
        } catch (e) { console.log('Firestore read error:', e); }
      } else { setUser(null); }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Listen for all users (founders only)
  useEffect(() => {
    if (!isFounder || !db) return;
    try {
      const unsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
        setAllUsersData(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      });
      return () => unsubscribe();
    } catch (e) { console.log('Firestore listener error:', e); }
  }, [isFounder]);

  // Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (selectedLesson) { setSelectedLesson(null); setQuizMode(false); setShowQuizResult(false); setQuizReviewMode(null); }
        else if (selectedVolume) { setSelectedVolume(null); }
        else if (chatOpen) { setChatOpen(false); }
      }
      if (selectedLesson && !quizMode && !isQuizDay(selectedLesson.volume, selectedLesson.day)) {
        if (e.key === 'ArrowLeft') { navigateLesson(-1); }
        if (e.key === 'ArrowRight') { navigateLesson(1); }
        if (e.key === 'Enter' && !completedLessons[selectedLesson.volume]?.includes(selectedLesson.day)) { completeLesson(); }
      }
      if (selectedVolume && !selectedLesson) {
        const trackConfig = TRACK_CONFIG[userTrack];
        const maxDays = selectedVolume === 1 ? trackConfig.v1 : selectedVolume === 2 ? trackConfig.v2 : trackConfig.v3;
        if (e.key >= '1' && e.key <= '9') {
          const dayNum = parseInt(e.key);
          if (dayNum <= maxDays) { 
            setSelectedLesson({ volume: selectedVolume, day: dayNum }); 
            // V2.6.5: Start lesson in chat mode
            if (!isQuizDay(selectedVolume, dayNum)) {
              startLessonChat(selectedVolume, dayNum);
            }
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedLesson, selectedVolume, quizMode, chatOpen, completedLessons, userTrack]);

  const signInWithGoogle = async () => {
    if (!auth) return;
    try { await signInWithPopup(auth, googleProvider); } catch (e) { console.error('Sign in error:', e); }
  };

  const handleSignOut = async () => {
    if (!auth) return;
    await signOut(auth);
    setUser(null);
    setCurrentView('home');
  };

  const saveUserData = async (updates) => {
    if (!user || !db) return;
    try {
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email, name: user.name, photoURL: user.photo, completedLessons, quizScores, track: userTrack,
        lastUpdated: new Date().toISOString(), ...updates
      }, { merge: true });
    } catch (e) { console.log('Save error:', e); }
  };

  // Founder can change a user's track
  const setUserTrackById = async (userId, newTrack) => {
    if (!isFounder || !db) return;
    try {
      await setDoc(doc(db, 'users', userId), { track: newTrack }, { merge: true });
    } catch (e) { console.log('Track update error:', e); }
  };

  // Reset all progress (with backup for undo)
  const resetProgress = async () => {
    // Save current state for undo
    setLastProgressBackup({
      completedLessons: { ...completedLessons },
      quizScores: { ...quizScores },
      streak,
      interactiveScores: { ...interactiveScores }
    });
    // Reset everything
    const emptyLessons = { 1: [], 2: [], 3: [] };
    setCompletedLessons(emptyLessons);
    setQuizScores({});
    setStreak(0);
    setInteractiveScores({});
    setShowResetConfirm(false);
    await saveUserData({ completedLessons: emptyLessons, quizScores: {}, streak: 0, interactiveScores: {} });
  };

  // Undo last reset
  const undoReset = async () => {
    if (!lastProgressBackup) return;
    setCompletedLessons(lastProgressBackup.completedLessons);
    setQuizScores(lastProgressBackup.quizScores);
    setStreak(lastProgressBackup.streak);
    setInteractiveScores(lastProgressBackup.interactiveScores);
    await saveUserData(lastProgressBackup);
    setLastProgressBackup(null);
  };

  // ============================================================
  // V3.2: ADMIN PORTAL FUNCTIONS
  // ============================================================
  
  // Admin: Unlock specific days for a user
  const adminUnlockDays = async (userId, volume, daysToUnlock) => {
    if (!isFounder || !db) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) return;
      const userData = userDoc.data();
      
      // Get current completed lessons
      const currentCompleted = userData.completedLessons || { 1: [], 2: [], 3: [] };
      const currentDates = userData.completionDates || {};
      
      // Add the days to completed (mark them as done so they unlock the next batch)
      const now = new Date().toISOString();
      daysToUnlock.forEach(day => {
        if (!currentCompleted[volume].includes(day)) {
          currentCompleted[volume].push(day);
          currentDates[`${volume}-${day}`] = now;
        }
      });
      
      // Save back
      await setDoc(doc(db, 'users', userId), {
        completedLessons: currentCompleted,
        completionDates: currentDates
      }, { merge: true });
      
      // If this is current user, update local state too
      if (user && userId === user.uid) {
        setCompletedLessons(currentCompleted);
        setCompletionDates(currentDates);
      }
      
      return true;
    } catch (e) {
      console.log('Admin unlock error:', e);
      return false;
    }
  };
  
  // V3.4: Admin function to UNMARK days (mark as open/incomplete)
  const adminUnmarkDays = async (userId, volume, daysToUnmark) => {
    if (!isFounder || !db) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) return;
      const userData = userDoc.data();
      
      // Get current completed lessons
      const currentCompleted = userData.completedLessons || { 1: [], 2: [], 3: [] };
      const currentDates = userData.completionDates || {};
      
      // Remove the days from completed
      daysToUnmark.forEach(day => {
        const idx = currentCompleted[volume].indexOf(day);
        if (idx > -1) {
          currentCompleted[volume].splice(idx, 1);
          delete currentDates[`${volume}-${day}`];
        }
      });
      
      // Save back
      await setDoc(doc(db, 'users', userId), {
        completedLessons: currentCompleted,
        completionDates: currentDates
      }, { merge: true });
      
      // If this is current user, update local state too
      if (user && userId === user.uid) {
        setCompletedLessons({...currentCompleted});
        setCompletionDates({...currentDates});
      }
      
      return true;
    } catch (e) {
      console.log('Admin unmark error:', e);
      return false;
    }
  };
  
  // V3.4: User function to unmark their own completed day
  const userUnmarkDay = async (volume, day) => {
    if (!user || !db) return false;
    try {
      const newCompleted = {...completedLessons};
      const newDates = {...completionDates};
      
      const idx = newCompleted[volume].indexOf(day);
      if (idx > -1) {
        newCompleted[volume] = newCompleted[volume].filter(d => d !== day);
        delete newDates[`${volume}-${day}`];
      }
      
      setCompletedLessons(newCompleted);
      setCompletionDates(newDates);
      
      await setDoc(doc(db, 'users', user.uid), {
        completedLessons: newCompleted,
        completionDates: newDates
      }, { merge: true });
      
      return true;
    } catch (e) {
      console.log('Unmark error:', e);
      return false;
    }
  };
  
  // ============================================================
  // V3.5: PROFILE EDITING FUNCTIONS
  // ============================================================
  
  // Open profile editor with current values
  const openProfileEditor = () => {
    setEditName(user?.name || '');
    setEditPhotoUrl(user?.photo || '');
    setUploadingPhoto(false);
    setEditingProfile(true);
  };
  
  // V3.6: Convert photo to base64 (no Firebase Storage needed - just works)
  const uploadProfilePhoto = async (file) => {
    if (!file) return null;
    
    setUploadingPhoto(true);
    try {
      // Compress and convert to base64
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = dataUrl;
      });
      
      // Resize to max 200x200 for profile photos
      const maxSize = 200;
      let width = img.width;
      let height = img.height;
      
      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to compressed JPEG base64
      const compressedBase64 = canvas.toDataURL('image/jpeg', 0.8);
      
      setUploadingPhoto(false);
      return compressedBase64;
    } catch (e) {
      console.log('Photo conversion error:', e);
      setUploadingPhoto(false);
      return null;
    }
  };
  
  // V3.6: Handle file selection from device
  const handlePhotoFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Photo must be under 5MB');
      return;
    }
    
    const url = await uploadProfilePhoto(file);
    if (url) {
      setEditPhotoUrl(url);
    } else {
      alert('\u{274C} Failed to upload photo. Try again.');
    }
  };
  
  // Save profile changes to Firebase
  const saveProfileChanges = async () => {
    if (!user || !db) return;
    try {
      const updates = {};
      if (editName.trim()) updates.name = editName.trim();
      if (editPhotoUrl.trim()) updates.photo = editPhotoUrl.trim();
      updates.photoURL = editPhotoUrl.trim() || user.photo || '';
      
      await setDoc(doc(db, 'users', user.uid), updates, { merge: true });
      
      // Update local user state
      setUser(prev => ({
        ...prev,
        name: editName.trim() || prev.name,
        photo: editPhotoUrl.trim() || prev.photo
      }));
      
      setEditingProfile(false);
      alert('\u{2705} Profile updated!');
    } catch (e) {
      console.log('Profile save error:', e);
      alert('\u{274C} Failed to save. Try again.');
    }
  };
  
  // Admin: Update another user's profile
  const adminUpdateUserProfile = async (userId, newName, newPhotoUrl) => {
    if (!isFounder || !db) return false;
    try {
      const updates = {};
      if (newName?.trim()) updates.name = newName.trim();
      if (newPhotoUrl?.trim()) {
        updates.photo = newPhotoUrl.trim();
        updates.photoURL = newPhotoUrl.trim();
      }
      
      await setDoc(doc(db, 'users', userId), updates, { merge: true });
      return true;
    } catch (e) {
      console.log('Admin profile update error:', e);
      return false;
    }
  };
  
  // ============================================================
  // V3.5: NOTIFICATION FUNCTIONS
  // ============================================================
  
  // Check current notification permission
  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    } else {
      setNotificationPermission('unsupported');
    }
  };
  
  // Request notification permission
  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      alert('Your browser does not support notifications.');
      return false;
    }
    
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      
      if (permission === 'granted') {
        // Save preference to Firebase
        if (user && db) {
          await setDoc(doc(db, 'users', user.uid), {
            notificationsEnabled: true,
            notificationPermission: permission
          }, { merge: true });
        }
        
        // Show test notification
        new Notification('GMG University', {
          body: 'Notifications enabled! We\'ll remind you to complete your daily lessons.',
          icon: GMG_LOGO
        });
        return true;
      }
      return false;
    } catch (e) {
      console.log('Notification permission error:', e);
      return false;
    }
  };
  
  // Toggle email notifications
  const toggleEmailNotifications = async (enabled) => {
    setEmailNotifications(enabled);
    if (user && db) {
      await setDoc(doc(db, 'users', user.uid), {
        emailNotifications: enabled
      }, { merge: true });
    }
  };
  
  // Schedule daily reminder (browser notification)
  const scheduleReminder = () => {
    if (notificationPermission !== 'granted') return;
    
    // Calculate time until 9 AM tomorrow
    const now = new Date();
    const tomorrow9am = new Date(now);
    tomorrow9am.setDate(tomorrow9am.getDate() + 1);
    tomorrow9am.setHours(9, 0, 0, 0);
    
    const msUntilReminder = tomorrow9am - now;
    
    // Set timeout for notification (only works while tab is open)
    setTimeout(() => {
      if (Notification.permission === 'granted') {
        new Notification('GMG University', {
          body: 'Time for your daily fundraising lesson! \u{1F4DA}',
          icon: GMG_LOGO
        });
      }
    }, msUntilReminder);
  };
  
  // Check notification permission on mount
  useEffect(() => {
    checkNotificationPermission();
  }, []);
  
  // ============================================================
  
  // Admin: Force unlock all content for a user (marks all days complete)
  const adminForceUnlockAll = async (userId) => {
    if (!isFounder || !db) return;
    try {
      const now = new Date().toISOString();
      const allCompleted = {
        1: Array.from({ length: 30 }, (_, i) => i + 1),
        2: Array.from({ length: 30 }, (_, i) => i + 1),
        3: Array.from({ length: 15 }, (_, i) => i + 1)
      };
      const allDates = {};
      [1, 2, 3].forEach(vol => {
        const days = vol === 3 ? 15 : 30;
        for (let d = 1; d <= days; d++) {
          allDates[`${vol}-${d}`] = now;
        }
      });
      
      await setDoc(doc(db, 'users', userId), {
        completedLessons: allCompleted,
        completionDates: allDates,
        adminUnlocked: true
      }, { merge: true });
      
      if (user && userId === user.uid) {
        setCompletedLessons(allCompleted);
        setCompletionDates(allDates);
      }
      
      return true;
    } catch (e) {
      console.log('Admin force unlock error:', e);
      return false;
    }
  };
  
  // Admin: Reset a specific user's progress
  const adminResetUser = async (userId) => {
    if (!isFounder || !db) return;
    try {
      const resetData = {
        completedLessons: { 1: [], 2: [], 3: [] },
        completionDates: {},
        quizScores: {},
        interactiveScores: {},
        bookmarks: [],
        streak: 0,
        lastActivity: null,
        scrollPositions: {},
        adminUnlocked: false
      };
      
      await setDoc(doc(db, 'users', userId), resetData, { merge: true });
      
      if (user && userId === user.uid) {
        setCompletedLessons({ 1: [], 2: [], 3: [] });
        setCompletionDates({});
        setQuizScores({});
        setInteractiveScores({});
        setStreak(0);
      }
      
      return true;
    } catch (e) {
      console.log('Admin reset error:', e);
      return false;
    }
  };
  
  // Admin: Set user's volume access level
  const adminSetVolumeAccess = async (userId, accessLevel) => {
    if (!isFounder || !db) return;
    try {
      await setDoc(doc(db, 'users', userId), {
        volumeAccess: accessLevel // 'v1', 'v1v2', 'all'
      }, { merge: true });
      return true;
    } catch (e) {
      console.log('Admin access error:', e);
      return false;
    }
  };
  
  // Admin: Add a new user (pre-register them)
  const adminAddUser = async (email, name, track, accessLevel) => {
    if (!isFounder || !db) return;
    try {
      // Create a user document with their email as ID
      const userId = email.toLowerCase().replace(/[^a-z0-9]/g, '_');
      await setDoc(doc(db, 'users', userId), {
        email: email.toLowerCase(),
        name: name,
        track: track,
        volumeAccess: accessLevel,
        completedLessons: { 1: [], 2: [], 3: [] },
        completionDates: {},
        quizScores: {},
        interactiveScores: {},
        bookmarks: [],
        streak: 0,
        invitedBy: user.email,
        invitedAt: new Date().toISOString(),
        status: 'invited' // Will change to 'active' when they sign in
      });
      return true;
    } catch (e) {
      console.log('Admin add user error:', e);
      return false;
    }
  };
  
  // Admin: Unlock specific batch for user (unlock next N days)
  const adminUnlockNextBatch = async (userId, volume) => {
    if (!isFounder || !db) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) return;
      const userData = userDoc.data();
      
      const currentCompleted = userData.completedLessons?.[volume] || [];
      const userTrack = userData.track || 'standard';
      const batchSize = userTrack === 'fast' ? 2 : 1;
      
      // Find the highest completed day
      const maxCompleted = currentCompleted.length > 0 ? Math.max(...currentCompleted) : 0;
      
      // Unlock next batch
      const daysToUnlock = [];
      for (let i = 1; i <= batchSize; i++) {
        daysToUnlock.push(maxCompleted + i);
      }
      
      return await adminUnlockDays(userId, volume, daysToUnlock);
    } catch (e) {
      console.log('Admin unlock batch error:', e);
      return false;
    }
  };

  // ============================================================

  // ============================================================
  // V3.3: UPDATE CHECKER FUNCTIONS
  // ============================================================
  
  const checkForUpdates = async (silent = false) => {
    if (isCheckingUpdate) return;
    setIsCheckingUpdate(true);
    
    try {
      const response = await fetch('/version.json?' + Date.now(), { cache: 'no-store' });
      if (!response.ok) throw new Error('Version check failed');
      
      const latest = await response.json();
      const currentParts = APP_VERSION.split('.').map(Number);
      const latestParts = latest.version.split('.').map(Number);
      
      let needsUpdate = false;
      for (let i = 0; i < Math.max(currentParts.length, latestParts.length); i++) {
        const current = currentParts[i] || 0;
        const latestNum = latestParts[i] || 0;
        if (latestNum > current) { needsUpdate = true; break; }
        if (current > latestNum) break;
      }
      
      if (needsUpdate) {
        setUpdateAvailable(latest);
        setUpdateDismissed(false);
      } else if (!silent) {
        alert('\u{2705} You\'re on the latest version (v' + APP_VERSION + ')');
      }
    } catch (err) {
      console.log('Update check error:', err);
      if (!silent) alert('\u{274C} Could not check for updates. Try again later.');
    } finally {
      setIsCheckingUpdate(false);
    }
  };
  
  const applyUpdate = async () => {
    try {
      // Mark that we're updating so it doesn't keep popping up
      localStorage.setItem('gmg_updating', 'true');
      
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) { await reg.unregister(); }
      }
      if ('caches' in window) {
        const names = await caches.keys();
        for (const name of names) { await caches.delete(name); }
      }
      // DON'T clear localStorage - it breaks the update flow
      // DON'T clear sessionStorage - it causes the popup to reappear
      window.location.reload(true);
    } catch (err) {
      window.location.reload(true);
    }
  };
  
  const dismissUpdate = () => {
    setUpdateDismissed(true);
    sessionStorage.setItem('updateDismissed', 'true');
  };
  
  // Auto-check for updates on mount (silent)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!sessionStorage.getItem('updateDismissed')) {
        checkForUpdates(true);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // ============================================================

  // V3.7 COMPLETE REWRITE: Simple 2-days-at-a-time unlock
  // Rule: Days 1-2 always unlocked. Complete Day N to unlock Day N+2.
  // Example: Complete Day 1 → Day 3 unlocks. Complete Day 2 → Day 4 unlocks.
  const isDayUnlocked = (volume, day) => {
    // Founder test mode bypasses all locks
    if (testUnlockMode && isFounder) return true;
    
    // Can't access V2 until V1 is 100% complete
    if (volume === 2) {
      const v1Complete = completedLessons[1]?.length >= 30;
      if (!v1Complete) return false;
    }
    // Can't access V3 until V2 is 100% complete
    if (volume === 3) {
      const v2Complete = completedLessons[2]?.length >= 30;
      if (!v2Complete) return false;
    }
    
    const completed = completedLessons[volume] || [];
    
    // Days 1 and 2 are ALWAYS unlocked (your first 2 days)
    if (day <= 2) return true;
    
    // For Day 3+: You need Day N-2 to be completed
    // Day 3 unlocks when Day 1 is done
    // Day 4 unlocks when Day 2 is done
    // Day 5 unlocks when Day 3 is done
    // etc.
    const requiredDay = day - 2;
    return completed.includes(requiredDay);
  };
  
  // V3.4: Check if a volume is accessible (previous volume complete)
  const isVolumeAccessible = (volume) => {
    if (volume === 1) return true;
    if (volume === 2) return completedLessons[1]?.length >= 30;
    if (volume === 3) return completedLessons[2]?.length >= 30;
    return false;
  };
  
  // V3.7: Get hint text for what to complete next
  const getUnlockCountdown = () => {
    // With the new simple logic, there's no time-based waiting
    // Just complete a lesson to unlock the next one
    for (const vol of [1, 2, 3]) {
      const completed = completedLessons[vol] || [];
      if (completed.length === 0) continue;
      if (completed.length >= 30) continue; // Volume complete
      
      // Find first locked day
      for (let day = 3; day <= 30; day++) {
        const requiredDay = day - 2;
        if (!completed.includes(requiredDay)) {
          return `Complete Day ${requiredDay} to unlock Day ${day}`;
        }
      }
    }
    return null;
  };

  // V2.6.5.5: Get time until midnight (for countdown)
  const getTimeUntilMidnight = () => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const diff = midnight - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return { hours, mins, totalMs: diff };
  };

  const completeLesson = async () => {
    if (!selectedLesson) return;
    const updated = { ...completedLessons };
    if (!updated[selectedLesson.volume].includes(selectedLesson.day)) {
      updated[selectedLesson.volume] = [...updated[selectedLesson.volume], selectedLesson.day];
      setCompletedLessons(updated);
      setShowConfetti(true);
      
      // V2.6.5.5: Record completion date
      const dateKey = `${selectedLesson.volume}-${selectedLesson.day}`;
      const newCompletionDates = { ...completionDates, [dateKey]: new Date().toISOString() };
      setCompletionDates(newCompletionDates);
      
      // Update streak
      const newStreak = streak + 1;
      setStreak(newStreak);
      // Update weekly progress
      const newWeeklyProgress = { 
        ...weeklyProgress, 
        lessonsThisWeek: (weeklyProgress.lessonsThisWeek || 0) + 1,
        daysActive: (weeklyProgress.daysActive || 0) + 1
      };
      setWeeklyProgress(newWeeklyProgress);
      // Check time of day for early bird / night owl
      const timeCheck = checkTimeBasedActivity();
      const timeData = {};
      if (timeCheck.earlyBird) timeData.earlyBirdSessions = 1;
      if (timeCheck.nightOwl) timeData.nightOwlSessions = 1;
      
      setSyncStatus('syncing');
      await saveUserData({ completedLessons: updated, streak: newStreak, weeklyProgress: newWeeklyProgress, completionDates: newCompletionDates, learningTrack, ...timeData });
      setSyncStatus('synced');
    }
  };

  // V2.5: Allow users to mark a lesson as uncomplete (with confirmation)
  const uncompleteLesson = async () => {
    if (!selectedLesson) return;
    if (!completedLessons[selectedLesson.volume]?.includes(selectedLesson.day)) return;
    
    if (window.confirm('Mark this lesson as uncomplete?\n\nYour streak and stats won\'t be affected.')) {
      const updated = { ...completedLessons };
      updated[selectedLesson.volume] = updated[selectedLesson.volume].filter(d => d !== selectedLesson.day);
      setCompletedLessons(updated);
      setSyncStatus('syncing');
      await saveUserData({ completedLessons: updated });
      setSyncStatus('synced');
    }
  };

  const answerQuestion = async (answer) => {
    const quiz = getQuiz(selectedLesson.volume, selectedLesson.day);
    const correct = quiz.questions[currentQuestion].a;
    const isCorrect = answer === correct;
    const newAnswers = [...quizAnswers, { selected: answer, correct, isCorrect }];
    setQuizAnswers(newAnswers);
    
    // V3.8: Track wrong answers for spaced repetition
    if (!isCorrect) {
      const questionKey = `${selectedLesson.volume}-${selectedLesson.day}-${currentQuestion}`;
      const existing = spacedRepetition[questionKey] || { wrongCount: 0, interval: 1 };
      const now = new Date();
      const nextReview = new Date(now.getTime() + existing.interval * 24 * 60 * 60 * 1000);
      
      const newSpacedRep = {
        ...spacedRepetition,
        [questionKey]: {
          wrongCount: existing.wrongCount + 1,
          lastSeen: now.toISOString(),
          nextReview: nextReview.toISOString(),
          interval: existing.interval, // Stays same until correct
          question: quiz.questions[currentQuestion].q,
          correctAnswer: correct,
          volume: selectedLesson.volume,
          day: selectedLesson.day,
          questionIndex: currentQuestion
        }
      };
      setSpacedRepetition(newSpacedRep);
      await saveUserData({ spacedRepetition: newSpacedRep });
    } else {
      // V3.8: If correct and was in spaced repetition, increase interval or remove
      const questionKey = `${selectedLesson.volume}-${selectedLesson.day}-${currentQuestion}`;
      if (spacedRepetition[questionKey]) {
        const existing = spacedRepetition[questionKey];
        const newInterval = Math.min(existing.interval * 2, 30); // Double interval, max 30 days
        
        if (newInterval >= 30) {
          // Mastered - remove from spaced repetition
          const { [questionKey]: _, ...rest } = spacedRepetition;
          setSpacedRepetition(rest);
          await saveUserData({ spacedRepetition: rest });
        } else {
          // Increase interval
          const now = new Date();
          const nextReview = new Date(now.getTime() + newInterval * 24 * 60 * 60 * 1000);
          const newSpacedRep = {
            ...spacedRepetition,
            [questionKey]: {
              ...existing,
              lastSeen: now.toISOString(),
              nextReview: nextReview.toISOString(),
              interval: newInterval
            }
          };
          setSpacedRepetition(newSpacedRep);
          await saveUserData({ spacedRepetition: newSpacedRep });
        }
      }
    }
    
    if (currentQuestion < quiz.questions.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 1000);
    } else {
      const score = newAnswers.filter(a => a.isCorrect).length;
      const pct = Math.round((score / quiz.questions.length) * 100);
      const newScores = { ...quizScores, [`${selectedLesson.volume}-${selectedLesson.day}`]: { score, total: quiz.questions.length, pct, date: new Date().toISOString() } };
      setQuizScores(newScores);
      // Track weekly quiz passes (80%+ counts as passed)
      if (pct >= 80) {
        const newWeeklyProgress = { 
          ...weeklyProgress, 
          quizzesPassed: (weeklyProgress.quizzesPassed || 0) + 1 
        };
        setWeeklyProgress(newWeeklyProgress);
        await saveUserData({ quizScores: newScores, weeklyProgress: newWeeklyProgress });
      } else {
        await saveUserData({ quizScores: newScores });
      }
      await completeLesson();
      setTimeout(() => setShowQuizResult(true), 1000);
    }
  };

  // ============================================================
  // V3.8: SPACED REPETITION FUNCTIONS
  // ============================================================
  
  // Get questions due for review
  const getReviewQuestions = () => {
    const now = new Date();
    const dueQuestions = Object.entries(spacedRepetition)
      .filter(([key, data]) => new Date(data.nextReview) <= now)
      .map(([key, data]) => ({ key, ...data }));
    return dueQuestions;
  };
  
  // Start review mode
  const startReviewMode = () => {
    const due = getReviewQuestions();
    if (due.length === 0) {
      alert('No questions due for review! Great job staying on top of your studies.');
      return;
    }
    setReviewQuestions(due);
    setCurrentQuestion(0);
    setQuizAnswers([]);
    setReviewMode(true);
  };
  
  // Answer review question
  const answerReviewQuestion = async (answer) => {
    const current = reviewQuestions[currentQuestion];
    const isCorrect = answer === current.correctAnswer;
    const newAnswers = [...quizAnswers, { selected: answer, correct: current.correctAnswer, isCorrect }];
    setQuizAnswers(newAnswers);
    
    // Update spaced repetition based on answer
    const now = new Date();
    if (isCorrect) {
      const newInterval = Math.min(current.interval * 2, 30);
      if (newInterval >= 30) {
        const { [current.key]: _, ...rest } = spacedRepetition;
        setSpacedRepetition(rest);
        await saveUserData({ spacedRepetition: rest });
      } else {
        const nextReview = new Date(now.getTime() + newInterval * 24 * 60 * 60 * 1000);
        const newSpacedRep = {
          ...spacedRepetition,
          [current.key]: { ...current, interval: newInterval, nextReview: nextReview.toISOString(), lastSeen: now.toISOString() }
        };
        setSpacedRepetition(newSpacedRep);
        await saveUserData({ spacedRepetition: newSpacedRep });
      }
    } else {
      // Reset interval to 1 day
      const nextReview = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      const newSpacedRep = {
        ...spacedRepetition,
        [current.key]: { ...current, interval: 1, nextReview: nextReview.toISOString(), lastSeen: now.toISOString(), wrongCount: current.wrongCount + 1 }
      };
      setSpacedRepetition(newSpacedRep);
      await saveUserData({ spacedRepetition: newSpacedRep });
    }
    
    if (currentQuestion < reviewQuestions.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 1000);
    } else {
      const score = newAnswers.filter(a => a.isCorrect).length;
      alert(`Review complete! You got ${score} of ${reviewQuestions.length} correct.`);
      setReviewMode(false);
      setReviewQuestions([]);
      setQuizAnswers([]);
    }
  };
  
  // ============================================================
  // V3.8: CHAT MEMORY FUNCTIONS
  // ============================================================
  
  // Clear chat history
  const clearChatHistory = () => {
    setMessages([
      { speaker: 'claudette', text: "Chat cleared! I'm Claudette, ready to help.", timestamp: new Date().toISOString() },
      { speaker: 'chanet', text: "Fresh start! I'm Chanet. What would you like to learn about?", timestamp: new Date().toISOString() }
    ]);
  };
  
  // ============================================================
  // V3.8: DISCUSSION THREAD FUNCTIONS
  // ============================================================
  
  // Load discussion comments for a lesson
  const loadDiscussionComments = async (volume, day) => {
    if (!db) return;
    try {
      const lessonId = `${volume}-${day}`;
      const commentsRef = collection(db, 'discussions', lessonId, 'comments');
      const snapshot = await getDocs(commentsRef);
      const comments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDiscussionComments(comments.sort((a, b) => b.timestamp?.toMillis?.() - a.timestamp?.toMillis?.() || 0));
    } catch (e) {
      console.log('Load discussion error:', e);
      setDiscussionComments([]);
    }
  };
  
  // Post a new comment
  const postComment = async () => {
    if (!discussionInput.trim() || !user || !db || !selectedLesson) return;
    try {
      const lessonId = `${selectedLesson.volume}-${selectedLesson.day}`;
      const commentsRef = collection(db, 'discussions', lessonId, 'comments');
      const newComment = {
        authorId: user.uid,
        authorName: user.name || user.email?.split('@')[0] || 'Fellow',
        authorPhoto: user.photo || '',
        text: discussionInput.trim(),
        timestamp: new Date(),
        likes: 0,
        likedBy: []
      };
      await setDoc(doc(commentsRef), newComment);
      setDiscussionInput('');
      await loadDiscussionComments(selectedLesson.volume, selectedLesson.day);
    } catch (e) {
      console.log('Post comment error:', e);
      alert('Could not post comment. Try again.');
    }
  };
  
  // Like a comment
  const likeComment = async (commentId) => {
    if (!user || !db || !selectedLesson) return;
    try {
      const lessonId = `${selectedLesson.volume}-${selectedLesson.day}`;
      const commentRef = doc(db, 'discussions', lessonId, 'comments', commentId);
      const comment = discussionComments.find(c => c.id === commentId);
      if (!comment) return;
      
      const alreadyLiked = comment.likedBy?.includes(user.uid);
      if (alreadyLiked) {
        // Unlike
        await setDoc(commentRef, {
          likes: Math.max(0, (comment.likes || 0) - 1),
          likedBy: comment.likedBy.filter(id => id !== user.uid)
        }, { merge: true });
      } else {
        // Like
        await setDoc(commentRef, {
          likes: (comment.likes || 0) + 1,
          likedBy: [...(comment.likedBy || []), user.uid]
        }, { merge: true });
      }
      await loadDiscussionComments(selectedLesson.volume, selectedLesson.day);
    } catch (e) {
      console.log('Like comment error:', e);
    }
  };
  
  // ============================================================
  // V3.8: CERTIFICATE FUNCTIONS
  // ============================================================
  
  // Generate certificate ID
  const generateCertificateId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let id = 'GMG-';
    id += new Date().getFullYear() + '-';
    for (let i = 0; i < 6; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return id;
  };
  
  // Issue certificate for completed volume
  const issueCertificate = async (volume) => {
    if (!user || !db) return null;
    
    // Check if volume is complete
    const completed = completedLessons[volume] || [];
    const required = volume === 3 ? 15 : 30;
    if (completed.length < required) {
      alert(`Complete all ${required} days in Volume ${volume} to earn your certificate.`);
      return null;
    }
    
    // Check if already issued
    if (certificates[volume]) {
      return certificates[volume];
    }
    
    // Calculate average quiz score for this volume
    const quizDays = volume === 3 ? [5, 10, 15] : [5, 10, 15, 20, 25, 30];
    const scores = quizDays.map(day => quizScores[`${volume}-${day}`]?.pct || 0);
    const avgScore = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    
    const certId = generateCertificateId();
    const certData = {
      certId,
      recipientName: user.name || user.email?.split('@')[0] || 'Fellow',
      recipientEmail: user.email,
      recipientUid: user.uid,
      volumeCompleted: volume,
      volumeTitle: volume === 1 ? 'Fundraising Fundamentals' : volume === 2 ? 'GMG Methodologies' : 'CPP Consultant Model',
      completionDate: new Date().toISOString(),
      quizAverage: avgScore,
      issueDate: new Date().toISOString(),
      verified: true
    };
    
    try {
      // Save to Firebase
      await setDoc(doc(db, 'certificates', certId), certData);
      
      // Update local state
      const newCerts = { ...certificates, [volume]: certData };
      setCertificates(newCerts);
      await saveUserData({ certificates: newCerts });
      
      return certData;
    } catch (e) {
      console.log('Certificate issue error:', e);
      alert('Could not generate certificate. Try again.');
      return null;
    }
  };

  // ============================================================
  // V3.9: PWA INSTALL PROMPT FUNCTIONS
  // ============================================================
  
  // Handle PWA install prompt
  const handleInstallApp = async () => {
    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt();
      const { outcome } = await deferredInstallPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowInstallPrompt(false);
        setDeferredInstallPrompt(null);
      }
    }
  };
  
  // Dismiss install prompt (remember for 7 days)
  const dismissInstallPrompt = () => {
    setShowInstallPrompt(false);
    localStorage.setItem('installDismissed', Date.now().toString());
  };
  
  // Check if install was dismissed recently
  const wasInstallDismissedRecently = () => {
    const dismissed = localStorage.getItem('installDismissed');
    if (!dismissed) return false;
    const dismissedTime = parseInt(dismissed);
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    return Date.now() - dismissedTime < sevenDays;
  };
  
  // ============================================================
  // V3.9: PUSH NOTIFICATION FUNCTIONS
  // ============================================================
  
  // VAPID key for Firebase Cloud Messaging
  const VAPID_KEY = 'BI2B-JUB1Caculb-en-PVK-QgAMpz0SI1DsGaXgtM1rvFlOjGkxypzcySixHx4tzfcgELqnNtEHL4c2mCZivC5c';
  
  // Request push notification permission and get token
  const enablePushNotifications = async () => {
    if (!('Notification' in window)) {
      alert('Push notifications are not supported on this device.');
      return;
    }
    
    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        // Register service worker if not already registered
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
          console.log('Service Worker registered:', registration);
          
          // Get FCM token
          const { getToken } = await import('firebase/messaging');
          const { getMessaging } = await import('firebase/messaging');
          
          try {
            const messaging = getMessaging(app);
            const token = await getToken(messaging, { 
              vapidKey: VAPID_KEY,
              serviceWorkerRegistration: registration 
            });
            
            console.log('Push token:', token);
            setPushToken(token);
            setPushEnabled(true);
            
            // Save token to Firebase for sending notifications later
            if (user && db) {
              await setDoc(doc(db, 'pushTokens', user.uid), {
                token: token,
                oderId: user.uid,
                email: user.email,
                name: user.name || user.email?.split('@')[0] || 'Fellow',
                createdAt: new Date().toISOString(),
                platform: navigator.userAgent.includes('iPhone') ? 'ios' : 'web'
              });
            }
            
            alert('Daily reminders enabled! You will get a notification each day to keep your streak going.');
          } catch (e) {
            console.log('FCM token error:', e);
            alert('Could not enable push notifications. Make sure you have added the app to your home screen.');
          }
        }
      } else {
        alert('Permission denied. You can enable notifications in your browser settings.');
      }
    } catch (error) {
      console.log('Push error:', error);
      alert('Could not enable push notifications. Try again later.');
    }
  };
  
  // ============================================================
  // V3.9: TIERED BANTER FUNCTIONS
  // ============================================================
  
  // Get user tier based on total lessons completed
  const getUserTier = () => {
    const total = (completedLessons[1]?.length || 0) + 
                  (completedLessons[2]?.length || 0) + 
                  (completedLessons[3]?.length || 0);
    if (total >= 30) return 3; // Power user - unlocks special banter
    if (total >= 10) return 2; // Regular user
    return 1; // New user
  };
  
  // Get banter content based on user tier
  const getTieredBanter = () => {
    const tier = getUserTier();
    let banterPool = [...BANTER_CONTENT];
    
    // Tier 3 users get special personality variants
    if (tier >= 3) {
      // 20% chance of flirty Chanet or tired Claudette
      if (Math.random() < 0.2) {
        banterPool = [
          ...banterPool,
          ...FLIRTY_CHANET_BANTER,
          ...TIRED_CLAUDETTE_BANTER
        ];
      }
    }
    
    return banterPool;
  };
  
  // ============================================================
  // V3.9: OFFLINE MODE FUNCTIONS
  // ============================================================
  
  // Cache lessons for offline access
  const downloadVolumeForOffline = async (volume) => {
    if (!('caches' in window)) {
      alert('Offline mode is not supported on this device.');
      return;
    }
    
    try {
      const cache = await caches.open('gmg-lessons-v3.9');
      
      // Get all lesson content for this volume
      const lessons = getLessons(volume);
      const trackConfig = TRACK_CONFIG[userTrack];
      const maxDays = volume === 1 ? trackConfig.v1 : volume === 2 ? trackConfig.v2 : trackConfig.v3;
      
      // Cache each lesson's content
      for (let day = 1; day <= maxDays; day++) {
        const content = getContent(volume, day);
        const lessonData = JSON.stringify({ volume, day, content, title: lessons[day - 1] });
        const response = new Response(lessonData, {
          headers: { 'Content-Type': 'application/json' }
        });
        await cache.put(`/lesson/${volume}/${day}`, response);
      }
      
      // Mark volume as downloaded
      const newOffline = [...offlineVolumes, volume];
      setOfflineVolumes(newOffline);
      localStorage.setItem('offlineVolumes', JSON.stringify(newOffline));
      
      alert(`Volume ${volume} downloaded! You can now access it offline.`);
    } catch (e) {
      console.log('Offline download error:', e);
      alert('Could not download for offline. Try again.');
    }
  };
  
  // Check if a volume is available offline
  const isVolumeOffline = (volume) => {
    return offlineVolumes.includes(volume);
  };
  
  // Load offline volumes from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('offlineVolumes');
    if (saved) {
      try {
        setOfflineVolumes(JSON.parse(saved));
      } catch (e) {
        console.log('Error loading offline volumes:', e);
      }
    }
  }, []);
  
  // ============================================================
  // V3.9: PWA INSTALL PROMPT EFFECT
  // ============================================================
  
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
      // Only show if not already installed and not dismissed recently
      if (!isStandalone() && !wasInstallDismissedRecently()) {
        // Delay showing prompt until user has been on app for 30 seconds
        setTimeout(() => {
          setShowInstallPrompt(true);
        }, 30000);
      }
    };
    
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const navigateLesson = (dir) => {
    if (!selectedLesson) return;
    const trackConfig = TRACK_CONFIG[userTrack];
    const max = selectedLesson.volume === 1 ? trackConfig.v1 : selectedLesson.volume === 2 ? trackConfig.v2 : trackConfig.v3;
    const newDay = selectedLesson.day + dir;
    
    // V3.7: Check if the next day is unlocked
    if (newDay >= 1 && newDay <= max) {
      if (dir > 0 && !isDayUnlocked(selectedLesson.volume, newDay)) {
        // Next day is locked - tell them what to complete
        setSelectedLesson(null);
        setQuizMode(false); 
        setShowQuizResult(false);
        const requiredDay = newDay - 2;
        alert(`Complete Day ${requiredDay} to unlock Day ${newDay}`);
        return;
      }
      setSelectedLesson({ volume: selectedLesson.volume, day: newDay });
      setQuizMode(false); setShowQuizResult(false); setQuizAnswers([]); setCurrentQuestion(0);
      // V3.4: Start lesson chat for new lesson
      if (!isQuizDay(selectedLesson.volume, newDay)) {
        startLessonChat(selectedLesson.volume, newDay);
      }
    }
  };

  // ============================================================
  // V2.2 FEATURE: AI-Powered sendMessage with Timestamps
  // ============================================================
  // UPDATED FROM V2.1: Now includes timestamps on all messages
  // for iMessage-style time display.
  //
  // HOW IT WORKS:
  // 1. User sends message -> immediately show in chat with timestamp
  // 2. Start banter loop (shows fun messages while waiting)
  // 3. Call Claude API in background
  // 4. When API returns, clear banter and show real answer with timestamp
  //
  // DEPENDENCIES: callClaudeAPI, BANTER_CONTENT, chatLoading state
  // ============================================================
  const sendMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userText = chatInput;
    setChatInput('');
    
    // Add user message immediately with timestamp
    setMessages(prev => [...prev, { 
      speaker: 'user', 
      text: userText, 
      timestamp: new Date().toISOString() 
    }]);
    
    // Start loading state
    setChatLoading(true);
    setBanterMessages([]);
    
    // Pick which persona will answer (alternate based on message count)
    const answeringPersona = messages.length % 2 === 0 ? 'claudette' : 'chanet';
    
    // V3.9: Use tiered banter based on user's lesson completion
    const banterPool = getTieredBanter();
    
    // Start banter loop
    let banterIndex = 0;
    banterIntervalRef.current = setInterval(() => {
      const banter = banterPool[banterIndex % banterPool.length];
      setBanterMessages(prev => [...prev.slice(-1), banter]);
      banterIndex++;
    }, 2000);
    
    // Show initial typing indicator
    setBanterMessages([{ speaker: answeringPersona, text: '...', isTyping: true }]);
    
    try {
      // V3.9: Send last 30 messages for conversation memory (increased from 20)
      const response = await callClaudeAPI(userText, answeringPersona, messages.slice(-30));
      
      // Clear banter and show real response with timestamp
      clearInterval(banterIntervalRef.current);
      setBanterMessages([]);
      setMessages(prev => [...prev, { 
        speaker: answeringPersona, 
        text: response,
        timestamp: new Date().toISOString()
      }]);
      
    } catch (error) {
      console.error('Chat error:', error);
      clearInterval(banterIntervalRef.current);
      setBanterMessages([]);
      setMessages(prev => [...prev, { 
        speaker: answeringPersona, 
        text: answeringPersona === 'claudette' 
          ? "I'm having trouble connecting right now. Check the lesson content, or try again in a moment."
          : "Oops, something went sideways! Give it another shot, or the lesson might have your answer.",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setChatLoading(false);
    }
  };
  
  // Cleanup banter interval on unmount
  useEffect(() => {
    return () => {
      if (banterIntervalRef.current) {
        clearInterval(banterIntervalRef.current);
      }
    };
  }, []);

  // ============================================================
  // V2.4 FEATURE: Achievement Badge Checker
  // ============================================================
  // Checks all badges against user data and returns newly earned ones.
  // Called after completing lessons, quizzes, or practice scenarios.
  // ============================================================
  const checkForNewBadges = (userData) => {
    const newlyEarned = [];
    
    ACHIEVEMENT_BADGES.forEach(badge => {
      const alreadyEarned = earnedBadges.includes(badge.id);
      const nowEarned = badge.condition(userData);
      
      if (nowEarned && !alreadyEarned) {
        newlyEarned.push(badge);
      }
    });
    
    if (newlyEarned.length > 0) {
      const allEarned = [...earnedBadges, ...newlyEarned.map(b => b.id)];
      setEarnedBadges(allEarned);
      saveUserData({ earnedBadges: allEarned });
      
      // Show celebration modal for first new badge
      setShowBadgeModal(newlyEarned[0]);
    }
    
    return newlyEarned;
  };

  // Calculate XP from various activities
  const calculateTotalXP = (data) => {
    let xp = 0;
    xp += (data.totalLessons || 0) * 10; // 10 XP per lesson
    
    Object.values(data.quizScores || {}).forEach(score => {
      xp += Math.round(score.pct / 2); // Up to 50 XP per quiz
    });
    
    xp += (data.practiceCompleted || 0) * 25; // 25 XP per practice
    xp += (data.streak || 0) * 5; // 5 XP per streak day
    
    // Badge bonuses
    (data.earnedBadges || []).forEach(badgeId => {
      const badge = ACHIEVEMENT_BADGES.find(b => b.id === badgeId);
      if (badge) {
        const bonus = badge.rarity === 'legendary' ? 500 : badge.rarity === 'rare' ? 200 : badge.rarity === 'uncommon' ? 100 : 50;
        xp += bonus;
      }
    });
    
    return xp;
  };

  // Get weekly challenge progress
  const getWeeklyChallengeProgress = (challengeId) => {
    const challenge = WEEKLY_CHALLENGES.find(c => c.id === challengeId);
    if (!challenge) return 0;
    
    const current = weeklyProgress[challenge.metric] || 0;
    return Math.min(current / challenge.target, 1);
  };

  // Update leaderboard from Firebase
  const refreshLeaderboard = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = [];
      
      usersSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.name && data.totalLessons > 0) {
          users.push({
            id: doc.id,
            name: data.name,
            photo: data.photo,
            totalLessons: data.completedLessons ? 
              (data.completedLessons[1]?.length || 0) + 
              (data.completedLessons[2]?.length || 0) + 
              (data.completedLessons[3]?.length || 0) : 0,
            streak: data.streak || 0,
            xp: calculateTotalXP({
              totalLessons: data.completedLessons ? 
                (data.completedLessons[1]?.length || 0) + 
                (data.completedLessons[2]?.length || 0) + 
                (data.completedLessons[3]?.length || 0) : 0,
              quizScores: data.quizScores || {},
              practiceCompleted: data.practiceCompleted || 0,
              streak: data.streak || 0,
              earnedBadges: data.earnedBadges || []
            })
          });
        }
      });
      
      // Sort by XP descending
      users.sort((a, b) => b.xp - a.xp);
      setLeaderboardData(users.slice(0, 20)); // Top 20
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    }
  };

  // Check time of day for early bird / night owl badges
  const checkTimeBasedActivity = () => {
    const hour = new Date().getHours();
    if (hour < 7) {
      return { earlyBird: true, nightOwl: false };
    } else if (hour >= 22) {
      return { earlyBird: false, nightOwl: true };
    }
    return { earlyBird: false, nightOwl: false };
  };

  // ============================================================
  // V2.3 FEATURE: Start Practice Scenario
  // ============================================================
  // Opens a roleplay session where the AI plays a specific role
  // (donor, board member, program officer) and the user practices
  // their fundraising skills in a safe environment.
  // ============================================================
  const startPracticeScenario = (scenario) => {
    setActiveScenario(scenario);
    setPracticeMode(true);
    setPracticeMessages([
      {
        speaker: 'system',
        text: `SCENARIO: ${scenario.title}\n\n${scenario.setup}`,
        timestamp: new Date().toISOString()
      },
      {
        speaker: scenario.persona,
        text: scenario.openingLine,
        timestamp: new Date().toISOString(),
        isRoleplay: true,
        role: scenario.personaRole
      }
    ]);
  };

  // Send message in practice mode (uses different system prompt)
  const sendPracticeMessage = async () => {
    if (!chatInput.trim() || practiceLoading || !activeScenario) return;
    
    const userText = chatInput;
    setChatInput('');
    
    setPracticeMessages(prev => [...prev, {
      speaker: 'user',
      text: userText,
      timestamp: new Date().toISOString()
    }]);
    
    setPracticeLoading(true);
    
    const scenarioPrompt = `You are playing a CHARACTER in a fundraising roleplay training exercise.

CHARACTER: ${activeScenario.personaRole}
SCENARIO: ${activeScenario.setup}

CRITICAL RULES:
1. STAY IN CHARACTER as ${activeScenario.personaRole}. Do not break character.
2. Respond naturally as this person would respond.
3. If appropriate for the character, ask follow-up questions.
4. Be realistic. Don't make it too easy or too hard.
5. After 4-5 exchanges, you can wrap up the conversation naturally.
6. At the end, briefly step out of character to give 2-3 specific pieces of feedback on what they did well and what they could improve.

SUCCESS CRITERIA the user should aim for:
${activeScenario.successCriteria.map((c, i) => `${i + 1}. ${c}`).join('\n')}

Remember: You are ${activeScenario.personaRole}, not an AI assistant. Stay in character.`;

    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ANTHROPIC_API_KEY,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true'
        },
        body: JSON.stringify({
          model: CLAUDE_MODEL,
          max_tokens: 1024,
          system: scenarioPrompt,
          messages: practiceMessages
            .filter(m => m.speaker !== 'system')
            .map(msg => ({
              role: msg.speaker === 'user' ? 'user' : 'assistant',
              content: msg.text
            }))
            .concat([{ role: 'user', content: userText }])
        })
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const data = await response.json();
      setPracticeMessages(prev => [...prev, {
        speaker: activeScenario.persona,
        text: data.content[0].text,
        timestamp: new Date().toISOString(),
        isRoleplay: true,
        role: activeScenario.personaRole
      }]);
    } catch (error) {
      console.error('Practice error:', error);
      setPracticeMessages(prev => [...prev, {
        speaker: activeScenario.persona,
        text: "Let me pause here. Having some technical difficulties. Try again?",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setPracticeLoading(false);
    }
  };

  const endPracticeSession = () => {
    // Count as complete if user had at least 4 exchanges (user messages)
    const userMessageCount = practiceMessages.filter(m => m.speaker === 'user').length;
    if (userMessageCount >= 3 && activeScenario) {
      const newCount = practiceCompleted + 1;
      setPracticeCompleted(newCount);
      // Track weekly practice
      const newWeeklyProgress = { 
        ...weeklyProgress, 
        practiceThisWeek: (weeklyProgress.practiceThisWeek || 0) + 1 
      };
      setWeeklyProgress(newWeeklyProgress);
      saveUserData({ practiceCompleted: newCount, weeklyProgress: newWeeklyProgress });
    }
    setPracticeMode(false);
    setActiveScenario(null);
    setPracticeMessages([]);
  };

  // ============================================================
  // V2.3 FEATURE: Audio Narration (Text-to-Speech)
  // ============================================================
  // Uses Web Speech API to read lesson content aloud.
  // Accessibility feature plus convenience for busy learners.
  // ============================================================
  const speakText = (text) => {
    if (!('speechSynthesis' in window)) {
      alert('Text-to-speech is not supported in your browser.');
      return;
    }

    // Stop any current speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    
    // Try to get a nice voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v => v.name.includes('Samantha') || v.name.includes('Google US English'));
    if (preferredVoice) utterance.voice = preferredVoice;

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    speechSynthRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  // ============================================================
  // V2.6.5 FEATURE: iMessage Lesson Interface
  // ============================================================
  // Transforms static lesson content into conversational learning.
  // Claudette or Chanet guides users through each lesson in chat format.
  // Content is delivered in chunks with typing indicators.
  // Users can ask questions anytime (fires knowledge + Claude API).
  // ============================================================
  
  // Pick a persona for the lesson (alternates or random)
  const getLessonPersona = (volume, day) => {
    // Alternate based on day number, or could be random
    const seed = volume * 100 + day;
    return seed % 2 === 0 ? 'claudette' : 'chanet';
  };

  // Start a lesson in chat mode
  const startLessonChat = (volume, day) => {
    const persona = getLessonPersona(volume, day);
    const content = getContent(volume, day);
    const userName = user?.name?.split(' ')[0] || 'there';
    
    // V2.9.0: Clear delivered sections tracker for new lesson
    deliveredSectionsRef.current = new Set();
    
    setCurrentLessonPersona(persona);
    setLessonSectionIndex(0);
    setReadAloudMode(false);
    setShowActivityInChat(false); // Reset activity state
    setActivityMode(false); // V2.6.5.3: Reset activity Q&A mode
    setActivityQuestions([]); // V2.6.5.3: Clear activity questions
    setCurrentActivityQuestion(0); // V2.6.5.3: Reset question index
    setLessonChatInput(''); // V2.6.5.3: Clear chat input
    
    // Initial greeting
    const greeting = persona === 'claudette'
      ? `Hey ${userName}! Welcome to Day ${day}. Today we're diving into "${content.title}" and I'm excited to walk you through it.`
      : `What's up ${userName}! Day ${day} is all about "${content.title}" and trust me, this is going to be good.`;
    
    setLessonMessages([
      {
        speaker: persona,
        text: greeting,
        timestamp: new Date().toISOString()
      }
    ]);
    
    // Ask if ready after a brief delay
    setTimeout(() => {
      const readyPrompt = persona === 'claudette'
        ? "Ready to get started?"
        : "You ready to jump in?";
      
      setLessonMessages(prev => [...prev, {
        speaker: persona,
        text: readyPrompt,
        timestamp: new Date().toISOString()
      }]);
      setAwaitingLessonChoice('start');
    }, 1500);
    
    setLessonChatMode(true);
  };

  // Deliver a section of the lesson with typing delay
  const deliverLessonSection = async (sectionIndex, readAloud = false) => {
    const content = getContent(selectedLesson.volume, selectedLesson.day);
    const sections = content.sections || [];
    
    // V2.9.0: Prevent duplicate delivery of the same section
    const sectionKey = `${selectedLesson.volume}-${selectedLesson.day}-${sectionIndex}`;
    if (deliveredSectionsRef.current.has(sectionKey)) {
      console.log('V2.9.0: Skipping duplicate section:', sectionKey);
      return;
    }
    
    // V2.9.0: Prevent concurrent delivery
    if (isDeliveringRef.current) {
      console.log('V2.9.0: Already delivering, skipping:', sectionKey);
      return;
    }
    isDeliveringRef.current = true;
    deliveredSectionsRef.current.add(sectionKey);
    
    if (sectionIndex >= sections.length) {
      isDeliveringRef.current = false;
      // All sections delivered, ask about activity/quiz
      setLessonChatLoading(true);
      await new Promise(r => setTimeout(r, 1000));
      setLessonChatLoading(false);
      
      const isQuiz = isQuizDay(selectedLesson.volume, selectedLesson.day);
      const finishMsg = currentLessonPersona === 'claudette'
        ? isQuiz 
          ? "That's the lesson content! Now let's test what you learned with a quick quiz. Ready?"
          : "And that wraps up today's lesson! Ready to mark it complete?"
        : isQuiz
          ? "Alright, that's the good stuff! Time for a quick quiz to lock it in. You got this?"
          : "Boom, lesson done! Want to mark this one complete?";
      
      setLessonMessages(prev => [...prev, {
        speaker: currentLessonPersona,
        text: finishMsg,
        timestamp: new Date().toISOString()
      }]);
      setAwaitingLessonChoice('activity');
      return;
    }
    
    const section = sections[sectionIndex];
    setLessonChatLoading(true);
    
    // Typing delay (longer for more content)
    const delay = Math.min(2000, 800 + (section.c?.length || 0) / 10);
    await new Promise(r => setTimeout(r, delay));
    
    setLessonChatLoading(false);
    
    // Build the message with header and content
    const sectionText = section.h 
      ? `**${section.h}**\n\n${section.c || ''}`
      : section.c || '';
    
    // V2.9.0: Add unique message ID to prevent React duplicates
    const messageId = `section-${sectionKey}-${Date.now()}`;
    
    setLessonMessages(prev => [...prev, {
      id: messageId,
      speaker: currentLessonPersona,
      text: sectionText,
      timestamp: new Date().toISOString(),
      isContent: true
    }]);
    
    // V2.9.0: Release the delivery lock
    isDeliveringRef.current = false;
    
    // Read aloud if requested
    if (readAloud && section.c) {
      const textToRead = (section.h ? section.h + '. ' : '') + section.c.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ');
      speakText(textToRead);
    }
    
    setLessonSectionIndex(sectionIndex + 1);
    
    // Check if we're halfway through
    const totalSections = sections.length;
    const halfwayPoint = Math.floor(totalSections / 2);
    
    if (sectionIndex === halfwayPoint && totalSections > 2) {
      // V2.9.0: Prevent duplicate halfway message
      const halfwayKey = `halfway-${selectedLesson.volume}-${selectedLesson.day}`;
      if (deliveredSectionsRef.current.has(halfwayKey)) {
        // Already shown halfway, continue to next section
        setTimeout(() => {
          deliverLessonSection(sectionIndex + 1, readAloud);
        }, readAloud ? 500 : 2500);
        return;
      }
      deliveredSectionsRef.current.add(halfwayKey);
      
      setTimeout(async () => {
        setLessonChatLoading(true);
        await new Promise(r => setTimeout(r, 800));
        setLessonChatLoading(false);
        
        const midCheck = currentLessonPersona === 'claudette'
          ? "We're about halfway through. Any questions so far, or should we keep going?"
          : "Halfway there! You following along okay? Ask me anything or let's keep rolling.";
        
        setLessonMessages(prev => [...prev, {
          id: `halfway-${Date.now()}`,
          speaker: currentLessonPersona,
          text: midCheck,
          timestamp: new Date().toISOString()
        }]);
        setAwaitingLessonChoice('continue');
      }, 2000);
    } else if (sectionIndex < sections.length - 1) {
      // Auto-continue to next section after a pause
      setTimeout(() => {
        deliverLessonSection(sectionIndex + 1, readAloud);
      }, readAloud ? 500 : 2500);
    } else {
      // This is the LAST section - show completion options
      setTimeout(async () => {
        setLessonChatLoading(true);
        await new Promise(r => setTimeout(r, 1000));
        setLessonChatLoading(false);
        
        const isQuiz = isQuizDay(selectedLesson.volume, selectedLesson.day);
        const finishMsg = currentLessonPersona === 'claudette'
          ? isQuiz 
            ? "That's the lesson content! Now let's test what you learned with a quick quiz. Ready?"
            : "And that wraps up today's lesson! Ready to mark it complete?"
          : isQuiz
            ? "Alright, that's the good stuff! Time for a quick quiz to lock it in. You got this?"
            : "Boom, lesson done! Want to mark this one complete?";
        
        setLessonMessages(prev => [...prev, {
          speaker: currentLessonPersona,
          text: finishMsg,
          timestamp: new Date().toISOString()
        }]);
        setAwaitingLessonChoice('activity');
      }, 2500);
    }
  };

  // Handle user's choice button clicks
  const handleLessonChoice = (choice) => {
    // Add user's choice as a message
    const choiceText = choice === 'yes' ? "Yes, let's go!" 
      : choice === 'read' ? "Yes, but read it to me"
      : choice === 'continue' ? "Keep going!"
      : choice === 'quiz' ? "I'm ready for the quiz!"
      : choice === 'complete' ? "Mark it complete!"
      : choice === 'doActivity' ? "Let's do the activity!"
      : "Actually, let me come back later";
    
    setLessonMessages(prev => [...prev, {
      speaker: 'user',
      text: choiceText,
      timestamp: new Date().toISOString()
    }]);
    
    setAwaitingLessonChoice(null);
    
    if (choice === 'no' || choice === 'later') {
      // Exit lesson chat
      setTimeout(() => {
        setLessonChatMode(false);
        setLessonMessages([]);
        setSelectedLesson(null);
        setCurrentView('home');
        setShowActivityInChat(false);
      }, 500);
      return;
    }
    
    if (choice === 'read') {
      setReadAloudMode(true);
    }
    
    if (choice === 'yes' || choice === 'read' || choice === 'continue') {
      // Start or continue delivering content
      setTimeout(() => {
        deliverLessonSection(lessonSectionIndex, choice === 'read' || readAloudMode);
      }, 800);
    }
    
    if (choice === 'quiz') {
      // Start the quiz
      setTimeout(() => {
        setLessonChatMode(false);
        setQuizMode(true);
      }, 500);
    }
    
    if (choice === 'doActivity') {
      // V2.6.5.3: Convert activity to conversational Q&A format
      const content = getContent(selectedLesson.volume, selectedLesson.day);
      const interactive = content.interactive;
      
      let questions = [];
      
      if (interactive.type === 'matching') {
        // Each pair becomes a question
        const shuffledPairs = [...interactive.pairs].sort(() => Math.random() - 0.5);
        shuffledPairs.forEach((pair, idx) => {
          const wrongAnswers = interactive.pairs
            .filter(p => p.left !== pair.left)
            .map(p => p.right)
            .sort(() => Math.random() - 0.5)
            .slice(0, 3);
          
          const allOptions = [pair.right, ...wrongAnswers].sort(() => Math.random() - 0.5);
          const correctIndex = allOptions.indexOf(pair.right);
          const letters = ['A', 'B', 'C', 'D'];
          
          questions.push({
            question: `What matches with "${pair.left}"?`,
            options: allOptions.map((opt, i) => ({ letter: letters[i], text: opt })),
            correctLetter: letters[correctIndex],
            correctAnswer: pair.right
          });
        });
      } else if (interactive.type === 'slider') {
        // Create multiple choice around the answer
        const answer = interactive.answer;
        const wrongOptions = [
          Math.max(0, answer - 25),
          Math.max(0, answer - 15),
          answer + 15,
          answer + 25
        ].filter((v, i, a) => a.indexOf(v) === i && v !== answer).sort(() => Math.random() - 0.5).slice(0, 3);
        
        const allOptions = [answer, ...wrongOptions].sort(() => Math.random() - 0.5);
        const correctIndex = allOptions.indexOf(answer);
        const letters = ['A', 'B', 'C', 'D'];
        
        questions.push({
          question: interactive.title,
          options: allOptions.map((opt, i) => ({ letter: letters[i], text: `${opt}%` })),
          correctLetter: letters[correctIndex],
          correctAnswer: `${answer}%`
        });
      } else if (interactive.type === 'sorting') {
        // V2.4 FIX: Only ask first 4 positions max to avoid confusing questions
        // For 6-item lists, asking "What comes FIFTH?" with random options is nonsensical
        const titleLower = interactive.title.toLowerCase();
        const isRanking = titleLower.includes('rank') || titleLower.includes('most') || titleLower.includes('least');
        const isOrdering = titleLower.includes('order') || titleLower.includes('put');
        
        // Only create questions for first 4 items max
        const maxQuestions = Math.min(4, interactive.items.length);
        
        for (let position = 0; position < maxQuestions; position++) {
          const correctItem = interactive.items[position];
          const positionWords = ['FIRST', 'SECOND', 'THIRD', 'FOURTH'];
          const otherItems = interactive.items.filter(item => item !== correctItem);
          const wrongOptions = otherItems.sort(() => Math.random() - 0.5).slice(0, 3);
          
          const allOptions = [correctItem, ...wrongOptions].sort(() => Math.random() - 0.5);
          const correctIndex = allOptions.indexOf(correctItem);
          const letters = ['A', 'B', 'C', 'D'];
          
          // Build contextual question based on activity title
          let questionText;
          if (isRanking) {
            const descriptor = position === 0 ? 'MOST common' : position === maxQuestions - 1 ? 'LEAST common' : `#${position + 1} most common`;
            questionText = `${interactive.title}\n\nWhich is ${descriptor}?`;
          } else if (isOrdering) {
            questionText = `${interactive.title}\n\nWhat comes ${positionWords[position] || '#' + (position + 1)}?`;
          } else {
            questionText = `${interactive.title}\n\nWhat goes in position ${position + 1}?`;
          }
          
          questions.push({
            question: questionText,
            options: allOptions.map((opt, i) => ({ letter: letters[i], text: opt })),
            correctLetter: letters[correctIndex],
            correctAnswer: correctItem
          });
        }
      }
      
      setActivityQuestions(questions);
      setCurrentActivityQuestion(0);
      setActivityMode(true);
      
      // Present first question after intro message
      setTimeout(() => {
        const activityMsg = currentLessonPersona === 'claudette'
          ? "Perfect! Let me ask you a few questions to reinforce what you learned."
          : "Alright! Let's lock this in with some quick questions.";
        
        setLessonMessages(prev => [...prev, {
          speaker: currentLessonPersona,
          text: activityMsg,
          timestamp: new Date().toISOString()
        }]);
        
        // Present first question after brief pause
        setTimeout(() => {
          if (questions.length > 0) {
            const q = questions[0];
            const questionText = `${q.question}\n\nA) ${q.options[0]?.text || ''}\nB) ${q.options[1]?.text || ''}\nC) ${q.options[2]?.text || ''}\nD) ${q.options[3]?.text || ''}`;
            
            setLessonMessages(prev => [...prev, {
              speaker: currentLessonPersona,
              text: questionText,
              timestamp: new Date().toISOString(),
              isQuestion: true
            }]);
          }
        }, 1500);
      }, 500);
    }
    
    if (choice === 'complete') {
      // Mark lesson complete - triggers first confetti
      completeLesson();
      setShowActivityInChat(false);
      setTimeout(() => {
        const doneMsg = currentLessonPersona === 'claudette'
          ? "Awesome, you're all set! \u{1F389} Great work today."
          : "Done and done! \u{1F389} You crushed it.";
        
        setLessonMessages(prev => [...prev, {
          speaker: currentLessonPersona,
          text: doneMsg,
          timestamp: new Date().toISOString()
        }]);
        
        // Exit after celebration - trigger second confetti on home return
        setTimeout(() => {
          setShowConfetti(true); // V2.8.0: Second confetti burst on home return
          setLessonChatMode(false);
          setLessonMessages([]);
          setSelectedLesson(null);
          setCurrentView('home');
        }, 2500);
      }, 500);
    }
  };

  // Handle activity answer from user
  const handleActivityAnswer = (userAnswer) => {
    const answer = userAnswer.toUpperCase().trim();
    const currentQ = activityQuestions[currentActivityQuestion];
    
    // Add user's answer to chat
    setLessonMessages(prev => [...prev, {
      speaker: 'user',
      text: userAnswer,
      timestamp: new Date().toISOString()
    }]);
    
    // Check if answer is correct (accept just letter or full text)
    const isCorrect = answer === currentQ.correctLetter || 
                      answer.startsWith(currentQ.correctLetter + ')') ||
                      answer.startsWith(currentQ.correctLetter + ' ');
    
    setLessonChatLoading(true);
    
    setTimeout(() => {
      setLessonChatLoading(false);
      
      if (isCorrect) {
        const nextQuestion = currentActivityQuestion + 1;
        
        if (nextQuestion >= activityQuestions.length) {
          // All questions answered correctly!
          // V2.6.5.5: Build enhanced recap showing each question with all options and correct answer highlighted
          const recapLines = activityQuestions.map((q, idx) => {
            const questionTitle = q.question.split('\n')[0]; // Get just the title
            const optionsList = q.options.map(opt => 
              opt.letter === q.correctLetter 
                ? `   \u2713 ${opt.letter}) ${opt.text}` 
                : `      ${opt.letter}) ${opt.text}`
            ).join('\n');
            return `**Q${idx + 1}: ${questionTitle}**\n${optionsList}`;
          }).join('\n\n');
          
          const successMsg = currentLessonPersona === 'claudette'
            ? "That's correct! You've completed the activity. Great job!"
            : "Yes! You nailed it! Activity complete!";
          
          setLessonMessages(prev => [...prev, {
            speaker: currentLessonPersona,
            text: successMsg,
            timestamp: new Date().toISOString()
          }]);
          
          // Show recap after brief pause
          setTimeout(() => {
            const recapHeader = currentLessonPersona === 'claudette'
              ? "Here's a quick recap of what you learned:"
              : "Here's what you got right:";
            const recapMsg = `**Activity Recap**\n${recapHeader}\n\n${recapLines}`;
            setLessonMessages(prev => [...prev, {
              speaker: currentLessonPersona,
              text: recapMsg,
              timestamp: new Date().toISOString(),
              isRecap: true
            }]);
            
            // Show mark complete option after recap
            setAwaitingLessonChoice('afterActivity');
          }, 1500);
          
          setActivityMode(false);
          
          // Record activity completion
          const key = `${selectedLesson.volume}-${selectedLesson.day}`;
          setInteractiveScores(prev => ({ ...prev, [key]: true }));
          
          // Clear questions AFTER recap is built
          setTimeout(() => {
            setActivityQuestions([]);
            setCurrentActivityQuestion(0);
          }, 2000);
        } else {
          // Move to next question
          const correctMsg = currentLessonPersona === 'claudette'
            ? "That's right! \u2713 Next question..."
            : "Yes! \u2713 Here's the next one...";
          
          setLessonMessages(prev => [...prev, {
            speaker: currentLessonPersona,
            text: correctMsg,
            timestamp: new Date().toISOString()
          }]);
          
          setCurrentActivityQuestion(nextQuestion);
          
          // Present next question after brief pause
          setTimeout(() => {
            const q = activityQuestions[nextQuestion];
            const questionText = `${q.question}\n\nA) ${q.options[0]?.text || ''}\nB) ${q.options[1]?.text || ''}\nC) ${q.options[2]?.text || ''}\nD) ${q.options[3]?.text || ''}`;
            
            setLessonMessages(prev => [...prev, {
              speaker: currentLessonPersona,
              text: questionText,
              timestamp: new Date().toISOString(),
              isQuestion: true
            }]);
          }, 1000);
        }
      } else {
        // Wrong answer
        const wrongMsg = currentLessonPersona === 'claudette'
          ? "Not quite. Try again! Pick A, B, C, or D."
          : "Nope! Give it another shot. A, B, C, or D?";
        
        setLessonMessages(prev => [...prev, {
          speaker: currentLessonPersona,
          text: wrongMsg,
          timestamp: new Date().toISOString()
        }]);
      }
    }, 800);
  };

  // Handle user typing a question mid-lesson
  const sendLessonChatMessage = async () => {
    if (!lessonChatInput.trim() || lessonChatLoading) return;
    
    const userInput = lessonChatInput;
    setLessonChatInput('');
    
    // If in activity mode, handle as answer
    if (activityMode) {
      handleActivityAnswer(userInput);
      return;
    }
    
    // Add user message
    setLessonMessages(prev => [...prev, {
      speaker: 'user',
      text: userInput,
      timestamp: new Date().toISOString()
    }]);
    
    setLessonChatLoading(true);
    
    // Build context for Claude
    const content = getContent(selectedLesson.volume, selectedLesson.day);
    const lessonContext = content.sections?.map(s => `${s.h || ''}: ${s.c || ''}`).join('\n\n') || '';
    
    const systemPrompt = `You are ${currentLessonPersona === 'claudette' ? 'Claudette' : 'Chanet'}, a friendly and knowledgeable fundraising tutor at GMG University.

The student is currently learning Day ${selectedLesson.day}: "${content.title}"

LESSON CONTENT FOR REFERENCE:
${lessonContext}

KEY TAKEAWAYS:
${content.keyTakeaways?.join('\n') || 'N/A'}

GMG KNOWLEDGE BASE:
${GMG_KNOWLEDGE}

STYLE GUIDE:
- Be warm, encouraging, and conversational
- Keep answers concise (2-4 sentences typically)
- Reference the lesson content when relevant
- If the question is off-topic, gently redirect to the lesson
- ${currentLessonPersona === 'claudette' ? 'Claudette is professional but warm' : 'Chanet is more casual and energetic'}
- Never use em dashes, bullet points, or formal language
- Speak naturally like a friendly mentor`;

    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ANTHROPIC_API_KEY,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true'
        },
        body: JSON.stringify({
          model: CLAUDE_MODEL,
          max_tokens: 512,
          system: systemPrompt,
          messages: [{ role: 'user', content: userInput }]
        })
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const data = await response.json();
      setLessonMessages(prev => [...prev, {
        speaker: currentLessonPersona,
        text: data.content[0].text,
        timestamp: new Date().toISOString()
      }]);
      
      // After answering, prompt to continue
      setTimeout(() => {
        const continuePrompt = currentLessonPersona === 'claudette'
          ? "Ready to continue with the lesson?"
          : "Want to keep going?";
        
        setLessonMessages(prev => [...prev, {
          speaker: currentLessonPersona,
          text: continuePrompt,
          timestamp: new Date().toISOString()
        }]);
        setAwaitingLessonChoice('continue');
      }, 1500);
      
    } catch (error) {
      console.error('Lesson chat error:', error);
      setLessonMessages(prev => [...prev, {
        speaker: currentLessonPersona,
        text: "Hmm, I'm having trouble connecting right now. Try asking again, or we can continue with the lesson.",
        timestamp: new Date().toISOString()
      }]);
      setAwaitingLessonChoice('continue');
    } finally {
      setLessonChatLoading(false);
    }
  };

  // ============================================================
  // V2.2 EFFECT: Smooth scroll to bottom on new messages
  // ============================================================
  // Scrolls chat to bottom whenever messages change or chat opens.
  // Uses smooth behavior for better UX.
  // ============================================================
  useEffect(() => {
    if (chatOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, chatOpen, banterMessages]);

  // V2.10: Auto-scroll lesson chat when messages change
  useEffect(() => {
    if (lessonChatMode && lessonMessagesEndRef.current) {
      lessonMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [lessonMessages, lessonChatMode, lessonChatLoading]);

  // ============================================================
  // V2.4 EFFECT: Calculate XP and Refresh Leaderboard
  // ============================================================
  useEffect(() => {
    if (user) {
      // Calculate totalLessons here since it's not available as state
      const total = completedLessons[1].length + completedLessons[2].length + completedLessons[3].length;
      
      // Calculate XP
      const xp = calculateTotalXP({
        totalLessons: total,
        quizScores,
        practiceCompleted,
        streak,
        earnedBadges
      });
      setTotalXP(xp);
      
      // Refresh leaderboard
      refreshLeaderboard();
      
      // Check for new badges
      checkForNewBadges({
        totalLessons: total,
        quizScores,
        practiceCompleted,
        streak,
        bookmarks,
        completedVolumes: [
          completedLessons[1]?.length >= 30 ? 1 : null,
          completedLessons[2]?.length >= 30 ? 2 : null,
          completedLessons[3]?.length >= 15 ? 3 : null
        ].filter(Boolean)
      });
    }
  }, [user, completedLessons, streak]);

  const trackConfig = TRACK_CONFIG[userTrack];
  const totalLessons = completedLessons[1].length + completedLessons[2].length + completedLessons[3].length;
  const progressPct = Math.round((totalLessons / trackConfig.total) * 100);

  if (authLoading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: GMG.pearlWhite }}><p style={{ color: GMG.charcoal }}>Loading...</p></div>;


  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', transition: 'background 0.3s ease' }}>
      {/* Animated Background - Different for dark/light mode */}
      <div style={{ position: 'fixed', inset: 0, background: darkMode 
        ? `linear-gradient(145deg, #0f1729 0%, #1B2A4E 30%, #2D4373 60%, #1B2A4E 100%)`
        : `linear-gradient(145deg, ${GMG.pearlWhite} 0%, ${GMG.pearl} 15%, ${GMG.silverLight}40 30%, ${GMG.roseGoldLight}25 50%, ${GMG.platinum} 70%, ${GMG.pearlWhite} 100%)`, 
        zIndex: 0, transition: 'background 0.3s ease' }}>
        <div style={{ position: 'absolute', width: '400px', height: '400px', borderRadius: '50%', background: `radial-gradient(circle, ${darkMode ? GMG.bronze : GMG.roseGold}25 0%, transparent 70%)`, top: '-150px', right: '-100px', animation: 'float1 8s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', width: '300px', height: '300px', borderRadius: '50%', background: `radial-gradient(circle, ${darkMode ? GMG.navyLight : GMG.silver}30 0%, transparent 70%)`, bottom: '50px', left: '-80px', animation: 'float2 10s ease-in-out infinite' }} />
      </div>
      <style>{`@keyframes float1 { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-30px) scale(1.05); } } @keyframes float2 { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(25px); } }`}</style>

      {/* V2.11: GLOBAL CONFETTI - renders on ANY view */}
      <Confetti active={showConfetti} onComplete={() => setShowConfetti(false)} />

      <main style={{ position: 'relative', zIndex: 1, padding: '20px 18px', paddingBottom: '100px', maxWidth: '500px', margin: '0 auto' }}>
        
        {/* V3.2: ADMIN PORTAL - Comprehensive admin dashboard for founders */}
        {founderView && isFounder && (
          <div>
            {/* Admin Header with Back Button */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <button onClick={() => { setFounderView(false); setAdminMode('list'); setSelectedAdminUser(null); }} style={{ background: 'none', border: 'none', fontSize: '15px', color: theme.accent, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                {'\u{2190}'} Back to App
              </button>
              <span style={{ fontSize: '12px', color: theme.textMuted }}>V3.2</span>
            </div>
            
            <h2 style={{ fontSize: '26px', fontWeight: '700', color: theme.text, marginBottom: '6px' }}>{'\u{1F451}'} Admin Portal</h2>
            <p style={{ color: theme.textMuted, marginBottom: '24px', fontSize: '14px' }}>Manage users, unlock content, track progress, and add new fellows.</p>
            
            {/* Admin Mode Tabs */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: `2px solid ${theme.border}`, paddingBottom: '12px' }}>
              {[
                { id: 'list', label: 'All Users', icon: '\u{1F465}' },
                { id: 'add', label: 'Add User', icon: '\u{2795}' },
                { id: 'stats', label: 'Analytics', icon: '\u{1F4CA}' }
              ].map(tab => (
                <button 
                  key={tab.id}
                  onClick={() => { setAdminMode(tab.id); setSelectedAdminUser(null); }}
                  style={{ 
                    padding: '10px 16px', 
                    borderRadius: '10px', 
                    border: 'none', 
                    background: adminMode === tab.id ? `linear-gradient(135deg, ${GMG.navyBlue}, ${GMG.bronze})` : `${theme.border}50`,
                    color: adminMode === tab.id ? '#fff' : theme.textMuted, 
                    fontSize: '13px', 
                    fontWeight: '600', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>

            {/* ADMIN MODE: USER LIST */}
            {adminMode === 'list' && !selectedAdminUser && (
              <div>
                {/* Search and Quick Actions */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
                  <input 
                    type="text"
                    placeholder="Search by name or email..."
                    value={adminSearchQuery}
                    onChange={e => setAdminSearchQuery(e.target.value)}
                    style={{ 
                      flex: 1, 
                      minWidth: '200px',
                      padding: '12px 16px', 
                      borderRadius: '10px', 
                      border: `1px solid ${theme.border}`, 
                      background: theme.card,
                      color: theme.text,
                      fontSize: '14px'
                    }}
                  />
                  <button onClick={() => {
                    const csv = ['Name,Email,Track,Status,V1 Complete,V2 Complete,V3 Complete,Total,Last Activity'];
                    allUsersData.forEach(u => {
                      const v1 = u.completedLessons?.[1]?.length || 0;
                      const v2 = u.completedLessons?.[2]?.length || 0;
                      const v3 = u.completedLessons?.[3]?.length || 0;
                      const total = v1 + v2 + v3;
                      csv.push(`"${u.name || 'Unknown'}","${u.email}","${u.track || 'standard'}","${u.status || 'active'}",${v1},${v2},${v3},${total},"${u.lastActivity || 'Never'}"`);
                    });
                    const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = `GMG_University_Users_${new Date().toISOString().split('T')[0]}.csv`;
                    link.click();
                  }} style={{ padding: '12px 16px', borderRadius: '10px', border: 'none', background: GMG.navyBlue, color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                    {'\u{1F4E5}'} Export
                  </button>
                </div>

                {/* Quick Stats Bar */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '20px' }}>
                  <div style={{ background: theme.card, borderRadius: '12px', padding: '14px', textAlign: 'center', border: `1px solid ${theme.border}` }}>
                    <p style={{ fontSize: '22px', fontWeight: '700', color: GMG.navyBlue, margin: 0 }}>{allUsersData.length}</p>
                    <p style={{ fontSize: '10px', color: theme.textMuted, margin: '2px 0 0 0' }}>Total Users</p>
                  </div>
                  <div style={{ background: theme.card, borderRadius: '12px', padding: '14px', textAlign: 'center', border: `1px solid ${theme.border}` }}>
                    <p style={{ fontSize: '22px', fontWeight: '700', color: GMG.success, margin: 0 }}>{allUsersData.filter(u => (u.completedLessons?.[1]?.length || 0) > 0).length}</p>
                    <p style={{ fontSize: '10px', color: theme.textMuted, margin: '2px 0 0 0' }}>Active</p>
                  </div>
                  <div style={{ background: theme.card, borderRadius: '12px', padding: '14px', textAlign: 'center', border: `1px solid ${theme.border}` }}>
                    <p style={{ fontSize: '22px', fontWeight: '700', color: GMG.roseGold, margin: 0 }}>{allUsersData.filter(u => u.track === 'fast').length}</p>
                    <p style={{ fontSize: '10px', color: theme.textMuted, margin: '2px 0 0 0' }}>Fast Track</p>
                  </div>
                  <div style={{ background: theme.card, borderRadius: '12px', padding: '14px', textAlign: 'center', border: `1px solid ${theme.border}` }}>
                    <p style={{ fontSize: '22px', fontWeight: '700', color: GMG.bronze, margin: 0 }}>{allUsersData.filter(u => (u.completedLessons?.[1]?.length || 0) >= 30).length}</p>
                    <p style={{ fontSize: '10px', color: theme.textMuted, margin: '2px 0 0 0' }}>V1 Done</p>
                  </div>
                </div>

                {/* User List */}
                {allUsersData.length === 0 ? (
                  <GlassCard style={{ padding: '40px', textAlign: 'center' }}>
                    <p style={{ fontSize: '40px', margin: '0 0 12px 0' }}>{'\u{1F465}'}</p>
                    <p style={{ color: theme.textMuted }}>No users yet. Add users or wait for them to sign in.</p>
                  </GlassCard>
                ) : (
                  allUsersData
                    .filter(u => {
                      if (!adminSearchQuery) return true;
                      const q = adminSearchQuery.toLowerCase();
                      return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                    })
                    .map(u => {
                      const uTrack = u.track || 'standard';
                      const uConfig = TRACK_CONFIG[uTrack];
                      const v1 = u.completedLessons?.[1]?.length || 0;
                      const v2 = u.completedLessons?.[2]?.length || 0;
                      const v3 = u.completedLessons?.[3]?.length || 0;
                      const uTotal = v1 + v2 + v3;
                      const uPct = Math.round((uTotal / uConfig.total) * 100);
                      const userPhoto = u.photoURL || u.photo;
                      const hasPhoto = userPhoto && userPhoto.length > 10;
                      
                      return (
                        <GlassCard 
                          key={u.id} 
                          style={{ padding: '16px', marginBottom: '12px', cursor: 'pointer', transition: 'transform 0.2s' }}
                          onClick={() => setSelectedAdminUser(u)}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {hasPhoto ? (
                              <img src={userPhoto} alt={u.name} style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', border: `2px solid ${GMG.roseGold}40` }} />
                            ) : (
                              <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: `linear-gradient(135deg, ${GMG.roseGold}40, ${GMG.navyBlue}40)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                                {u.name?.charAt(0)?.toUpperCase() || '\u{1F464}'}
                              </div>
                            )}
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                                <p style={{ fontSize: '15px', fontWeight: '600', color: theme.text, margin: 0 }}>{u.name || 'Unknown'}</p>
                                <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: uTrack === 'fast' ? `${GMG.roseGold}20` : `${GMG.navyBlue}20`, color: uTrack === 'fast' ? GMG.roseGold : GMG.navyBlue }}>
                                  {uTrack === 'fast' ? '2/day' : '1/day'}
                                </span>
                              </div>
                              <p style={{ fontSize: '12px', color: theme.textMuted, margin: 0 }}>{u.email}</p>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <p style={{ fontSize: '18px', fontWeight: '700', color: theme.accent, margin: 0 }}>{uPct}%</p>
                              <p style={{ fontSize: '10px', color: theme.textMuted, margin: 0 }}>V1:{v1} V2:{v2} V3:{v3}</p>
                            </div>
                            <span style={{ fontSize: '16px', color: theme.textMuted }}>{'\u{2192}'}</span>
                          </div>
                        </GlassCard>
                      );
                    })
                )}
              </div>
            )}

            {/* ADMIN MODE: INDIVIDUAL USER MANAGEMENT */}
            {adminMode === 'list' && selectedAdminUser && (
              <div>
                <button onClick={() => setSelectedAdminUser(null)} style={{ background: 'none', border: 'none', fontSize: '14px', color: theme.accent, cursor: 'pointer', padding: 0, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {'\u{2190}'} Back to User List
                </button>
                
                {/* User Header */}
                <GlassCard style={{ padding: '20px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                    {(selectedAdminUser.photoURL || selectedAdminUser.photo) ? (
                      <img src={selectedAdminUser.photoURL || selectedAdminUser.photo} alt="" style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: `3px solid ${GMG.roseGold}` }} />
                    ) : (
                      <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: `linear-gradient(135deg, ${GMG.roseGold}, ${GMG.navyBlue})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', color: '#fff' }}>
                        {selectedAdminUser.name?.charAt(0)?.toUpperCase() || '\u{1F464}'}
                      </div>
                    )}
                    <div>
                      <h3 style={{ fontSize: '20px', fontWeight: '700', color: theme.text, margin: '0 0 4px 0' }}>{selectedAdminUser.name || 'Unknown User'}</h3>
                      <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0 }}>{selectedAdminUser.email}</p>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: `${GMG.navyBlue}20`, color: GMG.navyBlue }}>
                          {(selectedAdminUser.track || 'standard') === 'fast' ? 'Fast Track (2/day)' : 'Standard (1/day)'}
                        </span>
                        {selectedAdminUser.streak > 0 && (
                          <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: `${GMG.gold}20`, color: GMG.gold }}>
                            {'\u{1F525}'} {selectedAdminUser.streak} streak
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Progress Overview */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    {[1, 2, 3].map(vol => {
                      const completed = selectedAdminUser.completedLessons?.[vol]?.length || 0;
                      const total = vol === 3 ? 15 : 30;
                      const pct = Math.round((completed / total) * 100);
                      return (
                        <div key={vol} style={{ background: `${theme.bg}80`, borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                          <p style={{ fontSize: '11px', color: theme.textMuted, margin: '0 0 4px 0' }}>Volume {vol}</p>
                          <p style={{ fontSize: '20px', fontWeight: '700', color: vol === 1 ? GMG.roseGold : vol === 2 ? GMG.navyBlue : GMG.bronze, margin: 0 }}>{completed}/{total}</p>
                          <p style={{ fontSize: '11px', color: theme.textMuted, margin: '2px 0 0 0' }}>{pct}%</p>
                        </div>
                      );
                    })}
                  </div>
                </GlassCard>

                {/* Admin Actions */}
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: theme.text, marginBottom: '12px' }}>{'\u{2699}'} Admin Actions</h4>
                
                {/* V3.5: Edit User Profile */}
                <GlassCard style={{ padding: '16px', marginBottom: '12px' }}>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: theme.text, margin: '0 0 12px 0' }}>{'\u{270F}'} Edit Profile</p>
                  
                  {!adminEditingUser ? (
                    <button 
                      onClick={() => {
                        setAdminEditName(selectedAdminUser.name || '');
                        setAdminEditPhoto(selectedAdminUser.photoURL || selectedAdminUser.photo || '');
                        setAdminEditingUser(true);
                      }}
                      style={{ padding: '10px 20px', borderRadius: '10px', border: `1px solid ${theme.border}`, background: 'transparent', color: theme.text, fontSize: '13px', cursor: 'pointer' }}
                    >
                      {'\u{270F}'} Edit Name & Photo
                    </button>
                  ) : (
                    <div>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ fontSize: '11px', fontWeight: '600', color: theme.textMuted, display: 'block', marginBottom: '4px' }}>NAME</label>
                        <input 
                          type="text"
                          value={adminEditName}
                          onChange={(e) => setAdminEditName(e.target.value)}
                          placeholder="User's name"
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${theme.border}`, fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ fontSize: '11px', fontWeight: '600', color: theme.textMuted, display: 'block', marginBottom: '4px' }}>PHOTO URL</label>
                        <input 
                          type="url"
                          value={adminEditPhoto}
                          onChange={(e) => setAdminEditPhoto(e.target.value)}
                          placeholder="https://example.com/photo.jpg"
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${theme.border}`, fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button 
                          onClick={() => setAdminEditingUser(false)}
                          style={{ flex: 1, padding: '10px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: 'transparent', color: theme.text, fontSize: '13px', cursor: 'pointer' }}
                        >
                          Cancel
                        </button>
                        <button 
                          onClick={async () => {
                            const success = await adminUpdateUserProfile(selectedAdminUser.id, adminEditName, adminEditPhoto);
                            if (success) {
                              alert('\u{2705} Profile updated!');
                              setAdminEditingUser(false);
                              // Refresh user data
                              const userDoc = await getDoc(doc(db, 'users', selectedAdminUser.id));
                              if (userDoc.exists()) setSelectedAdminUser({ id: selectedAdminUser.id, ...userDoc.data() });
                            } else {
                              alert('\u{274C} Failed to update profile');
                            }
                          }}
                          style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: GMG.navyBlue, color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  )}
                </GlassCard>
                
                {/* Track Change */}
                <GlassCard style={{ padding: '16px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: theme.text, margin: '0 0 2px 0' }}>Learning Track</p>
                      <p style={{ fontSize: '12px', color: theme.textMuted, margin: 0 }}>Change lessons per day</p>
                    </div>
                    <select 
                      value={selectedAdminUser.track || 'standard'} 
                      onChange={async (e) => {
                        await setUserTrackById(selectedAdminUser.id, e.target.value);
                        setSelectedAdminUser({...selectedAdminUser, track: e.target.value});
                        alert('\u{2705} Track updated!');
                      }} 
                      style={{ padding: '10px 16px', borderRadius: '10px', border: `1px solid ${theme.border}`, fontSize: '13px', background: theme.card, color: theme.text, cursor: 'pointer' }}
                    >
                      <option value="standard">Standard (1/day)</option>
                      <option value="fast">Fast Track (2/day)</option>
                    </select>
                  </div>
                </GlassCard>

                {/* Unlock Next Batch */}
                <GlassCard style={{ padding: '16px', marginBottom: '12px' }}>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: theme.text, margin: '0 0 4px 0' }}>{'\u{1F513}'} Unlock Next Batch</p>
                  <p style={{ fontSize: '12px', color: theme.textMuted, margin: '0 0 12px 0' }}>Manually unlock the next set of lessons for any volume</p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[1, 2, 3].map(vol => (
                      <button 
                        key={vol}
                        onClick={async () => {
                          if (window.confirm(`Unlock next batch for Volume ${vol}?`)) {
                            const success = await adminUnlockNextBatch(selectedAdminUser.id, vol);
                            if (success) {
                              alert('\u{2705} Next batch unlocked!');
                              // Refresh user data
                              const userDoc = await getDoc(doc(db, 'users', selectedAdminUser.id));
                              if (userDoc.exists()) setSelectedAdminUser({ id: selectedAdminUser.id, ...userDoc.data() });
                            } else {
                              alert('\u{274C} Failed to unlock. Try again.');
                            }
                          }
                        }}
                        style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: vol === 1 ? GMG.roseGold : vol === 2 ? GMG.navyBlue : GMG.bronze, color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        V{vol} +Batch
                      </button>
                    ))}
                  </div>
                </GlassCard>

                {/* Unlock Specific Days */}
                <GlassCard style={{ padding: '16px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: theme.text, margin: '0 0 4px 0' }}>{'\u{1F4C5}'} Manage Day Status</p>
                      <p style={{ fontSize: '12px', color: theme.textMuted, margin: 0 }}>Click days to {adminMarkMode === 'complete' ? 'mark complete' : 'mark open (undo)'}</p>
                    </div>
                    {/* V3.4: Toggle for Mark Complete vs Mark Open */}
                    <div style={{ display: 'flex', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${theme.border}` }}>
                      <button 
                        onClick={() => setAdminMarkMode('complete')}
                        style={{ 
                          padding: '8px 12px', 
                          border: 'none', 
                          background: adminMarkMode === 'complete' ? GMG.success : 'transparent',
                          color: adminMarkMode === 'complete' ? '#fff' : theme.textMuted,
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        {'\u{2705}'} Complete
                      </button>
                      <button 
                        onClick={() => setAdminMarkMode('open')}
                        style={{ 
                          padding: '8px 12px', 
                          border: 'none', 
                          background: adminMarkMode === 'open' ? GMG.danger : 'transparent',
                          color: adminMarkMode === 'open' ? '#fff' : theme.textMuted,
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        {'\u{1F513}'} Open
                      </button>
                    </div>
                  </div>
                  {[1, 2, 3].map(vol => {
                    const completed = selectedAdminUser.completedLessons?.[vol] || [];
                    const totalDays = vol === 3 ? 15 : 30;
                    return (
                      <div key={vol} style={{ marginBottom: vol < 3 ? '16px' : 0 }}>
                        <p style={{ fontSize: '12px', fontWeight: '600', color: vol === 1 ? GMG.roseGold : vol === 2 ? GMG.navyBlue : GMG.bronze, margin: '0 0 8px 0' }}>Volume {vol}</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {Array.from({ length: totalDays }, (_, i) => i + 1).map(day => {
                            const isComplete = completed.includes(day);
                            const canClick = adminMarkMode === 'complete' ? !isComplete : isComplete;
                            return (
                              <button
                                key={day}
                                onClick={async () => {
                                  if (!canClick) return;
                                  if (adminMarkMode === 'complete') {
                                    await adminUnlockDays(selectedAdminUser.id, vol, [day]);
                                  } else {
                                    await adminUnmarkDays(selectedAdminUser.id, vol, [day]);
                                  }
                                  const userDoc = await getDoc(doc(db, 'users', selectedAdminUser.id));
                                  if (userDoc.exists()) setSelectedAdminUser({ id: selectedAdminUser.id, ...userDoc.data() });
                                }}
                                style={{ 
                                  width: '28px', 
                                  height: '28px', 
                                  borderRadius: '6px', 
                                  border: adminMarkMode === 'open' && isComplete ? `2px solid ${GMG.danger}` : 'none',
                                  background: isComplete ? GMG.success : `${theme.textMuted}20`,
                                  color: isComplete ? '#fff' : theme.textMuted,
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  cursor: canClick ? 'pointer' : 'default',
                                  opacity: canClick ? 1 : 0.5
                                }}
                              >
                                {day}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </GlassCard>

                {/* Force Unlock All */}
                <GlassCard style={{ padding: '16px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: theme.text, margin: '0 0 2px 0' }}>{'\u{1F680}'} Unlock Everything</p>
                      <p style={{ fontSize: '12px', color: theme.textMuted, margin: 0 }}>Grant immediate access to all content</p>
                    </div>
                    <button 
                      onClick={async () => {
                        if (window.confirm('\u{26A0} Unlock ALL content for this user?\n\nThis marks all 75 lessons as complete.')) {
                          const success = await adminForceUnlockAll(selectedAdminUser.id);
                          if (success) {
                            alert('\u{2705} All content unlocked!');
                            const userDoc = await getDoc(doc(db, 'users', selectedAdminUser.id));
                            if (userDoc.exists()) setSelectedAdminUser({ id: selectedAdminUser.id, ...userDoc.data() });
                          }
                        }
                      }}
                      style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: `linear-gradient(135deg, ${GMG.gold}, ${GMG.bronze})`, color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                    >
                      {'\u{1F513}'} Unlock All
                    </button>
                  </div>
                </GlassCard>

                {/* Reset User */}
                <GlassCard style={{ padding: '16px', marginBottom: '12px', borderColor: `${GMG.danger}40` }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: GMG.danger, margin: '0 0 2px 0' }}>{'\u{26A0}'} Reset Progress</p>
                      <p style={{ fontSize: '12px', color: theme.textMuted, margin: 0 }}>Clear all lessons, quizzes, and streaks</p>
                    </div>
                    <button 
                      onClick={async () => {
                        if (window.confirm(`\u{26A0} Reset ${selectedAdminUser.name || 'this user'}?\n\nThis clears ALL progress and cannot be undone.`)) {
                          if (window.confirm('\u{1F534} FINAL CONFIRMATION\n\nProceed with reset?')) {
                            const success = await adminResetUser(selectedAdminUser.id);
                            if (success) {
                              alert('\u{2705} User progress reset!');
                              const userDoc = await getDoc(doc(db, 'users', selectedAdminUser.id));
                              if (userDoc.exists()) setSelectedAdminUser({ id: selectedAdminUser.id, ...userDoc.data() });
                            }
                          }
                        }
                      }}
                      style={{ padding: '10px 20px', borderRadius: '10px', border: `1px solid ${GMG.danger}`, background: 'transparent', color: GMG.danger, fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                    >
                      {'\u{1F5D1}'} Reset
                    </button>
                  </div>
                </GlassCard>
              </div>
            )}

            {/* ADMIN MODE: ADD NEW USER */}
            {adminMode === 'add' && (
              <div>
                <GlassCard style={{ padding: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: theme.text, marginBottom: '4px' }}>{'\u{2795}'} Add New Fellow</h3>
                  <p style={{ fontSize: '13px', color: theme.textMuted, marginBottom: '20px' }}>Pre-register a user so they can sign in with Google.</p>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: theme.text, display: 'block', marginBottom: '6px' }}>Email Address *</label>
                    <input 
                      type="email"
                      placeholder="fellow@email.com"
                      value={newUserEmail}
                      onChange={e => setNewUserEmail(e.target.value)}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${theme.border}`, background: theme.card, color: theme.text, fontSize: '14px' }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: theme.text, display: 'block', marginBottom: '6px' }}>Full Name</label>
                    <input 
                      type="text"
                      placeholder="Jane Smith"
                      value={newUserName}
                      onChange={e => setNewUserName(e.target.value)}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${theme.border}`, background: theme.card, color: theme.text, fontSize: '14px' }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: theme.text, display: 'block', marginBottom: '6px' }}>Learning Track</label>
                    <select 
                      value={newUserTrack}
                      onChange={e => setNewUserTrack(e.target.value)}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${theme.border}`, background: theme.card, color: theme.text, fontSize: '14px', cursor: 'pointer' }}
                    >
                      <option value="fast">Fast Track (2 lessons/day)</option>
                      <option value="standard">Standard (1 lesson/day)</option>
                    </select>
                  </div>
                  
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: theme.text, display: 'block', marginBottom: '6px' }}>Volume Access</label>
                    <select 
                      value={newUserAccess}
                      onChange={e => setNewUserAccess(e.target.value)}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${theme.border}`, background: theme.card, color: theme.text, fontSize: '14px', cursor: 'pointer' }}
                    >
                      <option value="all">All Volumes (V1, V2, V3)</option>
                      <option value="v1v2">Volume 1 + 2 Only</option>
                      <option value="v1">Volume 1 Only</option>
                    </select>
                  </div>
                  
                  <button 
                    onClick={async () => {
                      if (!newUserEmail) {
                        alert('Please enter an email address.');
                        return;
                      }
                      if (!newUserEmail.includes('@')) {
                        alert('Please enter a valid email address.');
                        return;
                      }
                      
                      const success = await adminAddUser(newUserEmail, newUserName, newUserTrack, newUserAccess);
                      if (success) {
                        alert(`\u{2705} ${newUserName || newUserEmail} has been added!\n\nThey can now sign in with Google using this email.`);
                        setNewUserEmail('');
                        setNewUserName('');
                        setNewUserTrack('fast');
                        setNewUserAccess('all');
                      } else {
                        alert('\u{274C} Failed to add user. Try again.');
                      }
                    }}
                    style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: `linear-gradient(135deg, ${GMG.navyBlue}, ${GMG.bronze})`, color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}
                  >
                    {'\u{2795}'} Add Fellow
                  </button>
                </GlassCard>
                
                <GlassCard style={{ padding: '16px', marginTop: '16px', background: `${GMG.navyBlue}10` }}>
                  <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0 }}>
                    <strong>Note:</strong> When the user signs in with Google, their account will automatically be linked if they use this email address. They'll start with the track and volume access you set here.
                  </p>
                </GlassCard>
              </div>
            )}

            {/* ADMIN MODE: ANALYTICS */}
            {adminMode === 'stats' && (
              <div>
                <GlassCard style={{ padding: '20px', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: theme.text, marginBottom: '16px' }}>{'\u{1F4CA}'} Program Analytics</h3>
                  
                  {/* Volume Completion Rates */}
                  <div style={{ marginBottom: '20px' }}>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: theme.textMuted, marginBottom: '12px' }}>VOLUME COMPLETION RATES</p>
                    {[1, 2, 3].map(vol => {
                      const totalUsers = allUsersData.length;
                      const completers = allUsersData.filter(u => (u.completedLessons?.[vol]?.length || 0) >= (vol === 3 ? 15 : 30)).length;
                      const pct = totalUsers > 0 ? Math.round((completers / totalUsers) * 100) : 0;
                      return (
                        <div key={vol} style={{ marginBottom: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontSize: '13px', color: theme.text }}>Volume {vol}</span>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: vol === 1 ? GMG.roseGold : vol === 2 ? GMG.navyBlue : GMG.bronze }}>{completers}/{totalUsers} ({pct}%)</span>
                          </div>
                          <div style={{ height: '8px', background: `${theme.textMuted}20`, borderRadius: '4px' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: vol === 1 ? GMG.roseGold : vol === 2 ? GMG.navyBlue : GMG.bronze, borderRadius: '4px', transition: 'width 0.5s' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Average Progress */}
                  <div style={{ marginBottom: '20px' }}>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: theme.textMuted, marginBottom: '12px' }}>AVERAGE PROGRESS</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                      {[1, 2, 3].map(vol => {
                        const totalDays = vol === 3 ? 15 : 30;
                        const avgCompleted = allUsersData.length > 0 
                          ? Math.round(allUsersData.reduce((sum, u) => sum + (u.completedLessons?.[vol]?.length || 0), 0) / allUsersData.length)
                          : 0;
                        return (
                          <div key={vol} style={{ background: `${theme.bg}80`, borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                            <p style={{ fontSize: '24px', fontWeight: '700', color: vol === 1 ? GMG.roseGold : vol === 2 ? GMG.navyBlue : GMG.bronze, margin: 0 }}>{avgCompleted}</p>
                            <p style={{ fontSize: '11px', color: theme.textMuted, margin: '2px 0 0 0' }}>of {totalDays} days</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Track Distribution */}
                  <div>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: theme.textMuted, marginBottom: '12px' }}>TRACK DISTRIBUTION</p>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ flex: 1, background: `${GMG.roseGold}15`, borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                        <p style={{ fontSize: '24px', fontWeight: '700', color: GMG.roseGold, margin: 0 }}>{allUsersData.filter(u => u.track === 'fast').length}</p>
                        <p style={{ fontSize: '11px', color: theme.textMuted, margin: '2px 0 0 0' }}>Fast Track</p>
                      </div>
                      <div style={{ flex: 1, background: `${GMG.navyBlue}15`, borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                        <p style={{ fontSize: '24px', fontWeight: '700', color: GMG.navyBlue, margin: 0 }}>{allUsersData.filter(u => !u.track || u.track === 'standard').length}</p>
                        <p style={{ fontSize: '11px', color: theme.textMuted, margin: '2px 0 0 0' }}>Standard</p>
                      </div>
                    </div>
                  </div>
                </GlassCard>
                
                {/* Bulk Actions */}
                <GlassCard style={{ padding: '20px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: theme.text, marginBottom: '16px' }}>{'\u{26A1}'} Bulk Actions</h3>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button onClick={async () => {
                      if (window.confirm('Set ALL users to Fast Track (2 lessons/day)?')) {
                        for (const u of allUsersData) {
                          await setUserTrackById(u.id, 'fast');
                        }
                        alert('\u{2705} All users set to Fast Track!');
                        window.location.reload();
                      }
                    }} style={{ padding: '10px 16px', borderRadius: '10px', border: 'none', background: GMG.roseGold, color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                      All {'\u{2192}'} Fast
                    </button>
                    <button onClick={async () => {
                      if (window.confirm('Set ALL users to Standard Track (1 lesson/day)?')) {
                        for (const u of allUsersData) {
                          await setUserTrackById(u.id, 'standard');
                        }
                        alert('\u{2705} All users set to Standard Track!');
                        window.location.reload();
                      }
                    }} style={{ padding: '10px 16px', borderRadius: '10px', border: 'none', background: GMG.navyBlue, color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                      All {'\u{2192}'} Standard
                    </button>
                    <button onClick={async () => {
                      if (window.confirm('\u{26A0} RESET ALL USERS?\n\nThis clears ALL progress for EVERYONE.\n\nThis cannot be undone!')) {
                        if (window.confirm('\u{1F534} FINAL CONFIRMATION\n\nProceed with full program reset?')) {
                          for (const u of allUsersData) {
                            await adminResetUser(u.id);
                          }
                          alert('\u{2705} All progress reset!');
                          window.location.reload();
                        }
                      }
                    }} style={{ padding: '10px 16px', borderRadius: '10px', border: `1px solid ${GMG.danger}`, background: 'transparent', color: GMG.danger, fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                      {'\u{26A0}'} Reset All
                    </button>
                  </div>
                </GlassCard>
              </div>
            )}
          </div>
        )}

        

        {/* HOME VIEW */}
        {currentView === 'home' && !selectedVolume && !founderView && (
          <div>
            <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <img src={GMG_LOGO} alt="GMG" style={{ width: '48px', height: '48px', borderRadius: '12px' }} />
                <div><h1 style={{ fontSize: '20px', fontWeight: '700', color: theme.text, margin: 0 }}>GMG University</h1><p style={{ fontSize: '12px', color: theme.textMuted, margin: 0 }}>Excellence Rooted in Experience</p></div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                {user && <TrackBadge track={userTrack} size="small" />}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button onClick={() => setDarkMode(!darkMode)} style={{ width: '32px', height: '32px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: darkMode ? `${GMG.navyBlue}50` : 'transparent', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{darkMode ? '\u{1F319}' : '\u{2600}'}</button>
                  <SessionTimer />
                  <SyncStatus status={syncStatus} />
                </div>
              </div>
            </header>

            {/* V3.9: PWA Install Prompt Banner */}
            {showInstallPrompt && (
              <div style={{ 
                background: `linear-gradient(135deg, ${GMG.navyBlue}, ${GMG.roseGold})`,
                borderRadius: '16px',
                padding: '16px 20px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ fontSize: '28px' }}>{'\u{1F4F2}'}</div>
                  <div>
                    <p style={{ fontSize: '14px', fontWeight: '600', color: '#fff', margin: 0 }}>Install GMG University</p>
                    <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', margin: '2px 0 0 0' }}>Add to home screen for the best experience</p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={dismissInstallPrompt}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.3)',
                      background: 'transparent',
                      color: '#fff',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    Later
                  </button>
                  <button
                    onClick={handleInstallApp}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      background: '#fff',
                      color: GMG.navyBlue,
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Install
                  </button>
                </div>
              </div>
            )}

            {!user ? (
              <GlassCard glow style={{ padding: '28px', textAlign: 'center', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '22px', fontWeight: '600', color: theme.text, marginBottom: '12px' }}>Welcome, Future Fundraiser</h2>
                <p style={{ fontSize: '14px', color: theme.textMuted, marginBottom: '20px' }}>Sign in with Google to track your progress.</p>
                <button onClick={signInWithGoogle} style={{ padding: '14px 32px', borderRadius: '12px', border: 'none', background: '#4285f4', color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', margin: '0 auto' }}>
                  <span>{'\u{1F512}'}</span> Sign in with Google
                </button>
              </GlassCard>
            ) : (
              <>
                <GlassCard style={{ padding: '24px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {/* V2.4 FIX: Proper photo fallback like CohortLeaderboard */}
                      {user.photo && user.photo.length > 10 ? (
                        <img src={user.photo} alt="" style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: `linear-gradient(135deg, ${GMG.roseGold}, ${GMG.navyBlue})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                          {user.name?.charAt(0)?.toUpperCase() || '\u{1F464}'}
                        </div>
                      )}
                      <div><p style={{ fontSize: '13px', color: theme.textMuted, margin: 0 }}>Welcome back,</p><h2 style={{ fontSize: '18px', fontWeight: '600', color: theme.text, margin: 0 }}>{user.name}</h2></div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                      {isFounder && <button onClick={() => setFounderView(true)} style={{ padding: '10px 16px', borderRadius: '10px', border: 'none', background: `linear-gradient(135deg, ${GMG.navyBlue}, ${GMG.bronze})`, color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>{'\u{1F4CA}'} Admin Portal</button>}
                      {isFounder && (
                        <button 
                          onClick={() => setTestUnlockMode(!testUnlockMode)} 
                          style={{ 
                            padding: '8px 12px', 
                            borderRadius: '8px', 
                            border: testUnlockMode ? '2px solid #22c55e' : `1px solid ${theme.border}`, 
                            background: testUnlockMode ? '#22c55e20' : 'transparent', 
                            color: testUnlockMode ? '#22c55e' : theme.textMuted, 
                            fontSize: '11px', 
                            fontWeight: '500', 
                            cursor: 'pointer' 
                          }}
                        >
                          {testUnlockMode ? '\u{1F513} TEST MODE ON' : '\u{1F512} Test Mode Off'}
                        </button>
                      )}
                      <StreakCounter streak={streak} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <ProgressRing progress={progressPct} size={90} />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: theme.text, margin: '0 0 4px 0' }}>{totalLessons} of {trackConfig.total} lessons</p>
                      <p style={{ fontSize: '12px', color: theme.textMuted, margin: 0 }}>Est. {Math.ceil((trackConfig.total - totalLessons) * 0.8)} days remaining</p>
                    </div>
                  </div>
                </GlassCard>
                {allUsersData.length > 0 && <CohortLeaderboard users={allUsersData} currentUserEmail={user.email} currentUserPhoto={user.photo} onUserClick={(u) => setViewingUser(u)} />}
              </>
            )}

            <h3 style={{ fontSize: '13px', fontWeight: '600', letterSpacing: '2px', color: theme.textMuted, marginBottom: '16px' }}>CURRICULUM</h3>
            {[
              { vol: 1, title: 'Fundraising Foundations', icon: '\u{1F4DA}', color: GMG.roseGold },
              { vol: 2, title: 'The GMG Way', icon: '\u{1F3AF}', color: GMG.navyBlue },
              { vol: 3, title: 'CPP Model', icon: '\u{1F680}', color: GMG.bronze }
            ].filter(v => {
              // V3.4: Hide V3 for limited access users (like fellows)
              if (v.vol === 3 && user && LIMITED_ACCESS.includes(user.email)) return false;
              return true;
            }).map(v => {
              const days = v.vol === 1 ? trackConfig.v1 : v.vol === 2 ? trackConfig.v2 : trackConfig.v3;
              const hasIncomplete = completedLessons[v.vol].length < days && completedLessons[v.vol].length > 0;
              const nextDay = completedLessons[v.vol].length + 1;
              
              // V3.4: Check if volume is accessible (previous volume complete)
              const volumeAccessible = isVolumeAccessible(v.vol);
              const isLocked = !volumeAccessible;
              const prevVolumeProgress = v.vol === 2 ? completedLessons[1]?.length || 0 : v.vol === 3 ? completedLessons[2]?.length || 0 : 0;
              const prevVolumeTotal = v.vol === 2 ? 30 : v.vol === 3 ? 30 : 0;
              
              return (
                <GlassCard 
                  key={v.vol} 
                  onClick={() => {
                    if (!user) { signInWithGoogle(); return; }
                    if (isLocked) {
                      alert(`\u{1F512} Volume ${v.vol} is locked!\n\nComplete all ${prevVolumeTotal} days of Volume ${v.vol - 1} first.\n\nProgress: ${prevVolumeProgress}/${prevVolumeTotal} days complete.`);
                      return;
                    }
                    setSelectedVolume(v.vol);
                  }} 
                  style={{ 
                    padding: '20px', 
                    marginBottom: '14px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '16px', 
                    position: 'relative',
                    opacity: isLocked ? 0.6 : 1,
                    cursor: isLocked ? 'not-allowed' : 'pointer'
                  }}
                >
                  {/* Incomplete indicator */}
                  {hasIncomplete && !isLocked && (
                    <div style={{ position: 'absolute', top: '12px', right: '12px', width: '10px', height: '10px', borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 6px #ef4444' }} />
                  )}
                  {/* Offline indicator */}
                  {isVolumeOffline(v.vol) && !isLocked && !hasIncomplete && (
                    <div style={{ position: 'absolute', top: '12px', right: '12px', fontSize: '14px' }} title="Available offline">{'\u{2601}'}</div>
                  )}
                  {/* Lock indicator for locked volumes */}
                  {isLocked && (
                    <div style={{ position: 'absolute', top: '12px', right: '12px', fontSize: '16px' }}>{'\u{1F512}'}</div>
                  )}
                  <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: isLocked ? `${theme.textMuted}20` : `${v.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', position: 'relative' }}>
                    {isLocked ? '\u{1F512}' : v.icon}
                    {completedLessons[v.vol].length === days && !isLocked && <div style={{ position: 'absolute', bottom: '-4px', right: '-4px', fontSize: '16px' }}>{'\u{2705}'}</div>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '10px', color: theme.textMuted, margin: 0, letterSpacing: '1px' }}>VOLUME {v.vol}</p>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: isLocked ? theme.textMuted : theme.text, margin: '4px 0' }}>{v.title}</h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      {isLocked ? (
                        <p style={{ fontSize: '12px', color: theme.textMuted, margin: 0 }}>Complete V{v.vol - 1} first ({prevVolumeProgress}/{prevVolumeTotal})</p>
                      ) : completedLessons[v.vol].length === days ? (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              issueCertificate(v.vol).then(cert => {
                                if (cert) {
                                  setCertificateModal({ volume: v.vol, score: cert.quizAverage });
                                }
                              });
                            }}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '8px',
                              border: 'none',
                              background: `linear-gradient(135deg, ${v.color}, ${GMG.gold})`,
                              color: '#fff',
                              fontSize: '11px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            {certificates[v.vol] ? '\u{1F4DC} View Certificate' : '\u{1F3C6} Get Certificate'}
                          </button>
                        </>
                      ) : (
                        <>
                          <p style={{ fontSize: '12px', color: theme.textMuted, margin: 0 }}>{completedLessons[v.vol].length}/{days} days</p>
                          {hasIncomplete && <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: `${v.color}20`, color: v.color, fontWeight: '600' }}>Day {nextDay} next</span>}
                          {/* V3.9: Offline download button */}
                          {!isVolumeOffline(v.vol) && 'caches' in window && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadVolumeForOffline(v.vol);
                              }}
                              style={{
                                padding: '4px 8px',
                                borderRadius: '6px',
                                border: `1px solid ${theme.border}`,
                                background: 'transparent',
                                color: theme.textMuted,
                                fontSize: '10px',
                                cursor: 'pointer'
                              }}
                            >
                              {'\u{2B07}'} Offline
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <span style={{ color: isLocked ? theme.textMuted : v.color, fontSize: '20px' }}>{isLocked ? '\u{1F512}' : '\u{2192}'}</span>
                </GlassCard>
              );
            })}
            {user && <QuickJumpMenu completedDays={[...completedLessons[1], ...completedLessons[2], ...completedLessons[3]]} onJump={(day) => setSelectedVolume(1)} />}
          </div>
        )}

        {/* LEARN VIEW - V2.4: Respects day locks */}
        {currentView === 'learn' && !selectedVolume && !founderView && (
          <div>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: theme.text, marginBottom: '20px' }}>Learn</h2>
            <p style={{ color: theme.textMuted, marginBottom: '20px' }}>Continue your fundraising education.</p>
            {[1, 2, 3].map(vol => {
              const days = vol === 1 ? trackConfig.v1 : vol === 2 ? trackConfig.v2 : trackConfig.v3;
              const titles = getLessons(vol);
              const nextDay = completedLessons[vol].length + 1;
              const color = vol === 1 ? GMG.roseGold : vol === 2 ? GMG.navyBlue : GMG.bronze;
              if (nextDay > days) return null;
              
              // V2.4: Check if next day is actually unlocked
              const dayIsUnlocked = isDayUnlocked(vol, nextDay);
              const countdown = getUnlockCountdown();
              
              return (
                <div key={vol} onClick={() => { 
                  if (!dayIsUnlocked) {
                    alert(countdown ? `This lesson is locked! ${countdown}` : 'Complete the previous lessons first.');
                    return;
                  }
                  setSelectedVolume(vol); 
                  setSelectedLesson({ volume: vol, day: nextDay }); 
                  // V2.6.5: Start lesson in chat mode
                  if (!isQuizDay(vol, nextDay)) {
                    startLessonChat(vol, nextDay);
                  }
                }} style={{ 
                  background: theme.card, 
                  borderRadius: '16px', 
                  padding: '18px', 
                  border: `1px solid ${theme.border}`, 
                  marginBottom: '12px', 
                  cursor: dayIsUnlocked ? 'pointer' : 'not-allowed',
                  opacity: dayIsUnlocked ? 1 : 0.5
                }}>
                  <p style={{ fontSize: '11px', color: theme.textMuted, margin: 0, letterSpacing: '1px' }}>
                    {dayIsUnlocked ? `CONTINUE VOLUME ${vol}` : `LOCKED - VOLUME ${vol}`}
                  </p>
                  <h4 style={{ fontSize: '15px', fontWeight: '600', color: theme.text, margin: '6px 0' }}>Day {nextDay}: {titles[nextDay - 1]}</h4>
                  <p style={{ fontSize: '12px', color: dayIsUnlocked ? color : theme.textMuted, margin: 0 }}>
                    {dayIsUnlocked ? getDuration(vol, nextDay) : (countdown || 'Complete previous days first')}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {/* PROGRESS VIEW - V2.4: Enhanced with Social Features */}
        {currentView === 'progress' && !selectedVolume && !founderView && (
          <div>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: theme.text, marginBottom: '20px' }}>Your Progress</h2>
            
            {/* XP and Overall Progress */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
              <div style={{ flex: 1, background: theme.card, borderRadius: '16px', padding: '16px', border: `1px solid ${theme.border}`, textAlign: 'center' }}>
                <p style={{ fontSize: '32px', fontWeight: '700', color: theme.accent, margin: 0 }}>{progressPct}%</p>
                <p style={{ fontSize: '12px', color: theme.textMuted, margin: '4px 0 0 0' }}>Complete</p>
              </div>
              <div style={{ flex: 1, background: `linear-gradient(135deg, ${GMG.navyBlue}15, ${GMG.roseGold}10)`, borderRadius: '16px', padding: '16px', border: `1px solid ${GMG.navyBlue}30`, textAlign: 'center' }}>
                <p style={{ fontSize: '32px', fontWeight: '700', color: GMG.navyBlue, margin: 0 }}>{totalXP.toLocaleString()}</p>
                <p style={{ fontSize: '12px', color: theme.textMuted, margin: '4px 0 0 0' }}>Total XP</p>
              </div>
            </div>

            {/* V3.8: Spaced Repetition Review */}
            {Object.keys(spacedRepetition).length > 0 && (
              <GlassCard style={{ padding: '16px', marginBottom: '20px', background: `linear-gradient(135deg, ${GMG.roseGold}15, ${GMG.bronze}10)` }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <h3 style={{ fontSize: '15px', fontWeight: '600', color: theme.text, margin: '0 0 4px 0' }}>{'\u{1F4DA}'} Review Mode</h3>
                    <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0 }}>
                      {getReviewQuestions().length} questions due for review
                    </p>
                  </div>
                  <button 
                    onClick={startReviewMode}
                    disabled={getReviewQuestions().length === 0}
                    style={{ 
                      padding: '10px 20px', 
                      borderRadius: '12px', 
                      border: 'none', 
                      background: getReviewQuestions().length > 0 ? `linear-gradient(135deg, ${GMG.roseGold}, ${GMG.bronze})` : `${theme.textMuted}30`,
                      color: '#fff', 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      cursor: getReviewQuestions().length > 0 ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {getReviewQuestions().length > 0 ? 'Start Review' : 'All Caught Up!'}
                  </button>
                </div>
              </GlassCard>
            )}

            {/* Weekly Challenges */}
            <GlassCard style={{ padding: '16px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '600', color: theme.text, margin: 0 }}>{'\u{1F3C6}'} Weekly Challenges</h3>
                <span style={{ fontSize: '11px', color: theme.textMuted }}>Resets Sunday</span>
              </div>
              {WEEKLY_CHALLENGES.map(challenge => {
                const progress = getWeeklyChallengeProgress(challenge.id);
                const current = weeklyProgress[challenge.metric] || 0;
                const isComplete = progress >= 1;
                return (
                  <div key={challenge.id} style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '13px', color: isComplete ? '#22c55e' : theme.text }}>
                        {challenge.icon} {challenge.name}
                      </span>
                      <span style={{ fontSize: '12px', color: isComplete ? '#22c55e' : theme.textMuted }}>
                        {isComplete ? '\u{2713}' : `${current}/${challenge.target}`} - {challenge.xp} XP
                      </span>
                    </div>
                    <div style={{ height: '6px', background: `${theme.textMuted}20`, borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ 
                        width: `${progress * 100}%`, 
                        height: '100%', 
                        background: isComplete ? '#22c55e' : `linear-gradient(90deg, ${GMG.navyBlue}, ${GMG.roseGold})`,
                        borderRadius: '3px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                );
              })}
            </GlassCard>

            {/* Achievement Badges */}
            <GlassCard style={{ padding: '16px', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '600', color: theme.text, margin: '0 0 12px 0' }}>{'\u{1F3C5}'} Badges ({earnedBadges.length}/{ACHIEVEMENT_BADGES.length})</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {ACHIEVEMENT_BADGES.map(badge => {
                  const earned = earnedBadges.includes(badge.id);
                  const rarityColors = BADGE_RARITY_COLORS[badge.rarity];
                  return (
                    <div 
                      key={badge.id}
                      onClick={() => earned && setShowBadgeModal(badge)}
                      style={{ 
                        width: '56px', 
                        height: '56px', 
                        borderRadius: '12px',
                        background: earned ? rarityColors.bg : `${theme.textMuted}10`,
                        border: `2px solid ${earned ? rarityColors.border : theme.textMuted}30`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                        opacity: earned ? 1 : 0.3,
                        cursor: earned ? 'pointer' : 'default',
                        transition: 'transform 0.2s ease'
                      }}
                      title={earned ? `${badge.name}: ${badge.description}` : '???'}
                    >
                      {earned ? badge.icon : '\u{1F512}'}
                    </div>
                  );
                })}
              </div>
            </GlassCard>

            {/* Leaderboard */}
            <GlassCard style={{ padding: '16px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '600', color: theme.text, margin: 0 }}>{'\u{1F4CA}'} Leaderboard</h3>
                <button 
                  onClick={refreshLeaderboard}
                  style={{ 
                    padding: '6px 12px', 
                    borderRadius: '8px', 
                    border: `1px solid ${theme.border}`,
                    background: 'transparent',
                    fontSize: '11px',
                    cursor: 'pointer',
                    color: theme.textMuted
                  }}
                >
                  Refresh
                </button>
              </div>
              {leaderboardData.length === 0 ? (
                <p style={{ fontSize: '13px', color: theme.textMuted, textAlign: 'center', padding: '20px 0' }}>
                  No leaderboard data yet. Complete lessons to appear!
                </p>
              ) : (
                leaderboardData.slice(0, 5).map((entry, i) => (
                  <LeaderboardEntry 
                    key={entry.id}
                    rank={i + 1}
                    user={entry}
                    isCurrentUser={user?.email === entry.id}
                  />
                ))
              )}
              {leaderboardData.length > 5 && (
                <button 
                  onClick={() => setCurrentView('leaderboard')}
                  style={{ 
                    width: '100%',
                    padding: '10px',
                    marginTop: '8px',
                    borderRadius: '10px',
                    border: `1px solid ${theme.border}`,
                    background: 'transparent',
                    fontSize: '13px',
                    cursor: 'pointer',
                    color: theme.accent
                  }}
                >
                  View Full Leaderboard
                </button>
              )}
            </GlassCard>

            {/* Quiz Scores */}
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: theme.text, marginBottom: '12px' }}>Quiz Scores</h3>
            {Object.keys(quizScores).length === 0 ? (
              <p style={{ color: theme.textMuted }}>No quizzes completed yet.</p>
            ) : (
              Object.entries(quizScores).map(([k, v]) => (
                <div key={k} style={{ background: theme.card, borderRadius: '12px', padding: '14px', border: `1px solid ${theme.border}`, marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Volume {k.split('-')[0]}, Day {k.split('-')[1]}</span>
                  <span style={{ fontWeight: '700', color: v.pct >= 80 ? '#22c55e' : v.pct >= 60 ? GMG.gold : '#ef4444' }}>{v.pct}%</span>
                </div>
              ))
            )}
          </div>
        )}

        {/* ============================================================
            V2.3 FEATURE: PRACTICE MODE VIEW
            ============================================================
            Roleplay scenarios where users practice fundraising skills
            with AI personas playing donors, board members, etc.
            ============================================================ */}
        {currentView === 'practice' && !selectedVolume && !founderView && !practiceMode && (
          <div>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: theme.text, marginBottom: '8px' }}>Practice Mode</h2>
            <p style={{ fontSize: '14px', color: theme.textMuted, marginBottom: '24px' }}>Roleplay real fundraising scenarios with AI feedback.</p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {PRACTICE_SCENARIOS.map(scenario => (
                <GlassCard 
                  key={scenario.id}
                  onClick={() => startPracticeScenario(scenario)}
                  style={{ 
                    padding: '16px', 
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                    <div style={{ 
                      width: '48px', 
                      height: '48px', 
                      borderRadius: '12px', 
                      background: `linear-gradient(135deg, ${GMG.roseGold}20, ${GMG.navyBlue}15)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      flexShrink: 0
                    }}>
                      {scenario.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: '600', color: theme.text, margin: 0 }}>{scenario.title}</h3>
                        <span style={{ 
                          padding: '2px 8px', 
                          borderRadius: '6px', 
                          background: scenario.difficulty === 'Beginner' ? '#22c55e20' : scenario.difficulty === 'Intermediate' ? '#f59e0b20' : '#ef444420',
                          color: scenario.difficulty === 'Beginner' ? '#22c55e' : scenario.difficulty === 'Intermediate' ? '#f59e0b' : '#ef4444',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>
                          {scenario.difficulty}
                        </span>
                      </div>
                      <p style={{ fontSize: '13px', color: theme.textMuted, margin: '0 0 8px 0', lineHeight: '1.4' }}>{scenario.setup.substring(0, 100)}...</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '11px', color: theme.textMuted }}>{'\u{23F1}'} {scenario.duration}</span>
                        <span style={{ fontSize: '11px', color: theme.textMuted }}>Playing: {scenario.personaRole}</span>
                      </div>
                    </div>
                    <div style={{ color: theme.textMuted, fontSize: '20px' }}>{'\u{203A}'}</div>
                  </div>
                </GlassCard>
              ))}
            </div>
            
            <div style={{ marginTop: '24px', padding: '16px', borderRadius: '12px', background: `${GMG.navyBlue}08`, border: `1px dashed ${GMG.navyBlue}30` }}>
              <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0, lineHeight: '1.5' }}>
                <strong style={{ color: theme.text }}>How it works:</strong> You'll practice a real fundraising conversation with Claudette or Chanet playing the other person. After a few exchanges, they'll give you specific feedback on what you did well and what to improve.
              </p>
            </div>
          </div>
        )}

        {/* Practice Mode Active Session */}
        {currentView === 'practice' && practiceMode && activeScenario && (
          <div style={{ position: 'fixed', inset: 0, background: theme.bg, zIndex: 150, display: 'flex', flexDirection: 'column' }}>
            <header style={{ 
              padding: '14px 18px', 
              borderBottom: `1px solid ${theme.border}`, 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px', 
              background: darkMode ? 'rgba(27, 42, 78, 0.5)' : 'rgba(255,255,255,0.97)'
            }}>
              <button onClick={endPracticeSession} style={{ width: '40px', height: '40px', borderRadius: '12px', border: 'none', background: `${theme.textMuted}12`, fontSize: '18px', cursor: 'pointer' }}>{'\u{2190}'}</button>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '11px', color: GMG.roseGold, margin: 0, fontWeight: '600' }}>PRACTICE MODE</p>
                <h3 style={{ fontSize: '15px', fontWeight: '600', color: theme.text, margin: 0 }}>{activeScenario.title}</h3>
              </div>
              <img src={activeScenario.persona === 'claudette' ? CLAUDETTE_IMG : CHANET_IMG} alt="" style={{ width: '36px', height: '36px', borderRadius: '50%' }} />
            </header>

            <div style={{ flex: 1, overflow: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', background: darkMode ? 'linear-gradient(180deg, #0f1729 0%, #1B2A4E 100%)' : 'linear-gradient(180deg, #f5f5f5 0%, #fff 100%)' }}>
              {practiceMessages.map((msg, i) => {
                if (msg.speaker === 'system') {
                  return (
                    <div key={i} style={{ background: `${GMG.navyBlue}10`, borderRadius: '12px', padding: '14px', marginBottom: '8px', border: `1px solid ${GMG.navyBlue}20` }}>
                      <p style={{ fontSize: '13px', color: theme.text, margin: 0, whiteSpace: 'pre-line', lineHeight: '1.5' }}>{msg.text}</p>
                    </div>
                  );
                }
                const isUser = msg.speaker === 'user';
                return (
                  <div key={i} style={{ display: 'flex', gap: '8px', flexDirection: isUser ? 'row-reverse' : 'row', alignItems: 'flex-end' }}>
                    {!isUser && <img src={activeScenario.persona === 'claudette' ? CLAUDETTE_IMG : CHANET_IMG} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%' }} />}
                    {isUser && user?.photo && <img src={user.photo} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%' }} />}
                    <div style={{ maxWidth: '75%' }}>
                      {!isUser && msg.role && <span style={{ fontSize: '10px', fontWeight: '600', color: GMG.roseGold, marginLeft: '12px', textTransform: 'uppercase' }}>{msg.role}</span>}
                      <div style={{ padding: '10px 14px', borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px', fontSize: '15px', lineHeight: '1.4', background: isUser ? `linear-gradient(135deg, ${GMG.navyBlue} 0%, #2D4373 100%)` : darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', color: isUser ? '#fff' : theme.text, whiteSpace: 'pre-wrap' }}>{msg.text}</div>
                    </div>
                  </div>
                );
              })}
              {practiceLoading && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                  <img src={activeScenario.persona === 'claudette' ? CLAUDETTE_IMG : CHANET_IMG} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
                  <div style={{ padding: '12px 16px', borderRadius: '18px 18px 18px 4px', background: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
                    <span style={{ display: 'flex', gap: '4px' }}>{[0, 1, 2].map(j => <span key={j} style={{ width: '8px', height: '8px', borderRadius: '50%', background: theme.textMuted, animation: `bounce 1.4s infinite ease-in-out ${j * 0.2}s` }} />)}</span>
                  </div>
                </div>
              )}
            </div>

            <div style={{ padding: '12px 16px', paddingBottom: 'max(12px, env(safe-area-inset-bottom))', borderTop: `1px solid ${theme.border}`, display: 'flex', flexDirection: 'column', gap: '10px', background: darkMode ? 'rgba(27, 42, 78, 0.5)' : 'rgba(250,249,246,0.95)' }}>
              {/* Multiple Choice Options for Elevator Pitch */}
              {activeScenario.responseOptions && practiceMessages.length === 2 && !practiceLoading && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
                  <p style={{ fontSize: '12px', color: theme.textMuted, margin: 0, textAlign: 'center' }}>Choose your response:</p>
                  {activeScenario.responseOptions.map((opt, i) => (
                    <button 
                      key={i} 
                      onClick={() => {
                        // Add user's choice as message
                        setPracticeMessages(prev => [...prev, {
                          speaker: 'user',
                          text: opt.text,
                          timestamp: new Date().toISOString()
                        }]);
                        setPracticeLoading(true);
                        // Show feedback after delay
                        setTimeout(() => {
                          const feedbackStyle = opt.quality === 'strong' ? '\u{2705} Strong!' : opt.quality === 'medium' ? '\u{1F536} Okay, but...' : '\u{274C} Weak';
                          setPracticeMessages(prev => [...prev, {
                            speaker: activeScenario.persona,
                            text: `${feedbackStyle}\n\n${opt.feedback}`,
                            timestamp: new Date().toISOString(),
                            isRoleplay: true,
                            role: activeScenario.personaRole
                          }]);
                          setPracticeLoading(false);
                        }, 1500);
                      }}
                      style={{ 
                        padding: '12px 16px', 
                        borderRadius: '12px', 
                        border: `1px solid ${theme.border}`, 
                        background: theme.card, 
                        color: theme.text, 
                        fontSize: '14px', 
                        textAlign: 'left', 
                        cursor: 'pointer',
                        lineHeight: '1.4'
                      }}
                    >
                      {opt.text}
                    </button>
                  ))}
                </div>
              )}
              {/* Free text input (always available, but MC shown first for elevator pitch) */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && sendPracticeMessage()} placeholder={activeScenario.responseOptions && practiceMessages.length === 2 ? "Or type your own response..." : "Your response..."} disabled={practiceLoading} style={{ flex: 1, padding: '12px 18px', borderRadius: '24px', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, fontSize: '15px', outline: 'none', background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff', color: theme.text }} />
                <button onClick={sendPracticeMessage} disabled={practiceLoading || !chatInput.trim()} style={{ width: '44px', height: '44px', borderRadius: '50%', border: 'none', background: (practiceLoading || !chatInput.trim()) ? `${theme.textMuted}30` : `linear-gradient(135deg, ${GMG.roseGold} 0%, ${GMG.navyBlue} 100%)`, color: '#fff', cursor: (practiceLoading || !chatInput.trim()) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{practiceLoading ? '\u{23F3}' : '\u{2191}'}</button>
              </div>
            </div>
          </div>
        )}

        {/* PROFILE VIEW */}
        {currentView === 'profile' && !selectedVolume && !founderView && (
          <div>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: theme.text, marginBottom: '20px' }}>Profile</h2>
            {user ? (
              <>
                {/* V3.5: Editable Profile Card */}
                <GlassCard style={{ padding: '20px', marginBottom: '20px', textAlign: 'center' }}>
                  <div 
                    onClick={openProfileEditor}
                    style={{ cursor: 'pointer', position: 'relative', display: 'inline-block' }}
                  >
                    {user.photo ? (
                      <img src={user.photo} alt="" style={{ width: '80px', height: '80px', borderRadius: '50%', marginBottom: '12px', border: `3px solid ${GMG.roseGold}40` }} />
                    ) : (
                      <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: `linear-gradient(135deg, ${GMG.roseGold}, ${GMG.navyBlue})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', color: '#fff', marginBottom: '12px', margin: '0 auto 12px' }}>
                        {user.name?.charAt(0)?.toUpperCase() || '\u{1F464}'}
                      </div>
                    )}
                    <div style={{ position: 'absolute', bottom: '8px', right: '-4px', width: '28px', height: '28px', borderRadius: '50%', background: GMG.navyBlue, border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>
                      {'\u{270F}'}
                    </div>
                  </div>
                  <h3 
                    onClick={openProfileEditor}
                    style={{ fontSize: '18px', fontWeight: '600', color: theme.text, margin: 0, cursor: 'pointer' }}
                  >
                    {user.name} <span style={{ fontSize: '12px', color: theme.textMuted }}>{'\u{270F}'}</span>
                  </h3>
                  <p style={{ fontSize: '14px', color: theme.textMuted, margin: '4px 0 12px 0' }}>{user.email}</p>
                  <TrackBadge track={userTrack} />
                  <p style={{ fontSize: '11px', color: theme.textMuted, marginTop: '10px' }}>Tap photo or name to edit</p>
                </GlassCard>
                
                <GlassCard style={{ padding: '16px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: theme.text, margin: 0 }}>Learning Track</p>
                      <p style={{ fontSize: '12px', color: theme.textMuted, margin: '4px 0 0 0' }}>{trackConfig.total} total days</p>
                    </div>
                    <select value={userTrack} onChange={async (e) => { 
                      const newTrack = e.target.value; 
                      setUserTrack(newTrack); 
                      setSyncStatus('syncing');
                      await saveUserData({ track: newTrack }); 
                      setSyncStatus('synced');
                    }} style={{ padding: '8px 12px', borderRadius: '10px', border: `1px solid ${theme.border}`, fontSize: '13px', background: '#fff', cursor: 'pointer' }}>
                      <option value="standard">Standard (1 lesson/day)</option>
                      <option value="fast">Fast Track (2 lessons/day)</option>
                    </select>
                  </div>
                  <p style={{ fontSize: '11px', color: theme.textMuted, marginTop: '8px', fontStyle: 'italic' }}>Both tracks have the same 75 lessons. Fast Track just moves faster.</p>
                </GlassCard>

                <GlassCard style={{ padding: '16px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: theme.text, margin: 0 }}>Current Streak</p>
                      <p style={{ fontSize: '12px', color: theme.textMuted, margin: '4px 0 0 0' }}>Keep it going!</p>
                    </div>
                    <StreakCounter streak={streak} />
                  </div>
                </GlassCard>

                <GlassCard style={{ padding: '16px', marginBottom: '20px' }}>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: theme.text, margin: '0 0 8px 0' }}>Overall Progress</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <ProgressRing progress={progressPct} size={70} strokeWidth={6} />
                    <div>
                      <p style={{ fontSize: '20px', fontWeight: '700', color: theme.accent, margin: 0 }}>{totalLessons}</p>
                      <p style={{ fontSize: '12px', color: theme.textMuted, margin: 0 }}>lessons completed</p>
                    </div>
                  </div>
                </GlassCard>

                {/* Dark Mode Toggle */}
                <GlassCard style={{ padding: '16px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: theme.text, margin: 0 }}>Dark Mode</p>
                      <p style={{ fontSize: '12px', color: theme.textMuted, margin: '4px 0 0 0' }}>Navy theme with bronze accents</p>
                    </div>
                    <button onClick={() => setDarkMode(!darkMode)} style={{ width: '56px', height: '32px', borderRadius: '16px', border: 'none', background: darkMode ? GMG.roseGold : `${theme.textMuted}30`, cursor: 'pointer', position: 'relative', transition: 'background 0.3s ease' }}>
                      <div style={{ position: 'absolute', top: '4px', left: darkMode ? '28px' : '4px', width: '24px', height: '24px', borderRadius: '50%', background: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', transition: 'left 0.3s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>
                        {darkMode ? '\u{1F319}' : '\u{2600}'}
                      </div>
                    </button>
                  </div>
                </GlassCard>

                {/* V3.5: Notifications Settings */}
                <GlassCard style={{ padding: '16px', marginBottom: '16px' }}>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: theme.text, margin: '0 0 12px 0' }}>{'\u{1F514}'} Notifications</p>
                  
                  {/* Browser Notifications */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '12px', borderBottom: `1px solid ${theme.border}` }}>
                    <div>
                      <p style={{ fontSize: '13px', fontWeight: '500', color: theme.text, margin: 0 }}>Browser Notifications</p>
                      <p style={{ fontSize: '11px', color: theme.textMuted, margin: '2px 0 0 0' }}>
                        {notificationPermission === 'granted' ? '\u{2705} Enabled' : 
                         notificationPermission === 'denied' ? '\u{274C} Blocked in browser settings' : 
                         notificationPermission === 'unsupported' ? '\u{26A0} Not supported' : 
                         'Get daily lesson reminders'}
                      </p>
                    </div>
                    {notificationPermission === 'granted' ? (
                      <span style={{ padding: '6px 12px', borderRadius: '8px', background: `${GMG.success}15`, color: GMG.success, fontSize: '12px', fontWeight: '600' }}>{'\u{2705}'} ON</span>
                    ) : notificationPermission === 'denied' ? (
                      <span style={{ padding: '6px 12px', borderRadius: '8px', background: `${GMG.error}15`, color: GMG.error, fontSize: '12px' }}>Blocked</span>
                    ) : notificationPermission !== 'unsupported' && (
                      <button 
                        onClick={requestNotificationPermission}
                        style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', background: GMG.navyBlue, color: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        Enable
                      </button>
                    )}
                  </div>
                  
                  {/* Email Notifications */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ fontSize: '13px', fontWeight: '500', color: theme.text, margin: 0 }}>Email Reminders</p>
                      <p style={{ fontSize: '11px', color: theme.textMuted, margin: '2px 0 0 0' }}>Daily email if you miss a lesson</p>
                    </div>
                    <button 
                      onClick={() => toggleEmailNotifications(!emailNotifications)} 
                      style={{ width: '56px', height: '32px', borderRadius: '16px', border: 'none', background: emailNotifications ? GMG.success : `${theme.textMuted}30`, cursor: 'pointer', position: 'relative', transition: 'background 0.3s ease' }}
                    >
                      <div style={{ position: 'absolute', top: '4px', left: emailNotifications ? '28px' : '4px', width: '24px', height: '24px', borderRadius: '50%', background: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', transition: 'left 0.3s ease' }} />
                    </button>
                  </div>
                  
                  {/* V3.9: Push Notifications */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${theme.border}` }}>
                    <div>
                      <p style={{ fontSize: '13px', fontWeight: '500', color: theme.text, margin: 0 }}>{'\u{1F514}'} Push Notifications</p>
                      <p style={{ fontSize: '11px', color: theme.textMuted, margin: '2px 0 0 0' }}>Daily reminder on your device</p>
                    </div>
                    {pushEnabled ? (
                      <div style={{ padding: '8px 16px', borderRadius: '8px', background: `${GMG.success}15`, color: GMG.success, fontSize: '12px', fontWeight: '600' }}>
                        {'\u{2713}'} Enabled
                      </div>
                    ) : (
                      <button 
                        onClick={enablePushNotifications}
                        style={{ 
                          padding: '8px 16px', 
                          borderRadius: '8px', 
                          border: 'none', 
                          background: `linear-gradient(135deg, ${GMG.navyBlue}, ${GMG.roseGold})`, 
                          color: '#fff', 
                          fontSize: '12px', 
                          fontWeight: '600', 
                          cursor: 'pointer' 
                        }}
                      >
                        Enable
                      </button>
                    )}
                  </div>
                  
                  {/* iOS PWA Warning */}
                  {/iPhone|iPad|iPod/.test(navigator.userAgent) && !window.matchMedia('(display-mode: standalone)').matches && (
                    <div style={{ marginTop: '12px', padding: '10px', borderRadius: '8px', background: `${GMG.warning}10`, border: `1px solid ${GMG.warning}30` }}>
                      <p style={{ fontSize: '11px', color: GMG.warning, margin: 0 }}>{'\u{1F4F1}'} <strong>Tip:</strong> Add to Home Screen for best notification support. Tap Share → Add to Home Screen.</p>
                    </div>
                  )}
                </GlassCard>

                {/* Interactive Scores */}
                {Object.keys(interactiveScores).length > 0 && (
                  <GlassCard style={{ padding: '16px', marginBottom: '16px' }}>
                    <p style={{ fontSize: '14px', fontWeight: '600', color: theme.text, margin: '0 0 12px 0' }}>{'\u{1F4CB}'} Interactive Scores</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {Object.entries(interactiveScores).map(([key, score]) => (
                        <div key={key} style={{ padding: '6px 12px', borderRadius: '8px', background: score.completed ? `${GMG.success}15` : `${GMG.warning}15`, fontSize: '12px', color: score.completed ? GMG.success : GMG.warning }}>
                          V{key.split('-')[0]} D{key.split('-')[1]}: {score.completed ? '\u{2713}' : 'In Progress'}
                        </div>
                      ))}
                    </div>
                  </GlassCard>
                )}

                {/* Reset Progress Section */}
                <GlassCard style={{ padding: '16px', marginBottom: '16px', border: `1px solid ${GMG.error}30` }}>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: theme.text, margin: '0 0 8px 0' }}>{'\u{26A0}'} Reset Progress</p>
                  <p style={{ fontSize: '12px', color: theme.textMuted, margin: '0 0 12px 0' }}>Start over from scratch. You can undo this once.</p>
                  {!showResetConfirm ? (
                    <button onClick={() => setShowResetConfirm(true)} style={{ padding: '10px 16px', borderRadius: '10px', border: `1px solid ${GMG.error}`, background: 'transparent', color: GMG.error, fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                      Reset All Progress
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={resetProgress} style={{ padding: '10px 16px', borderRadius: '10px', border: 'none', background: GMG.error, color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                        Yes, Reset Everything
                      </button>
                      <button onClick={() => setShowResetConfirm(false)} style={{ padding: '10px 16px', borderRadius: '10px', border: `1px solid ${theme.border}`, background: 'transparent', color: theme.text, fontSize: '13px', cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  )}
                </GlassCard>

                {/* Undo Reset Button (only shows after reset) */}
                {lastProgressBackup && (
                  <GlassCard style={{ padding: '16px', marginBottom: '16px', border: `1px solid ${GMG.success}30`, background: `${GMG.success}08` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <p style={{ fontSize: '14px', fontWeight: '600', color: GMG.success, margin: 0 }}>{'\u{21A9}'} Undo Available</p>
                        <p style={{ fontSize: '12px', color: theme.textMuted, margin: '4px 0 0 0' }}>Restore your previous progress</p>
                      </div>
                      <button onClick={undoReset} style={{ padding: '10px 16px', borderRadius: '10px', border: 'none', background: GMG.success, color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                        Undo Reset
                      </button>
                    </div>
                  </GlassCard>
                )}

                {/* V3.3: Check for Updates Button */}
                <GlassCard style={{ padding: '16px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '600', color: theme.text, margin: '0 0 2px 0' }}>{'\u{1F504}'} App Updates</p>
                      <p style={{ fontSize: '12px', color: theme.textMuted, margin: 0 }}>Current version: {APP_VERSION}</p>
                    </div>
                    <button 
                      onClick={() => checkForUpdates(false)}
                      disabled={isCheckingUpdate}
                      style={{ 
                        padding: '10px 16px', 
                        borderRadius: '10px', 
                        border: 'none', 
                        background: isCheckingUpdate ? `${theme.textMuted}50` : `linear-gradient(135deg, ${GMG.navyBlue}, ${GMG.bronze})`, 
                        color: '#fff', 
                        fontSize: '13px', 
                        fontWeight: '600', 
                        cursor: isCheckingUpdate ? 'not-allowed' : 'pointer',
                        opacity: isCheckingUpdate ? 0.7 : 1
                      }}
                    >
                      {isCheckingUpdate ? 'Checking...' : 'Check for Updates'}
                    </button>
                  </div>
                </GlassCard>

                <button onClick={handleSignOut} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: `1px solid ${theme.border}`, background: 'transparent', color: theme.text, fontSize: '15px', cursor: 'pointer' }}>Sign Out</button>
              </>
            ) : (
              <button onClick={signInWithGoogle} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: '#4285f4', color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>Sign in with Google</button>
            )}
          </div>
        )}


        {/* VOLUME VIEW */}
        {selectedVolume && !selectedLesson && !founderView && (
          <div>
            <button onClick={() => { setSelectedVolume(null); setCurrentView('home'); }} style={{ background: 'none', border: 'none', fontSize: '15px', color: theme.accent, marginBottom: '16px', cursor: 'pointer', padding: 0 }}>{'\u{2190}'} Back</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
              <span style={{ fontSize: '40px' }}>{selectedVolume === 1 ? '\u{1F4DA}' : selectedVolume === 2 ? '\u{1F3AF}' : '\u{1F680}'}</span>
              <div>
                <p style={{ fontSize: '11px', color: theme.textMuted, letterSpacing: '1px', margin: 0 }}>VOLUME {selectedVolume}</p>
                <h2 style={{ fontSize: '22px', fontWeight: '600', color: selectedVolume === 1 ? GMG.roseGold : selectedVolume === 2 ? GMG.navyBlue : GMG.bronze, margin: 0 }}>
                  {selectedVolume === 1 ? 'Fundraising Foundations' : selectedVolume === 2 ? 'The GMG Way' : 'CPP Model'}
                </h2>
              </div>
            </div>
            
            {/* Volume Progress Map */}
            <VolumeProgressMap 
              volume={selectedVolume}
              completedDays={completedLessons[selectedVolume] || []}
              totalDays={selectedVolume === 1 ? trackConfig.v1 : selectedVolume === 2 ? trackConfig.v2 : trackConfig.v3}
              color={selectedVolume === 1 ? GMG.roseGold : selectedVolume === 2 ? GMG.navyBlue : GMG.bronze}
              onDayClick={(day) => { 
                // V2.6.5.5: Check if day is unlocked before allowing click
                if (!isDayUnlocked(selectedVolume, day)) {
                  const countdown = getUnlockCountdown();
                  if (countdown) {
                    alert(`This lesson is locked! ${countdown}`);
                  } else {
                    alert('Complete the previous lessons first.');
                  }
                  return;
                }
                setSelectedLesson({ volume: selectedVolume, day }); 
                setQuizMode(false); 
                setShowQuizResult(false); 
                setQuizAnswers([]); 
                setCurrentQuestion(0);
                // V2.6.5: Start lesson in chat mode (unless it's a quiz day)
                if (!isQuizDay(selectedVolume, day)) {
                  startLessonChat(selectedVolume, day);
                }
              }}
              lessonsPerDay={trackConfig.lessonsPerDay}
              isDayUnlockedFn={isDayUnlocked}
              countdownText={getUnlockCountdown()}
            />

            {/* Certificate Button if Volume Complete */}
            {(() => {
              const days = selectedVolume === 1 ? trackConfig.v1 : selectedVolume === 2 ? trackConfig.v2 : trackConfig.v3;
              const isComplete = completedLessons[selectedVolume]?.length >= days;
              if (!isComplete) return null;
              const avgScore = Object.entries(quizScores)
                .filter(([k]) => k.startsWith(`${selectedVolume}-`))
                .reduce((acc, [, v], _, arr) => acc + v.pct / arr.length, 0);
              return (
                <GlassCard glow style={{ padding: '16px', marginBottom: '20px', textAlign: 'center' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>{'\u{1F393}'}</div>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: theme.text, margin: '0 0 4px 0' }}>Volume Complete!</p>
                  <p style={{ fontSize: '12px', color: theme.textMuted, margin: '0 0 12px 0' }}>Average Score: {Math.round(avgScore)}%</p>
                  <button onClick={() => setCertificateModal({ volume: selectedVolume, score: Math.round(avgScore) })} 
                    style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: `linear-gradient(135deg, ${GMG.roseGold}, ${GMG.bronze})`, color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                    {'\u{1F3C6}'} Get Certificate
                  </button>
                </GlassCard>
              );
            })()}

            {getLessons(selectedVolume).slice(0, selectedVolume === 1 ? trackConfig.v1 : selectedVolume === 2 ? trackConfig.v2 : trackConfig.v3).map((title, i) => {
              const dayNum = i + 1;
              const isCompleted = completedLessons[selectedVolume]?.includes(dayNum);
              const isQuiz = isQuizDay(selectedVolume, dayNum);
              const color = selectedVolume === 1 ? GMG.roseGold : selectedVolume === 2 ? GMG.navyBlue : GMG.bronze;
              const hasScore = quizScores[`${selectedVolume}-${dayNum}`];
              const isBookmarked = bookmarks.includes(`${selectedVolume}-${dayNum}`);
              // V3.7: Simple unlock check
              const isLocked = !isDayUnlocked(selectedVolume, dayNum);
              const requiredDay = dayNum > 2 ? dayNum - 2 : null;
              
              return (
                <div 
                  key={dayNum} 
                  onClick={() => { 
                    if (isLocked) {
                      if (requiredDay) {
                        alert(`Complete Day ${requiredDay} to unlock Day ${dayNum}`);
                      }
                      return;
                    }
                    // V3.4: If already completed, ask if they want to unmark or view
                    if (isCompleted) {
                      const action = window.confirm(`Day ${dayNum} is marked complete.\n\nClick OK to unmark (mark as incomplete)\nClick Cancel to view the lesson`);
                      if (action) {
                        userUnmarkDay(selectedVolume, dayNum);
                        return;
                      }
                    }
                    setSelectedLesson({ volume: selectedVolume, day: dayNum }); 
                    setQuizMode(false); setShowQuizResult(false); setQuizAnswers([]); setCurrentQuestion(0);
                    // V2.6.5: Start lesson in chat mode
                    if (!isQuiz) {
                      startLessonChat(selectedVolume, dayNum);
                    }
                  }}
                  style={{ background: theme.card, borderRadius: '14px', padding: '14px', display: 'flex', alignItems: 'center', gap: '12px', cursor: isLocked ? 'not-allowed' : 'pointer', border: `1px solid ${isQuiz ? color + '50' : theme.border}`, marginBottom: '10px', opacity: isLocked ? 0.5 : 1 }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: isCompleted ? color : isLocked ? `${theme.textMuted}10` : `${theme.textMuted}15`, color: isCompleted ? '#fff' : isLocked ? '#999' : theme.text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '15px' }}>
                    {isLocked ? '\u{1F512}' : isQuiz ? '\u{1F4DD}' : dayNum}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', color: isLocked ? '#999' : theme.text, margin: 0 }}>{title}</h4>
                      {isBookmarked && <span style={{ fontSize: '12px' }}>{'\u{2B50}'}</span>}
                    </div>
                    <p style={{ fontSize: '12px', color: theme.textMuted, margin: '2px 0 0 0' }}>
                      {isLocked ? (requiredDay ? `Complete Day ${requiredDay} first` : 'Locked') : isCompleted ? (hasScore ? `Score: ${hasScore.pct}%` : '\u{2713} Complete - tap to undo') : (isQuiz ? '5 questions' : getDuration(selectedVolume, dayNum))}
                    </p>
                  </div>
                  <span style={{ color: isCompleted ? '#22c55e' : isLocked ? '#ccc' : theme.textMuted, fontSize: '18px' }}>{isCompleted ? '\u{2713}' : isLocked ? '' : '\u{2192}'}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* LESSON VIEW */}
        {selectedLesson && !founderView && (
          <div style={{ position: 'fixed', inset: 0, background: theme.bg, zIndex: 150, display: 'flex', flexDirection: 'column' }}>
            
            {/* V2.6.5: iMessage Lesson Chat Interface */}
            {lessonChatMode && !quizMode && !showQuizResult && (
              <>
                <header style={{ 
                  padding: '14px 18px', 
                  borderBottom: `1px solid ${theme.border}`, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px', 
                  background: darkMode ? 'rgba(27, 42, 78, 0.95)' : 'rgba(255,255,255,0.97)'
                }}>
                  <button onClick={() => { 
                    if (isSpeaking) stopSpeaking();
                    setLessonChatMode(false); 
                    setLessonMessages([]); 
                    setSelectedLesson(null); 
                    setCurrentView('home'); 
                  }} style={{ width: '40px', height: '40px', borderRadius: '12px', border: 'none', background: `${theme.textMuted}12`, fontSize: '18px', cursor: 'pointer' }}>{'\u{2190}'}</button>
                  <img 
                    src={currentLessonPersona === 'claudette' ? CLAUDETTE_IMG : CHANET_IMG} 
                    alt="" 
                    style={{ width: '40px', height: '40px', borderRadius: '50%', border: `2px solid ${currentLessonPersona === 'claudette' ? GMG.navyBlue : GMG.roseGold}` }} 
                  />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '15px', fontWeight: '600', color: theme.text, margin: 0 }}>
                      {currentLessonPersona === 'claudette' ? 'Claudette' : 'Chanet'}
                    </p>
                    <p style={{ fontSize: '11px', color: theme.textMuted, margin: 0 }}>
                      Day {selectedLesson.day}: {getContent(selectedLesson.volume, selectedLesson.day).title}
                    </p>
                  </div>
                  {/* Read Aloud Toggle */}
                  <button 
                    onClick={() => {
                      if (isSpeaking) {
                        stopSpeaking();
                        setReadAloudMode(false);
                      } else {
                        setReadAloudMode(!readAloudMode);
                      }
                    }} 
                    style={{ 
                      padding: '8px 12px', 
                      borderRadius: '10px', 
                      border: `1px solid ${readAloudMode || isSpeaking ? GMG.roseGold : theme.border}`, 
                      background: readAloudMode || isSpeaking ? `${GMG.roseGold}15` : 'transparent', 
                      color: readAloudMode || isSpeaking ? GMG.roseGold : theme.textMuted, 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      cursor: 'pointer' 
                    }}
                  >
                    {isSpeaking ? '\u{1F507} Stop' : readAloudMode ? '\u{1F50A} On' : '\u{1F508} Read'}
                  </button>
                </header>

                {/* Chat Messages Area */}
                <div style={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  padding: '16px', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '12px',
                  background: darkMode ? 'linear-gradient(180deg, #0f1729 0%, #1B2A4E 100%)' : 'linear-gradient(180deg, #f5f5f5 0%, #fff 100%)'
                }}>
                  {lessonMessages.map((msg, i) => {
                    const isUser = msg.speaker === 'user';
                    const personaImg = currentLessonPersona === 'claudette' ? CLAUDETTE_IMG : CHANET_IMG;
                    return (
                      <div key={msg.id || `msg-${i}`} style={{ display: 'flex', gap: '8px', flexDirection: isUser ? 'row-reverse' : 'row', alignItems: 'flex-end' }}>
                        {!isUser && <img src={personaImg} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%' }} />}
                        {isUser && user?.photo && <img src={user.photo} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%' }} />}
                        <div style={{ maxWidth: '80%' }}>
                          <div style={{ 
                            padding: msg.isContent ? '14px 16px' : '10px 14px', 
                            borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px', 
                            fontSize: '15px', 
                            lineHeight: '1.5', 
                            background: isUser 
                              ? `linear-gradient(135deg, ${GMG.navyBlue} 0%, #2D4373 100%)` 
                              : msg.isContent 
                                ? (darkMode ? 'rgba(255,255,255,0.12)' : '#fff')
                                : (darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'),
                            color: isUser ? '#fff' : theme.text,
                            whiteSpace: 'pre-wrap',
                            boxShadow: msg.isContent ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                            border: msg.isContent ? `1px solid ${theme.border}` : 'none'
                          }}>
                            {msg.text.split('**').map((part, pi) => 
                              pi % 2 === 1 
                                ? <strong key={pi} style={{ color: isUser ? '#fff' : theme.accent }}>{part}</strong>
                                : <span key={pi}>{part}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Typing Indicator */}
                  {lessonChatLoading && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                      <img src={currentLessonPersona === 'claudette' ? CLAUDETTE_IMG : CHANET_IMG} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
                      <div style={{ padding: '12px 16px', borderRadius: '18px 18px 18px 4px', background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }}>
                        <span style={{ display: 'flex', gap: '4px' }}>
                          {[0, 1, 2].map(j => (
                            <span key={j} style={{ 
                              width: '8px', 
                              height: '8px', 
                              borderRadius: '50%', 
                              background: theme.textMuted, 
                              animation: `bounce 1.4s infinite ease-in-out ${j * 0.2}s` 
                            }} />
                          ))}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* V2.10: Scroll anchor for lesson messages */}
                  <div ref={lessonMessagesEndRef} />
                  
                  {/* V2.6.5.3: Activities now handled through conversational Q&A in chat messages */}
                  
                  {/* Choice Buttons */}
                  {awaitingLessonChoice && !lessonChatLoading && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center', padding: '12px 0' }}>
                      {awaitingLessonChoice === 'start' && (
                        <>
                          <button onClick={() => handleLessonChoice('yes')} style={{ padding: '12px 24px', borderRadius: '20px', border: 'none', background: `linear-gradient(135deg, ${GMG.navyBlue}, ${GMG.navyLight})`, color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                            Yes, let's go!
                          </button>
                          <button onClick={() => handleLessonChoice('no')} style={{ padding: '12px 24px', borderRadius: '20px', border: `1px solid ${theme.border}`, background: 'transparent', color: theme.textMuted, fontSize: '14px', cursor: 'pointer' }}>
                            Not right now
                          </button>
                        </>
                      )}
                      {awaitingLessonChoice === 'continue' && (
                        <>
                          <button onClick={() => handleLessonChoice('continue')} style={{ padding: '12px 24px', borderRadius: '20px', border: 'none', background: `linear-gradient(135deg, ${GMG.navyBlue}, ${GMG.navyLight})`, color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                            Keep going!
                          </button>
                          <button onClick={() => handleLessonChoice('later')} style={{ padding: '12px 24px', borderRadius: '20px', border: `1px solid ${theme.border}`, background: 'transparent', color: theme.textMuted, fontSize: '14px', cursor: 'pointer' }}>
                            Save for later
                          </button>
                        </>
                      )}
                      {awaitingLessonChoice === 'activity' && (
                        <>
                          {isQuizDay(selectedLesson.volume, selectedLesson.day) ? (
                            <button onClick={() => handleLessonChoice('quiz')} style={{ padding: '12px 24px', borderRadius: '20px', border: 'none', background: `linear-gradient(135deg, ${GMG.roseGold}, ${GMG.bronze})`, color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                              {'\u{1F4DD}'} Start the Quiz
                            </button>
                          ) : (
                            <>
                              {/* Check if lesson has interactive activity */}
                              {getContent(selectedLesson.volume, selectedLesson.day).interactive ? (
                                <button onClick={() => handleLessonChoice('doActivity')} style={{ padding: '12px 24px', borderRadius: '20px', border: 'none', background: `linear-gradient(135deg, ${GMG.roseGold}, ${GMG.bronze})`, color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                                  {'\u{1F4CB}'} Do Activity
                                </button>
                              ) : (
                                <button onClick={() => handleLessonChoice('complete')} style={{ padding: '12px 24px', borderRadius: '20px', border: 'none', background: `linear-gradient(135deg, ${GMG.success}, #16a34a)`, color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                                  {'\u{2713}'} Mark Complete
                                </button>
                              )}
                            </>
                          )}
                          <button onClick={() => handleLessonChoice('later')} style={{ padding: '12px 24px', borderRadius: '20px', border: `1px solid ${theme.border}`, background: 'transparent', color: theme.textMuted, fontSize: '14px', cursor: 'pointer' }}>
                            Come back later
                          </button>
                        </>
                      )}
                      {awaitingLessonChoice === 'afterActivity' && (
                        <>
                          <button onClick={() => handleLessonChoice('complete')} style={{ padding: '12px 24px', borderRadius: '20px', border: 'none', background: `linear-gradient(135deg, ${GMG.success}, #16a34a)`, color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                            {'\u{2713}'} Mark Complete
                          </button>
                          <button onClick={() => handleLessonChoice('later')} style={{ padding: '12px 24px', borderRadius: '20px', border: `1px solid ${theme.border}`, background: 'transparent', color: theme.textMuted, fontSize: '14px', cursor: 'pointer' }}>
                            Come back later
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Chat Input */}
                <div style={{ 
                  padding: '12px 16px', 
                  paddingBottom: 'max(12px, env(safe-area-inset-bottom))', 
                  borderTop: `1px solid ${theme.border}`, 
                  display: 'flex', 
                  gap: '10px', 
                  background: darkMode ? 'rgba(27, 42, 78, 0.95)' : 'rgba(250,249,246,0.95)' 
                }}>
                  <input 
                    type="text" 
                    value={lessonChatInput} 
                    onChange={e => setLessonChatInput(e.target.value)} 
                    onKeyPress={e => e.key === 'Enter' && sendLessonChatMessage()} 
                    placeholder={activityMode ? "Type A, B, C, or D..." : "Ask a question..."} 
                    disabled={lessonChatLoading} 
                    style={{ 
                      flex: 1, 
                      padding: '12px 18px', 
                      borderRadius: '24px', 
                      border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, 
                      fontSize: '15px', 
                      outline: 'none', 
                      background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff', 
                      color: theme.text 
                    }} 
                  />
                  <button 
                    onClick={sendLessonChatMessage} 
                    disabled={lessonChatLoading || !lessonChatInput.trim()} 
                    style={{ 
                      width: '44px', 
                      height: '44px', 
                      borderRadius: '50%', 
                      border: 'none', 
                      background: (lessonChatLoading || !chatInput.trim()) ? `${theme.textMuted}30` : `linear-gradient(135deg, ${GMG.roseGold} 0%, ${GMG.navyBlue} 100%)`, 
                      color: '#fff', 
                      cursor: (lessonChatLoading || !chatInput.trim()) ? 'not-allowed' : 'pointer', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontSize: '18px'
                    }}
                  >
                    {lessonChatLoading ? '\u{23F3}' : '\u{2191}'}
                  </button>
                </div>
              </>
            )}

            {/* Original Static Lesson View (fallback/quiz mode) */}
            {(!lessonChatMode || quizMode || showQuizResult) && (
            <>
            <header style={{ padding: '14px 18px', borderBottom: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(255,255,255,0.97)' }}>
              <button onClick={() => { setSelectedLesson(null); setQuizMode(false); setShowQuizResult(false); }} style={{ width: '40px', height: '40px', borderRadius: '12px', border: 'none', background: `${theme.textMuted}12`, fontSize: '18px', cursor: 'pointer' }}>{'\u{2190}'}</button>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '11px', color: theme.textMuted, margin: 0 }}>DAY {selectedLesson.day}</p>
                <h3 style={{ fontSize: '15px', fontWeight: '600', color: theme.text, margin: 0 }}>{isQuizDay(selectedLesson.volume, selectedLesson.day) ? 'Quiz' : getContent(selectedLesson.volume, selectedLesson.day).title}</h3>
              </div>
              <button onClick={() => { setSelectedLesson(null); setSelectedVolume(null); setCurrentView('home'); }} style={{ padding: '8px 12px', borderRadius: '10px', border: `1px solid ${theme.border}`, background: 'transparent', fontSize: '12px', fontWeight: '500', cursor: 'pointer', color: theme.textMuted }}>{'\u{1F3E0}'} Home</button>
              {!isQuizDay(selectedLesson.volume, selectedLesson.day) && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  {/* V2.3: Audio Narration Toggle */}
                  <button onClick={() => { 
                    if (isSpeaking) { 
                      stopSpeaking(); 
                    } else { 
                      const c = getContent(selectedLesson.volume, selectedLesson.day); 
                      const sectionText = c.sections ? c.sections.map(s => (s.h || '') + '. ' + (s.c || '')).join('. ') : '';
                      const fullText = (c.title || '') + '. ' + sectionText;
                      speakText(fullText.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ')); 
                    }
                  }} style={{ width: '40px', height: '40px', borderRadius: '10px', border: `1px solid ${theme.border}`, background: isSpeaking ? `${GMG.roseGold}20` : 'transparent', fontSize: '18px', cursor: 'pointer' }}>{isSpeaking ? '\u23F9' : '\u{1F50A}'}</button>
                  <button onClick={() => { const key = `${selectedLesson.volume}-${selectedLesson.day}`; const newBM = bookmarks.includes(key) ? bookmarks.filter(b => b !== key) : [...bookmarks, key]; setBookmarks(newBM); saveUserData({ bookmarks: newBM }); }} style={{ width: '40px', height: '40px', borderRadius: '10px', border: `1px solid ${theme.border}`, background: bookmarks.includes(`${selectedLesson.volume}-${selectedLesson.day}`) ? `${GMG.gold}20` : 'transparent', fontSize: '18px', cursor: 'pointer' }}>{bookmarks.includes(`${selectedLesson.volume}-${selectedLesson.day}`) ? '\u2B50' : '\u2606'}</button>
                  <button onClick={() => { setChatOpen(true); setChatInput(`I'm studying Day ${selectedLesson.day}: ${getContent(selectedLesson.volume, selectedLesson.day).title}. `); }} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '10px', border: 'none', background: `linear-gradient(135deg, ${GMG.navyBlue}, ${GMG.navyLight})`, color: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer', boxShadow: `0 2px 8px ${GMG.navyBlue}40` }}><img src={CLAUDETTE_IMG} alt="" style={{ width: '20px', height: '20px', borderRadius: '50%' }} />Ask Claudette</button>
                </div>
              )}
            </header>

            <div style={{ flex: 1, overflow: 'auto', padding: '20px 18px', paddingBottom: '140px' }}>
              {isQuizDay(selectedLesson.volume, selectedLesson.day) ? (
                /* QUIZ MODE */
                <div>
                  {!quizMode && !showQuizResult && (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <div style={{ fontSize: '64px', marginBottom: '16px' }}>{'\u{1F4DD}'}</div>
                      <h2 style={{ fontSize: '22px', fontWeight: '600', color: theme.text, marginBottom: '8px' }}>Ready for Your Quiz?</h2>
                      <p style={{ fontSize: '14px', color: theme.textMuted, marginBottom: '24px' }}>5 questions, no retakes allowed</p>
                      {quizScores[`${selectedLesson.volume}-${selectedLesson.day}`] ? (
                        <div style={{ background: theme.card, borderRadius: '16px', padding: '24px', border: `1px solid ${theme.border}` }}>
                          <p style={{ fontSize: '14px', color: theme.textMuted, margin: '0 0 8px 0' }}>Your Score</p>
                          <p style={{ fontSize: '48px', fontWeight: '700', color: quizScores[`${selectedLesson.volume}-${selectedLesson.day}`].pct >= 80 ? '#22c55e' : theme.accent, margin: 0 }}>
                            {quizScores[`${selectedLesson.volume}-${selectedLesson.day}`].pct}%
                          </p>
                        </div>
                      ) : (
                        <button onClick={() => { setQuizMode(true); setCurrentQuestion(0); setQuizAnswers([]); }} style={{ padding: '16px 48px', borderRadius: '12px', border: 'none', background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentAlt})`, color: '#fff', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}>
                          Start Quiz {'\u{2192}'}
                        </button>
                      )}
                    </div>
                  )}
                  {quizMode && !showQuizResult && getQuiz(selectedLesson.volume, selectedLesson.day) && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <span style={{ fontSize: '13px', color: theme.textMuted }}>Question {currentQuestion + 1} of 5</span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {[0,1,2,3,4].map(i => (
                            <div key={i} style={{ width: '10px', height: '10px', borderRadius: '50%', background: i < currentQuestion ? (quizAnswers[i]?.isCorrect ? '#22c55e' : '#ef4444') : i === currentQuestion ? theme.accent : `${theme.textMuted}30` }} />
                          ))}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                        <img src={getQuiz(selectedLesson.volume, selectedLesson.day).questions[currentQuestion].speaker === 'claudette' ? CLAUDETTE_IMG : CHANET_IMG} alt="" style={{ width: '44px', height: '44px', borderRadius: '50%', border: `3px solid ${getQuiz(selectedLesson.volume, selectedLesson.day).questions[currentQuestion].speaker === 'claudette' ? GMG.navyBlue : GMG.roseGold}` }} />
                        <div style={{ flex: 1 }}>
                          <p style={{ fontSize: '12px', fontWeight: '700', color: getQuiz(selectedLesson.volume, selectedLesson.day).questions[currentQuestion].speaker === 'claudette' ? GMG.navyBlue : GMG.roseGold, margin: '0 0 4px 0', letterSpacing: '0.5px' }}>
                            {getQuiz(selectedLesson.volume, selectedLesson.day).questions[currentQuestion].speaker === 'claudette' ? 'CLAUDETTE ASKS' : 'CHANET ASKS'}
                          </p>
                          <div style={{ position: 'relative', background: getQuiz(selectedLesson.volume, selectedLesson.day).questions[currentQuestion].speaker === 'claudette' ? `${GMG.navyBlue}12` : `${GMG.roseGold}12`, borderRadius: '16px', borderTopLeftRadius: '4px', padding: '16px 18px', border: `2px solid ${getQuiz(selectedLesson.volume, selectedLesson.day).questions[currentQuestion].speaker === 'claudette' ? GMG.navyBlue : GMG.roseGold}30` }}>
                            <p style={{ fontSize: '16px', fontWeight: '500', color: theme.text, margin: 0, lineHeight: 1.5 }}>{getQuiz(selectedLesson.volume, selectedLesson.day).questions[currentQuestion].q}</p>
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {getQuiz(selectedLesson.volume, selectedLesson.day).questions[currentQuestion].opts.map((opt, i) => {
                          const letter = opt[0];
                          const answered = quizAnswers[currentQuestion];
                          const isSelected = answered?.selected === letter;
                          const isCorrect = answered?.correct === letter;
                          return (
                            <button key={i} onClick={() => !answered && answerQuestion(letter)} disabled={!!answered}
                              style={{ padding: '16px', borderRadius: '12px', border: `2px solid ${answered ? (isCorrect ? '#22c55e' : isSelected ? '#ef4444' : theme.border) : theme.border}`, background: answered ? (isCorrect ? '#22c55e15' : isSelected ? '#ef444415' : theme.card) : theme.card, textAlign: 'left', fontSize: '14px', color: theme.text, cursor: answered ? 'default' : 'pointer' }}>
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {showQuizResult && (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <Confetti active={quizAnswers.filter(a => a.isCorrect).length >= 4} />
                      <div style={{ fontSize: '64px', marginBottom: '16px' }}>{quizAnswers.filter(a => a.isCorrect).length >= 4 ? '\u{1F389}' : '\u{1F4DA}'}</div>
                      <h2 style={{ fontSize: '22px', fontWeight: '600', color: theme.text, marginBottom: '8px' }}>Quiz Complete!</h2>
                      <p style={{ fontSize: '48px', fontWeight: '700', color: theme.accent, margin: '16px 0' }}>{Math.round((quizAnswers.filter(a => a.isCorrect).length / 5) * 100)}%</p>
                      <p style={{ fontSize: '14px', color: theme.textMuted, marginBottom: '24px' }}>{quizAnswers.filter(a => a.isCorrect).length} of 5 correct</p>
                      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button onClick={() => setQuizReviewMode(`${selectedLesson.volume}-${selectedLesson.day}`)} style={{ padding: '14px 24px', borderRadius: '12px', border: `2px solid ${theme.accent}`, background: 'transparent', color: theme.accent, fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>{'\u{1F4DD}'} Review Answers</button>
                        <button onClick={() => navigateLesson(1)} style={{ padding: '14px 32px', borderRadius: '12px', border: 'none', background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentAlt})`, color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>Continue {'\u{2192}'}</button>
                      </div>
                    </div>
                  )}
                  {quizReviewMode === `${selectedLesson.volume}-${selectedLesson.day}` && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', color: theme.text, margin: 0 }}>{'\u{1F4DD}'} Quiz Review</h3>
                        <button onClick={() => setQuizReviewMode(null)} style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: 'transparent', fontSize: '13px', cursor: 'pointer' }}>Close Review</button>
                      </div>
                      {getQuiz(selectedLesson.volume, selectedLesson.day)?.questions.map((q, idx) => {
                        const userAnswer = quizScores[`${selectedLesson.volume}-${selectedLesson.day}`]?.answers?.[idx] || quizAnswers[idx];
                        const isCorrect = userAnswer?.isCorrect;
                        return (
                          <div key={idx} style={{ background: isCorrect ? '#22c55e10' : '#ef444410', borderRadius: '14px', padding: '16px', marginBottom: '14px', border: `2px solid ${isCorrect ? '#22c55e30' : '#ef444430'}` }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                              <span style={{ fontSize: '18px' }}>{isCorrect ? '\u{2705}' : '\u{274C}'}</span>
                              <span style={{ fontSize: '13px', fontWeight: '600', color: isCorrect ? '#22c55e' : '#ef4444' }}>Question {idx + 1}</span>
                            </div>
                            <p style={{ fontSize: '14px', fontWeight: '500', color: theme.text, margin: '0 0 12px 0' }}>{q.q}</p>
                            <p style={{ fontSize: '13px', color: theme.textMuted, margin: '0 0 4px 0' }}>Your answer: <span style={{ color: isCorrect ? '#22c55e' : '#ef4444', fontWeight: '600' }}>{userAnswer?.selected || '?'}</span></p>
                            {!isCorrect && <p style={{ fontSize: '13px', color: '#22c55e', margin: 0 }}>Correct answer: <span style={{ fontWeight: '600' }}>{q.a}</span></p>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                /* LESSON CONTENT */
                <div>
                  {(() => {
                    const content = getContent(selectedLesson.volume, selectedLesson.day);
                    const volColor = selectedLesson.volume === 1 ? GMG.roseGold : selectedLesson.volume === 2 ? GMG.navyBlue : GMG.bronze;
                    return (
                      <>
                        <div style={{ background: `linear-gradient(135deg, ${volColor}20, ${volColor}08)`, borderRadius: '16px', padding: '20px', marginBottom: '24px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: volColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: '700' }}>{selectedLesson.day}</div>
                            <div>
                              <p style={{ fontSize: '11px', color: theme.textMuted, margin: 0 }}>VOLUME {selectedLesson.volume}</p>
                              <h1 style={{ fontSize: '18px', fontWeight: '700', color: theme.text, margin: '4px 0 0 0' }}>{content.title}</h1>
                            </div>
                          </div>
                        </div>

                        {/* V3.8: Video Lesson (if available) */}
                        {content.videoUrl && (
                          <div style={{ marginBottom: '24px', borderRadius: '16px', overflow: 'hidden', background: '#000' }}>
                            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                              <iframe
                                src={content.videoUrl}
                                title={content.title}
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  border: 'none'
                                }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                            <div style={{ padding: '12px 16px', background: theme.card }}>
                              <p style={{ fontSize: '12px', color: theme.textMuted, margin: 0 }}>
                                {'\u{1F4F9}'} Video lesson available. Watch above, then continue reading below.
                              </p>
                            </div>
                          </div>
                        )}

                        {content.sections?.map((section, idx) => (
                          <div key={idx} style={{ marginBottom: '28px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                              <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: `${volColor}20`, color: volColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700' }}>{idx + 1}</div>
                              <h2 style={{ fontSize: '16px', fontWeight: '600', color: theme.text, margin: 0 }}>{section.h}</h2>
                            </div>
                            {section.c.split('\n\n').map((p, pIdx) => (
                              <p key={pIdx} style={{ fontSize: '15px', lineHeight: 1.75, color: theme.text, marginBottom: '14px' }}>{p}</p>
                            ))}
                          </div>
                        ))}

                        {/* Interactive Element - Hidden behind Start button to prevent cheating */}
                        {content.interactive && (
                          <div style={{ background: darkMode ? `${GMG.bronze}15` : `${GMG.navyBlue}08`, borderRadius: '16px', padding: '20px', marginBottom: '24px', border: `1px solid ${darkMode ? GMG.bronze : GMG.navyBlue}20` }}>
                            <h3 style={{ fontSize: '15px', fontWeight: '600', color: darkMode ? GMG.bronze : GMG.navyBlue, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>{'\u{1F4CB}'}</span> {content.interactive.title}
                              {interactiveScores[`${selectedLesson.volume}-${selectedLesson.day}`]?.completed && <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '6px', background: `${GMG.success}20`, color: GMG.success }}>{'\u{2713}'} Completed</span>}
                            </h3>
                            
                            {!showActivityModal && !interactiveScores[`${selectedLesson.volume}-${selectedLesson.day}`]?.completed ? (
                              <div style={{ textAlign: 'center', padding: '30px', background: `linear-gradient(135deg, ${GMG.navyBlue}15, ${GMG.roseGold}10)`, borderRadius: '16px' }}>
                                <div style={{ fontSize: '48px', marginBottom: '12px' }}>{'\u{1F3AF}'}</div>
                                <h4 style={{ fontSize: '18px', fontWeight: '700', color: theme.text, marginBottom: '8px' }}>Ready to Test Your Knowledge?</h4>
                                <p style={{ fontSize: '14px', color: theme.textMuted, marginBottom: '20px' }}>Complete this activity based on what you just learned.</p>
                                <button onClick={() => setShowActivityModal(true)} style={{ padding: '16px 40px', borderRadius: '14px', border: 'none', background: `linear-gradient(135deg, ${GMG.roseGold}, ${GMG.bronze})`, color: '#fff', fontSize: '16px', fontWeight: '700', cursor: 'pointer', boxShadow: `0 4px 15px ${GMG.roseGold}40` }}>
                                  {'\u{1F680}'} START ACTIVITY
                                </button>
                              </div>
                            ) : (
                              <>
                                {showActivityModal && (
                                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                                    <div style={{ background: theme.bg, borderRadius: '20px', padding: '24px', maxWidth: '500px', width: '100%', maxHeight: '80vh', overflow: 'auto' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                        <h3 style={{ fontSize: '18px', fontWeight: '600', color: theme.text, margin: 0 }}>{'\u{1F4CB}'} {content.interactive.title}</h3>
                                        <button onClick={() => setShowActivityModal(false)} style={{ width: '36px', height: '36px', borderRadius: '50%', border: 'none', background: `${theme.textMuted}20`, fontSize: '18px', cursor: 'pointer' }}>{'\u{2715}'}</button>
                                      </div>
                                      {content.interactive.type === 'matching' && <MatchingGame pairs={content.interactive.pairs} onComplete={() => {
                                        const key = `${selectedLesson.volume}-${selectedLesson.day}`;
                                        const newScores = { ...interactiveScores, [key]: { completed: true, type: 'matching', date: new Date().toISOString() } };
                                        setInteractiveScores(newScores);
                                        saveUserData({ interactiveScores: newScores });
                                        setTimeout(() => setShowActivityModal(false), 1500);
                                      }} />}
                                      {content.interactive.type === 'slider' && <SliderGame answer={content.interactive.answer} tolerance={content.interactive.tolerance} onComplete={() => {
                                        const key = `${selectedLesson.volume}-${selectedLesson.day}`;
                                        const newScores = { ...interactiveScores, [key]: { completed: true, type: 'slider', date: new Date().toISOString() } };
                                        setInteractiveScores(newScores);
                                        saveUserData({ interactiveScores: newScores });
                                        setTimeout(() => setShowActivityModal(false), 1500);
                                      }} />}
                                      {content.interactive.type === 'sorting' && <SortingGame items={content.interactive.items} onComplete={() => {
                                        const key = `${selectedLesson.volume}-${selectedLesson.day}`;
                                        const newScores = { ...interactiveScores, [key]: { completed: true, type: 'sorting', date: new Date().toISOString() } };
                                        setInteractiveScores(newScores);
                                        saveUserData({ interactiveScores: newScores });
                                        setTimeout(() => setShowActivityModal(false), 1500);
                                      }} />}
                                    </div>
                                  </div>
                                )}
                                {interactiveScores[`${selectedLesson.volume}-${selectedLesson.day}`]?.completed && (
                                  <div style={{ textAlign: 'center', padding: '20px', background: `${GMG.success}10`, borderRadius: '12px' }}>
                                    <span style={{ fontSize: '32px' }}>{'\u{2705}'}</span>
                                    <p style={{ fontSize: '14px', color: GMG.success, fontWeight: '600', margin: '8px 0 0 0' }}>Activity Completed!</p>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                        {/* Key Takeaways */}
                        <div style={{ background: theme.card, borderRadius: '16px', padding: '20px', border: `1px solid ${theme.border}` }}>
                          <h3 style={{ fontSize: '15px', fontWeight: '600', color: theme.text, marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>{'\u{1F4A1}'}</span> Key Takeaways
                          </h3>
                          {content.keyTakeaways?.map((t, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: volColor, marginTop: '8px', flexShrink: 0 }} />
                              <p style={{ fontSize: '14px', lineHeight: 1.6, color: theme.text, margin: 0 }}>{t}</p>
                            </div>
                          ))}
                        </div>
                        
                        {/* V3.8: Discussion Thread */}
                        <div style={{ background: theme.card, borderRadius: '16px', padding: '20px', border: `1px solid ${theme.border}`, marginTop: '16px' }}>
                          <div 
                            onClick={() => {
                              if (!showDiscussion) {
                                loadDiscussionComments(selectedLesson.volume, selectedLesson.day);
                              }
                              setShowDiscussion(!showDiscussion);
                            }}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                          >
                            <h3 style={{ fontSize: '15px', fontWeight: '600', color: theme.text, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>{'\u{1F4AC}'}</span> Discussion
                              {discussionComments.length > 0 && (
                                <span style={{ background: GMG.roseGold, color: '#fff', fontSize: '11px', padding: '2px 8px', borderRadius: '10px' }}>
                                  {discussionComments.length}
                                </span>
                              )}
                            </h3>
                            <span style={{ color: theme.textMuted, fontSize: '18px' }}>{showDiscussion ? '\u{25B2}' : '\u{25BC}'}</span>
                          </div>
                          
                          {showDiscussion && (
                            <div style={{ marginTop: '16px' }}>
                              {/* Comment Input */}
                              <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                                <input
                                  type="text"
                                  value={discussionInput}
                                  onChange={(e) => setDiscussionInput(e.target.value)}
                                  placeholder="Share your thoughts..."
                                  style={{
                                    flex: 1,
                                    padding: '12px 16px',
                                    borderRadius: '12px',
                                    border: `1px solid ${theme.border}`,
                                    background: theme.bg,
                                    color: theme.text,
                                    fontSize: '14px'
                                  }}
                                  onKeyPress={(e) => e.key === 'Enter' && postComment()}
                                />
                                <button
                                  onClick={postComment}
                                  disabled={!discussionInput.trim()}
                                  style={{
                                    padding: '12px 16px',
                                    borderRadius: '12px',
                                    border: 'none',
                                    background: discussionInput.trim() ? GMG.navyBlue : `${theme.textMuted}30`,
                                    color: '#fff',
                                    fontSize: '14px',
                                    cursor: discussionInput.trim() ? 'pointer' : 'not-allowed'
                                  }}
                                >
                                  Post
                                </button>
                              </div>
                              
                              {/* Comments List */}
                              {discussionComments.length === 0 ? (
                                <p style={{ fontSize: '13px', color: theme.textMuted, textAlign: 'center', padding: '20px 0' }}>
                                  No comments yet. Be the first to share your thoughts!
                                </p>
                              ) : (
                                discussionComments.map(comment => (
                                  <div key={comment.id} style={{ padding: '12px 0', borderBottom: `1px solid ${theme.border}` }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                      {comment.authorPhoto ? (
                                        <img src={comment.authorPhoto} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} />
                                      ) : (
                                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: `linear-gradient(135deg, ${GMG.roseGold}, ${GMG.navyBlue})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '14px', fontWeight: '600' }}>
                                          {comment.authorName?.charAt(0)?.toUpperCase() || '?'}
                                        </div>
                                      )}
                                      <div style={{ flex: 1 }}>
                                        <p style={{ fontSize: '13px', fontWeight: '600', color: theme.text, margin: 0 }}>{comment.authorName}</p>
                                        <p style={{ fontSize: '11px', color: theme.textMuted, margin: 0 }}>
                                          {comment.timestamp?.toDate ? new Date(comment.timestamp.toDate()).toLocaleDateString() : 'Just now'}
                                        </p>
                                      </div>
                                      <button
                                        onClick={() => likeComment(comment.id)}
                                        style={{
                                          background: 'transparent',
                                          border: 'none',
                                          cursor: 'pointer',
                                          fontSize: '14px',
                                          color: comment.likedBy?.includes(user?.uid) ? GMG.roseGold : theme.textMuted
                                        }}
                                      >
                                        {comment.likedBy?.includes(user?.uid) ? '\u{2764}' : '\u{1F90D}'} {comment.likes || 0}
                                      </button>
                                    </div>
                                    <p style={{ fontSize: '14px', color: theme.text, margin: 0, lineHeight: 1.5 }}>{comment.text}</p>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Bottom Navigation for Lessons */}
            {!quizMode && !showQuizResult && !isQuizDay(selectedLesson.volume, selectedLesson.day) && !lessonChatMode && (
              <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '14px 18px', background: 'rgba(255,255,255,0.97)', borderTop: `1px solid ${theme.border}` }}>
                <div style={{ display: 'flex', gap: '12px', maxWidth: '500px', margin: '0 auto' }}>
                  <button onClick={() => navigateLesson(-1)} style={{ flex: 1, padding: '14px', borderRadius: '12px', border: `1px solid ${theme.border}`, background: 'transparent', color: theme.text, fontSize: '14px', cursor: 'pointer' }}>{'\u{2190}'} Prev</button>
                  {completedLessons[selectedLesson.volume]?.includes(selectedLesson.day) ? (
                    <button onClick={uncompleteLesson} style={{ flex: 2, padding: '14px', borderRadius: '12px', border: `2px solid ${GMG.success}`, background: `${GMG.success}15`, color: GMG.success, fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                      {'\u{2713}'} Complete (tap to undo)
                    </button>
                  ) : (
                    <button onClick={completeLesson} style={{ flex: 2, padding: '14px', borderRadius: '12px', border: 'none', background: `linear-gradient(135deg, ${selectedLesson.volume === 1 ? GMG.roseGold : selectedLesson.volume === 2 ? GMG.navyBlue : GMG.bronze}, ${selectedLesson.volume === 1 ? GMG.roseGoldLight : selectedLesson.volume === 2 ? GMG.navyLight : GMG.gold})`, color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                      Mark Complete
                    </button>
                  )}
                  <button onClick={() => navigateLesson(1)} style={{ flex: 1, padding: '14px', borderRadius: '12px', border: `1px solid ${theme.border}`, background: 'transparent', color: theme.text, fontSize: '14px', cursor: 'pointer' }}>Next {'\u{2192}'}</button>
                </div>
              </div>
            )}
            </>
            )}
          </div>
        )}
      </main>

      {/* Bottom Tab Bar - V2.1: Hides when keyboard open, iOS safe area */}
      {/* V2.5: Also hides during active practice mode */}
      {!selectedLesson && !founderView && !keyboardOpen && !practiceMode && (
        <nav style={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          display: 'flex', 
          justifyContent: 'space-around', 
          padding: '10px 0 20px 0',
          paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
          background: darkMode ? 'rgba(15, 23, 41, 0.97)' : 'rgba(255,255,255,0.97)', 
          borderTop: `1px solid ${theme.border}`, 
          zIndex: Z_INDEX.nav
        }}>
          {[
            { id: 'home', icon: '\u{1F3E0}', label: 'Home' }, 
            { id: 'practice', icon: '\u{1F3AF}', label: 'Practice' },
            { id: 'progress', icon: '\u{1F4CA}', label: 'Progress' }, 
            { id: 'profile', icon: '\u{1F464}', label: 'Profile' }
          ].map(tab => {
            const isLocked = !user && tab.id !== 'home';
            return (
              <button 
                key={tab.id} 
                onClick={() => { 
                  if (isLocked) return; // Block if not signed in
                  setCurrentView(tab.id); 
                  setSelectedVolume(null); 
                  setPracticeMode(false); 
                  setActiveScenario(null); 
                }} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px', 
                  color: isLocked ? `${theme.textMuted}50` : (currentView === tab.id ? theme.accent : theme.textMuted), 
                  cursor: isLocked ? 'not-allowed' : 'pointer', 
                  padding: '4px 8px',
                  opacity: isLocked ? 0.4 : 1
                }}
              >
                <span style={{ fontSize: '22px' }}>{isLocked ? '\u{1F512}' : tab.icon}</span>
                <span style={{ fontSize: '11px', fontWeight: currentView === tab.id ? '600' : '400' }}>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      )}

      {/* Chat FAB - V2.1: Hides when keyboard open, uses Z_INDEX constant */}
      {!selectedLesson && !founderView && !keyboardOpen && (
        <button onClick={() => setChatOpen(true)} style={{ position: 'fixed', bottom: '100px', right: '20px', width: '56px', height: '56px', borderRadius: '50%', background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentAlt})`, border: 'none', fontSize: '24px', cursor: 'pointer', zIndex: Z_INDEX.fab, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>{'\u{1F4AC}'}</button>
      )}

      {/* ============================================================
          V2.2 FEATURE: iMessage-Style Chat Panel
          ============================================================
          REPLACES: V2.1 basic chat panel
          
          KEY CHANGES:
          - User messages on right with Google photo and blue bubbles
          - AI messages on left with persona photos and gray bubbles
          - True iMessage bubble styling (rounded corners specific to sender)
          - Timestamps shown on messages
          - Smooth scroll to bottom on new messages
          - Better visual hierarchy
          
          BUBBLE STYLING:
          - User: Blue/navy background, white text, rounded corners 18px 18px 4px 18px
          - AI: Gray/card background, dark text, rounded corners 18px 18px 18px 4px
          ============================================================ */}
      {chatOpen && (
        <div 
          onClick={() => setChatOpen(false)} 
          style={{ 
            position: 'fixed', 
            inset: 0, 
            background: 'rgba(0,0,0,0.5)', 
            zIndex: Z_INDEX.chat, 
            display: 'flex', 
            alignItems: 'flex-end', 
            justifyContent: 'center',
            backdropFilter: 'blur(4px)'
          }}
        >
          <div 
            onClick={e => e.stopPropagation()} 
            style={{ 
              width: '100%', 
              maxWidth: '500px', 
              height: '80vh', 
              background: darkMode ? '#0f1729' : '#fff', 
              borderRadius: '24px 24px 0 0', 
              display: 'flex', 
              flexDirection: 'column',
              boxShadow: '0 -10px 40px rgba(0,0,0,0.2)'
            }}
          >
            {/* Chat Header - iMessage style */}
            <div style={{ 
              padding: '16px 18px', 
              borderBottom: `1px solid ${theme.border}`, 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              background: darkMode ? 'rgba(27, 42, 78, 0.5)' : 'rgba(250,249,246,0.9)'
            }}>
              <div style={{ display: 'flex', position: 'relative' }}>
                <img src={CLAUDETTE_IMG} alt="" style={{ width: '40px', height: '40px', borderRadius: '50%', border: `2px solid ${GMG.navyBlue}` }} />
                <img src={CHANET_IMG} alt="" style={{ width: '40px', height: '40px', borderRadius: '50%', marginLeft: '-12px', border: `2px solid ${GMG.roseGold}` }} />
                <span style={{ 
                  position: 'absolute', 
                  bottom: 0, 
                  right: 0, 
                  width: '12px', 
                  height: '12px', 
                  background: '#22c55e', 
                  borderRadius: '50%', 
                  border: '2px solid #fff' 
                }} />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: theme.text, margin: 0 }}>Claudette & Chanet</h3>
                <p style={{ fontSize: '12px', color: theme.textMuted, margin: 0 }}>
                  {chatLoading ? 'typing...' : `Online • ${messages.length} messages`}
                </p>
              </div>
              {/* V3.8: Clear Chat button */}
              <button 
                onClick={clearChatHistory}
                title="Clear chat history"
                style={{ 
                  width: '36px', 
                  height: '36px', 
                  borderRadius: '50%', 
                  border: 'none', 
                  background: `${theme.textMuted}15`, 
                  fontSize: '14px', 
                  cursor: 'pointer',
                  color: theme.textMuted,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '8px'
                }}
              >
                {'\u{1F5D1}'}
              </button>
              <button 
                onClick={() => setChatOpen(false)} 
                style={{ 
                  width: '36px', 
                  height: '36px', 
                  borderRadius: '50%', 
                  border: 'none', 
                  background: `${theme.textMuted}15`, 
                  fontSize: '18px', 
                  cursor: 'pointer',
                  color: theme.textMuted,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {'\u2715'}
              </button>
            </div>
            
            {/* Chat Messages - iMessage style */}
            <div style={{ 
              flex: 1, 
              overflow: 'auto', 
              padding: '16px', 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '8px',
              background: darkMode 
                ? 'linear-gradient(180deg, #0f1729 0%, #1B2A4E 100%)'
                : 'linear-gradient(180deg, #f5f5f5 0%, #fff 100%)'
            }}>
              {messages.map((msg, i) => {
                const isUser = msg.speaker === 'user';
                const showTimestamp = i === 0 || 
                  (messages[i-1] && new Date(msg.timestamp).getTime() - new Date(messages[i-1].timestamp).getTime() > 300000);
                
                return (
                  <React.Fragment key={i}>
                    {/* Timestamp separator */}
                    {showTimestamp && msg.timestamp && (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '8px 0',
                        fontSize: '11px',
                        color: theme.textMuted,
                        fontWeight: '500'
                      }}>
                        {formatMessageTime(msg.timestamp)}
                      </div>
                    )}
                    
                    {/* Message row */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '8px', 
                      flexDirection: isUser ? 'row-reverse' : 'row',
                      alignItems: 'flex-end',
                      animation: 'fadeIn 0.2s ease'
                    }}>
                      {/* Avatar */}
                      {isUser ? (
                        user?.photo ? (
                          <img 
                            src={user.photo} 
                            alt="" 
                            style={{ 
                              width: '28px', 
                              height: '28px', 
                              borderRadius: '50%',
                              flexShrink: 0
                            }} 
                          />
                        ) : (
                          <div style={{ 
                            width: '28px', 
                            height: '28px', 
                            borderRadius: '50%', 
                            background: `linear-gradient(135deg, ${GMG.navyBlue}, ${GMG.roseGold})`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: '#fff',
                            fontWeight: '600',
                            flexShrink: 0
                          }}>
                            {user?.name?.[0] || 'U'}
                          </div>
                        )
                      ) : (
                        <img 
                          src={msg.speaker === 'claudette' ? CLAUDETTE_IMG : CHANET_IMG} 
                          alt="" 
                          style={{ 
                            width: '28px', 
                            height: '28px', 
                            borderRadius: '50%',
                            flexShrink: 0
                          }} 
                        />
                      )}
                      
                      {/* Message bubble */}
                      <div style={{ maxWidth: '75%', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {/* Persona name for AI messages */}
                        {!isUser && (
                          <span style={{ 
                            fontSize: '10px', 
                            fontWeight: '600', 
                            color: msg.speaker === 'claudette' ? GMG.navyBlue : GMG.roseGold,
                            marginLeft: '12px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {msg.speaker === 'claudette' ? 'Claudette' : 'Chanet'}
                          </span>
                        )}
                        
                        {/* Bubble */}
                        <div style={{ 
                          padding: '10px 14px', 
                          borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                          fontSize: '15px',
                          lineHeight: '1.4',
                          background: isUser 
                            ? `linear-gradient(135deg, ${GMG.navyBlue} 0%, #2D4373 100%)`
                            : darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                          color: isUser ? '#fff' : theme.text,
                          boxShadow: isUser 
                            ? '0 2px 8px rgba(27, 42, 78, 0.3)'
                            : '0 1px 4px rgba(0,0,0,0.05)'
                        }}>
                          {msg.text}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              
              {/* Banter messages while loading */}
              {chatLoading && banterMessages.map((msg, i) => (
                <div 
                  key={`banter-${i}`} 
                  style={{ 
                    display: 'flex', 
                    gap: '8px', 
                    alignItems: 'flex-end',
                    opacity: msg.isTyping ? 0.8 : 0.9, 
                    animation: 'fadeIn 0.3s ease' 
                  }}
                >
                  <img 
                    src={msg.speaker === 'claudette' ? CLAUDETTE_IMG : CHANET_IMG} 
                    alt="" 
                    style={{ width: '28px', height: '28px', borderRadius: '50%' }} 
                  />
                  <div style={{ maxWidth: '75%' }}>
                    <span style={{ 
                      fontSize: '10px', 
                      fontWeight: '600', 
                      color: msg.speaker === 'claudette' ? GMG.navyBlue : GMG.roseGold,
                      marginLeft: '12px',
                      textTransform: 'uppercase'
                    }}>
                      {msg.speaker === 'claudette' ? 'Claudette' : 'Chanet'}
                    </span>
                    <div style={{ 
                      padding: '10px 14px', 
                      borderRadius: '18px 18px 18px 4px',
                      fontSize: '15px',
                      background: msg.isTyping 
                        ? (darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)')
                        : `linear-gradient(135deg, ${GMG.roseGold}15, ${GMG.navyBlue}10)`,
                      color: theme.text,
                      fontStyle: msg.isTyping ? 'normal' : 'italic'
                    }}>
                      {msg.isTyping ? (
                        <span style={{ display: 'flex', gap: '4px', padding: '4px 0' }}>
                          <span style={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            background: theme.textMuted,
                            animation: 'bounce 1.4s infinite ease-in-out',
                            animationDelay: '0s'
                          }} />
                          <span style={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            background: theme.textMuted,
                            animation: 'bounce 1.4s infinite ease-in-out',
                            animationDelay: '0.2s'
                          }} />
                          <span style={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            background: theme.textMuted,
                            animation: 'bounce 1.4s infinite ease-in-out',
                            animationDelay: '0.4s'
                          }} />
                        </span>
                      ) : msg.text}
                    </div>
                  </div>
                </div>
              ))}
              
              <div ref={messagesEndRef} />
            </div>
            
            {/* Animation keyframes */}
            <style>{`
              @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
              @keyframes bounce { 0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; } 40% { transform: scale(1); opacity: 1; } }
            `}</style>
            
            {/* Input Bar - iMessage style */}
            <div style={{ 
              padding: '12px 16px', 
              paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
              borderTop: `1px solid ${theme.border}`, 
              display: 'flex', 
              gap: '10px',
              alignItems: 'center',
              background: darkMode ? 'rgba(27, 42, 78, 0.5)' : 'rgba(250,249,246,0.95)'
            }}>
              <input 
                type="text" 
                value={chatInput} 
                onChange={e => setChatInput(e.target.value)} 
                onKeyPress={e => e.key === 'Enter' && sendMessage()} 
                placeholder={chatLoading ? "Thinking..." : "Ask about fundraising..."} 
                disabled={chatLoading}
                style={{ 
                  flex: 1, 
                  padding: '12px 18px', 
                  borderRadius: '24px', 
                  border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, 
                  fontSize: '15px', 
                  outline: 'none',
                  background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                  color: theme.text,
                  opacity: chatLoading ? 0.6 : 1,
                  transition: 'all 0.2s ease'
                }} 
              />
              <button 
                onClick={sendMessage} 
                disabled={chatLoading || !chatInput.trim()}
                style={{ 
                  width: '44px', 
                  height: '44px', 
                  borderRadius: '50%', 
                  border: 'none', 
                  background: (chatLoading || !chatInput.trim())
                    ? `${theme.textMuted}30`
                    : `linear-gradient(135deg, ${GMG.navyBlue} 0%, ${GMG.roseGold} 100%)`, 
                  color: '#fff', 
                  cursor: (chatLoading || !chatInput.trim()) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  boxShadow: (chatLoading || !chatInput.trim()) ? 'none' : '0 4px 12px rgba(183, 110, 121, 0.3)'
                }}
              >
                {chatLoading ? (
                  <span style={{ fontSize: '18px' }}>{'\u23F3'}</span>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 2L11 13" />
                    <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* V3.8: Review Mode Modal */}
      {reviewMode && reviewQuestions.length > 0 && (
        <div style={{
          position: 'fixed', inset: 0,
          background: darkMode ? '#0f1729' : '#fff',
          zIndex: 30000,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{ 
            padding: '16px 20px', 
            borderBottom: `1px solid ${theme.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: theme.text, margin: 0 }}>{'\u{1F4DA}'} Review Mode</h2>
              <p style={{ fontSize: '13px', color: theme.textMuted, margin: '4px 0 0 0' }}>
                Question {currentQuestion + 1} of {reviewQuestions.length}
              </p>
            </div>
            <button
              onClick={() => {
                setReviewMode(false);
                setReviewQuestions([]);
                setQuizAnswers([]);
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: `1px solid ${theme.border}`,
                background: 'transparent',
                color: theme.text,
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Exit Review
            </button>
          </div>
          
          {/* Progress Dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '16px' }}>
            {reviewQuestions.map((_, i) => (
              <div 
                key={i} 
                style={{ 
                  width: '10px', 
                  height: '10px', 
                  borderRadius: '50%', 
                  background: i < currentQuestion 
                    ? (quizAnswers[i]?.isCorrect ? '#22c55e' : '#ef4444') 
                    : i === currentQuestion 
                      ? GMG.navyBlue 
                      : `${theme.textMuted}30` 
                }} 
              />
            ))}
          </div>
          
          {/* Question */}
          <div style={{ flex: 1, padding: '20px', overflow: 'auto' }}>
            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
              {/* Context */}
              <p style={{ fontSize: '12px', color: theme.textMuted, marginBottom: '8px' }}>
                From Volume {reviewQuestions[currentQuestion]?.volume}, Day {reviewQuestions[currentQuestion]?.day}
              </p>
              
              {/* Question Text */}
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: theme.text, marginBottom: '24px', lineHeight: 1.5 }}>
                {reviewQuestions[currentQuestion]?.question}
              </h3>
              
              {/* Answer Options */}
              {(() => {
                const current = reviewQuestions[currentQuestion];
                const quiz = getQuiz(current?.volume, current?.day);
                const question = quiz?.questions?.[current?.questionIndex];
                const answered = quizAnswers[currentQuestion];
                
                if (!question) return null;
                
                return question.options.map((opt, idx) => {
                  const isSelected = answered?.selected === opt;
                  const isCorrect = opt === current.correctAnswer;
                  const showResult = !!answered;
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => !answered && answerReviewQuestion(opt)}
                      disabled={!!answered}
                      style={{
                        width: '100%',
                        padding: '16px',
                        marginBottom: '12px',
                        borderRadius: '12px',
                        border: `2px solid ${
                          showResult 
                            ? (isCorrect ? '#22c55e' : isSelected ? '#ef4444' : theme.border)
                            : theme.border
                        }`,
                        background: showResult
                          ? (isCorrect ? '#22c55e15' : isSelected ? '#ef444415' : theme.card)
                          : theme.card,
                        color: theme.text,
                        fontSize: '15px',
                        textAlign: 'left',
                        cursor: answered ? 'default' : 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {opt}
                      {showResult && isCorrect && <span style={{ marginLeft: '8px' }}>{'\u{2713}'}</span>}
                      {showResult && isSelected && !isCorrect && <span style={{ marginLeft: '8px' }}>{'\u{2717}'}</span>}
                    </button>
                  );
                });
              })()}
              
              {/* Wrong count indicator */}
              <p style={{ fontSize: '12px', color: theme.textMuted, textAlign: 'center', marginTop: '20px' }}>
                You've gotten this wrong {reviewQuestions[currentQuestion]?.wrongCount || 0} time(s)
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Certificate Modal */}
      {certificateModal && user && (
        <CertificateModal 
          user={user} 
          volume={certificateModal.volume} 
          score={certificateModal.score} 
          onClose={() => setCertificateModal(null)} 
        />
      )}

      {/* V2.4: Badge Celebration Modal */}
      {showBadgeModal && (
        <BadgeModal 
          badge={showBadgeModal}
          onClose={() => setShowBadgeModal(null)}
        />
      )}

      {/* User Profile Modal */}
      {viewingUser && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000, padding: '20px'
        }} onClick={() => setViewingUser(null)}>
          <div style={{
            background: theme.card, borderRadius: '20px', padding: '24px',
            maxWidth: '400px', width: '100%', position: 'relative',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setViewingUser(null)} style={{
              position: 'absolute', top: '12px', right: '12px',
              background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: theme.textMuted
            }}>{`\u{2715}`}</button>
            
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              {(viewingUser.photoURL || viewingUser.photo) ? (
                <img src={viewingUser.photoURL || viewingUser.photo} alt={viewingUser.name} style={{
                  width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover',
                  border: `3px solid ${GMG.roseGold}`, marginBottom: '12px'
                }} />
              ) : (
                <div style={{
                  width: '80px', height: '80px', borderRadius: '50%', 
                  background: `linear-gradient(135deg, ${GMG.roseGold}, ${GMG.navyBlue})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '32px', margin: '0 auto 12px'
                }}>{COHORT[viewingUser.email]?.avatar || '\u{1F464}'}</div>
              )}
              <h3 style={{ fontSize: '20px', fontWeight: '700', color: theme.text, margin: '0 0 4px 0' }}>{viewingUser.name}</h3>
              <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0 }}>{viewingUser.email}</p>
              {viewingUser.track === 'fast' && (
                <span style={{ display: 'inline-block', marginTop: '8px', padding: '4px 12px', borderRadius: '12px', background: `linear-gradient(90deg, ${GMG.gold}, #f59e0b)`, color: '#fff', fontSize: '11px', fontWeight: '600' }}>{'\u{26A1}'} FAST TRACK</span>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
              {[1, 2, 3].map(vol => {
                const completed = viewingUser.completedLessons?.[vol]?.length || 0;
                const volConfig = TRACK_CONFIG[viewingUser.track || 'standard'];
                const total = vol === 1 ? volConfig.v1 : vol === 2 ? volConfig.v2 : volConfig.v3;
                const pct = Math.round((completed / total) * 100);
                return (
                  <div key={vol} style={{ textAlign: 'center', padding: '12px', borderRadius: '12px', background: theme.bg }}>
                    <p style={{ fontSize: '20px', fontWeight: '700', color: vol === 1 ? GMG.roseGold : vol === 2 ? GMG.navyBlue : GMG.bronze, margin: 0 }}>{pct}%</p>
                    <p style={{ fontSize: '10px', color: theme.textMuted, margin: '4px 0 0 0' }}>Volume {vol}</p>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', borderRadius: '12px', background: theme.bg }}>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '18px', fontWeight: '700', color: GMG.gold, margin: 0 }}>{'\u{1F525}'} {viewingUser.streak || 0}</p>
                <p style={{ fontSize: '10px', color: theme.textMuted, margin: 0 }}>Day Streak</p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '18px', fontWeight: '700', color: theme.accent, margin: 0 }}>
                  {(viewingUser.completedLessons?.[1]?.length || 0) + (viewingUser.completedLessons?.[2]?.length || 0) + (viewingUser.completedLessons?.[3]?.length || 0)}
                </p>
                <p style={{ fontSize: '10px', color: theme.textMuted, margin: 0 }}>Lessons Done</p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '18px', fontWeight: '700', color: GMG.success, margin: 0 }}>
                  {Object.keys(viewingUser.quizScores || {}).length}
                </p>
                <p style={{ fontSize: '10px', color: theme.textMuted, margin: 0 }}>Quizzes</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Offline Indicator */}
      <OfflineIndicator />

      {/* V3.5: Profile Edit Modal */}
      {editingProfile && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 20000, padding: '20px'
        }} onClick={() => setEditingProfile(false)}>
          <div style={{
            background: theme.card, borderRadius: '24px', padding: '28px',
            maxWidth: '380px', width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: theme.text, margin: '0 0 20px 0', textAlign: 'center' }}>{'\u{270F}'} Edit Profile</h3>
            
            {/* Photo Preview with Upload Spinner */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                {editPhotoUrl ? (
                  <img src={editPhotoUrl} alt="Preview" style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: `3px solid ${GMG.roseGold}` }} onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: `linear-gradient(135deg, ${GMG.roseGold}, ${GMG.navyBlue})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px', color: '#fff', margin: '0 auto' }}>
                    {editName?.charAt(0)?.toUpperCase() || '\u{1F464}'}
                  </div>
                )}
                {uploadingPhoto && (
                  <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: '30px', height: '30px', border: '3px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  </div>
                )}
              </div>
              
              {/* Upload Button */}
              <div style={{ marginTop: '12px' }}>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handlePhotoFileSelect}
                  id="photo-upload"
                  style={{ display: 'none' }}
                  disabled={uploadingPhoto}
                />
                <label 
                  htmlFor="photo-upload"
                  style={{ 
                    display: 'inline-block',
                    padding: '10px 20px', 
                    borderRadius: '10px', 
                    border: 'none', 
                    background: uploadingPhoto ? `${theme.textMuted}50` : GMG.navyBlue, 
                    color: '#fff', 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    cursor: uploadingPhoto ? 'not-allowed' : 'pointer'
                  }}
                >
                  {uploadingPhoto ? 'Uploading...' : '\u{1F4F7} Choose Photo'}
                </label>
                {editPhotoUrl && !uploadingPhoto && (
                  <button 
                    onClick={() => setEditPhotoUrl('')}
                    style={{ marginLeft: '8px', padding: '10px 16px', borderRadius: '10px', border: `1px solid ${theme.border}`, background: 'transparent', color: theme.textMuted, fontSize: '14px', cursor: 'pointer' }}
                  >
                    Remove
                  </button>
                )}
              </div>
              <p style={{ fontSize: '11px', color: theme.textMuted, margin: '10px 0 0 0' }}>
                Tap to select a photo from your device. Leave blank for initials.
              </p>
            </div>
            
            {/* Name Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: theme.textMuted, display: 'block', marginBottom: '6px' }}>DISPLAY NAME</label>
              <input 
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Your name"
                style={{ 
                  width: '100%', 
                  padding: '14px 16px', 
                  borderRadius: '12px', 
                  border: `1px solid ${theme.border}`, 
                  fontSize: '16px',
                  background: theme.bg,
                  color: theme.text,
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                onClick={() => setEditingProfile(false)} 
                disabled={uploadingPhoto}
                style={{ flex: 1, padding: '14px', borderRadius: '12px', border: `1px solid ${theme.border}`, background: 'transparent', color: theme.text, fontSize: '15px', cursor: uploadingPhoto ? 'not-allowed' : 'pointer', opacity: uploadingPhoto ? 0.5 : 1 }}
              >
                Cancel
              </button>
              <button 
                onClick={saveProfileChanges}
                disabled={uploadingPhoto}
                style={{ flex: 1, padding: '14px', borderRadius: '12px', border: 'none', background: uploadingPhoto ? `${theme.textMuted}50` : `linear-gradient(135deg, ${GMG.roseGold}, ${GMG.bronze})`, color: '#fff', fontSize: '15px', fontWeight: '600', cursor: uploadingPhoto ? 'not-allowed' : 'pointer' }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* V3.3: Update Available Modal */}
      {updateAvailable && !updateDismissed && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 20000, padding: '20px'
        }} onClick={dismissUpdate}>
          <div style={{
            background: theme.card, borderRadius: '24px', padding: '32px',
            maxWidth: '360px', width: '100%', textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>{'\u{1F680}'}</div>
            <h3 style={{ fontSize: '22px', fontWeight: '700', color: theme.text, margin: '0 0 8px 0' }}>Update Available!</h3>
            <p style={{ fontSize: '15px', color: theme.textMuted, margin: '0 0 20px 0' }}>Version {updateAvailable.version} is ready to install</p>
            
            {updateAvailable.changelog && (
              <div style={{ background: theme.bg, borderRadius: '12px', padding: '14px', marginBottom: '20px', textAlign: 'left' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: theme.textMuted, margin: '0 0 8px 0' }}>WHAT'S NEW:</p>
                <p style={{ fontSize: '13px', color: theme.text, margin: 0, whiteSpace: 'pre-line' }}>{updateAvailable.changelog}</p>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={dismissUpdate} style={{ flex: 1, padding: '14px', borderRadius: '12px', border: `1px solid ${theme.border}`, background: 'transparent', color: theme.text, fontSize: '15px', cursor: 'pointer' }}>
                Later
              </button>
              <button onClick={applyUpdate} style={{ flex: 1, padding: '14px', borderRadius: '12px', border: 'none', background: `linear-gradient(135deg, ${GMG.navyBlue}, ${GMG.bronze})`, color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
                Update Now
              </button>
            </div>
            
            <p style={{ fontSize: '11px', color: theme.textMuted, marginTop: '16px' }}>Current: v{APP_VERSION}</p>
          </div>
        </div>
      )}

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Update Available Banner */}
      {updateAvailable && <UpdateBanner onRefresh={doRefresh} />}
    </div>
  );
}
